import React, { Fragment, Suspense, lazy, useState, useEffect, useLayoutEffect, useRef, useId, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LIVE_TV_CATEGORIES, buildLiveTvHaystack, matchesLiveTvCategory } from './livetvCategories';
import {
  focusZflixNavRail,
  focusZflixNavHome,
  focusZflixContentPrimary,
  focusZflixLastContent,
  focusZflixDiscoverGrid,
  focusZflixRomsPrimary,
  focusZflixGamesGrid,
  setZflixPostPlayerFocusTarget,
  focusZflixResumeCardAfterPlayer,
} from './tvSpatialNav';
import LiveTvView from './LiveTvView';
import { liveSaverOn, setLiveSaver } from './liveSaver';
import { TvSendReceiver, TvSendPicker, TvSearchSend, TvRemote, tvSendEligible, useTvAutoUpdate } from './TvSend';
import { UpNextOverlay, fetchNextEpisode, noteViewerActive } from './UpNext';
import { useMyList, setInMyList, myListEligible, myListKey, reloadMyList } from './MyList';
import { OfflineButton, registerOfflinePage, offlineSupported } from './Offline';
import { useTrailer, TrailerPlayer, BackdropTrailer, isTvApp, RoundCorners } from './Trailer';
import RecordingsView from './Recordings';
import BooksView from './BooksView';
import TvSelect from './TvSelect';
import { usePhoneBack } from './phoneBack';
import { startPerfReports } from './perfReport';
import { applyZflixDesktopBrowserDensity, readZflixForceTvUi, setZflixForceTvUi } from './zflixDensity';
import {
  readHomeShelfCache,
  writeHomeShelfCache,
  homeShelfHasContent,
  readMoviesGridCache,
  writeMoviesGridCache,
  readMoviesBrowseCache,
  writeMoviesBrowseCache,
  readShowsGridCache,
  writeShowsGridCache,
  readShowsBrowseCache,
  writeShowsBrowseCache,
  invalidateMoviesBrowseResume,
  invalidateShowsBrowseResume,
  invalidateHomeResume,
  readMusicGridCache,
  writeMusicGridCache,
  clearZflixShelfCaches,
} from './zflixShelfCache';
import { fetchZflixList } from './zflixApi';
import {
  clearZflixJellyfinContextCache,
  fetchZflixJellyfinContext,
  peekZflixJellyfinContext,
  prefetchZflixJellyfinContext,
} from './zflixJellyfinContext';
import {
  SplashScreen,
  SplashPreviewPicker,
  isSplashPreviewQuery,
  initialSplashPhase,
  clearSplashShownThisSession,
  readSplashVariant,
  writeSplashVariant,
  readSplashSoundEnabled,
  writeSplashSoundEnabled,
  SPLASH_VARIANT_OPTIONS,
  unlockSplashAudioFromGesture,
} from './splash';

startPerfReports();

const GamesView = lazy(() => import('./GamesView'));
const RadioView = lazy(() => import('./RadioView'));
const SportsView = lazy(() => import('./SportsView'));

/** hls.js is about half the app, so it arrives just after the first screen instead of holding it up. */
let Hls = null;
let hlsLoading = null;
const loadHls = () => {
  if (!hlsLoading) {
    hlsLoading = import('hls.js').then(
      (m) => {
        Hls = m.default;
      },
      () => {
        hlsLoading = null;
      },
    );
  }
  return hlsLoading;
};
if (typeof window !== 'undefined') setTimeout(loadHls, 1500);

function scheduleFocusAfterPlayerClose() {
  queueMicrotask(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!focusZflixResumeCardAfterPlayer()) focusZflixNavRail();
      });
    });
  });
}

function parseJellyfinPlaySessionFromUrl(url) {
  if (typeof url !== 'string') return null;
  const m = url.match(/(?:[?&])PlaySessionId=([^&]+)/i);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Phones: let the screen turn freely. A home-screen copy of Zflix installed while the site said "landscape only" keeps
 * that until Chrome refreshes it (up to days), so the page asks for "any" itself. unlock() would fall back to that old
 * landscape setting, so leaving a sideways-only screen asks for "any" again instead.
 */
function zflixIsPhoneScreen() {
  if (typeof document === 'undefined') return false;
  const cl = document.documentElement.classList;
  if (cl.contains('zflix-tv-apk-density') || cl.contains('zflix-android-webview')) return false;
  try {
    if (window.localStorage.getItem('zflixForceTvUi') === '1') return false;
  } catch (_) {}
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}
function zflixLetScreenTurn() {
  if (!zflixIsPhoneScreen()) return;
  try {
    const p = window.screen?.orientation?.lock?.('any');
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (_) {}
}

/** Subtitle size and style, per profile on each device (Zach, 2026-11-03: they were on the big side). */
const SUB_SIZES = { smaller: { label: 'Smaller', pct: 70 }, small: { label: 'Small', pct: 85 }, normal: { label: 'Normal', pct: 100 }, large: { label: 'Large', pct: 125 } };
const subLookKey = () => `zflix_sub_look_${readZflixProfileCookie() || 'default'}`;
function readSubLook() {
  try {
    const v = JSON.parse(window.localStorage.getItem(subLookKey()) || 'null');
    if (v && SUB_SIZES[v.size]) return { size: v.size, style: v.style === 'outline' ? 'outline' : 'box' };
  } catch (_) {}
  return { size: 'normal', style: 'box' };
}
function subLookCss(look) {
  const pct = (SUB_SIZES[look.size] || SUB_SIZES.normal).pct;
  const style = look.style === 'outline'
    ? 'background-color:transparent;text-shadow:0 0 4px #000,0 0 3px #000,1px 1px 2px #000,-1px -1px 2px #000;'
    : 'background-color:rgba(0,0,0,0.72);';
  return `video::cue{font-size:${pct}%;color:#fff;${style}}`;
}

/** The Fire TV app lets video start with sound on its own, so it never needs the muted "tap for sound" start. */
function zflixSoundAlwaysAllowed() {
  return window.__zflixTvClient === true || /ZFLIX-TV/.test(navigator.userAgent || '');
}

function ZFlixLogo({ size = 'normal' }) {
 const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
 const gradId = `zflix-zg-${uid}`;
 const filtId = `zflix-zf-${uid}`;
 /** 'inline' = one compact line (mark + wordmark side by side) for tight spots like the side-menu footer. */
 const inline = size === 'inline';
 const small = size === 'small' || inline;
 const dim = inline ? 18 : small ? 32 : 48;
 const blur = small ? 1 : 2;
 return (
   <div style={{ display: 'flex', flexDirection: inline ? 'row' : 'column', alignItems: 'center', gap: inline ? 6 : small ? 2 : 4 }}>
     <svg width={dim} height={dim} viewBox="0 0 48 48" fill="none" aria-hidden>
       <defs>
         <linearGradient id={gradId} x1="10" y1="8" x2="38" y2="40" gradientUnits="userSpaceOnUse">
           <stop offset="0%" stopColor="#ffeef0" />
           <stop offset="35%" stopColor="#ff6b7a" />
           <stop offset="100%" stopColor="#e50914" />
         </linearGradient>
         <filter id={filtId} width="130%" height="130%" x="-15%" y="-15%">
           <feDropShadow dx="0" dy="1" stdDeviation={blur} floodColor="#000" floodOpacity="0.5" />
         </filter>
       </defs>
       {/* Lift tile off dark headers (--bg2 / portrait chrome); previous #150000 read as an empty square */}
       <rect width="48" height="48" rx="8" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
       <polygon points="6,6 42,6 42,17 20,31 42,31 42,42 6,42 6,31 28,17 6,17" fill={`url(#${gradId})`} filter={`url(#${filtId})`} />
     </svg>
     <span style={{ fontSize: small ? 10 : 14, fontWeight: 900, color: '#fff', letterSpacing: small ? 3 : 4, fontFamily: 'Arial Black, sans-serif', textShadow: '0 1px 2px rgba(0,0,0,0.85)' }}>ZFLIX</span>
   </div>
 );
}

const Icon = ({ name, size = 20 }) => {
 const icons = {
   home: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>,
   movie: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/></svg>,
   tv: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/></svg>,
   music: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>,
   radio: (
     <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
       <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
     </svg>
   ),
   book: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4zm0 15l4.5-3L15 19v-9H6v9z"/></svg>,
   podcast: (
     <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
       <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
     </svg>
   ),
   photo: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>,
   record: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 4a8 8 0 100 16 8 8 0 000-16zm0 4.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7z"/></svg>,
   live: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>,
   search: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>,
   discover: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>,
   play: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>,
   pause: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>,
   plus: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>,
   check: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>,
   close: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>,
   back: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>,
   star: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>,
   fullscreen: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>,
   info: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>,
   next: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z"/></svg>,
   prev: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>,
   filters: (
     <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
       <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16l4-8 4 8h-8zm2-14v2h8V7h-8zM7 9v6h2V9H7zm4 0h10v2H11V9zM3 13v2h6v-2H3z" />
     </svg>
   ),
   options: (
     <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
       <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
     </svg>
   ),
   gamepad: (
     <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
       <path d="M6 9H4v2H2v2h2v2h2v-2h2V9H6zm14 0h-2v2h-2v2h2v2h2v-2h2V9h-2zM12 17.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z" />
       <path d="M15.5 4h-7C6.46 4 4 6.46 4 9.5v5C4 17.54 6.46 20 8.5 20h7c2.04 0 4.5-2.46 4.5-5.5v-5C20 6.46 17.54 4 15.5 4zm-7 14C7.57 18 6 16.43 6 14.5v-5C6 7.57 7.57 6 9.5 6h5c1.93 0 3.5 1.57 3.5 3.5v5c0 1.93-1.57 3.5-3.5 3.5h-5z" opacity="0.35" />
     </svg>
   ),
 };
 return icons[name] || null;
};

const Btn = ({ children, variant = 'primary', onClick, disabled, type = 'button', style = {}, title, ...rest }) => (
 <button type={type} title={title} onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', border: 'none', opacity: disabled ? 0.6 : 1, transition: 'all 0.15s', background: variant === 'primary' ? 'var(--accent)' : variant === 'success' ? 'var(--green)' : 'rgba(255,255,255,0.12)', color: 'var(--text)', ...style }} {...rest}>{children}</button>
);

const Tag = ({ children }) => (
 <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: 'var(--bg3)', color: 'var(--text2)', marginRight: 6, marginBottom: 4 }}>{children}</span>
);

const navItems = [
 { id: 'home', label: 'Home', icon: 'home' },
 { id: 'movies', label: 'Movies', icon: 'movie' },
 { id: 'shows', label: 'TV Shows', icon: 'tv' },
 { id: 'livetv', label: 'Live TV', icon: 'live' },
 { id: 'sports', label: 'Sports', icon: 'record' },
 { id: 'recordings', label: 'Recordings', icon: 'record' },
 { id: 'music', label: 'Music', icon: 'music' },
 { id: 'radio', label: 'Radio', icon: 'radio' },
 { id: 'podcasts', label: 'Podcasts', icon: 'podcast' },
 { id: 'audiobooks', label: 'Audiobooks', icon: 'book' },
 /** The reader is for phones and tablets, so TVs never list it. */
 ...(/\bTV\b|AFT|GoogleTV|SMART-TV/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '') ? [] : [{ id: 'books', label: 'Books', icon: 'book' }]),
 { id: 'photos', label: 'Photos', icon: 'photo' },
 { id: 'games', label: 'Games', icon: 'gamepad' },
 { id: 'discover', label: 'Discover', icon: 'discover' },
 /** Shown in bottom nav on small screens; desktop entry is in the sidebar footer (with Sign out). */
 { id: 'filters', label: 'Filters', icon: 'filters' },
];

let zflixPlaybackCapsPromise = null;

/**
 * What this device can decode, sent with library playback so Jellyfin copies what plays natively and
 * transcodes the rest to H.264/AAC. HEVC/AV1 only count when hardware-decoded (powerEfficient) —
 * Fire TV / phone WebViews often "support" AV1 via software dav1d, which stutters.
 */
function getZflixPlaybackCaps() {
  if (zflixPlaybackCapsPromise) return zflixPlaybackCapsPromise;
  zflixPlaybackCapsPromise = (async () => {
    const MS = window.MediaSource || window.ManagedMediaSource;
    const mseOk = (type) => {
      try {
        return !!MS && MS.isTypeSupported(type);
      } catch (_) {
        return false;
      }
    };
    const hwVideoOk = async (contentType) => {
      if (!mseOk(contentType)) return false;
      try {
        if (!navigator.mediaCapabilities?.decodingInfo) return false;
        const r = await navigator.mediaCapabilities.decodingInfo({
          type: 'media-source',
          video: { contentType, width: 1920, height: 1080, bitrate: 8_000_000, framerate: 24 },
        });
        return !!(r.supported && r.powerEfficient);
      } catch (_) {
        return false;
      }
    };
    const videoCodecs = ['h264'];
    if (await hwVideoOk('video/mp4; codecs="hvc1.1.6.L120.90"')) videoCodecs.push('hevc');
    if (await hwVideoOk('video/mp4; codecs="av01.0.08M.08"')) videoCodecs.push('av1');
    const audioCodecs = ['aac', 'mp3'];
    if (mseOk('audio/mp4; codecs="ac-3"')) audioCodecs.push('ac3');
    if (mseOk('audio/mp4; codecs="ec-3"')) audioCodecs.push('eac3');
    if (mseOk('audio/mp4; codecs="opus"')) audioCodecs.push('opus');
    return { videoCodecs, audioCodecs, saveData: !!navigator.connection?.saveData };
  })().catch(() => ({ videoCodecs: ['h264'], audioCodecs: ['aac', 'mp3'], saveData: false }));
  return zflixPlaybackCapsPromise;
}

/** Notify Movies/Shows/Home to refetch Jellyfin resume shelves. */
function bumpZflixResumeStale() {
  try {
    invalidateMoviesBrowseResume();
    invalidateShowsBrowseResume();
    invalidateHomeResume();
    window.dispatchEvent(new CustomEvent('zflix-resume-data-stale'));
  } catch (_) {}
}

let zflixResumeStaleTimer = null;
let zflixResumeStaleRetryTimer = null;

/** Defer resume refetch until Jellyfin persists session progress (embed + HLS stop are async). */
function scheduleZflixResumeStale({ delayMs = 900, retryMs = 2800 } = {}) {
  try {
    if (zflixResumeStaleTimer) clearTimeout(zflixResumeStaleTimer);
    if (zflixResumeStaleRetryTimer) clearTimeout(zflixResumeStaleRetryTimer);
    zflixResumeStaleTimer = setTimeout(() => {
      zflixResumeStaleTimer = null;
      bumpZflixResumeStale();
    }, delayMs);
    if (retryMs > delayMs) {
      zflixResumeStaleRetryTimer = setTimeout(() => {
        zflixResumeStaleRetryTimer = null;
        bumpZflixResumeStale();
      }, retryMs);
    }
  } catch (_) {
    bumpZflixResumeStale();
  }
}

function focusZflixModalPrimary(dialogEl) {
  if (!(dialogEl instanceof HTMLElement)) return;
  const pull = () => {
    if (!dialogEl.isConnected) return;
    const primary = dialogEl.querySelector('[data-zflix-modal-primary-action]:not([disabled])');
    if (primary instanceof HTMLElement) {
      primary.focus({ preventScroll: true });
      return;
    }
    /** No primary action (e.g. already requested): still pull focus into the sheet so D-pad isn't stuck on the grid behind it. */
    if (dialogEl.contains(document.activeElement) && document.activeElement !== dialogEl) return;
    const buttons = [...dialogEl.querySelectorAll('button:not([disabled]), a[href]')].filter((el) => el.offsetParent !== null);
    /** First focusable is usually the close (X) — prefer the next real action when there is one. */
    const fallback = buttons[1] || buttons[0] || dialogEl;
    fallback.focus({ preventScroll: true });
  };
  queueMicrotask(pull);
  /** TV WebViews sometimes hand focus back to the tile that opened the sheet after the click settles — re-check once. */
  setTimeout(() => {
    if (dialogEl.isConnected && !dialogEl.contains(document.activeElement)) pull();
  }, 200);
}

const mobileBottomNavDockIds = new Set(['home', 'movies', 'shows', 'livetv']);

/** Refetch continue-watching when playback ends, nav returns, or tab regains focus. */
function useZflixResumeRefresh() {
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const bump = () => setNonce((n) => n + 1);
    window.addEventListener('zflix-resume-data-stale', bump);
    /** Opening the app fires focus and visibility together, right after the page's first load: that is one refresh, not three. */
    let lastLook = Date.now();
    const look = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastLook < 15000) return;
      lastLook = Date.now();
      bump();
    };
    document.addEventListener('visibilitychange', look);
    window.addEventListener('focus', look);
    return () => {
      window.removeEventListener('zflix-resume-data-stale', bump);
      document.removeEventListener('visibilitychange', look);
      window.removeEventListener('focus', look);
    };
  }, []);
  return nonce;
}

/** Tracks html.zflix-tv-apk-density (MutationObserver — matches index.js applyZflixDesktopBrowserDensity). */
function useZflixTvApkDensity() {
  const [tvApk, setTvApk] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('zflix-tv-apk-density'),
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setTvApk(el.classList.contains('zflix-tv-apk-density'));
    const mo = new MutationObserver(sync);
    mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', sync);
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, []);
  return tvApk;
}

/** Remote Back / Escape variants (Samsung Tizen 10009, WebOS 461, etc.) */
function isZflixTvBackKey(e) {
  if (e.key === 'Escape' || e.key === 'BrowserBack' || e.key === 'XF86Back' || e.key === 'Back') return true;
  if (e.keyCode === 27 || e.keyCode === 10009 || e.keyCode === 461 || e.keyCode === 4) return true;
  return false;
}

/** Focus is in the main browsing region (cards/rows) — Back should move to sidebar / bottom nav. */
function shouldMoveFocusFromMainContentToNav(active) {
  if (!(active instanceof HTMLElement)) return false;
  if (active.closest('[role="dialog"]')) return false;
  if (active.closest('[data-zflix-player]')) return false;
  if (active.closest('[data-zflix-game-player]')) return false;
  const main = document.querySelector('.main-content');
  if (!main || !main.contains(active)) return false;
  if (
    active.tagName === 'INPUT' ||
    active.tagName === 'TEXTAREA' ||
    active.tagName === 'SELECT' ||
    active.isContentEditable
  )
    return false;
  return true;
}

/* ---------------------------------------------------------------- Profiles ---------------------------------------------------------------- */

/** Avatar choices (ids are stored by the backend; the look lives here). */
const PROFILE_AVATARS = {
  fox: { glyph: '🦊', bg: 'linear-gradient(135deg,#f97316,#c2410c)' },
  robot: { glyph: '🤖', bg: 'linear-gradient(135deg,#38bdf8,#1d4ed8)' },
  /** Glyphs are limited to emoji that older TVs have (Android 9): no joined sequences, nothing newer than 2018. */
  astronaut: { glyph: '🚀', bg: 'linear-gradient(135deg,#a78bfa,#5b21b6)' },
  cat: { glyph: '🐱', bg: 'linear-gradient(135deg,#fbbf24,#b45309)' },
  dog: { glyph: '🐶', bg: 'linear-gradient(135deg,#34d399,#047857)' },
  panda: { glyph: '🐼', bg: 'linear-gradient(135deg,#94a3b8,#334155)' },
  unicorn: { glyph: '🦄', bg: 'linear-gradient(135deg,#f472b6,#9d174d)' },
  ninja: { glyph: '🐲', bg: 'linear-gradient(135deg,#52525b,#18181b)' },
  alien: { glyph: '👽', bg: 'linear-gradient(135deg,#4ade80,#166534)' },
  lion: { glyph: '🦁', bg: 'linear-gradient(135deg,#facc15,#a16207)' },
  owl: { glyph: '🦉', bg: 'linear-gradient(135deg,#c084fc,#6b21a8)' },
  ghost: { glyph: '👻', bg: 'linear-gradient(135deg,#f87171,#991b1b)' },
};

const AVATAR_BACKDROPS = ['linear-gradient(145deg,#ff6b6b,#7c2d92)', 'linear-gradient(145deg,#38bdf8,#4338ca)', 'linear-gradient(145deg,#34d399,#0f766e)', 'linear-gradient(145deg,#fbbf24,#c2410c)', 'linear-gradient(145deg,#f472b6,#6d28d9)', 'linear-gradient(145deg,#a3e635,#15803d)'];

function ProfileAvatar({ avatar, size = 96 }) {
  /** Painted portraits are full pictures, so they fill the circle. Older choices below still draw for anyone who kept one. */
  if (/^pt-[a-z]+$/.test(String(avatar))) {
    return (
      <span aria-hidden style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, display: 'inline-block', background: 'var(--bg3)', boxShadow: '0 0 0 2px rgba(255,255,255,0.3), 0 4px 14px rgba(0,0,0,0.5)' }}>
        <img src={`/avatars/${avatar}.jpg`} alt="" loading="lazy" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
      </span>
    );
  }
  if (/^ch\d+$/.test(String(avatar))) {
    return (
      <span aria-hidden style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, display: 'inline-block', background: AVATAR_BACKDROPS[parseInt(String(avatar).slice(2), 10) % AVATAR_BACKDROPS.length], boxShadow: '0 0 0 2px rgba(255,255,255,0.22)' }}>
        <img src={`/avatars/${avatar}.svg`} alt="" loading="lazy" style={{ width: '100%', height: '100%', display: 'block' }} />
      </span>
    );
  }
  const a = PROFILE_AVATARS[avatar] || PROFILE_AVATARS.fox;
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(6, Math.round(size * 0.14)),
        background: a.bg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.56),
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {a.glyph}
    </span>
  );
}

function readZflixProfileCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)zflix_profile=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) {
    return null;
  }
}

const profileTileStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  padding: 10,
  borderRadius: 12,
  background: 'transparent',
  border: '2px solid transparent',
  color: 'var(--text)',
  cursor: 'pointer',
  minWidth: 120,
};

/** "Who's watching?" — full screen. `onClose` is only given when switching (a profile is already active). */
function ProfilePicker({ profiles, max, activeId, onPick, onAdd, onClose, busy }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) focusZflixModalPrimary(ref.current);
  }, [profiles.length]);
  const size = typeof window !== 'undefined' && window.innerWidth < 700 ? 84 : 120;
  return (
    <div
      data-zflix-modal-root
      data-zflix-profile-picker
      style={{ position: 'fixed', inset: 0, zIndex: 3000, overflow: 'hidden', background: 'radial-gradient(900px 560px at 18% 12%, rgba(229,9,20,0.38), transparent 62%), radial-gradient(860px 560px at 86% 88%, rgba(124,77,255,0.34), transparent 62%), radial-gradient(700px 480px at 70% 8%, rgba(0,163,163,0.22), transparent 60%), var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <style>{`
.zflix-profile-tile { transition: transform .18s ease, background .18s ease, box-shadow .18s ease, border-color .18s ease; }
.zflix-profile-tile:hover, .zflix-profile-tile:focus, .zflix-profile-tile:focus-visible { transform: translateY(-6px) scale(1.07); background: rgba(255,255,255,0.12) !important; border-color: #fff !important; box-shadow: 0 16px 36px rgba(0,0,0,0.55); outline: none; }
@keyframes zflix-picker-in { from { opacity: 0; transform: translateY(14px) scale(.96); } to { opacity: 1; transform: none; } }
`}</style>
      <div ref={ref} role="dialog" aria-modal="true" aria-label="Who's watching?" tabIndex={-1} style={{ outline: 'none', textAlign: 'center', maxWidth: 900 }}>
        <h1 style={{ fontSize: 'clamp(26px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 6, background: 'linear-gradient(90deg, #fff, #ffb3b8 55%, #c7b5ff)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Who's watching?</h1>
        <p style={{ color: 'var(--text2)', fontSize: 14, margin: '0 0 28px' }}>Pick your profile to jump back in</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, justifyContent: 'center' }}>
          {profiles.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className="zflix-profile-tile"
              disabled={busy}
              {...(p.id === activeId || (!activeId && p.original) ? { 'data-zflix-modal-primary-action': '' } : {})}
              onClick={() => onPick(p)}
              style={{ ...profileTileStyle, padding: 16, borderRadius: 18, background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.1)', animation: `zflix-picker-in .35s ease ${i * 0.06}s both` }}
            >
              <ProfileAvatar avatar={p.avatar} size={size} />
              <span style={{ fontSize: 16, fontWeight: 700, maxWidth: size + 20, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
            </button>
          ))}
          {profiles.length < max && (
            <button type="button" className="zflix-profile-tile" disabled={busy} aria-label="Add profile" onClick={onAdd} style={profileTileStyle}>
              <span
                aria-hidden
                style={{ width: size, height: size, borderRadius: Math.round(size * 0.14), border: '2px dashed var(--border)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}
              >
                <Icon name="plus" size={Math.round(size * 0.4)} />
              </span>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)' }}>Add profile</span>
            </button>
          )}
        </div>
        {onClose ? (
          <div style={{ marginTop: 28 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Create a profile, or edit one (name + avatar; the profile's history is tied to its id, never to its name). */
function ProfileEditor({ profile, avatars, usedAvatars, onSave, onDelete, onClose }) {
  const creating = !profile;
  const ref = useRef(null);
  const [name, setName] = useState(profile?.name || '');
  const [avatar, setAvatar] = useState(
    profile?.avatar || (avatars || []).find((a) => !(usedAvatars || []).includes(a)) || (avatars || [])[0] || 'fox',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (ref.current) focusZflixModalPrimary(ref.current);
  }, []);

  const save = async () => {
    const n = name.trim();
    if (!n) {
      setErr('Give the profile a name');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await onSave({ name: n, avatar });
    } catch (e) {
      setErr(e.message || 'Could not save');
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr('');
    try {
      await onDelete();
    } catch (e) {
      setErr(e.message || 'Could not delete');
      setBusy(false);
    }
  };

  return (
    <div
      data-zflix-modal-root
      data-zflix-profile-editor
      style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={busy ? undefined : onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={creating ? 'Add profile' : 'Edit profile'}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: '22px 22px 18px', width: '100%', maxWidth: 560, maxHeight: '94vh', overflowY: 'auto', outline: 'none' }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{creating ? 'Add profile' : 'Edit profile'}</h2>
        {confirmDelete ? (
          <div>
            <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 18 }}>
              Delete <strong style={{ color: 'var(--text)' }}>{profile.name}</strong>? Its watch history, Continue Watching, listening progress and Live TV history are erased for good.
            </p>
            {err ? <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{err}</div> : null}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn data-zflix-modal-primary-action variant="ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep profile</Btn>
              <Btn variant="primary" disabled={busy} onClick={remove}>{busy ? 'Deleting…' : 'Delete profile'}</Btn>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <ProfileAvatar avatar={avatar} size={72} />
              <input
                aria-label="Profile name"
                placeholder="Name"
                value={name}
                maxLength={20}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save();
                }}
                style={{ flex: 1, minWidth: 0, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 16, outline: 'none' }}
              />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>Avatar</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 8, marginBottom: 18 }}>
              {(avatars || []).map((a) => (
                <button
                  key={a}
                  type="button"
                  className="zflix-profile-avatar-choice"
                  aria-label={`Avatar ${a}`}
                  aria-pressed={avatar === a}
                  onClick={() => setAvatar(a)}
                  style={{ padding: 4, borderRadius: 10, background: 'transparent', border: avatar === a ? '2px solid var(--accent)' : '2px solid transparent', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                >
                  <ProfileAvatar avatar={a} size={56} />
                </button>
              ))}
            </div>
            {err ? <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{err}</div> : null}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Btn data-zflix-modal-primary-action variant="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : creating ? 'Create profile' : 'Save'}</Btn>
              <Btn variant="ghost" disabled={busy} onClick={onClose}>Cancel</Btn>
              {!creating && !profile.original && onDelete ? (
                <Btn variant="ghost" disabled={busy} onClick={() => setConfirmDelete(true)} style={{ marginLeft: 'auto', color: '#f87171' }}>Delete profile</Btn>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const zflixPairTokenFromUrl = () => {
 try {
   const t = new URLSearchParams(window.location.search).get('pair') || '';
   return /^[a-f0-9]{32,96}$/.test(t) ? t : '';
 } catch (_) {
   return '';
 }
};

/** Shown on the phone after it scans a TV's sign-in QR code (and is itself signed in). */
function PairApproveScreen({ token, onDone }) {
 const [info, setInfo] = useState(null);
 const [state, setState] = useState('idle');
 const [error, setError] = useState('');
 useEffect(() => {
   fetch(`/api/auth/pair/info?token=${encodeURIComponent(token)}`, { credentials: 'include' })
     .then((r) => r.json())
     .then((d) => setInfo(d && d.valid ? d : { valid: false }))
     .catch(() => setInfo({ valid: false }));
 }, [token]);
 const approve = async () => {
   setState('busy');
   setError('');
   try {
     const r = await fetch('/api/auth/pair/approve', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({ token }),
     });
     const d = await r.json().catch(() => ({}));
     if (!r.ok) {
       setError(d.error || 'Could not approve.');
       setState('idle');
       return;
     }
     setState('done');
   } catch (_) {
     setError('Could not reach server');
     setState('idle');
   }
 };
 const box = { width: '100%', maxWidth: 380, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 24px', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' };
 return (
   <div style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
     <div role="dialog" aria-modal="true" aria-label="Sign in a device" style={box}>
       <div style={{ marginBottom: 18 }}><ZFlixLogo /></div>
       {!info ? <p style={{ color: 'var(--text2)' }}>Checking…</p> : null}
       {info && !info.valid ? (
         <>
           <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>This sign-in code has expired or was already used. Scan the code currently on the TV.</p>
         </>
       ) : null}
       {info && info.valid && state !== 'done' && state !== 'cancelled' ? (
         <>
           <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>Sign in the device showing this code?</p>
           <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: 4, margin: '12px 0 8px' }}>{info.code}</div>
           <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 16 }}>Only approve if this matches the code on your own TV.</p>
           {error ? <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 10 }}>{error}</div> : null}
           <Btn onClick={approve} disabled={state === 'busy'} style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }}>{state === 'busy' ? 'Approving…' : 'Approve'}</Btn>
           <Btn variant="secondary" onClick={() => setState('cancelled')} style={{ width: '100%', justifyContent: 'center' }}>Cancel</Btn>
         </>
       ) : null}
       {state === 'done' ? (
         <>
           <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Approved</p>
           <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>The TV will sign in within a few seconds. You can close this page.</p>
         </>
       ) : null}
       {state === 'cancelled' ? <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>Nothing was approved. You can close this page.</p> : null}
     </div>
   </div>
 );
}

function LoginScreen({ onSuccess }) {
 const [password, setPassword] = useState('');
 const [error, setError] = useState('');
 const [busy, setBusy] = useState(false);
 const onLoginGesture = (e) => {
   unlockSplashAudioFromGesture(e);
 };
 const submit = async (e) => {
   e.preventDefault();
   onLoginGesture();
   setError('');
   setBusy(true);
   try {
     const r = await fetch('/api/auth/login', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({ password }),
     });
     const data = await r.json().catch(() => ({}));
     if (!r.ok) {
       const fallback =
         r.status >= 500
           ? 'Server error — Zflix backend may be down or restarting. Check Docker logs for zfin-backend.'
           : 'Wrong password';
       setError(data.error || fallback);
       setBusy(false);
       return;
     }
     const st = await fetch('/api/auth/status', { credentials: 'include' }).then((x) => x.json());
     if (!st.authenticated && !st.authDisabled) {
       setError('Login succeeded but this device did not keep the session cookie (common on TV browsers). Reinstall the latest ZFLIX TV app or open ZFLIX in Downloader/Silk instead.');
       setBusy(false);
       return;
     }
     prefetchZflixJellyfinContext();
     onSuccess();
   } catch {
     setError('Could not reach server');
   }
   setBusy(false);
 };
 /**
  * Phone sign-in: this screen shows a QR code; a phone (or iPad) that is signed in to Zflix scans it and approves, and
  * this device is signed in without typing the password. On a phone it sits under the password box, for scanning
  * with another signed-in device. Not shown on the device doing the approving.
  */
 const [pair, setPair] = useState(null);
 const pairWanted = typeof window !== 'undefined' && !zflixPairTokenFromUrl();
 const narrow = typeof window !== 'undefined' && window.innerWidth < 700;
 useEffect(() => {
   if (!pairWanted) return undefined;
   let cancelled = false;
   let poll = null;
   let renew = null;
   const begin = async () => {
     clearInterval(poll);
     clearTimeout(renew);
     /** If the server is slow or busy the code used to simply never appear. Give each try 8 s and keep trying. */
     const retrySoon = () => {
       if (!cancelled) renew = setTimeout(begin, 5000);
     };
     try {
       const ac = new AbortController();
       const giveUp = setTimeout(() => ac.abort(), 8000);
       const r = await fetch('/api/auth/pair/start', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         credentials: 'include',
         body: JSON.stringify({ origin: window.location.origin }),
         signal: ac.signal,
       });
       clearTimeout(giveUp);
       const d = await r.json();
       if (cancelled) return;
       if (!d || !d.id) return retrySoon();
       setPair(d);
       poll = setInterval(async () => {
         try {
           const st = await fetch(`/api/auth/pair/poll?id=${encodeURIComponent(d.id)}`, { credentials: 'include' }).then((x) => x.json());
           if (cancelled) return;
           if (st.status === 'approved') {
             clearInterval(poll);
             clearTimeout(renew);
             prefetchZflixJellyfinContext();
             onSuccess();
           } else if (st.status === 'expired') {
             begin();
           }
         } catch (_) {}
       }, 2500);
       clearTimeout(renew);
       renew = setTimeout(begin, Math.max(60, (d.expiresInSec || 300) - 20) * 1000);
     } catch (_) {
       retrySoon();
     }
   };
   begin();
   return () => {
     cancelled = true;
     clearInterval(poll);
     clearTimeout(renew);
   };
 }, [pairWanted]);
 return (
   <div style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(10,10,15,0.97)', display: 'flex', alignItems: narrow ? 'flex-start' : 'center', justifyContent: 'center', alignContent: narrow ? 'flex-start' : 'center', gap: 28, flexWrap: 'wrap', padding: 24, overflowY: 'auto' }}>
     {pairWanted && pair ? (
       <div aria-label="Sign in with your phone" style={{ order: narrow ? 2 : 0, width: narrow ? '100%' : 260, maxWidth: narrow ? 380 : 260, boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '22px 20px', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
         <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>{narrow ? 'Or sign in from another device' : 'Sign in with your phone'}</div>
         {pair.qrSvg ? (
           <img alt="QR code to sign in" src={`data:image/svg+xml;utf8,${encodeURIComponent(pair.qrSvg)}`} style={{ width: 190, height: 190, background: '#fff', borderRadius: 8, display: 'block', margin: '0 auto 12px' }} />
         ) : null}
         <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
           {narrow ? 'Scan this with the camera of a phone or iPad that is already signed in to Zflix, then tap Approve there. Check that it shows this code:' : "Scan with your phone's camera, then tap Approve. Check that the phone shows this code:"}
         </div>
         <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 3, marginTop: 8 }}>{pair.code}</div>
       </div>
     ) : null}
     <form role="dialog" aria-modal="true" aria-label="Sign in" onSubmit={submit} style={{ width: '100%', maxWidth: 380, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 24px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
       <div style={{ textAlign: 'center', marginBottom: 20 }}><ZFlixLogo /></div>
       <p style={{ fontSize: 13, color: 'var(--text2)', textAlign: 'center', marginBottom: 18, lineHeight: 1.5 }}>This library is private. Enter the site password to continue. Your browser will remember this device until the session expires.</p>
       <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password"
         style={{ width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 15, marginBottom: 12, boxSizing: 'border-box' }} />
       {error && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 10 }}>{error}</div>}
       <Btn type="submit" disabled={busy || !password} onPointerDown={onLoginGesture} onMouseDown={onLoginGesture} onTouchStart={onLoginGesture} style={{ width: '100%', justifyContent: 'center' }}>{busy ? 'Signing in…' : 'Continue'}</Btn>
     </form>
   </div>
 );
}

function Toast({ message, type, onClose }) {
 useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
 return (
   <div style={{ position: 'fixed', bottom: 80, right: 16, zIndex: 3000, background: 'var(--surface2)', border: `1px solid ${type === 'success' ? 'var(--green)' : type === 'error' ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', maxWidth: 320 }}>
     <Icon name={type === 'success' ? 'check' : 'close'} size={16} />{message}
   </div>
 );
}

/** Player quality menu. "Auto" plays the file as-is when the device and connection allow it. */
const ZFLIX_QUALITY_OPTIONS = [
  { label: 'Auto (best)', maxBitrate: 0, maxHeight: 0 },
  { label: '1080p · 8 Mbps', maxBitrate: 8_000_000, maxHeight: 1080 },
  { label: '720p · 4 Mbps', maxBitrate: 4_000_000, maxHeight: 720 },
  { label: '480p · 1.5 Mbps', maxBitrate: 1_500_000, maxHeight: 480 },
];

function Player({ item, onClose, mini = false, onExpand, onEnded }) {
 const onEndedRef = useRef(onEnded);
 onEndedRef.current = onEnded;
 const videoRef = useRef();
 const onCloseRef = useRef(onClose);
 const playSessionIdRef = useRef(null);
 const vodStopReportedRef = useRef(false);
 const lastJfProgressSentRef = useRef(0);
 const [playing, setPlaying] = useState(true);
 const [showControls, setShowControls] = useState(true);
 const [progress, setProgress] = useState(0);
 const [duration, setDuration] = useState(0);
 const [bootstrapping, setBootstrapping] = useState(true);
 const [buffering, setBuffering] = useState(false);
 const [needsSoundTap, setNeedsSoundTap] = useState(false);
 const [visualSkipEnabled, setVisualSkipEnabled] = useState(() => zflixReadVisualSkipEnabled());
 const [visualSkipCount, setVisualSkipCount] = useState(0);
 const visualSkipZonesRef = useRef([]);
 const visualSkipPadRef = useRef({ before: 0, after: 0 });
 /** Live clean playback: moments to silence, and whether the current mute is ours (not the viewer's). */
 const cleanMutesRef = useRef([]);
 const cleanMuteActiveRef = useRef(false);
 const cleanMuteApplyRef = useRef(null);
 const hideTimer = useRef();
 /** Remote control: focus stays on the player root; `focusIdx` / `menuIdx` are a virtual cursor over the controls and menus. */
 const rootRef = useRef(null);
 const uiRef = useRef({ handle: () => {}, back: () => false, menu: null });
 /**
  * Mini player (live TV): the same player, shrunk. It sits over the Live TV screen's preview box when that is on
  * screen, otherwise in the bottom-right corner, and leaves the remote to whatever is underneath.
  */
 const miniRef = useRef(mini);
 miniRef.current = mini;
 const onExpandRef = useRef(onExpand);
 onExpandRef.current = onExpand;
 /**
  * Live TV cushion. A channel starts with only a few seconds buffered (that is all the server has made yet), so any
  * hiccup upstream shows as "Buffering". While less than ~10 s is buffered ahead, play 5% slow (pitch is kept) so
  * the cushion grows by itself over the first few minutes; back to normal speed once ~14 s is banked.
  */
 useEffect(() => {
   if (!item.liveStreamId) return undefined;
   const iv = setInterval(() => {
     const v = videoRef.current;
     if (!v || v.paused || v.seeking || v.readyState < 3) return;
     const t = v.currentTime;
     let ahead = 0;
     for (let i = 0; i < v.buffered.length; i += 1) {
       if (v.buffered.start(i) <= t + 0.25 && v.buffered.end(i) > t) ahead = v.buffered.end(i) - t;
     }
     const want = ahead < 10 ? 0.95 : ahead > 14 ? 1 : v.playbackRate;
     if (Math.abs(v.playbackRate - want) > 0.001) {
       try {
         v.playbackRate = want;
       } catch (_) {}
     }
   }, 2000);
   return () => {
     clearInterval(iv);
     try {
       if (videoRef.current) videoRef.current.playbackRate = 1;
     } catch (_) {}
   };
 }, [item.liveStreamId]);

 /** Live TV: Up / Down change channel in full screen. `chBanner` = what is shown in the corner while flipping. */
 const [chBanner, setChBanner] = useState(null);
 const pendingChRef = useRef(null);
 const tuneTimerRef = useRef(null);
 const bannerTimerRef = useRef(null);
 /** What is on this channel right now, for the banner and the rewind / forward bar. */
 const programNow = () => (item.zflixLiveKey && window.__zflixLiveTvList?.().find((c) => c.key === item.zflixLiveKey)?.now) || '';
 /** Skip Intro / Skip Recap times for this episode, in seconds, found overnight by scripts/intro-scan.py. */
 const [segs, setSegs] = useState(null);
 useEffect(() => {
   setSegs(null);
   if (item.source !== 'jellyfin' || item.type !== 'episode' || item.liveStreamId || item.id == null) return undefined;
   let cancelled = false;
   fetch(`/api/library/items/${encodeURIComponent(item.id)}/segments`, { credentials: 'include' })
     .then((r) => r.json())
     .then((j) => {
       if (!cancelled && j && (j.intro || j.recap)) setSegs(j);
     })
     .catch(() => {});
   return () => {
     cancelled = true;
   };
 }, [item.id, item.source, item.type, item.liveStreamId]);
 /** Shows and movies: the title (and season, episode and its name) on the bar OK brings up, like Live TV's program. */
 const [vodTitle, setVodTitle] = useState(null);
 useEffect(() => {
   setVodTitle(null);
   if (item.source !== 'jellyfin' || item.liveStreamId || item.id == null) return undefined;
   if (item.type !== 'movie' && item.type !== 'episode') return undefined;
   let cancelled = false;
   fetch(`/api/library/items/${encodeURIComponent(item.id)}/player-title`, { credentials: 'include' })
     .then((r) => r.json())
     .then((j) => {
       if (!cancelled && j && j.title) setVodTitle({ title: j.title, sub: j.sub || '' });
     })
     .catch(() => {});
   return () => {
     cancelled = true;
   };
 }, [item.id, item.source, item.type, item.liveStreamId]);
 /** Phones and tablets only; a TV never sends touch events, so its remote never reaches these. */
 const swipeRef = useRef(null);
 const lastTouchAtRef = useRef(0);
 const tapRef = useRef({ side: 0, at: 0, timer: null, savedAt: 0 });
 /** Phones and tablets: the channel list inside the full-screen player. Null = closed. */
 const [chList, setChList] = useState(null);
 useEffect(() => {
   if (!item.liveStreamId || !item.title) return undefined;
   setChBanner({ name: item.title, logo: item.logo, pending: false, program: programNow() });
   clearTimeout(bannerTimerRef.current);
   bannerTimerRef.current = setTimeout(() => setChBanner(null), 6000);
   return () => clearTimeout(bannerTimerRef.current);
 }, [item.zflixLiveKey, item.liveStreamId, item.title, item.logo]);
 useEffect(() => () => clearTimeout(tuneTimerRef.current), []);
 /** Record what is on this channel now (until the program ends). */
 const recordLiveNow = async () => {
   try {
     const r = await fetch('/api/dvr/record-now', {
       method: 'POST',
       credentials: 'include',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ variantId: item.id, channelName: item.title }),
     });
     const j = await r.json().catch(() => ({}));
     setChBanner({ name: r.ok ? `Recording: ${j.title || item.title}` : j.error || 'Could not start recording', logo: item.logo, pending: false, note: r.ok ? j.warning || 'Find it under Recordings' : '' });
   } catch (_) {
     setChBanner({ name: 'Could not start recording', pending: false });
   }
   clearTimeout(bannerTimerRef.current);
   bannerTimerRef.current = setTimeout(() => setChBanner(null), 5000);
 };
 /** Stuck this long on a live channel is a bad copy, not a blip: try the next one, a few times at most. */
 const liveRetryRef = useRef({ key: null, tries: 0 });
 useEffect(() => {
   if (!buffering || !item.liveStreamId || !item.zflixLiveKey) return undefined;
   const r = liveRetryRef.current;
   if (r.key !== item.zflixLiveKey) {
     r.key = item.zflixLiveKey;
     r.tries = 0;
   }
   const t = setTimeout(() => {
     if (r.tries >= 3) return;
     r.tries += 1;
     if (window.__zflixLiveTvNextCopy?.(item.zflixLiveKey, item.id)) setChBanner({ name: item.name, logo: item.logo, pending: true, program: 'Trying another copy of this channel' });
   }, 15000);
   return () => clearTimeout(t);
 }, [buffering, item.zflixLiveKey, item.liveStreamId, item.id, item.name, item.logo]);

 const stepChannel = (dir) => {
   const neighbor = typeof window !== 'undefined' ? window.__zflixLiveTvNeighbor : null;
   if (typeof neighbor !== 'function') return false;
   const n = neighbor(pendingChRef.current || item.zflixLiveKey, dir);
   if (!n) return false;
   pendingChRef.current = n.key;
   setChBanner({ name: n.name, logo: n.logo, pending: true, program: n.program });
   clearTimeout(bannerTimerRef.current);
   bannerTimerRef.current = setTimeout(() => setChBanner(null), 25000);
   clearTimeout(tuneTimerRef.current);
   /** Wait for the remote to settle, so flipping past several channels only starts the last one. */
   tuneTimerRef.current = setTimeout(() => {
     const k = pendingChRef.current;
     pendingChRef.current = null;
     if (k && k !== item.zflixLiveKey) window.__zflixLiveTvTune?.(k);
     else setChBanner(null);
   }, 450);
   return true;
 };

 const [miniRect, setMiniRect] = useState(null);
 useEffect(() => {
   if (!mini) {
     setMiniRect(null);
     try {
       rootRef.current?.focus({ preventScroll: true });
     } catch (_) {}
     return undefined;
   }
   const place = () => {
     const slot = document.querySelector('[data-zflix-livetv-preview-slot="true"]');
     const r = slot ? slot.getBoundingClientRect() : null;
     const next =
       r && r.width > 40 && r.height > 20 && r.bottom > 0 && r.top < window.innerHeight
         ? { left: Math.round(r.left), top: Math.round(r.top), width: Math.round(r.width), height: Math.round(r.height), docked: true }
         : null;
     setMiniRect((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
   };
   place();
   const iv = setInterval(place, 200);
   window.addEventListener('resize', place);
   return () => {
     clearInterval(iv);
     window.removeEventListener('resize', place);
   };
 }, [mini]);
 /** Scrubbing: presses accumulate into one target (steps grow while you keep pressing); the video jumps once you pause.
  *  seekMode = the remote is on the timeline rather than on the buttons. */
 const [seekMode, setSeekMode] = useState(false);
 const [seekPreview, setSeekPreview] = useState(null);
 const seekAccRef = useRef({ n: 0, last: 0, target: null, timer: null });
 const resumeAtRef = useRef(null);
 const [tracks, setTracks] = useState({ audio: [], subtitles: [] });
 const [menu, setMenu] = useState(null);
 const [menuIdx, setMenuIdx] = useState(0);
 const [focusIdx, setFocusIdx] = useState(0);
 /** quality: what the viewer picked (0 = Auto). autoStep: on Auto, how far Zflix lowered it after stalls (0 = not). */
 const [playOpts, setPlayOpts] = useState({ audioStreamIndex: null, burnSub: null, quality: 0, autoStep: 0 });
 const [qualityNote, setQualityNote] = useState('');
 const autoStepDownRef = useRef(null);
 const [textSub, setTextSub] = useState(-1);
 /** 'loading' while a subtitle track is being fetched, 'failed' briefly if it could not be. */
 const [subNote, setSubNote] = useState(null);
 /** Subtitle text already fetched (by track url), so switching back to a track is instant. */
 const subTextRef = useRef(new Map());
 const ownSubTrackRef = useRef(null);
 const isJfVod =
   item.source === 'jellyfin' && !item.liveStreamId && (item.type === 'movie' || item.type === 'episode') && item.id != null;
 /** Play on TV (phones / computers only): hand this title, at this position, to a TV that has Zflix open. */
 const [tvSendOpen, setTvSendOpen] = useState(false);
 const tvSentRef = useRef(false);
 /** Seek bar and rewind / forward: library titles and Zflix recordings. */
 /** Live TV through Zflix keeps the last hour: pause, rewind and forward work inside that window. */
 /** A recording still in progress has no end yet, so it seeks like live TV: the bar is what has been saved so far. */
 const isLiveDvr = (typeof item.liveStreamId === 'string' && item.liveStreamId.startsWith('zdirect:')) || !!item.growing;
 /** A recording is written in 5-second pieces, a few seconds late, so its newest 30 seconds are never safe to play. */
 const edgeGap = item.growing ? 30 : 0;
 const canSeek = isJfVod || item.source === 'dvr' || isLiveDvr;
 /** The part of the video that can be reached: the whole title, or the saved hour of a live channel. */
 const seekWin = (v) => {
   if (!v) return null;
   if (isLiveDvr) {
     const r = v.seekable;
     return r && r.length ? { lo: r.start(0), hi: r.end(r.length - 1) } : null;
   }
   return Number.isFinite(v.duration) && v.duration > 0 ? { lo: 0, hi: v.duration } : null;
 };
 const onTvApp = typeof document !== 'undefined' && document.documentElement.classList.contains('zflix-tv-apk-density');
 const canTvSend = isJfVod && !mini && !onTvApp;

 onCloseRef.current = onClose;

 const reportJfVodProgress = (isPaused = false) => {
   const ps =
     playSessionIdRef.current ||
     parseJellyfinPlaySessionFromUrl(item.streamUrl) ||
     (item.playSessionId ? String(item.playSessionId) : null);
   if (
     !ps ||
     item.source !== 'jellyfin' ||
     item.liveStreamId ||
     (item.type !== 'movie' && item.type !== 'episode') ||
     item.id == null
   ) {
     return;
   }
   const v = videoRef.current;
   const pos = v && Number.isFinite(v.currentTime) ? v.currentTime : progress;
   if (!Number.isFinite(pos) || pos < 0) return;
   const dur = v && Number.isFinite(v.duration) ? v.duration : duration;
   fetch('/api/library/jellyfin-playing-progress', {
     method: 'POST',
     headers: { 'Content-Type': 'application/json' },
     /** itemId lets the backend write resume position straight to the user's Jellyfin UserData (session reports alone are dropped for API-key sessions). */
     body: JSON.stringify({
       playSessionId: ps,
       itemId: item.id,
       positionSec: pos,
       durationSec: Number.isFinite(dur) && dur > 0 ? dur : undefined,
       isPaused: !!isPaused,
     }),
     keepalive: true,
   }).catch(() => {});
 };

 const reportJfVodStopped = (completed) => {
   if (vodStopReportedRef.current) return;
   const ps =
     playSessionIdRef.current ||
     parseJellyfinPlaySessionFromUrl(item.streamUrl) ||
     (item.playSessionId ? String(item.playSessionId) : null);
   if (
     !ps ||
     item.source !== 'jellyfin' ||
     item.liveStreamId ||
     (item.type !== 'movie' && item.type !== 'episode') ||
     item.id == null
   ) {
     return;
   }
   const v = videoRef.current;
   const pos = v && Number.isFinite(v.currentTime) ? v.currentTime : progress;
   const dur = v && Number.isFinite(v.duration) ? v.duration : duration;
   /** Same rule as the server: into the credits = finished (last 7% of an hour-plus title, last 5% of shorter ones). */
   const nearlyDone = completed || (dur > 0 && pos / dur >= (dur >= 3600 ? 0.93 : 0.95));
   reportJfVodProgress(!!completed || nearlyDone);
   vodStopReportedRef.current = true;
   fetch('/api/library/jellyfin-playing-stopped', {
     method: 'POST',
     headers: { 'Content-Type': 'application/json' },
     body: JSON.stringify({
       playSessionId: ps,
       itemId: item.id,
       positionSec: nearlyDone && dur > 0 ? dur : pos,
       durationSec: dur > 0 ? dur : pos,
       completed: !!completed || nearlyDone,
     }),
     keepalive: true,
   })
     .catch(() => {})
     .finally(() => {
       scheduleZflixResumeStale({ delayMs: 500, retryMs: 2400 });
     });
 };

 const resetHide = () => {
   setShowControls(true);
   clearTimeout(hideTimer.current);
   hideTimer.current = setTimeout(() => {
     /** Stay up while a menu is open or the video is paused. */
     if (uiRef.current.menu || videoRef.current?.paused) return;
     setSeekMode(false);
     setShowControls(false);
   }, 4000);
 };

 /**
  * Phones: the app turns with the phone (manifest "any", Zach 2026-11-03: upright is easier as a remote), but a video
  * playing full size turns the screen sideways, and closing it (or shrinking it to the mini player) lets it turn back.
  */
 /** Phones and tablets: title, picture and Play/Pause/skip on the lock screen and in the notification shade. */
 useEffect(() => {
   if (mini || !zflixIsPhoneScreen() || !('mediaSession' in navigator) || typeof window.MediaMetadata !== 'function') return undefined;
   const ms = navigator.mediaSession;
   const set = (a, f) => {
     try {
       ms.setActionHandler(a, f);
     } catch (_) {}
   };
   try {
     const art = item.poster || item.backdrop;
     ms.metadata = new window.MediaMetadata({
       title: item.title || item.name || 'Zflix',
       artist: item.type === 'episode' ? item.seriesName || item.name || '' : item.year ? String(item.year) : '',
       album: 'Zflix',
       artwork: art ? [{ src: new URL(art, window.location.href).href, sizes: '300x450' }] : [],
     });
   } catch (_) {}
   const v = () => videoRef.current;
   set('play', () => v()?.play().catch(() => {}));
   set('pause', () => v()?.pause());
   set('seekbackward', (d) => {
     const x = v();
     if (x) x.currentTime = Math.max(0, x.currentTime - (d?.seekOffset || 10));
   });
   set('seekforward', (d) => {
     const x = v();
     if (x) x.currentTime = Math.min((x.duration || x.currentTime + 10) - 1, x.currentTime + (d?.seekOffset || 10));
   });
   return () => {
     ['play', 'pause', 'seekbackward', 'seekforward'].forEach((a) => set(a, null));
     try {
       ms.metadata = null;
     } catch (_) {}
   };
 }, [item.id, mini]);
 useEffect(() => {
   if (mini || !zflixIsPhoneScreen()) return undefined;
   try {
     const p = window.screen?.orientation?.lock?.('landscape');
     if (p && typeof p.catch === 'function') p.catch(() => {});
   } catch (_) {}
   return () => zflixLetScreenTurn();
 }, [mini]);
 /** TV-friendly: no top chrome — remote Back exits via applyTvBack; tap shows bottom scrubber only. */
 useEffect(() => {
   setShowControls(false);
   return () => clearTimeout(hideTimer.current);
 }, []);
 /** Remote / keyboard. The TV app sends arrows and play-pause as synthetic keydowns on window, and OK as a click on the focused element. */
 useEffect(() => {
   try {
     if (!miniRef.current) rootRef.current?.focus({ preventScroll: true });
   } catch (_) {}
   const onKey = (e) => {
     const t = e.target;
     if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
     /** Mini: the screen underneath owns the remote; only Play/Pause is ours, and it brings the player back up. */
     if (miniRef.current) {
       if (e.key === 'MediaPlayPause' || e.key === 'MediaPlay' || e.keyCode === 179 || ((e.key === ' ' || e.keyCode === 32) && !e.isTrusted)) {
         e.preventDefault();
         e.stopImmediatePropagation();
         onExpandRef.current?.();
       }
       return;
     }
     const k = e.key;
     const code = e.keyCode;
     let a = null;
     if (k === 'ArrowLeft' || code === 37) a = 'left';
     else if (k === 'ArrowRight' || code === 39) a = 'right';
     else if (k === 'ArrowUp' || code === 38) a = 'up';
     else if (k === 'ArrowDown' || code === 40) a = 'down';
     else if (k === 'Enter' || code === 13) a = 'ok';
     else if (k === ' ' || k === 'Spacebar' || code === 32 || k === 'MediaPlayPause' || k === 'MediaPlay' || k === 'MediaPause' || code === 179) a = 'playpause';
     else if (k === 'MediaRewind' || code === 227) a = 'rew';
     else if (k === 'MediaFastForward' || code === 228) a = 'ff';
     /** The Channel buttons on the remote. Different TVs report them differently. */
     else if (k === 'ChannelUp' || k === 'PageUp' || code === 427 || code === 33 || code === 166) a = 'chup';
     else if (k === 'ChannelDown' || k === 'PageDown' || code === 428 || code === 34 || code === 167) a = 'chdown';
     if (!a) return;
     e.preventDefault();
     e.stopImmediatePropagation();
     uiRef.current.handle(a);
   };
   window.addEventListener('keydown', onKey, true);
   window.__zflixPlayerBack = () => (miniRef.current ? false : uiRef.current.back());
   window.__zflixPlayerClose = () => uiRef.current.close();
   return () => {
     window.removeEventListener('keydown', onKey, true);
     delete window.__zflixPlayerBack;
     delete window.__zflixPlayerClose;
   };
 }, []);
 useEffect(() => {
   setBootstrapping(true);
   setBuffering(false);
   setNeedsSoundTap(false);
   vodStopReportedRef.current = false;
   lastJfProgressSentRef.current = 0;
   playSessionIdRef.current =
     parseJellyfinPlaySessionFromUrl(item.streamUrl) || (item.playSessionId ? String(item.playSessionId) : null);
   const video = videoRef.current;
   if (!video) return undefined;

   /** Jellyfin movies/episodes: start with subtitles off (tracks stay in manifest for manual/CC selection). */
   const jfVodNoAutoSubs = item.source === 'jellyfin' && !item.liveStreamId;
   const disableJfNativeTextTracks = () => {
     if (!jfVodNoAutoSubs) return;
     try {
       const tt = video.textTracks;
       for (let i = 0; i < tt.length; i++) {
         if (tt[i] !== ownSubTrackRef.current) tt[i].mode = 'disabled';
       }
     } catch (_) {}
   };

   let disposed = false;
   let hlsInstance = null;
   let resolvedPlaySessionId = item.playSessionId ? String(item.playSessionId) : null;

   /**
    * Automatic quality (Auto only, library titles only): a stall of 10 s, or 6 s of stalls in 90 s, steps the
    * picture down (720p, then 480p) and it carries on from the same spot. Not counted: the start, and just after a seek.
    */
   const autoQualityOn = item.source === 'jellyfin' && !item.liveStreamId && !item.growing;
   let everPlayed = false;
   let lastSeekAt = 0;
   let stallAt = 0;
   let stallTimer = null;
   const stalls = [];
   const stepDownIfSlow = (longOne) => {
     const now = Date.now();
     while (stalls.length && now - stalls[0].at > 90000) stalls.shift();
     const total = stalls.reduce((s, x) => s + x.ms, 0);
     if (longOne || (stalls.length >= 2 && total >= 6000)) {
       stalls.length = 0;
       autoStepDownRef.current?.();
     }
   };
   const onVidSeeking = () => {
     lastSeekAt = Date.now();
   };
   const onVidWaiting = () => {
     setBuffering(true);
     if (autoQualityOn && everPlayed && !video.paused && Date.now() - lastSeekAt > 5000 && !stallAt) {
       stallAt = Date.now();
       clearTimeout(stallTimer);
       stallTimer = setTimeout(() => {
         if (stallAt && !disposed) stepDownIfSlow(true);
       }, 10000);
     }
     /** Stalled at the end of a recording still being written: step back once and stay back, or it stalls every few seconds. */
     const r = video.seekable;
     if (item.growing && r && r.length && r.end(r.length - 1) - video.currentTime < 12) {
       video.currentTime = Math.max(r.start(0), r.end(r.length - 1) - 30);
     }
   };
   const onVidPlaying = () => {
     setBuffering(false);
     setBootstrapping(false);
     everPlayed = true;
     clearTimeout(stallTimer);
     if (stallAt) {
       stalls.push({ at: Date.now(), ms: Date.now() - stallAt });
       stallAt = 0;
       stepDownIfSlow(false);
     }
   };
   /** Enough is loaded to go on: no loading circle, even if the picture is paused. */
   const onVidCanPlay = () => {
     setBootstrapping(false);
     setBuffering(false);
   };
   video.addEventListener('waiting', onVidWaiting);
   video.addEventListener('playing', onVidPlaying);
   video.addEventListener('canplay', onVidCanPlay);
   video.addEventListener('seeking', onVidSeeking);

   const stopLiveTvPlaybackSession = () => {
     const lsid = item.liveStreamId;
     const psid = resolvedPlaySessionId;
     if (!lsid) return;
     fetch('/api/livetv/stop', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ liveStreamId: lsid, playSessionId: psid }),
       keepalive: true,
     }).catch(() => {});
   };

   let bailed = false;
   const bailPlaybackError = () => {
     if (bailed) return;
     bailed = true;
     stopLiveTvPlaybackSession();
     onCloseRef.current();
   };

   const tryPlayLive = () => {
     video.volume = 1;
     if (zflixSoundAlwaysAllowed() && !cleanMuteActiveRef.current) video.muted = false;
     video.play().catch((err) => {
       if (disposed) return;
       /** Only a blocked autoplay needs the muted start. An interrupted play() (stream reload, channel change) tries again with sound. */
       if (err?.name !== 'NotAllowedError' || zflixSoundAlwaysAllowed()) {
         setTimeout(() => {
           if (!disposed && video.paused) video.play().catch(() => {});
         }, 400);
         return;
       }
       video.muted = true;
       video.play().then(() => {
         setPlaying(true);
         setShowControls(true);
       }).catch(() => {
         setPlaying(false);
         setShowControls(true);
       });
     });
   };

   const isJfLive = !!item.liveStreamId;
   const hlsConfig = {
     manifestLoadingMaxRetry: 10,
     manifestLoadingTimeOut: 90000,
     levelLoadingMaxRetry: 10,
     levelLoadingTimeOut: 90000,
     fragLoadingMaxRetry: 10,
     fragLoadingTimeOut: 90000,
     capLevelToPlayerSize: false,
     /** Demux off main thread — software AV1 hitches badly when this was false. */
     enableWorker: true,
     /** LL-HLS defaults hurt Jellyfin movie playback (needless churn); keep it for tuner streams only. */
     /**
      * Live TV: steadiness over being close to live. No low-latency mode, and after a stall or reload rejoin
      * about six segments (~18 s) behind the newest one instead of right at the edge. Never speed up to catch up.
      */
     lowLatencyMode: false,
     ...(isJfLive || item.growing ? { liveSyncDurationCount: 6, maxLiveSyncPlaybackRate: 1 } : {}),
     /** Library / VoD: deeper buffer gives dav1d/MSE time to breathe. */
     maxBufferLength: isJfLive ? 60 : 90,
     maxBufferSize: isJfLive ? 60e6 : 180e6,
     /** Don’t pull the lowest rung first for a BW sample — Jellyfin ladders stall there unnecessarily. */
     testBandwidth: false,
     /** High floor so we don’t open on a mushy rung; ABR can still drop if decode/network can’t keep up (critical for AV1). */
     abrEwmaDefaultEstimate: isJfLive ? 12_000_000 : 40_000_000,
     abrEwmaDefaultEstimateMax: 2_147_483_647,
     abrBandWidthUpFactor: 0.85,
     /** Prefer measured throughput over playlist BANDWIDTH when they diverge (common with AV1). */
     abrMaxWithRealBitrate: true,
     startFragPrefetch: true,
     /** hls.js opens a growing stream at its newest second; a recording starts from the beginning. */
     ...(item.resumeAt ? { startPosition: item.resumeAt } : item.growing ? { startPosition: 0 } : {}),
     xhrSetup: (xhr) => {
       xhr.withCredentials = true;
     },
   };

   const attachHls = (src, handshakeFallback = null) => {
     if (disposed) return;
     const hls = new Hls(hlsConfig);
     hlsInstance = hls;
     let fatalRecoveries = 0;
     /** Only strip Jellyfin’s initial/default subtitle selection once — avoids fighting the user or mid-stream manifest churn. */
     let jfInitialSubsOffDone = false;
     const nudgeJfSubsOffOnce = () => {
       if (!jfVodNoAutoSubs || jfInitialSubsOffDone) return;
       try {
         const tracks = hls.subtitleTracks || [];
         if (!tracks.length) return;
         hls.subtitleTrack = -1;
         jfInitialSubsOffDone = true;
       } catch (_) {}
     };
     hls.loadSource(src);
     hls.attachMedia(video);
     hls.on(Hls.Events.MANIFEST_PARSED, () => {
       setBootstrapping(false);
       try {
         /** VoD: never hard-lock max rung — Chromium AV1 often software-decodes and looks awful under decoder stress. Live TV unchanged. */
         if (!item.liveStreamId) {
           hls.autoLevelEnabled = true;
           hls.startLevel = -1;
         }
       } catch (_) {
         hls.startLevel = -1;
       }
       nudgeJfSubsOffOnce();
       tryPlayLive();
     });
     hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, nudgeJfSubsOffOnce);
     hls.on(Hls.Events.ERROR, (_, data) => {
       if (!data.fatal) return;
       if (fatalRecoveries < 2) {
         if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
           fatalRecoveries++;
           try {
             hls.recoverMediaError();
             return;
           } catch (_) {}
         }
         if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
           fatalRecoveries++;
           try {
             hls.startLoad();
             return;
           } catch (_) {}
         }
       }
       try { hls.stopLoad(); } catch (_) {}
       try { hls.detachMedia(); } catch (_) {}
       try { hls.destroy(); } catch (_) {}
       if (handshakeFallback && src !== handshakeFallback) {
         resolvedPlaySessionId = item.playSessionId ? String(item.playSessionId) : null;
         playSessionIdRef.current =
           resolvedPlaySessionId || parseJellyfinPlaySessionFromUrl(handshakeFallback) || null;
         hlsInstance = null;
         attachHls(handshakeFallback, null);
         return;
       }
       if (video.canPlayType('application/vnd.apple.mpegurl')) {
         video.removeAttribute('src');
         video.load();
         const onVidErr = () => bailPlaybackError();
         video.onerror = onVidErr;
         video.src = src;
         tryPlayLive();
         return;
       }
       video.pause();
       video.removeAttribute('src');
       video.load();
       bailPlaybackError();
     });
   };

   const wantsJfHandshake =
     item.source === 'jellyfin' &&
     !item.liveStreamId &&
     (item.type === 'movie' || item.type === 'episode') &&
     item.id != null &&
     (typeof item.streamUrl !== 'string' ||
       !item.streamUrl ||
       (item.streamUrl.includes('/jellyfin/Videos/') && item.streamUrl.includes('master.m3u8')));

   const originalStreamUrl = typeof item.streamUrl === 'string' ? item.streamUrl : '';

   (async () => {
     let streamUrl = originalStreamUrl;
     let handshakeFallback = null;
     if (wantsJfHandshake) {
       try {
         const m = originalStreamUrl.match(/(?:\?|&)StartTimeTicks=(\d+)/);
         /** A track / quality change restarts the stream where the viewer was. */
         const restartAt = resumeAtRef.current;
         resumeAtRef.current = null;
         const startTimeTicks = m && restartAt == null ? Number(m[1]) : undefined;
         const quality = ZFLIX_QUALITY_OPTIONS[playOpts.quality || playOpts.autoStep || 0] || ZFLIX_QUALITY_OPTIONS[0];
         const caps = await getZflixPlaybackCaps();
         const ac = new AbortController();
         const timer = setTimeout(() => ac.abort(), 18000);
         let r;
         try {
           r = await fetch('/api/library/jellyfin-playback', {
             method: 'POST',
             headers: { 'Content-Type': 'application/json' },
             signal: ac.signal,
             body: JSON.stringify({
               itemId: item.id,
               audioStreamIndex: playOpts.audioStreamIndex != null ? playOpts.audioStreamIndex : item.audioStreamIndex,
               subtitleStreamIndex: playOpts.burnSub != null ? playOpts.burnSub : undefined,
               maxBitrate: quality.maxBitrate || undefined,
               maxHeight: quality.maxHeight || undefined,
               startTimeTicks: Number.isFinite(startTimeTicks) ? startTimeTicks : undefined,
               caps,
             }),
           });
         } finally {
           clearTimeout(timer);
         }
         const j = await r.json().catch(() => ({}));
         if (!disposed && r.ok && j.streamUrl) {
           handshakeFallback = originalStreamUrl || null;
           streamUrl = j.streamUrl;
           if (j.playSessionId) resolvedPlaySessionId = String(j.playSessionId);
           /** Pick up where this profile left off (the manifest covers the whole title, so just start there). */
           if (j.tracks) setTracks(j.tracks);
           const resumeSec = Number(j.resumeSec);
           if (restartAt != null && Number.isFinite(restartAt) && restartAt > 0) {
             hlsConfig.startPosition = restartAt;
           } else if (Number.isFinite(resumeSec) && resumeSec > 0) {
             hlsConfig.startPosition = resumeSec;
           }
         }
       } catch (_) {}
     }

     if (disposed) return;
     if (!streamUrl) {
       bailPlaybackError();
       return;
     }

     playSessionIdRef.current =
       resolvedPlaySessionId ||
       parseJellyfinPlaySessionFromUrl(streamUrl) ||
       parseJellyfinPlaySessionFromUrl(originalStreamUrl);

     await loadHls();
     if (!Hls) await loadHls();
     if (disposed) return;
     if (Hls?.isSupported()) {
       attachHls(streamUrl, handshakeFallback);
       return;
     }
     if (video.canPlayType('application/vnd.apple.mpegurl')) {
       const onVidErr = () => bailPlaybackError();
       video.onerror = onVidErr;
       video.addEventListener('loadedmetadata', disableJfNativeTextTracks);
       try {
         video.textTracks?.addEventListener?.('addtrack', disableJfNativeTextTracks);
       } catch (_) {}
       video.src = streamUrl;
       tryPlayLive();
       return;
     }
     video.onerror = () => bailPlaybackError();
     video.addEventListener('loadedmetadata', disableJfNativeTextTracks);
     try {
       video.textTracks?.addEventListener?.('addtrack', disableJfNativeTextTracks);
     } catch (_) {}
     video.src = streamUrl;
     tryPlayLive();
   })();

   return () => {
     disposed = true;
     const vid = videoRef.current;
     const vidEnded = !!(vid && vid.ended);
     reportJfVodStopped(vidEnded);
     video.removeEventListener('waiting', onVidWaiting);
     video.removeEventListener('playing', onVidPlaying);
     video.removeEventListener('canplay', onVidCanPlay);
     video.removeEventListener('seeking', onVidSeeking);
     clearTimeout(stallTimer);
     video.removeEventListener('loadedmetadata', disableJfNativeTextTracks);
     try {
       video.textTracks?.removeEventListener?.('addtrack', disableJfNativeTextTracks);
     } catch (_) {}
     if (hlsInstance) {
       try { hlsInstance.stopLoad(); } catch (_) {}
       try { hlsInstance.detachMedia(); } catch (_) {}
       try { hlsInstance.destroy(); } catch (_) {}
       hlsInstance = null;
     }
     video.onerror = null;
     video.pause();
     video.removeAttribute('src');
     video.load();
     stopLiveTvPlaybackSession();
   };
 }, [item.streamUrl, item.liveStreamId, item.playSessionId, item.id, item.source, item.type, item.audioStreamIndex, playOpts]);

 /**
  * Text subtitles: Jellyfin serves any text track as WebVTT, so it is added as a plain <track> (no re-encode).
  * The first load of subtitles stored inside a video means Jellyfin reads the whole file (30 s for a small one, nearly
  * 5 minutes for an 18 GB one), so the text is
  * fetched first (the server answers "not yet" until it is ready) with a note on screen, and the track only goes on
  * once it is here.
  */
 useEffect(() => {
   const video = videoRef.current;
   if (!video) return undefined;
   const s = tracks.subtitles.find((x) => x.index === textSub && x.text && x.url);
   if (!s) return undefined;
   /** Same titles as the live clean mute below. The server hides the words only on a title that has a mute plan. */
   const subUrl = item.source === 'jellyfin' && (item.zflixClean || item.type === 'episode') ? `${s.url}?clean=1` : s.url;
   let stopped = false;
   let el = null;
   let blobUrl = null;
   let iv = null;
   let timer = null;
   const attach = (text) => {
     blobUrl = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
     const t = document.createElement('track');
     t.kind = 'subtitles';
     t.label = s.label;
     t.src = blobUrl;
     t.default = true;
     video.appendChild(t);
     el = t;
     ownSubTrackRef.current = t.track;
     const show = () => {
       try {
         t.track.mode = 'showing';
       } catch (_) {}
     };
     show();
     t.addEventListener('load', show);
     iv = setInterval(show, 2000);
     setSubNote(null);
   };
   const giveUp = () => {
     setSubNote('failed');
     setTextSub(-1);
   };
   const known = subTextRef.current.get(subUrl);
   if (known) {
     attach(known);
   } else {
     setSubNote('loading');
     const started = Date.now();
     let failures = 0;
     const ask = async () => {
       let wait = false;
       try {
         const r = await fetch(subUrl, { credentials: 'include' });
         if (stopped) return;
         if (r.status === 200) {
           const text = await r.text();
           if (stopped) return;
           if (!text.includes('-->')) {
             giveUp();
             return;
           }
           subTextRef.current.set(subUrl, text);
           attach(text);
           return;
         }
         wait = r.status === 202;
       } catch (_) {}
       if (stopped) return;
       if (!wait) failures += 1;
       if (failures >= 3 || Date.now() - started > 15 * 60 * 1000) {
         giveUp();
         return;
       }
       timer = setTimeout(ask, 2000);
     };
     ask();
   }
   return () => {
     stopped = true;
     clearTimeout(timer);
     clearInterval(iv);
     ownSubTrackRef.current = null;
     if (el) el.remove();
     if (blobUrl) URL.revokeObjectURL(blobUrl);
   };
 }, [textSub, tracks, item.source, item.type, item.zflixClean]);

 useEffect(() => {
   if (subNote !== 'failed') return undefined;
   const t = setTimeout(() => setSubNote(null), 7000);
   return () => clearTimeout(t);
 }, [subNote]);
 useEffect(() => {
   if (!qualityNote) return undefined;
   const t = setTimeout(() => setQualityNote(''), 7000);
   return () => clearTimeout(t);
 }, [qualityNote]);

 /**
  * Live clean: "Play clean" on a movie, or any episode that has a mute plan (a series is cleaned as a whole).
  * Checked every 50ms while open so a mute starts on time; it only ever undoes a mute it applied itself.
  */
 useEffect(() => {
   cleanMutesRef.current = [];
   cleanMuteActiveRef.current = false;
   if (item.source !== 'jellyfin' || item.liveStreamId || item.id == null) return undefined;
   if (!(item.zflixClean || item.type === 'episode')) return undefined;
   let cancelled = false;
   fetch(`/api/library/items/${encodeURIComponent(item.id)}/clean-mutes`, { credentials: 'include' })
     .then((r) => r.json())
     .then((j) => {
       if (!cancelled) cleanMutesRef.current = Array.isArray(j.segments) ? j.segments : [];
     })
     .catch(() => {});
   const apply = () => {
     const v = videoRef.current;
     const segs = cleanMutesRef.current;
     if (!v || !segs.length) return;
     const t = v.currentTime;
     /** Sound already handed to the device keeps playing briefly after a mute (longest on TV sticks), so lead it. */
     const lead = window.__zflixTvClient ? 0.45 : 0.2;
     const inside = segs.some((x) => t >= x.startSec - lead && t < x.endSec + 0.15);
     if (inside && !v.muted) {
       cleanMuteActiveRef.current = true;
       v.muted = true;
     } else if (!inside && cleanMuteActiveRef.current) {
       cleanMuteActiveRef.current = false;
       v.muted = false;
     }
   };
   /**
    * Besides the 50ms tick, re-check the instant the position jumps (scene skip, scrubbing, resume), so a jump that
    * lands on a flagged line is silent from its first frame. Scene skips call this right after they move the video.
    */
   cleanMuteApplyRef.current = apply;
   const vid = videoRef.current;
   vid?.addEventListener('seeking', apply);
   vid?.addEventListener('seeked', apply);
   const iv = setInterval(apply, 50);
   return () => {
     cancelled = true;
     clearInterval(iv);
     vid?.removeEventListener('seeking', apply);
     vid?.removeEventListener('seeked', apply);
     cleanMuteApplyRef.current = null;
     const v = videoRef.current;
     if (v && cleanMuteActiveRef.current) v.muted = false;
     cleanMuteActiveRef.current = false;
   };
 }, [item.id, item.source, item.type, item.liveStreamId, item.zflixClean]);

 useEffect(() => {
   let cancelled = false;
   setVisualSkipCount(0);
   visualSkipZonesRef.current = [];
   visualSkipPadRef.current = { before: 0, after: 0 };
   if (item.source !== 'jellyfin' || item.liveStreamId) return undefined;
   if (item.type !== 'movie' && item.type !== 'episode') return undefined;
   if (item.id == null) return undefined;
   setVisualSkipEnabled(zflixReadVisualSkipEnabled());
   fetch(`/api/library/items/${encodeURIComponent(item.id)}/visual-skips`, { credentials: 'include' })
     .then((r) => r.json())
     .then((j) => {
       if (cancelled) return;
       const zones = Array.isArray(j.zones) ? j.zones : [];
       visualSkipZonesRef.current = zones;
       visualSkipPadRef.current = {
         before: Math.max(0, Number(j.playbackPadBeforeSec) || 0),
         after: Math.max(0, Number(j.playbackPadAfterSec) || 0),
       };
       setVisualSkipCount(zones.length);
     })
     .catch(() => {});
   return () => {
     cancelled = true;
   };
 }, [item.id, item.source, item.type, item.liveStreamId]);

 const handleClose = () => {
   reportJfVodStopped(!!videoRef.current?.ended);
   if (videoRef.current) {
     videoRef.current.pause();
     videoRef.current.removeAttribute('src');
     videoRef.current.load();
   }
   onClose();
 };

 const togglePlay = () => {
   const v = videoRef.current;
   if (!v) return;
   /** Autoplay often leaves video muted; first tap must restore audio — pausing while muted feels “sound broke”. */
   /** (A clean-playback mute is ours, not autoplay's: leave it alone so pausing never un-mutes a flagged line.) */
   if (v.muted && !cleanMuteActiveRef.current) {
     v.muted = false;
     v.volume = 1;
     v.play().catch(() => setPlaying(false));
     resetHide();
     return;
   }
   if (playing) v.pause();
   else {
     v.play().catch((err) => {
       if (err?.name !== 'NotAllowedError' || zflixSoundAlwaysAllowed()) return setPlaying(false);
       v.muted = true;
       v.play().catch(() => setPlaying(false));
     });
   }
 };
 const fmt = (s) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const ss = Math.floor(s % 60).toString().padStart(2, '0'); return h ? `${h}:${m.toString().padStart(2, '0')}:${ss}` : `${m}:${ss}`; };

 const commitSeek = () => {
   const a = seekAccRef.current;
   clearTimeout(a.timer);
   const t = a.target;
   a.target = null;
   setSeekPreview(null);
   if (t != null && videoRef.current) videoRef.current.currentTime = t;
 };
 /** dir = -1 / +1. Steps: 10s, then 30s, 1 min, 3 min, 5 min as presses keep coming (within 1.2s of each other). */
 const nudgeSeek = (dir, minPresses = 0) => {
   const v = videoRef.current;
   const w = seekWin(v);
   if (!v || !canSeek || !w) return;
   const a = seekAccRef.current;
   const now = Date.now();
   a.n = Math.max(now - a.last < 1200 ? a.n + 1 : 1, minPresses);
   a.last = now;
   const step = a.n <= 3 ? 10 : a.n <= 6 ? 30 : a.n <= 10 ? 60 : a.n <= 16 ? 180 : 300;
   const base = a.target != null ? a.target : v.currentTime;
   a.target = Math.min(Math.max(w.lo, base + dir * step), Math.max(w.lo, w.hi - (isLiveDvr ? Math.max(1, edgeGap) : 5)));
   setSeekPreview(a.target);
   clearTimeout(a.timer);
   a.timer = setTimeout(commitSeek, 700);
   resetHide();
 };
 const seekBy = (delta) => {
   const v = videoRef.current;
   const w = seekWin(v);
   if (!v || !canSeek || !w) return;
   v.currentTime = Math.min(Math.max(w.lo, v.currentTime + delta), Math.max(w.lo, w.hi - Math.max(2, edgeGap)));
   resetHide();
 };
 const onPlayerTouchStart = (e) => {
   tapRef.current.reveal = !showControls;
   resetHide();
   const t = e.touches[0];
   swipeRef.current = e.touches.length === 1 && !e.target.closest?.('button, input, a, [data-zflix-player-controls]') ? { x: t.clientX, y: t.clientY, at: Date.now() } : null;
 };
 /** Live TV: swipe up = next channel, swipe down = previous, the same as Up / Down on the remote. */
 const onPlayerTouchEnd = (e) => {
   lastTouchAtRef.current = Date.now();
   const s = swipeRef.current;
   const t = e.changedTouches[0];
   swipeRef.current = null;
   if (!s || !t || !item.liveStreamId || menu) return;
   const dx = t.clientX - s.x;
   const dy = t.clientY - s.y;
   if (Math.abs(dy) >= 60 && Math.abs(dy) > Math.abs(dx) * 1.5 && Date.now() - s.at < 1000) stepChannel(dy < 0 ? 1 : -1);
 };
 /** Double tap the left or right side to jump back or forward. One tap still plays and pauses, as it does with a mouse. */
 const onPlayerTap = (e) => {
   const v = videoRef.current;
   const box = rootRef.current?.getBoundingClientRect();
   const at = box ? (e.clientX - box.left) / box.width : 0.5;
   const side = at < 0.4 ? -1 : at > 0.6 ? 1 : 0;
   const tap = tapRef.current;
   const now = Date.now();
   clearTimeout(tap.timer);
   if (side && canSeek && tap.side === side && now - tap.at < 350) {
     tap.at = now;
     nudgeSeek(side);
     return;
   }
   tap.side = side;
   tap.at = now;
   /** With the bar hidden, a tap only brings it up (muted video still takes it, so the first tap turns the sound on). */
   if (tap.reveal && v && !v.muted) return;
   /** Only a pause can wait for a possible second tap: browsers refuse play and sound once the tap is over. */
   if (side && canSeek && playing && v && !v.muted) tap.timer = setTimeout(togglePlay, 300);
   else togglePlay();
 };
 /** Audio / burned-in subtitle / quality changes need a new stream from the server; it restarts at the current time. */
 const changeStream = (patch) => {
   const v = videoRef.current;
   resumeAtRef.current = v && Number.isFinite(v.currentTime) ? v.currentTime : null;
   setPlayOpts((p) => ({ ...p, ...patch }));
   setMenu(null);
 };
 /** Auto quality's step down: 720p first (Auto is already at most 8 Mbps away from home), then 480p, never lower. */
 autoStepDownRef.current = () => {
   if (playOpts.quality !== 0) return;
   const next = playOpts.autoStep ? playOpts.autoStep + 1 : 2;
   if (next >= ZFLIX_QUALITY_OPTIONS.length) return;
   const v = videoRef.current;
   resumeAtRef.current = v && Number.isFinite(v.currentTime) ? v.currentTime : null;
   setPlayOpts((p) => ({ ...p, autoStep: next }));
   setQualityNote(`Slow connection: picture lowered to ${ZFLIX_QUALITY_OPTIONS[next].maxHeight}p so it keeps playing.`);
 };
 const pickSubtitle = (s) => {
   setSubNote(null);
   if (s && !s.text) {
     setTextSub(-1);
     changeStream({ burnSub: s.index });
     return;
   }
   setTextSub(s ? s.index : -1);
   if (playOpts.burnSub != null) changeStream({ burnSub: null });
   else setMenu(null);
 };
 const [subLook, setSubLook] = useState(readSubLook);
 const saveSubLook = (v) => {
   setSubLook(v);
   try {
     window.localStorage.setItem(subLookKey(), JSON.stringify(v));
   } catch (_) {}
 };
 const buildMenuItems = (name) => {
   if (name === 'audio') {
     return tracks.audio.map((a) => ({ label: a.label, on: a.selected, act: () => changeStream({ audioStreamIndex: a.index }) }));
   }
   if (name === 'subs') {
     return [
       { label: 'Off', on: textSub < 0 && playOpts.burnSub == null, act: () => pickSubtitle(null) },
       ...tracks.subtitles.map((s) => ({
         label: s.text ? s.label : `${s.label} (burned in)`,
         on: s.text ? textSub === s.index : playOpts.burnSub === s.index,
         act: () => pickSubtitle(s),
       })),
       /** How they look (this profile, this device). The menu stays open so the change shows straight away. */
       ...Object.keys(SUB_SIZES).map((k) => ({ label: `Size: ${SUB_SIZES[k].label}`, on: subLook.size === k, act: () => saveSubLook({ ...subLook, size: k }) })),
       { label: 'Style: dark box', on: subLook.style !== 'outline', act: () => saveSubLook({ ...subLook, style: 'box' }) },
       { label: 'Style: outline only', on: subLook.style === 'outline', act: () => saveSubLook({ ...subLook, style: 'outline' }) },
     ];
   }
   if (name === 'quality') {
     return ZFLIX_QUALITY_OPTIONS.map((q, i) => ({
       label: i === 0 && playOpts.autoStep ? `Auto (lowered to ${ZFLIX_QUALITY_OPTIONS[playOpts.autoStep].maxHeight}p for a slow connection)` : q.label,
       on: playOpts.quality === i,
       act: () => changeStream({ quality: i, autoStep: 0 }),
     }));
   }
   return [];
 };
 const openMenu = (name) => {
   const items = buildMenuItems(name);
   setMenuIdx(Math.max(0, items.findIndex((it) => it.on)));
   setMenu(name);
   setShowControls(true);
 };
 const menuItems = menu ? buildMenuItems(menu) : [];
 const controls = [
   ...(canSeek ? [{ id: 'rew', label: '« Rewind', act: () => nudgeSeek(-1) }] : []),
   { id: 'play', icon: playing ? 'pause' : 'play', act: () => togglePlay() },
   ...(canSeek ? [{ id: 'fwd', label: 'Forward »', act: () => nudgeSeek(1) }] : []),
   ...(isLiveDvr ? [{ id: 'golive', label: 'Live ⏭', act: () => { const v = videoRef.current; const w = seekWin(v); if (v && w) v.currentTime = Math.max(w.lo, w.hi - Math.max(3, edgeGap)); } }] : []),
   ...(item.liveStreamId && !mini && !onTvApp && typeof window.__zflixLiveTvList === 'function' ? [{ id: 'chlist', label: 'Channels', act: () => setChList(window.__zflixLiveTvList()) }] : []),
   ...(item.liveStreamId && !mini ? [{ id: 'rec', label: '● Record', act: () => recordLiveNow() }] : []),
   ...(item.zflixLiveKey && !mini ? [{ id: 'saver', label: liveSaverOn() ? 'Data saver: on' : 'Data saver: off', act: () => { setLiveSaver(!liveSaverOn()); window.__zflixLiveTvTune?.(item.zflixLiveKey); } }] : []),
   ...(isJfVod && tracks.audio.length > 1 ? [{ id: 'audio', label: 'Audio', act: () => openMenu('audio') }] : []),
   ...(isJfVod && tracks.subtitles.length ? [{ id: 'subs', label: 'Subtitles', act: () => openMenu('subs') }] : []),
   ...(isJfVod ? [{ id: 'quality', label: 'Quality', act: () => openMenu('quality') }] : []),
 ];
 const playIdx = Math.max(0, controls.findIndex((c) => c.id === 'play'));
 const curIdx = Math.min(focusIdx, controls.length - 1);
 /** Seek bar range: the saved hour for live TV (read on each render; the clock ticks it along), else the whole title. */
 const liveWin = isLiveDvr ? seekWin(videoRef.current) : null;
 const barLo = liveWin ? liveWin.lo : 0;
 const barSpan = liveWin ? Math.max(1, liveWin.hi - liveWin.lo) : duration;

 const inSeg = (s) => Array.isArray(s) && progress >= s[0] && progress < s[1] - 1.5;
 const skipNow = segs ? (inSeg(segs.recap) ? { label: 'Skip Recap', to: segs.recap[1] } : inSeg(segs.intro) ? { label: 'Skip Intro', to: segs.intro[1] } : null) : null;
 const doSkip = () => {
   const v = videoRef.current;
   if (v && skipNow) v.currentTime = skipNow.to;
 };

 /** One entry point for the remote: arrows, OK, play/pause, rewind / fast-forward. */
 const handleRemote = (a) => {
   /** Someone is at the remote: "Are you still watching?" starts its count over. */
   noteViewerActive();
   try {
     rootRef.current?.focus({ preventScroll: true });
   } catch (_) {}
   if (a === 'chup' || a === 'chdown') {
     if (item.liveStreamId) stepChannel(a === 'chup' ? 1 : -1);
     return;
   }
   if (menu) {
     if (a === 'up') setMenuIdx((i) => Math.max(0, i - 1));
     else if (a === 'down') setMenuIdx((i) => Math.min(menuItems.length - 1, i + 1));
     else if (a === 'ok') menuItems[Math.min(menuIdx, menuItems.length - 1)]?.act();
     else if (a === 'left') setMenu(null);
     else if (a === 'playpause') togglePlay();
     return;
   }
   if (a === 'playpause') {
     togglePlay();
     resetHide();
     return;
   }
   /** Live TV in full screen: Up = next channel, Down = previous (in the Live TV list). */
   if (item.liveStreamId && (a === 'up' || a === 'down') && stepChannel(a === 'up' ? 1 : -1)) return;
   /** Dedicated rewind / fast-forward keys start at 1-minute steps. */
   if (a === 'rew' || a === 'ff') {
     setSeekMode(true);
     nudgeSeek(a === 'rew' ? -1 : 1, 7);
     return;
   }
   /** Skip Intro / Skip Recap on screen: OK presses it, like Netflix (Play/Pause still pauses). */
   if (a === 'ok' && skipNow && !seekMode && !menu) {
     doSkip();
     return;
   }
   /** On the timeline (controls hidden, or moved up to it): Left / Right scrub, OK jumps there now, Down goes to the buttons. */
   if (!showControls || seekMode) {
     if ((a === 'left' || a === 'right') && canSeek) {
       setSeekMode(true);
       nudgeSeek(a === 'left' ? -1 : 1);
       return;
     }
     if (a === 'ok' && seekAccRef.current.target != null) {
       commitSeek();
       resetHide();
       return;
     }
     setSeekMode(false);
     setFocusIdx(playIdx);
     /** With the bar hidden, OK only brings it up. Muted video still takes OK, so the first press turns the sound on. */
     if (a === 'ok' && (showControls || videoRef.current?.muted)) togglePlay();
     resetHide();
     return;
   }
   if (a === 'up' && canSeek) {
     setSeekMode(true);
     resetHide();
     return;
   }
   if (a === 'left') setFocusIdx(Math.max(0, curIdx - 1));
   else if (a === 'right') setFocusIdx(Math.min(controls.length - 1, curIdx + 1));
   else if (a === 'ok') controls[curIdx]?.act();
   else if (a === 'down') {
     if (!videoRef.current?.paused) {
       setShowControls(false);
       return;
     }
   }
   resetHide();
 };
 uiRef.current = {
   menu,
   handle: handleRemote,
   /** Back closes an open menu first; otherwise the app closes the player. */
   back: () => {
     if (chList) {
       setChList(null);
       return true;
     }
     if (!menu) return false;
     setMenu(null);
     return true;
   },
   close: handleClose,
 };

 return (
   <div
     ref={rootRef}
     tabIndex={-1}
     {...(mini ? { 'data-zflix-player-mini': 'true' } : { 'data-zflix-player': true })}
     title={mini ? 'Back to full screen' : undefined}
     style={
       mini
         ? {
             position: 'fixed',
             zIndex: 900,
             background: '#000',
             outline: 'none',
             cursor: 'pointer',
             /**
              * Fire TV draws video on a layer behind the page and shows it through a hole. A rounded clip around
              * the video makes that hole show black (sound, no picture), so the TV app gets square corners and no clip.
              */
             ...(typeof document !== 'undefined' && document.documentElement.classList.contains('zflix-tv-apk-density')
               ? {}
               : { overflow: 'hidden', borderRadius: miniRect ? 10 : 8 }),
             ...(miniRect
               ? { left: miniRect.left, top: miniRect.top, width: miniRect.width, height: miniRect.height }
               : { right: 20, bottom: 20, width: 'min(30vw, 420px)', aspectRatio: '16 / 9', boxShadow: '0 10px 40px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.18)' }),
           }
         : { position: 'fixed', inset: 0, zIndex: 2000, background: '#000', outline: 'none', touchAction: item.liveStreamId && !chList ? 'none' : 'manipulation' }
     }
     onTouchStart={mini ? undefined : onPlayerTouchStart}
     onTouchEnd={mini ? undefined : onPlayerTouchEnd}
     onMouseMove={mini ? undefined : resetHide}
     onClick={(e) => {
       if (mini) {
         onExpand?.();
         return;
       }
       /** detail 0 = the TV app's OK button (it calls .click() on the focused element), not a tap. */
       if (e.detail === 0) handleRemote('ok');
       else if (Date.now() - lastTouchAtRef.current < 800) onPlayerTap(e);
       else togglePlay();
     }}
   >
     <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }}
       onPlay={(e) => {
         setPlaying(true);
         setNeedsSoundTap(!!e.target.muted && !cleanMuteActiveRef.current);
         resetHide();
       }}
       onVolumeChange={(e) => {
         const el = e.target;
         /** A clean-playback mute (or its release) is not the viewer touching the sound. */
         if (cleanMuteActiveRef.current || cleanMutesRef.current.some((x) => Math.abs(el.currentTime - x.endSec) < 0.5)) return;
         setNeedsSoundTap(!!el.muted && !el.paused);
         if (!el.muted && el.volume > 0) setShowControls(true);
       }}
       onTimeUpdate={(e) => {
         const v = e.target;
         const pad = visualSkipPadRef.current;
         zflixApplyVisualSkips(v, visualSkipZonesRef.current, pad.before, pad.after, visualSkipEnabled);
         /** A scene skip may have just moved the video onto (or off) a flagged line. */
         cleanMuteApplyRef.current?.();
         setProgress(v.currentTime);
         if (item.recId && Date.now() - tapRef.current.savedAt > 15000) {
           tapRef.current.savedAt = Date.now();
           fetch(`/api/dvr/progress/${item.recId}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ position: v.currentTime, duration: Number.isFinite(v.duration) ? v.duration : 0 }) }).catch(() => {});
         }
         const ps = playSessionIdRef.current;
         if (!ps || item.liveStreamId || item.source !== 'jellyfin') return;
         if (item.type !== 'movie' && item.type !== 'episode') return;
         const now = Date.now();
         if (now - lastJfProgressSentRef.current < 8000) return;
         lastJfProgressSentRef.current = now;
         reportJfVodProgress(v.paused);
       }}
       onPause={() => {
         setPlaying(false);
         reportJfVodProgress(true);
       }}
       onLoadedMetadata={(e) => setDuration(e.target.duration)}
       onEnded={() => {
         reportJfVodStopped(true);
         onEndedRef.current?.(item);
       }}
     />
     {chBanner && !mini ? (
       <div style={{ position: 'absolute', top: onTvApp ? 28 : 72, left: 28, zIndex: 5, display: 'flex', alignItems: 'center', gap: 14, maxWidth: '60vw', padding: '12px 18px', borderRadius: 12, background: 'rgba(10,10,14,0.82)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', pointerEvents: 'none' }}>
         {chBanner.logo ? <img src={chBanner.logo} alt="" style={{ height: 40, maxWidth: 90, objectFit: 'contain' }} /> : null}
         <div style={{ minWidth: 0 }}>
           <div style={{ fontSize: 22, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chBanner.name}</div>
           {chBanner.program ? <div style={{ fontSize: 16, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chBanner.program}</div> : null}
           <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{chBanner.pending ? 'Changing channel…' : chBanner.note || (lastTouchAtRef.current ? 'Swipe up or down to change channel' : '▲ ▼ to change channel')}</div>
         </div>
       </div>
     ) : null}
     <style>{subLookCss(subLook)}</style>
     {/* A loading circle, not words (Zach, 2026-11-03). Not over a paused picture: a pause after a jump never sends "playing". */}
     {(bootstrapping || (buffering && playing)) && (
       <div role="status" aria-label={bootstrapping ? 'Starting playback' : 'Loading'} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 2 }}>
         <style>{'@keyframes zflix-spin{to{transform:rotate(360deg)}}'}</style>
         <div data-zflix-spinner style={{ width: 56, height: 56, borderRadius: '50%', border: '5px solid rgba(255,255,255,0.22)', borderTopColor: '#e50914', animation: 'zflix-spin 0.9s linear infinite', filter: 'drop-shadow(0 1px 6px rgba(0,0,0,0.7))' }} />
       </div>
     )}
     {qualityNote && !mini && !subNote && (
       <div style={{ position: 'absolute', top: 28, right: 28, zIndex: 5, maxWidth: '50vw', padding: '10px 16px', borderRadius: 10, background: 'rgba(10,10,14,0.82)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', fontSize: 15, pointerEvents: 'none' }}>
         {qualityNote}
       </div>
     )}
     {subNote && !mini && (
       <div style={{ position: 'absolute', top: 28, right: 28, zIndex: 5, maxWidth: '50vw', padding: '10px 16px', borderRadius: 10, background: 'rgba(10,10,14,0.82)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', fontSize: 15, pointerEvents: 'none' }}>
         {subNote === 'loading' ? 'Loading subtitles… the first time for a title can take a few minutes. Keep watching, they will appear on their own.' : 'Those subtitles would not load. Pick them again, or try another track.'}
       </div>
     )}
     {skipNow && !mini ? (
       <button
         type="button"
         tabIndex={-1}
         data-zflix-skip-segment
         onClick={(e) => {
           e.stopPropagation();
           doSkip();
         }}
         style={{ position: 'absolute', right: 32, bottom: showControls ? 150 : 56, zIndex: 6, padding: '10px 22px', fontSize: 17, fontWeight: 700, color: '#fff', background: 'rgba(20,20,24,0.82)', border: '2px solid rgba(255,255,255,0.9)', borderRadius: 8, cursor: 'pointer' }}
       >
         {skipNow.label}
       </button>
     ) : null}
     {needsSoundTap && !mini && (
       <div style={{ position: 'absolute', bottom: 100, left: 16, right: 16, textAlign: 'center', pointerEvents: 'none', zIndex: 3, color: '#fff', fontSize: 15, fontWeight: 600, textShadow: '0 1px 8px #000' }}>
         Tap video for sound
       </div>
     )}
     {menu && !mini && (
       <div
         onClick={(e) => e.stopPropagation()}
         style={{ position: 'absolute', right: 24, bottom: 120, zIndex: 4, minWidth: 260, maxWidth: '60vw', maxHeight: '60vh', overflowY: 'auto', background: 'rgba(18,18,24,0.96)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 8 }}
       >
         <div style={{ padding: '6px 14px 8px', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text2)' }}>
           {menu === 'audio' ? 'Audio' : menu === 'subs' ? 'Subtitles' : 'Quality'}
         </div>
         {menuItems.map((it, i) => (
           <div
             key={`${menu}-${i}`}
             ref={i === menuIdx ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
             onClick={it.act}
             style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 6, fontSize: 15, color: '#fff', cursor: 'pointer', background: i === menuIdx ? 'var(--accent)' : 'transparent' }}
           >
             <span style={{ width: 16 }}>{it.on ? '✓' : ''}</span>
             {it.label}
           </div>
         ))}
       </div>
     )}
     {chList && !mini ? (
       <div data-zflix-player-controls="true" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(360px, 85vw)', zIndex: 7, overflowY: 'auto', padding: '10px 8px', background: 'rgba(10,10,14,0.95)', borderLeft: '1px solid rgba(255,255,255,0.14)' }}>
         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px 10px' }}>
           <b style={{ fontSize: 16, color: '#fff' }}>Channels</b>
           <Btn variant="ghost" tabIndex={-1} onClick={() => setChList(null)} style={{ padding: '6px 12px', fontSize: 13 }}>Close</Btn>
         </div>
         {chList.map((c) => (
           <button
             key={c.key}
             type="button"
             ref={c.key === item.zflixLiveKey ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
             onClick={() => {
               setChList(null);
               if (c.key === item.zflixLiveKey) return;
               setChBanner({ name: c.name, logo: c.logo, pending: true });
               window.__zflixLiveTvTune?.(c.key);
             }}
             style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 54, padding: '6px 8px', marginBottom: 2, border: 'none', borderRadius: 8, textAlign: 'left', cursor: 'pointer', color: '#fff', background: c.key === item.zflixLiveKey ? 'rgba(229,9,20,0.3)' : 'transparent' }}
           >
             {c.logo ? <img src={c.logo} alt="" loading="lazy" style={{ width: 44, height: 32, objectFit: 'contain', flex: 'none' }} /> : <span style={{ width: 44, flex: 'none' }} />}
             <span style={{ minWidth: 0 }}>
               <span style={{ display: 'block', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
               {c.now ? <span style={{ display: 'block', fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.now}</span> : null}
             </span>
           </button>
         ))}
       </div>
     ) : null}
     {showControls && !mini && (
       <>
         {/* The TV remote has its own Back. This one does what a phone's Back button does. */}
         {!onTvApp ? (
           <Btn variant="ghost" tabIndex={-1} aria-label="Back" onClick={(e) => { e.stopPropagation(); window.history.back(); }} style={{ position: 'absolute', top: 12, left: 12, zIndex: 6, padding: '8px 14px', fontSize: 14 }}>
             <Icon name="back" size={18} /> Back
           </Btn>
         ) : null}
         <div data-zflix-player-controls="true" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 16, background: 'linear-gradient(to top,rgba(0,0,0,0.85),transparent)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
           {programNow() ? <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 6px #000' }}>{programNow()}</div> : null}
           {!item.liveStreamId && (vodTitle || item.title || item.name) ? (
             <div data-zflix-player-title="true" style={{ marginBottom: 8, color: '#fff', textShadow: '0 1px 6px #000', minWidth: 0 }}>
               <div style={{ fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vodTitle ? vodTitle.title : item.title || item.name}</div>
               {vodTitle?.sub ? <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vodTitle.sub}</div> : null}
             </div>
           ) : null}
           {(isLiveDvr ? !!liveWin : duration > 0) && (
             <div style={{ marginBottom: 12 }}>
               {seekPreview != null && (
                 <div style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 8, textShadow: '0 1px 6px #000' }}>
                   {liveWin ? (liveWin.hi - seekPreview > 8 ? `−${fmt(liveWin.hi - seekPreview)} from live` : 'Live') : fmt(seekPreview)} <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text2)' }}>{seekPreview >= progress ? '+' : '−'}{fmt(Math.abs(seekPreview - progress))}</span>
                 </div>
               )}
               <div style={{ height: seekMode ? 8 : 4, background: 'rgba(255,255,255,0.2)', borderRadius: 4, cursor: 'pointer', outline: seekMode ? '2px solid var(--accent)' : 'none', outlineOffset: 3, position: 'relative' }} onClick={e => { const r = e.currentTarget.getBoundingClientRect(); const at = barLo + ((e.clientX - r.left) / r.width) * barSpan; videoRef.current.currentTime = edgeGap && liveWin ? Math.min(at, liveWin.hi - edgeGap) : at; }}>
                 <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, ((progress - barLo) / barSpan) * 100))}%`, background: 'var(--accent)', borderRadius: 4 }} />
                 {seekPreview != null && (
                   <div style={{ position: 'absolute', top: -4, bottom: -4, width: 4, marginLeft: -2, left: `${Math.min(100, Math.max(0, ((seekPreview - barLo) / barSpan) * 100))}%`, background: '#fff', borderRadius: 2 }} />
                 )}
               </div>
               <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                 {liveWin ? (
                   <><span>{liveWin.hi - progress > 8 ? `−${fmt(liveWin.hi - progress)} behind live` : 'LIVE'}</span><span>{fmt(barSpan)} saved · ◀ ▶ to rewind</span></>
                 ) : (
                   <><span>{fmt(progress)}</span><span>{fmt(duration)}</span></>
                 )}
               </div>
             </div>
           )}
           <div style={{ display: 'flex', justifyContent: 'center', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
             {controls.map((c, i) => (
               <Btn
                 key={c.id}
                 variant="ghost"
                 tabIndex={-1}
                 onClick={() => {
                   setFocusIdx(i);
                   c.act();
                   resetHide();
                 }}
                 style={{
                   padding: c.icon ? '10px 24px' : '10px 16px',
                   fontSize: 14,
                   outline: i === curIdx && !seekMode ? '3px solid var(--accent)' : 'none',
                   background: i === curIdx && !seekMode ? 'rgba(255,255,255,0.16)' : undefined,
                 }}
               >
                 {c.icon ? <Icon name={c.icon} size={24} /> : c.label}
               </Btn>
             ))}
             <Btn variant="ghost" tabIndex={-1} onClick={() => {
              /**
               * Full screen applies to the whole player, not the bare <video>. With only the video full screen the
               * browser draws it in its own layer: subtitles did not show there and the player's own controls
               * (subtitles, audio, quality) were left behind. Phones that cannot do this fall back to the video.
               */
              const root = rootRef.current;
              const v = videoRef.current;
              const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
              if (fsEl) {
                (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
                return;
              }
              const req = root && (root.requestFullscreen || root.webkitRequestFullscreen);
              if (req) {
                try {
                  const p = req.call(root);
                  if (p && typeof p.catch === 'function') p.catch(() => v?.requestFullscreen?.());
                } catch (_) {
                  v?.requestFullscreen?.();
                }
              } else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();
              else v?.requestFullscreen?.();
            }} style={{ padding: '10px 16px' }} aria-label="Fullscreen"><Icon name="fullscreen" size={20} /></Btn>
             {canTvSend ? (
               <Btn variant="ghost" tabIndex={-1} onClick={() => setTvSendOpen(true)} style={{ padding: '10px 16px', fontSize: 14 }}>Play on TV</Btn>
             ) : null}
             {visualSkipCount > 0 ? (
               <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', cursor: 'pointer', userSelect: 'none' }}>
                 <input
                   type="checkbox"
                   checked={visualSkipEnabled}
                   onChange={(ev) => {
                     const on = ev.target.checked;
                     setVisualSkipEnabled(on);
                     try {
                       localStorage.setItem('zflix_visual_skip_enabled', on ? '1' : '0');
                     } catch (_) {}
                   }}
                 />
                 Skip mapped scenes ({visualSkipCount})
               </label>
             ) : null}
           </div>
         </div>
       </>
     )}
     {tvSendOpen && canTvSend ? (
       <TvSendPicker
         item={item}
         getPositionSec={() => videoRef.current?.currentTime || 0}
         onClose={() => {
           setTvSendOpen(false);
           /** It is playing on the TV now: closing the remote sheet closes this player too. */
           if (tvSentRef.current) onCloseRef.current?.();
         }}
         onPlaying={() => {
           tvSentRef.current = true;
           try {
             videoRef.current?.pause();
           } catch (_) {}
         }}
       />
     ) : null}
   </div>
 );
}

function AudioPlayer({ track, tracks, onClose }) {
 const audioRef = useRef();
 const [playing, setPlaying] = useState(true);
 const [progress, setProgress] = useState(0);
 const [duration, setDuration] = useState(0);
 const [currentIdx, setCurrentIdx] = useState(tracks ? tracks.findIndex(t => t.id === track.id) : 0);
 const current = tracks ? tracks[currentIdx] : track;

 useEffect(() => { if (audioRef.current) audioRef.current.play().catch(() => {}); }, [currentIdx]);

 const fmt = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

 return (
   <div className="zflix-audio-bar" style={{ background: 'var(--bg2)', borderTop: '1px solid var(--border)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)' }}>
     <audio ref={audioRef} src={current?.streamUrl} autoPlay onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onTimeUpdate={e => setProgress(e.target.currentTime)} onLoadedMetadata={e => setDuration(e.target.duration)} onEnded={() => setCurrentIdx(i => Math.min((tracks?.length || 1) - 1, i + 1))} />
     <div style={{ flex: 1, minWidth: 0 }}>
       <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{current?.name}</div>
       <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>Track {currentIdx + 1} of {tracks?.length || 1}</div>
     </div>
     <div style={{ flex: 2, minWidth: 0 }}>
       <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, cursor: 'pointer', marginBottom: 4 }} onClick={e => { const r = e.currentTarget.getBoundingClientRect(); audioRef.current.currentTime = ((e.clientX - r.left) / r.width) * duration; }}>
         <div style={{ height: '100%', width: `${duration ? (progress / duration) * 100 : 0}%`, background: 'var(--accent)', borderRadius: 2 }} />
       </div>
       <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)' }}>
         <span>{fmt(progress)}</span><span>{fmt(duration)}</span>
       </div>
     </div>
     <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
       <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0} style={{ background: 'none', border: 'none', color: currentIdx === 0 ? 'var(--text3)' : 'var(--text)', cursor: 'pointer', padding: 4 }}><Icon name="prev" size={20} /></button>
       <button onClick={() => playing ? audioRef.current?.pause() : audioRef.current?.play()} style={{ background: 'var(--accent)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><Icon name={playing ? 'pause' : 'play'} size={20} /></button>
       <button onClick={() => setCurrentIdx(i => Math.min((tracks?.length || 1) - 1, i + 1))} disabled={currentIdx === (tracks?.length || 1) - 1} style={{ background: 'none', border: 'none', color: currentIdx === (tracks?.length || 1) - 1 ? 'var(--text3)' : 'var(--text)', cursor: 'pointer', padding: 4 }}><Icon name="next" size={20} /></button>
       <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', padding: 4, marginLeft: 8 }}><Icon name="close" size={18} /></button>
     </div>
   </div>
 );
}

function AudiobookPlayer({ session, onClose }) {
 const audioRef = useRef();
 const [playing, setPlaying] = useState(true);
 const [progress, setProgress] = useState(0);
 const [duration, setDuration] = useState(0);
 const [currentIdx, setCurrentIdx] = useState(0);
 const tracks = session?.tracks || [];
 const current = tracks[currentIdx];

 useEffect(() => {
   return () => {
     const sid = session?.sessionId;
     if (sid) {
       const closeBase = session?.episodeId ? '/api/podcasts/session' : '/api/audiobooks/session';
       fetch(`${closeBase}/${encodeURIComponent(sid)}/close`, {
         method: 'POST',
         credentials: 'include',
         headers: { 'Content-Type': 'application/json' },
         body: '{}',
       }).catch(() => {});
     }
   };
 }, [session?.sessionId]);

 useEffect(() => {
   if (audioRef.current) audioRef.current.play().catch(() => {});
 }, [currentIdx, current?.streamUrl]);

 const fmt = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
 if (!session || tracks.length === 0) return null;

 return (
   <div className="zflix-audio-bar" style={{ background: 'var(--bg2)', borderTop: '1px solid var(--border)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)' }}>
     <img src={session.coverUrl} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
     <audio
       ref={audioRef}
       src={current.streamUrl}
       autoPlay
       onPlay={() => setPlaying(true)}
       onPause={() => setPlaying(false)}
       onTimeUpdate={(e) => setProgress(e.target.currentTime)}
       onLoadedMetadata={(e) => setDuration(e.target.duration)}
       onEnded={() => setCurrentIdx((i) => Math.min(tracks.length - 1, i + 1))}
     />
     <div style={{ flex: 1, minWidth: 0 }}>
       <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.title}</div>
       <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
         {session.author ? `${session.author} · ` : ''}
         {current.title || `Track ${currentIdx + 1}`} ({currentIdx + 1}/{tracks.length})
       </div>
     </div>
     <div style={{ flex: 2, minWidth: 0 }}>
       <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, cursor: 'pointer', marginBottom: 4 }} onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); if (audioRef.current && duration) audioRef.current.currentTime = ((e.clientX - r.left) / r.width) * duration; }}>
         <div style={{ height: '100%', width: `${duration ? (progress / duration) * 100 : 0}%`, background: 'var(--accent)', borderRadius: 2 }} />
       </div>
       <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)' }}>
         <span>{fmt(progress)}</span><span>{fmt(duration)}</span>
       </div>
     </div>
     <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
       <button type="button" onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))} disabled={currentIdx === 0} style={{ background: 'none', border: 'none', color: currentIdx === 0 ? 'var(--text3)' : 'var(--text)', cursor: 'pointer', padding: 4 }}><Icon name="prev" size={20} /></button>
       <button type="button" onClick={() => (playing ? audioRef.current?.pause() : audioRef.current?.play())} style={{ background: 'var(--accent)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><Icon name={playing ? 'pause' : 'play'} size={20} /></button>
       <button type="button" onClick={() => setCurrentIdx((i) => Math.min(tracks.length - 1, i + 1))} disabled={currentIdx === tracks.length - 1} style={{ background: 'none', border: 'none', color: currentIdx === tracks.length - 1 ? 'var(--text3)' : 'var(--text)', cursor: 'pointer', padding: 4 }}><Icon name="next" size={20} /></button>
       <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', padding: 4, marginLeft: 8 }}><Icon name="close" size={18} /></button>
     </div>
   </div>
 );
}

function AudiobooksView({ onItemClick, searchQuery }) {
 const [libraries, setLibraries] = useState([]);
 const [libId, setLibId] = useState('');
 const [items, setItems] = useState([]);
 const [total, setTotal] = useState(0);
 const [page, setPage] = useState(0);
 const [loading, setLoading] = useState(true);
 const [loadingMoreAbs, setLoadingMoreAbs] = useState(false);
 const [svc, setSvc] = useState(null);
 const itemsLenRef = useRef(0);
 const totalAbsRef = useRef(0);
 const [contAbs, setContAbs] = useState([]);
 const [recentAbs, setRecentAbs] = useState([]);
 const [genreRowsAbs, setGenreRowsAbs] = useState([]);
 const [browseLoadingAbs, setBrowseLoadingAbs] = useState(false);
 const [absSearchItems, setAbsSearchItems] = useState([]);
 const [absSearchLoading, setAbsSearchLoading] = useState(false);
 const absSearchTimer = useRef();

 useEffect(() => {
   fetch('/api/audiobooks/status')
     .then((r) => r.json())
     .then(setSvc)
     .catch(() => {});
 }, []);

 useEffect(() => {
   fetch('/api/audiobooks/libraries')
     .then((r) => r.json())
     .then((libs) => {
       const arr = Array.isArray(libs) ? libs : [];
       setLibraries(arr);
       if (arr[0]?.id) setLibId(arr[0].id);
       setLoading(false);
     })
     .catch(() => setLoading(false));
 }, []);

 useEffect(() => {
   setPage(0);
   setItems([]);
 }, [libId]);

 useEffect(() => {
   const q = (searchQuery || '').trim();
   clearTimeout(absSearchTimer.current);
   if (!q || !libId) {
     setAbsSearchItems([]);
     setAbsSearchLoading(false);
     return;
   }
   setAbsSearchLoading(true);
   absSearchTimer.current = setTimeout(() => {
     fetch(`/api/audiobooks/search?q=${encodeURIComponent(q)}&libraryId=${encodeURIComponent(libId)}`)
       .then((r) => r.json())
       .then((d) => {
         setAbsSearchItems(Array.isArray(d.items) ? d.items : []);
       })
       .catch(() => setAbsSearchItems([]))
       .finally(() => setAbsSearchLoading(false));
   }, 350);
   return () => clearTimeout(absSearchTimer.current);
 }, [searchQuery, libId]);

 useEffect(() => {
   itemsLenRef.current = items.length;
 }, [items.length]);
 useEffect(() => {
   totalAbsRef.current = total;
 }, [total]);

 useEffect(() => {
   if (!libId) return;
   if ((searchQuery || '').trim()) return;
   const firstPage = page === 0;
   if (firstPage) setLoading(true);
   else setLoadingMoreAbs(true);
   fetch(`/api/audiobooks/items?libraryId=${encodeURIComponent(libId)}&limit=48&page=${page}`)
     .then((r) => r.json())
     .then((d) => {
       const chunk = d.items || [];
       setItems((prev) => (page === 0 ? chunk : [...prev, ...chunk]));
       setTotal(typeof d.total === 'number' ? d.total : chunk.length);
     })
     .catch(() => {})
     .finally(() => {
       setLoading(false);
       setLoadingMoreAbs(false);
     });
 }, [libId, page, searchQuery]);

 useEffect(() => {
   if (!libId || !svc || svc.configured === false) {
     setContAbs([]);
     setRecentAbs([]);
     setGenreRowsAbs([]);
     setBrowseLoadingAbs(false);
     return;
   }
   let cancelled = false;
   setBrowseLoadingAbs(true);
   (async () => {
     try {
       const [rCont, rRecent, rGenres] = await Promise.all([
         fetch(`/api/audiobooks/in-progress?libraryId=${encodeURIComponent(libId)}`).then((r) => (r.ok ? r.json() : [])),
         fetch(`/api/audiobooks/recent?libraryId=${encodeURIComponent(libId)}`).then((r) => (r.ok ? r.json() : [])),
         fetch(`/api/audiobooks/genres?libraryId=${encodeURIComponent(libId)}`).then((r) => (r.ok ? r.json() : [])),
       ]);
       const genres = Array.isArray(rGenres) ? rGenres.slice(0, 6) : [];
       const rows = await Promise.all(
         genres.map(async (g) => {
           const rr = await fetch(
             `/api/audiobooks/by-genre?libraryId=${encodeURIComponent(libId)}&genre=${encodeURIComponent(g.name)}&limit=18`,
           );
           const rowItems = rr.ok ? await rr.json() : [];
           return { key: g.name, title: g.name, items: Array.isArray(rowItems) ? rowItems : [] };
         }),
       );
       if (!cancelled) {
         setContAbs(Array.isArray(rCont) ? rCont : []);
         setRecentAbs(Array.isArray(rRecent) ? rRecent : []);
         setGenreRowsAbs(rows.filter((row) => row.items?.length));
         setBrowseLoadingAbs(false);
       }
     } catch {
       if (!cancelled) setBrowseLoadingAbs(false);
     }
   })();
   return () => { cancelled = true; };
 }, [libId, svc]);

 if (svc && svc.configured === false) {
   return (
     <div style={{ maxWidth: 560 }}>
       <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Audiobooks</h2>
       <p style={{ color: 'var(--text2)', lineHeight: 1.6, marginBottom: 14 }}>
         This tab talks to <strong>Audiobookshelf</strong> (your player). Add <code style={{ fontSize: 12 }}>AUDIOBOOKSHELF_API_KEY</code> in compose (Audiobookshelf → Settings → API keys — Bearer token), rebuild <code style={{ fontSize: 12 }}>zfin-backend</code>, then reload.
       </p>
       {svc.bookshelfConfigured ? (
         <p style={{ color: 'var(--text2)', lineHeight: 1.6, fontSize: 13 }}>
           Discover → <strong>eBooks</strong> uses ebook Bookshelf; Discover → <strong>Audiobooks</strong> uses the optional audiobook-only instance when{' '}
           <code style={{ fontSize: 12 }}>BOOKSHELF_AUDIOBOOK_URL</code> / <code style={{ fontSize: 12 }}>BOOKSHELF_AUDIOBOOK_API_KEY</code> are set on{' '}
           <code style={{ fontSize: 12 }}>zfin-backend</code>
           {svc.bookshelfAudiobookConfigured ? ' (configured).' : ' (not set yet — audiobook requests will fail until you add them).'}
         </p>
       ) : (
         <p style={{ color: 'var(--text2)', lineHeight: 1.6, fontSize: 13 }}>
           To request titles, set <code style={{ fontSize: 12 }}>BOOKSHELF_URL</code> and <code style={{ fontSize: 12 }}>BOOKSHELF_API_KEY</code> (same key as in your Bookshelf <code style={{ fontSize: 12 }}>config.xml</code>).
         </p>
       )}
     </div>
   );
 }

 return (
   <div>
     {svc?.bookshelfConfigured && !svc?.bookshelfAudiobookConfigured && (
       <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
         Tip: run a second Bookshelf on port <strong>8788</strong> for audiobook grabs only, then set <code style={{ fontSize: 11 }}>BOOKSHELF_AUDIOBOOK_*</code> in <code style={{ fontSize: 11 }}>zfin/docker-compose.yml</code>.
       </p>
     )}
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
       <h2 style={{ fontSize: 24, fontWeight: 700 }}>Audiobooks</h2>
       <span style={{ color: 'var(--text2)', fontSize: 12 }}>{total} titles</span>
       {libraries.length > 0 && (
         <TvSelect
           value={libId}
           onChange={(e) => {
             setLibId(e.target.value);
             setPage(0);
           }}
           style={{ marginLeft: 'auto', padding: '8px 12px', borderRadius: 8, background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }}
         >
           {libraries.map((l) => (
             <option key={l.id} value={l.id}>
               {l.name}
             </option>
           ))}
         </TvSelect>
       )}
     </div>
     {svc?.configured && libId && !(searchQuery || '').trim() && (
       <>
         <MediaRow title="Continue listening" items={contAbs} onItemClick={onItemClick} loading={browseLoadingAbs && contAbs.length === 0} />
         <MediaRow title="Recently added" items={recentAbs} onItemClick={onItemClick} loading={browseLoadingAbs && recentAbs.length === 0} />
         {genreRowsAbs.map((row) => (
           <MediaRow key={row.key} title={row.title} items={row.items} onItemClick={onItemClick} />
         ))}
       </>
     )}
     {!libId && !loading && <div style={{ color: 'var(--text2)', padding: 40 }}>No audiobook libraries found in Audiobookshelf.</div>}
     {(searchQuery || '').trim() ? (
       <>
         {absSearchLoading && <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 24 }}>Searching…</div>}
         {!absSearchLoading && absSearchItems.length === 0 && (
           <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>No audiobooks match “{searchQuery.trim()}”.</div>
         )}
         {!absSearchLoading && absSearchItems.length > 0 && (
           <div className="card-grid">
             {absSearchItems.map((it) => (
               <MediaCard key={it.id} item={it} onClick={onItemClick} />
             ))}
           </div>
         )}
       </>
     ) : (
     <div className="card-grid">
       {loading && !items.length
         ? [...Array(16)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} /></div>)
         : items.map((it) => <MediaCard key={it.id} item={it} onClick={onItemClick} />)}
     </div>
     )}
     {!(searchQuery || '').trim() && loadingMoreAbs && items.length > 0 && (
       <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
     )}
     {!(searchQuery || '').trim() && !loading && items.length > 0 && items.length < total && (
       <InfiniteScrollSentinel
         hasMore={items.length < total}
         loading={loadingMoreAbs}
         onLoad={() => {
           if (loadingMoreAbs || itemsLenRef.current >= totalAbsRef.current) return;
           setPage((p) => p + 1);
         }}
       />
     )}
   </div>
 );
}

function PodcastsView({ onItemClick, searchQuery }) {
 const [libraries, setLibraries] = useState([]);
 const [libId, setLibId] = useState('');
 const [items, setItems] = useState([]);
 const [total, setTotal] = useState(0);
 const [page, setPage] = useState(0);
 const [loading, setLoading] = useState(true);
 const [loadingMore, setLoadingMore] = useState(false);
 const [svc, setSvc] = useState(null);
 const itemsLenRef = useRef(0);
 const totalRef = useRef(0);
 const [contPod, setContPod] = useState([]);
 const [recentEp, setRecentEp] = useState([]);
 const [browseLoading, setBrowseLoading] = useState(false);
 const [searchItems, setSearchItems] = useState([]);
 const [searchLoading, setSearchLoading] = useState(false);
 const searchTimer = useRef();

 useEffect(() => {
   fetch('/api/podcasts/status')
     .then((r) => r.json())
     .then(setSvc)
     .catch(() => {});
 }, []);

 useEffect(() => {
   fetch('/api/podcasts/libraries')
     .then((r) => r.json())
     .then((libs) => {
       const arr = Array.isArray(libs) ? libs : [];
       setLibraries(arr);
       if (arr[0]?.id) setLibId(arr[0].id);
       setLoading(false);
     })
     .catch(() => setLoading(false));
 }, []);

 useEffect(() => {
   setPage(0);
   setItems([]);
 }, [libId]);

 useEffect(() => {
   const q = (searchQuery || '').trim();
   clearTimeout(searchTimer.current);
   if (!q) {
     setSearchItems([]);
     setSearchLoading(false);
     return;
   }
   setSearchLoading(!!libId);
   searchTimer.current = setTimeout(() => {
     if (!libId) {
       setSearchItems([]);
       setSearchLoading(false);
       return;
     }
     fetch(`/api/podcasts/search?q=${encodeURIComponent(q)}&libraryId=${encodeURIComponent(libId)}`)
       .then((r) => r.json())
       .then((d) => setSearchItems(Array.isArray(d.items) ? d.items : []))
       .catch(() => setSearchItems([]))
       .finally(() => setSearchLoading(false));
   }, 350);
   return () => clearTimeout(searchTimer.current);
 }, [searchQuery, libId]);

 useEffect(() => {
   itemsLenRef.current = items.length;
 }, [items.length]);
 useEffect(() => {
   totalRef.current = total;
 }, [total]);

 useEffect(() => {
   if (!libId) return;
   if ((searchQuery || '').trim()) return;
   const firstPage = page === 0;
   if (firstPage) setLoading(true);
   else setLoadingMore(true);
   fetch(`/api/podcasts?libraryId=${encodeURIComponent(libId)}&limit=48&page=${page}`)
     .then((r) => r.json())
     .then((d) => {
       const chunk = d.items || [];
       setItems((prev) => (page === 0 ? chunk : [...prev, ...chunk]));
       setTotal(typeof d.total === 'number' ? d.total : chunk.length);
     })
     .catch(() => {})
     .finally(() => {
       setLoading(false);
       setLoadingMore(false);
     });
 }, [libId, page, searchQuery]);

 useEffect(() => {
   if (!libId || !svc?.configured) {
     setContPod([]);
     setRecentEp([]);
     setBrowseLoading(false);
     return;
   }
   let cancelled = false;
   setBrowseLoading(true);
   (async () => {
     try {
       const [rCont, rRecent] = await Promise.all([
         fetch(`/api/podcasts/in-progress?libraryId=${encodeURIComponent(libId)}`).then((r) => (r.ok ? r.json() : [])),
         fetch(`/api/podcasts/recent-episodes?libraryId=${encodeURIComponent(libId)}`).then((r) => (r.ok ? r.json() : [])),
       ]);
       if (!cancelled) {
         setContPod(Array.isArray(rCont) ? rCont : []);
         setRecentEp(Array.isArray(rRecent) ? rRecent : []);
         setBrowseLoading(false);
       }
     } catch {
       if (!cancelled) setBrowseLoading(false);
     }
   })();
   return () => { cancelled = true; };
 }, [libId, svc]);

 if (svc && svc.configured === false) {
   return (
     <div style={{ maxWidth: 560 }}>
       <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Podcasts</h2>
       <p style={{ color: 'var(--text2)', lineHeight: 1.6 }}>
         Add <code style={{ fontSize: 12 }}>AUDIOBOOKSHELF_API_KEY</code> in compose (same Audiobookshelf instance as Audiobooks), rebuild <code style={{ fontSize: 12 }}>zfin-backend</code>, then reload.
       </p>
     </div>
   );
 }

 return (
   <div>
     {svc?.podcastDiscoverAvailable || svc?.podcastIndexConfigured ? (
       <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
         Search and request new shows in Discover → <strong>Podcasts</strong>. This tab is your subscribed library in Audiobookshelf.
       </p>
     ) : null}
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
       <h2 style={{ fontSize: 24, fontWeight: 700 }}>Podcasts</h2>
       <span style={{ color: 'var(--text2)', fontSize: 12 }}>{total} shows</span>
       {libraries.length > 0 && (
         <TvSelect
           value={libId}
           onChange={(e) => {
             setLibId(e.target.value);
             setPage(0);
           }}
           style={{ marginLeft: 'auto', padding: '8px 12px', borderRadius: 8, background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }}
         >
           {libraries.map((l) => (
             <option key={l.id} value={l.id}>
               {l.name}
             </option>
           ))}
         </TvSelect>
       )}
     </div>
     {svc?.configured && libId && !(searchQuery || '').trim() && (
       <>
         <MediaRow title="Continue listening" items={contPod} onItemClick={onItemClick} loading={browseLoading && contPod.length === 0} />
         <MediaRow title="Recent episodes" items={recentEp} onItemClick={onItemClick} loading={browseLoading && recentEp.length === 0} />
       </>
     )}
     {!libId && !loading && <div style={{ color: 'var(--text2)', padding: 40 }}>No podcast libraries in Audiobookshelf.</div>}
     {(searchQuery || '').trim() ? (
       <>
         {searchLoading && (
           <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 24 }}>Searching…</div>
         )}
         {!searchLoading && searchItems.length > 0 && (
           <div className="card-grid">
             {searchItems.map((it) => (
               <MediaCard key={it.id} item={it} onClick={onItemClick} />
             ))}
           </div>
         )}
         {!searchLoading && searchItems.length === 0 && (
           <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>
             No podcasts in your library match “{searchQuery.trim()}”. Try Discover → Podcasts to request new shows.
           </div>
         )}
       </>
     ) : (
       <div className="card-grid">
         {loading && !items.length
           ? [...Array(16)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} /></div>)
           : items.map((it) => <MediaCard key={it.id} item={it} onClick={onItemClick} />)}
       </div>
     )}
     {!(searchQuery || '').trim() && loadingMore && items.length > 0 && (
       <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
     )}
     {!(searchQuery || '').trim() && !loading && items.length > 0 && items.length < total && (
       <InfiniteScrollSentinel
         hasMore={items.length < total}
         loading={loadingMore}
         onLoad={() => {
           if (loadingMore || itemsLenRef.current >= totalRef.current) return;
           setPage((p) => p + 1);
         }}
       />
     )}
   </div>
 );
}

/** Shows recent ebook + audiobook Readarr requests (BOOK_REQUESTS_FILE via backend). */
function ReadarrRequestLogPanel({ tab }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch('/api/request/book', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
  }, [tab]);
  const shelfFilter = tab === 'audiobooks' ? 'audiobook' : 'ebook';
  const recent = [...rows].reverse().filter((r) => r && r.shelf === shelfFilter).slice(0, 14);
  if (recent.length === 0) return null;
  return (
    <div
      style={{
        fontSize: 12,
        color: 'var(--text2)',
        marginBottom: 16,
        padding: '10px 12px',
        background: 'var(--surface)',
        borderRadius: 8,
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Recent Readarr requests ({tab === 'audiobooks' ? 'audiobooks' : 'eBooks'})</div>
      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
        {recent.map((r) => (
          <li key={r.id}>
            <span style={{ color: r.status === 'added' ? '#6bcf7f' : 'var(--accent)' }}>{r.status}</span>
            {' · '}
            {r.title || '—'}
            {r.author ? ` — ${r.author}` : ''}
            {r.readarrBookId != null ? (
              <span style={{ color: 'var(--text3)' }}> · book #{r.readarrBookId}</span>
            ) : null}
            <span style={{ color: 'var(--text3)' }}> ({r.added})</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BooksDiscoverView({ onItemClick, showToast, searchQuery }) {
 const [bookResults, setBookResults] = useState([]);
 const [bookSearchErr, setBookSearchErr] = useState('');
 const [bookSearchLoading, setBookSearchLoading] = useState(false);
 const [popular, setPopular] = useState([]);
 const [popularPage, setPopularPage] = useState(1);
 const [popularTotal, setPopularTotal] = useState(0);
 const [loadingPopular, setLoadingPopular] = useState(true);
 const [loadingMorePopular, setLoadingMorePopular] = useState(false);
 const bookTimer = useRef();
 const popularAppendRef = useRef(false);

 const loadPopular = (page, reset) => {
   if (!reset && popularAppendRef.current) return;
   if (!reset) popularAppendRef.current = true;
   if (reset) popularAppendRef.current = false;
   if (reset) setLoadingPopular(true);
   else setLoadingMorePopular(true);
   fetch(`/api/discover/books/popular?page=${page}`)
     .then((r) => r.json())
     .then((d) => {
       const items = d.items || [];
       setPopular((prev) => (reset ? items : [...prev, ...items]));
       setPopularTotal(typeof d.total === 'number' ? d.total : items.length);
       setPopularPage(page);
     })
     .catch(() => {
       if (reset) showToast('Could not load popular eBooks', 'error');
     })
     .finally(() => {
       popularAppendRef.current = false;
       setLoadingPopular(false);
       setLoadingMorePopular(false);
     });
 };

 useEffect(() => {
   loadPopular(1, true);
 }, []);

 useEffect(() => {
   clearTimeout(bookTimer.current);
   const q = (searchQuery || '').trim();
   if (!q) {
     setBookResults([]);
     setBookSearchErr('');
     setBookSearchLoading(false);
     return;
   }
   setBookSearchLoading(true);
   setBookSearchErr('');
   bookTimer.current = setTimeout(() => {
     fetch(`/api/discover/bookshelf-search?q=${encodeURIComponent(q)}`)
       .then(async (r) => {
         const d = await r.json().catch(() => ({}));
         if (!r.ok) {
           setBookResults([]);
           setBookSearchErr(typeof d.error === 'string' ? d.error : 'Search failed');
           return;
         }
         setBookResults(d.items || []);
       })
       .catch(() => {
         setBookResults([]);
         setBookSearchErr('Search failed');
       })
       .finally(() => setBookSearchLoading(false));
   }, 350);
 }, [searchQuery]);

 const searching = !!(searchQuery || '').trim();

 return (
   <div data-zflix-discover-grid>
     {searching ? (
       <>
         {bookSearchErr ? <div style={{ fontSize: 13, color: 'var(--accent)', marginBottom: 12 }}>{bookSearchErr}</div> : null}
         {!bookSearchLoading && !bookSearchErr && bookResults.length === 0 ? (
           <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>No matches.</div>
         ) : null}
         <div className="card-grid">
           {bookSearchLoading && bookResults.length === 0
             ? [...Array(16)].map((_, i) => (
                 <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}>
                   <div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} />
                 </div>
               ))
             : bookResults.map((it, idx) => (
                 <MediaCard key={it.listKey || `book-${idx}`} item={it} onClick={onItemClick} />
               ))}
         </div>
       </>
     ) : (
       <>
         <div className="card-grid">
           {loadingPopular && !popular.length
             ? [...Array(20)].map((_, i) => (
                 <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}>
                   <div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} />
                 </div>
               ))
             : popular.map((it) => (
                 <MediaCard key={it.openLibraryKey || `${it.title}-${it.author}-${it.year}`} item={it} onClick={onItemClick} />
               ))}
         </div>
         <InfiniteScrollSentinel
           hasMore={popular.length > 0 && popular.length < popularTotal}
           loading={loadingMorePopular}
           onLoad={() => {
             if (loadingMorePopular || popular.length >= popularTotal) return;
             loadPopular(popularPage + 1, false);
           }}
         />
       </>
     )}
   </div>
 );
}

function RomsDiscoverView({ showToast }) {
 const [romQuery, setRomQuery] = useState('');
 const [romPlatform, setRomPlatform] = useState('');
 const [platforms, setPlatforms] = useState([]);
 const [romResults, setRomResults] = useState([]);
 const [romList, setRomList] = useState([]);
 const romTimer = useRef();

 const reloadRomList = () => {
   fetch('/api/request/rom')
     .then((r) => r.json())
     .then((d) => setRomList(Array.isArray(d) ? d : []))
     .catch(() => {});
 };

 useEffect(() => {
   fetch('/api/request/rom/platforms')
     .then((r) => r.json())
     .then((d) => {
       const pl = d.platforms || [];
       setPlatforms(pl);
       if (pl[0]?.id) setRomPlatform(pl[0].id);
     })
     .catch(() => {});
   reloadRomList();
 }, []);

 useEffect(() => {
   clearTimeout(romTimer.current);
   if (!romQuery.trim()) {
     setRomResults([]);
     return;
   }
   romTimer.current = setTimeout(() => {
     const pl = platforms.find((p) => p.id === romPlatform);
     const pid = pl?.igdbPlatformId;
     let url = `/api/discover/roms?q=${encodeURIComponent(romQuery.trim())}`;
     if (pid) url += `&platform_id=${pid}`;
     fetch(url)
       .then((r) => r.json())
       .then((d) => setRomResults(d.items || []))
       .catch(() => setRomResults([]));
   }, 350);
 }, [romQuery, romPlatform, platforms]);

 const submitRom = async (game) => {
   const pl = platforms.find((p) => p.id === romPlatform);
   try {
     const res = await fetch('/api/request/rom', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
         game: game.title || game.name,
         platform: romPlatform,
         platform_label: pl?.label,
         cover: game.cover,
         year: game.year,
         igdb_id: game.igdbId,
       }),
     });
     const data = await res.json();
     if (!res.ok) {
       showToast(data.error || 'ROM request failed', 'error');
       return;
     }
     showToast(`${data.game} queued for ROM fetch`, 'success');
     reloadRomList();
   } catch {
     showToast('ROM request failed', 'error');
   }
 };

 return (
   <div>
     <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, maxWidth: 720, lineHeight: 1.5 }}>
       Queue ROMs for your Archive.org helper script. Configure <code style={{ fontSize: 12 }}>ROM_REQUESTS_FILE</code> and Twitch credentials for IGDB search if needed.
     </p>
     <div data-zflix-rom-controls data-zflix-horizontal-controls style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
       <TvSelect value={romPlatform} aria-label="Game console / platform" tabIndex={0} onChange={(e) => setRomPlatform(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 }}>
         {platforms.map((p) => (
           <option key={p.id} value={p.id}>
             {p.label}
           </option>
         ))}
       </TvSelect>
       <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', flex: 1, minWidth: 200 }}>
         <Icon name="search" size={14} />
         <input style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 13, flex: 1 }} placeholder="Search IGDB…" value={romQuery} onChange={(e) => setRomQuery(e.target.value)} />
       </div>
     </div>
     <div data-zflix-rom-results style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
       {romResults.map((g) => (
         <div
           key={g.igdbId}
           tabIndex={0}
           aria-label={`Queue ${g.title || g.name}`}
           onClick={() => submitRom(g)}
           onKeyDown={(e) => {
             if (e.key === 'Enter' || e.key === ' ') {
               e.preventDefault();
               submitRom(g);
             }
           }}
           style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--surface)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)', cursor: 'pointer', outline: 'none' }}
         >
           {g.cover ? <img src={g.cover} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }} /> : <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🎮</div>}
           <div style={{ flex: 1, minWidth: 0 }}>
             <div style={{ fontWeight: 600 }}>{g.title || g.name}</div>
             <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{g.year || ''}{(g.platforms || []).length ? ` · ${g.platforms.slice(0, 4).join(', ')}` : ''}</div>
           </div>
           <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 12, fontWeight: 600, background: 'var(--accent)', color: '#fff', borderRadius: 6 }}>
             <Icon name="plus" size={14} /> Queue
           </span>
         </div>
       ))}
     </div>
     {!romQuery.trim() && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>Pick a platform and search to queue downloads for your <code style={{ fontSize: 11 }}>download_roms.py</code> runner.</div>}

     <div data-zflix-rom-queue>
       <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Queued ROM requests</div>
       {romList.length === 0 ? (
         <div style={{ color: 'var(--text3)', fontSize: 13 }}>None yet.</div>
       ) : (
         <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
           {romList.map((r) => (
             <div
               key={r.id}
               tabIndex={0}
               aria-label={`${r.game} — ${r.status}`}
               style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, outline: 'none' }}
             >
               <span style={{ flex: 1 }}>
                 <b>{r.game}</b> <span style={{ color: 'var(--text3)' }}>({r.platform_label || r.platform})</span>
               </span>
               <Tag>{r.status}</Tag>
               <button
                 type="button"
                 style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}
                 onClick={() => fetch(`/api/request/rom/${r.id}`, { method: 'DELETE' }).then(() => reloadRomList())}
               >
                 Remove
               </button>
             </div>
           ))}
         </div>
       )}
     </div>
   </div>
 );
}

/** Jellyfin watch progress (PlaybackPositionTicks / RunTimeTicks), shown as a red bar like Netflix. */
function PlaybackProgressBar({ fraction, height = 4 }) {
  if (fraction == null || fraction <= 0 || fraction >= 1) return null;
  const pct = Math.min(100, Math.max(0, fraction * 100));
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height,
        background: 'rgba(0,0,0,0.55)',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 4,
      }}
      aria-hidden
    >
      <div style={{ height: '100%', width: `${pct}%`, background: '#e50914' }} />
    </div>
  );
}

/** New Season and New Episode banners: one copy for every card, asked for at most every 5 minutes. */
const newShowBadges = { map: {}, at: 0, redraws: new Set() };
function loadNewShowBadges() {
 if (Date.now() - newShowBadges.at < 5 * 60 * 1000) return;
 newShowBadges.at = Date.now();
 fetch('/api/shows/new-badges')
   .then((r) => (r.ok ? r.json() : null))
   .then((map) => {
     if (!map || typeof map !== 'object' || Array.isArray(map)) return;
     newShowBadges.map = map;
     newShowBadges.redraws.forEach((redraw) => redraw((n) => n + 1));
   })
   .catch(() => {});
}
function useNewShowBadge(seriesKey) {
 const [, redraw] = useState(0);
 useEffect(() => {
   newShowBadges.redraws.add(redraw);
   loadNewShowBadges();
   return () => {
     newShowBadges.redraws.delete(redraw);
   };
 }, []);
 return newShowBadges.map[seriesKey] || null;
}
function clearNewShowBadge(seriesKey) {
 const { [seriesKey]: _gone, ...rest } = newShowBadges.map;
 newShowBadges.map = rest;
 newShowBadges.redraws.forEach((redraw) => redraw((n) => n + 1));
 fetch('/api/shows/new-badges/seen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seriesId: seriesKey }) }).catch(() => {});
}

function MediaCard({ item, onClick }) {
 const [hover, setHover] = useState(false);
 const [focused, setFocused] = useState(false);
 const [posterFailed, setPosterFailed] = useState(false);
 const [imgSrc, setImgSrc] = useState(null);
 const jellyfinDomId = item.source === 'jellyfin' && item.id != null ? String(item.id) : null;
 const isDiscover =
   item.source === 'tmdb' ||
   item.source === 'tmdb-person' ||
   item.source === 'lastfm' ||
   item.source === 'lidarr' ||
   item.source === 'audiobook-discover' ||
   item.source === 'book-discover' ||
   item.source === 'bookshelf-search' ||
   item.source === 'podcast-discover' ||
   item.source === 'podcast-index';
 const isRoundPortrait = item.type === 'person' || item.type === 'artist' || item.type === 'musicartist';
 const aspectRatio = item.type === 'album' ? '1/1' : isRoundPortrait ? '1/1' : '2/3';
 const cardRadius = isRoundPortrait ? 9999 : 8;
 const showOverlay = hover || focused;
 const seriesKey = item.seriesId || item.id;
 const newBadge = useNewShowBadge(seriesKey);
 const releaseStrip = !newBadge && item.type !== 'recording' && !item.topRank && (item.releaseStatus === 'theaters' || item.releaseStatus === 'upcoming');
 const activate = () => {
   if (newBadge) clearNewShowBadge(seriesKey);
   onClick(item);
 };
 const thumb =
   item.type === 'musicartist'
     ? (item.poster || item.cover || null)
     : (item.poster || item.cover || item.fallbackPoster);
 useEffect(() => {
   setPosterFailed(false);
   setImgSrc(thumb || null);
 }, [item.id, item.tmdbId, thumb]);
 const placeholderGlyph =
   item.type === 'musicartist' || item.type === 'artist'
     ? null
     : item.type === 'person'
       ? '🎭'
       : item.type === 'album'
       ? '💿'
       : item.type === 'podcast' || item.type === 'podcast-episode'
         ? '🎙'
         : item.type === 'audiobook-request' || item.source === 'audiobookshelf'
         ? '📚'
         : isDiscover
           ? '🔍'
           : '🎬';
 const showPosterImg = imgSrc && !posterFailed;
 const placeholderLetter =
   !showPosterImg && (item.type === 'album' || item.type === 'musicartist' || item.type === 'artist') && (item.title || item.name)
     ? String(item.title || item.name).trim().charAt(0).toUpperCase() || null
     : null;
 const displayTitle = item.title || item.name;
 return (
  <div role="button" tabIndex={0} aria-label={displayTitle} data-zflix-jellyfin-id={jellyfinDomId || undefined}
     className={isRoundPortrait ? 'media-card media-card--round-portrait' : 'media-card'}
     style={{ cursor: 'pointer', borderRadius: cardRadius, overflow: isRoundPortrait ? 'visible' : 'hidden', background: 'var(--surface)', transition: 'transform 0.2s, box-shadow 0.2s', transform: showOverlay ? 'scale(1.05)' : 'scale(1)', boxShadow: showOverlay ? '0 8px 24px rgba(0,0,0,0.6)' : 'none', position: 'relative' }}
     onMouseEnter={() => {
       /** TV WebViews keep an invisible pointer parked mid-screen; whatever tile sits under it looked "selected". */
       const cl = document.documentElement.classList;
       if (cl.contains('zflix-tv-apk-density') || cl.contains('zflix-android-webview')) return;
       setHover(true);
     }}
     onMouseLeave={() => setHover(false)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
     onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
     onClick={activate}>
     <div {...(newBadge || item.type === 'recording' || releaseStrip ? { 'data-zflix-has-strip': true } : {})} style={{ position: 'relative', width: '100%', aspectRatio, background: 'var(--bg3)', borderRadius: isRoundPortrait ? '50%' : 0, overflow: 'hidden' }}>
       {/* New Season and New Episode get their own strip, and the poster sits below it instead of under it. */}
       {newBadge ? <div data-zflix-new-strip><span>{newBadge.label}</span></div> : null}
       {/* Discover movies not out for home viewing yet: the same strip (Zach, 2026-11-03), amber in theaters, slate coming soon. */}
       {releaseStrip ? (
         <div data-zflix-new-strip data-zflix-release-strip={item.releaseStatus}><span>{item.releaseStatus === 'theaters' ? 'IN THEATERS' : 'COMING SOON'}</span></div>
       ) : null}
       {/* Recordings use the same strip: grey "Recorded" once done, red "Recording" only while it is still being recorded. */}
       {item.type === 'recording' && !newBadge ? (
         <div data-zflix-new-strip data-zflix-rec-strip={item.growing ? 'live' : 'done'}><span>{item.growing ? 'RECORDING' : 'RECORDED'}</span></div>
       ) : null}
       {item.recAway || item.recLogo ? (
         item.recAway ? (
           /** One poster for a game: the two team colors run into each other and the logos overlap across the middle. */
           <div style={{ position: 'absolute', top: 'var(--zf-strip, 0px)', left: 0, right: 0, bottom: 0, zIndex: 1, overflow: 'hidden', background: `linear-gradient(145deg, ${item.recAwayColor || '#2a2a33'} 12%, ${item.recHomeColor || '#15151b'} 88%)` }}>
             <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.14), rgba(0,0,0,0.38) 78%)' }} />
             <img src={item.recAway} alt="" loading="lazy" style={{ position: 'absolute', left: '-4%', top: '12%', width: '74%', aspectRatio: '1/1', objectFit: 'contain', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.55))' }} />
             <img src={item.recHome} alt="" loading="lazy" style={{ position: 'absolute', right: '-4%', bottom: '12%', width: '74%', aspectRatio: '1/1', objectFit: 'contain', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.55))' }} />
           </div>
         ) : (
           <div style={{ position: 'absolute', top: 'var(--zf-strip, 0px)', left: 0, right: 0, bottom: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, boxSizing: 'border-box', background: 'var(--bg3)' }}>
             <img src={item.recLogo} alt="" loading="lazy" style={{ width: '70%', maxHeight: '60%', objectFit: 'contain', borderRadius: 8, background: 'rgba(255,255,255,0.92)', padding: 6, boxSizing: 'border-box' }} />
           </div>
         )
       ) : null}
       {showPosterImg ? (
         <img
           src={imgSrc}
           alt=""
           style={{ position: 'absolute', top: 'var(--zf-strip, 0px)', left: 0, right: 0, width: '100%', height: 'calc(100% - var(--zf-strip, 0px))', objectFit: 'cover', borderRadius: isRoundPortrait ? '50%' : 0 }}
           onError={() => {
             const fb = item.fallbackPoster && String(item.fallbackPoster).trim() ? item.fallbackPoster : null;
             if (fb && imgSrc !== fb) {
               setImgSrc(fb);
               return;
             }
             setPosterFailed(true);
           }}
         />
       ) : (
         <div
           style={{
             position: 'absolute',
             inset: 0,
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             fontSize: placeholderLetter ? 28 : 32,
             fontWeight: placeholderLetter ? 700 : 400,
             color: placeholderLetter ? 'var(--text2)' : 'inherit',
             background: placeholderLetter
               ? 'linear-gradient(145deg, var(--bg3) 0%, var(--surface) 100%)'
               : undefined,
           }}
         >
           {placeholderLetter || placeholderGlyph}
         </div>
       )}
       <PlaybackProgressBar fraction={item.resumeProgress} />
       {showOverlay && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 3 }}><div style={{ background: 'rgba(229,9,20,0.9)', borderRadius: '50%', width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={isDiscover ? 'plus' : 'play'} size={20} /></div></div>}
       {item.rating && <div data-zflix-rating-badge style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.75)', borderRadius: '0 0 0 6px', padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: 'var(--gold)', zIndex: 5 }}><Icon name="star" size={10} />{item.rating?.toFixed(1)}</div>}
       {/* Discover movies: not out for home viewing yet (a request now would only find cinema rips, or nothing). */}
       {item.topRank ? (
         <div data-zflix-release-badge="top" style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(229,9,20,0.95)', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, color: '#fff', letterSpacing: '0.3px', zIndex: 5 }}>#{item.topRank}</div>
       ) : null}
     </div>
     <div className="media-card__meta" style={{ padding: isRoundPortrait ? '8px 4px 10px' : '8px 10px 10px' }}>
       <div
         className="media-card__title"
         style={{ fontSize: 12, fontWeight: 600, ...(isRoundPortrait ? {} : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }}
         title={isRoundPortrait && displayTitle ? String(displayTitle) : undefined}
       >{displayTitle}</div>
       <div className="media-card__subtitle" style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{item.subtitle || item.authorLabel || item.author || item.artist || item.year || item.publishedYear || ''}</div>
     </div>
   </div>
 );
}

function MediaRow({ title, items, onItemClick, loading, mediaRowKind }) {
 if (!loading && (!items || items.length === 0)) return null;
 return (
   <div style={{ marginBottom: 28 }} {...(mediaRowKind ? { 'data-zflix-media-row': mediaRowKind } : {})}>
     <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 10 }}>{title}</div>
     <div className="media-row">
       {loading ? [...Array(8)].map((_, i) => <div key={i} style={{ flexShrink: 0, width: 'var(--card-width)', borderRadius: 8, overflow: 'hidden', background: 'var(--surface)' }}><div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite' }} /></div>) :
         items.map(item => <div key={item.listKey || item.id || item.tmdbId} style={{ flexShrink: 0, width: 'var(--card-width)' }}><MediaCard item={item} onClick={onItemClick} /></div>)}
     </div>
   </div>
 );
}

/** Loads the next page when the user scrolls near the bottom of `.content-area` (no button). */
function InfiniteScrollSentinel({ hasMore, loading, onLoad }) {
 const ref = useRef(null);
 const onLoadRef = useRef(onLoad);
 onLoadRef.current = onLoad;
 useEffect(() => {
   const el = ref.current;
   if (!el || !hasMore || loading) return;
   /** Main scroll is `.content-area`, not the document — viewport root often never intersects the sentinel. */
   const root = typeof document !== 'undefined' ? document.querySelector('.content-area') : null;
   const opts = { rootMargin: '480px', threshold: 0 };
   if (root) opts.root = root;
   const obs = new IntersectionObserver((entries) => {
     if (entries.some((x) => x.isIntersecting)) onLoadRef.current();
   }, opts);
   obs.observe(el);
   return () => obs.disconnect();
 }, [hasMore, loading]);
 if (!hasMore) return null;
 return <div ref={ref} data-zflix-infinite-sentinel="true" style={{ height: 2, marginTop: 16 }} aria-hidden />;
}

function EpisodesModal({ show, onClose, onPlay, onToast }) {
 const sheetRef = useRef(null);
 const [seasons, setSeasons] = useState([]);
 const [selectedSeason, setSelectedSeason] = useState(null);
 const [episodes, setEpisodes] = useState([]);
 const [loadingSeasons, setLoadingSeasons] = useState(true);
 const [loadingEps, setLoadingEps] = useState(false);
 const [reportEpId, setReportEpId] = useState(null);
 const [sonarrReportConfirmEp, setSonarrReportConfirmEp] = useState(null);

 const submitSonarrBadEpisodeReport = async (ep) => {
   setReportEpId(ep.id);
   try {
     const r = await fetch('/api/library/report-bad-stream', {
       method: 'POST',
       credentials: 'include',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ jellyfinItemId: ep.id }),
     });
     const j = await r.json().catch(() => ({}));
     if (!r.ok) {
       onToast?.(typeof j.error === 'string' ? j.error : 'Report failed', 'error');
       return;
     }
     onToast?.(j.message || 'Sonarr update queued.', 'success');
   } catch {
     onToast?.('Network error', 'error');
   } finally {
     setReportEpId(null);
   }
 };

 useEffect(() => {
   if (sheetRef.current) focusZflixModalPrimary(sheetRef.current);
   /** Also when the player hands back the episode that was just watched (new esumeEpisode). */
 }, [show.id, show.resumeEpisode]);

 useEffect(() => {
   fetch(`/api/shows/${show.id}/seasons`).then(r => r.json()).then(data => {
     setSeasons(data);
     /** Opened from Continue Watching: start on the season of the episode being resumed. */
     const want = show.resumeEpisode?.seasonIndex;
     const startSeason = (want != null && data.find((s) => s.index === want)) || data[0];
     if (startSeason) loadEpisodes(startSeason);
     setLoadingSeasons(false);
   }).catch(() => setLoadingSeasons(false));
 }, [show.id]);

 useEffect(() => {
   if (!episodes.length || !sheetRef.current) return;
   focusZflixModalPrimary(sheetRef.current);
 }, [episodes]);

 const loadEpisodes = (season) => {
   setSelectedSeason(season);
   setLoadingEps(true);
   setEpisodes([]);
   fetch(`/api/shows/${show.id}/seasons/${season.id}/episodes`).then(r => r.json()).then(data => { setEpisodes(data); setLoadingEps(false); }).catch(() => setLoadingEps(false));
 };

 return (
   <div data-zflix-modal-root style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
     <div role="dialog" aria-modal="true" aria-label={show.title} ref={sheetRef} tabIndex={-1} style={{ background: 'var(--bg2)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 900, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none' }} onClick={e => e.stopPropagation()}>
       {/* Header sits outside the scrolling list, so the first episode can never end up hidden underneath it. */}
       <div style={{ flexShrink: 0, background: 'var(--bg2)', padding: '16px 20px', borderBottom: '1px solid var(--border)', zIndex: 1 }}>
         <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
           <h2 style={{ fontSize: 20, fontWeight: 700, flex: 1 }}>{show.title}</h2>
           <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><Icon name="close" size={18} /></button>
         </div>
         {show.resumeEpisode ? (
           <div style={{ padding: '0 5px 10px' }}>
             <Btn data-zflix-modal-primary-action variant="primary" onClick={() => onPlay(show.resumeEpisode)} style={{ maxWidth: '100%' }}>
               <Icon name="play" size={16} />
               <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                 {show.resumeEpisode.resumeProgress > 0 ? 'Resume' : 'Play next'}{show.resumeEpisode.seasonIndex != null ? ` S${show.resumeEpisode.seasonIndex}` : ''}{show.resumeEpisode.episodeIndex != null ? ` E${show.resumeEpisode.episodeIndex}` : ''}
                 {show.resumeEpisode.name ? ` · ${show.resumeEpisode.name}` : ''}
               </span>
             </Btn>
             <Btn
               variant="ghost"
               type="button"
               style={{ marginLeft: 10, padding: '8px 14px', fontSize: 13 }}
               onClick={async () => {
                 /** Takes the show off Continue Watching until an episode of it is watched again. */
                 try {
                   await fetch('/api/shows/continue/hide', {
                     method: 'POST',
                     credentials: 'include',
                     headers: { 'Content-Type': 'application/json' },
                     body: JSON.stringify({ seriesId: show.id, episodeId: show.resumeEpisode.id }),
                   });
                 } catch (_) {}
                 bumpZflixResumeStale();
                 onToast?.('Removed from Continue Watching', 'success');
                 onClose();
               }}
             >
               Remove from Continue Watching
             </Btn>
           </div>
         ) : null}
         {/* Wraps instead of scrolling sideways: every season stays reachable with a remote, and the padding leaves room for the focus ring. */}
         <div data-zflix-linear-tab-strip style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 5 }}>
           {loadingSeasons ? <div style={{ color: 'var(--text2)', fontSize: 13 }}>Loading...</div> :
             seasons.map(s => <button key={s.id} onClick={() => loadEpisodes(s)} style={{ padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: selectedSeason?.id === s.id ? 600 : 400, background: selectedSeason?.id === s.id ? 'var(--accent)' : 'var(--surface)', color: selectedSeason?.id === s.id ? '#fff' : 'var(--text2)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>{s.name}</button>)}
         </div>
       </div>
       <div style={{ padding: '8px 20px 40px', flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', scrollPadding: 16 }}>
         {sonarrReportConfirmEp ? (
           <div
             role="presentation"
             style={{
               position: 'fixed',
               inset: 0,
               zIndex: 1200,
               background: 'rgba(0,0,0,0.72)',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               padding: 20,
             }}
             onClick={() => setSonarrReportConfirmEp(null)}
           >
             <div
               role="alertdialog"
               aria-labelledby="zflix-sonarr-report-title"
               aria-describedby="zflix-sonarr-report-desc"
               style={{
                 background: 'var(--bg2)',
                 border: '1px solid var(--border)',
                 borderRadius: 12,
                 padding: '22px 24px',
                 maxWidth: 440,
                 width: '100%',
                 boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
               }}
               onClick={(e) => e.stopPropagation()}
             >
               <h3 id="zflix-sonarr-report-title" style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>
                 Are you sure you want to delete this episode?
               </h3>
               <p id="zflix-sonarr-report-desc" style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55, marginBottom: 20 }}>
                 This file will be deleted for good and Sonarr will look for a new copy. The episode won’t play until a replacement downloads, which can take a while. If no other copy can be found, it stays missing.
               </p>
               <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                 <button
                   type="button"
                   autoFocus
                   onClick={() => setSonarrReportConfirmEp(null)}
                   style={{
                     padding: '8px 16px',
                     borderRadius: 8,
                     border: '1px solid var(--border)',
                     background: 'transparent',
                     color: 'var(--text)',
                     cursor: 'pointer',
                     fontSize: 13,
                     fontWeight: 600,
                   }}
                 >
                   Cancel
                 </button>
                 <button
                   type="button"
                   onClick={() => {
                     const ep = sonarrReportConfirmEp;
                     setSonarrReportConfirmEp(null);
                     if (ep) submitSonarrBadEpisodeReport(ep);
                   }}
                   style={{
                     padding: '8px 16px',
                     borderRadius: 8,
                     border: 'none',
                     background: '#f85149',
                     color: '#fff',
                     cursor: 'pointer',
                     fontSize: 13,
                     fontWeight: 600,
                   }}
                 >
                   Yes, delete and replace
                 </button>
               </div>
             </div>
           </div>
         ) : null}
         {loadingEps ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Loading episodes...</div> :
           episodes.map((ep, epIdx) => (
             <div key={ep.id} style={{ display: 'flex', gap: 14, padding: '12px 8px', borderBottom: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s' }}
               onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
               onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
               <div style={{ position: 'relative', flexShrink: 0, borderRadius: 6, overflow: 'hidden' }}>
                 {ep.thumb ? <img src={ep.thumb} alt="" style={{ width: 160, height: 90, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} /> : <div style={{ width: 160, height: 90, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>📺</div>}
                 <PlaybackProgressBar fraction={ep.resumeProgress} height={3} />
               </div>
               <div style={{ flex: 1, minWidth: 0 }}>
                 <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                   <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>E{ep.index}</span>
                   <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{ep.name}</span>
                   {ep.runtime && <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{ep.runtime}m</span>}
                 </div>
                 {ep.overview && <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 8 }}>{ep.overview}</p>}
                 <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                   <button
                     type="button"
                     {...(epIdx === 0 ? { 'data-zflix-modal-primary-action': '' } : {})}
                     onClick={() => { onPlay(ep); onClose(); }}
                     style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)', border: 'none', borderRadius: 6, padding: '6px 14px', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                   >
                     <Icon name="play" size={14} /> Play
                   </button>
                   <OfflineButton id={ep.id} compact />
                   <button
                     type="button"
                     onClick={() => setSonarrReportConfirmEp(ep)}
                     disabled={reportEpId === ep.id}
                     style={{
                       display: 'inline-flex',
                       alignItems: 'center',
                       gap: 6,
                       background: 'rgba(248, 81, 73, 0.08)',
                       border: '1px solid rgba(248, 81, 73, 0.35)',
                       borderRadius: 6,
                       padding: '6px 12px',
                       color: '#f85149',
                       fontSize: 11,
                       fontWeight: 600,
                       cursor: reportEpId === ep.id ? 'default' : 'pointer',
                       opacity: reportEpId === ep.id ? 0.6 : 1,
                     }}
                   >
                     {reportEpId === ep.id ? 'Working…' : 'Bad file → Sonarr'}
                   </button>
                 </div>
               </div>
             </div>
           ))}
       </div>
     </div>
   </div>
 );
}

function PlaylistPickerModal({ trackIds, trackLabel, onClose, onDone }) {
 const sheetRef = useRef(null);
 const [playlists, setPlaylists] = useState([]);
 const [loading, setLoading] = useState(true);
 const [newName, setNewName] = useState('');
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState('');

 useEffect(() => {
   if (sheetRef.current) focusZflixModalPrimary(sheetRef.current);
 }, []);

 useEffect(() => {
   fetch('/api/music/playlists')
     .then((r) => r.json())
     .then((d) => setPlaylists(d.items || []))
     .catch(() => setPlaylists([]))
     .finally(() => setLoading(false));
 }, []);

 const addTo = async (playlistId) => {
   if (busy || !trackIds?.length) return;
   setBusy(true);
   setErr('');
   try {
     const res = await fetch(`/api/music/playlists/${encodeURIComponent(playlistId)}/tracks`, {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ itemIds: trackIds }),
     });
     const data = await res.json().catch(() => ({}));
     if (!res.ok) throw new Error(data.error || 'Could not add to playlist');
     onDone?.(data);
     onClose();
   } catch (e) {
     setErr(e.message || 'Failed');
   } finally {
     setBusy(false);
   }
 };

 const createAndAdd = async () => {
   const name = newName.trim();
   if (!name || busy) return;
   setBusy(true);
   setErr('');
   try {
     const cr = await fetch('/api/music/playlists', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ name }),
     });
     const created = await cr.json().catch(() => ({}));
     if (!cr.ok) throw new Error(created.error || 'Could not create playlist');
     await addTo(created.id);
   } catch (e) {
     setErr(e.message || 'Failed');
     setBusy(false);
   }
 };

 return (
   <div data-zflix-modal-root style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
     <div role="dialog" aria-modal="true" aria-label="Add to playlist" ref={sheetRef} tabIndex={-1} style={{ background: 'var(--bg2)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 480, maxHeight: '70vh', overflowY: 'auto', outline: 'none' }} onClick={(e) => e.stopPropagation()}>
       <div style={{ position: 'sticky', top: 0, background: 'var(--bg2)', padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, zIndex: 1 }}>
         <h2 style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>Add to playlist</h2>
         <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><Icon name="close" size={18} /></button>
       </div>
       <div style={{ padding: '12px 20px 28px' }}>
         {trackLabel ? <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>{trackLabel}</div> : null}
         <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
           <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New playlist name" style={{ flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 14 }} onKeyDown={(e) => { if (e.key === 'Enter') createAndAdd(); }} />
           <Btn onClick={createAndAdd} disabled={busy || !newName.trim()} style={{ padding: '10px 14px', fontSize: 13, flexShrink: 0 }}>Create</Btn>
         </div>
         {err ? <div style={{ color: '#f85149', fontSize: 12, marginBottom: 10 }}>{err}</div> : null}
         {loading ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 24 }}>Loading playlists…</div> :
           playlists.length === 0 ? <div style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center', padding: 16 }}>No playlists yet — create one above.</div> :
             playlists.map((p) => (
               <button key={p.id} type="button" disabled={busy} onClick={() => addTo(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 8px', border: 'none', background: 'transparent', color: 'var(--text)', cursor: busy ? 'default' : 'pointer', borderRadius: 8, textAlign: 'left' }}
                 onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
                 onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                 {p.poster ? <img src={p.poster} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} /> : <div style={{ width: 44, height: 44, borderRadius: 6, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🎵</div>}
                 <div style={{ flex: 1, minWidth: 0 }}>
                   <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                   <div style={{ fontSize: 11, color: 'var(--text3)' }}>{p.itemCount || 0} tracks</div>
                 </div>
                 <Icon name="plus" size={18} />
               </button>
             ))}
       </div>
     </div>
   </div>
 );
}

function AlbumTracksModal({ album, onClose, onPlay, onAddToPlaylist }) {
 const sheetRef = useRef(null);
 const [tracks, setTracks] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
   if (sheetRef.current) focusZflixModalPrimary(sheetRef.current);
 }, [album.id]);

 useEffect(() => {
   fetch(`/api/music/${album.id}/tracks`).then(r => r.json()).then(data => { setTracks(data); setLoading(false); }).catch(() => setLoading(false));
 }, [album.id]);

 const fmt = (s) => s ? `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}` : '';

 return (
   <div data-zflix-modal-root style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
     <div role="dialog" aria-modal="true" aria-label={album.title} ref={sheetRef} tabIndex={-1} style={{ background: 'var(--bg2)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 700, maxHeight: '85vh', overflowY: 'scroll', WebkitOverflowScrolling: 'touch', outline: 'none' }} onClick={e => e.stopPropagation()}>
       <div style={{ position: 'sticky', top: 0, background: 'var(--bg2)', padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14, zIndex: 1 }}>
         {album.poster && <img src={album.poster} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
         <div style={{ flex: 1, minWidth: 0 }}>
           <h2 style={{ fontSize: 18, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{album.title}</h2>
           <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{album.artist}{album.year ? ` • ${album.year}` : ''}</div>
         </div>
         {tracks.length > 0 && <Btn data-zflix-modal-primary-action onClick={() => { onPlay(tracks[0], tracks); onClose(); }} style={{ padding: '8px 16px', fontSize: 13 }}><Icon name="play" size={14} /> Play All</Btn>}
         <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><Icon name="close" size={18} /></button>
       </div>
       <div style={{ padding: '8px 20px 40px' }}>
         {loading ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Loading tracks...</div> :
           tracks.length === 0 ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>No tracks found</div> :
             tracks.map((t, i) => (
               <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderRadius: 8 }}>
                 <button type="button" onClick={() => { onPlay(t, tracks); onClose(); }} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, minWidth: 0, textAlign: 'left' }}>
                   <span style={{ fontSize: 13, color: 'var(--text3)', width: 24, textAlign: 'right', flexShrink: 0 }}>{t.index || i + 1}</span>
                   <span style={{ flex: 1, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                   {t.runtime && <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>{fmt(t.runtime)}</span>}
                   <div style={{ background: 'var(--accent)', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="play" size={14} /></div>
                 </button>
                 {onAddToPlaylist ? (
                   <button type="button" aria-label={`Add ${t.name} to playlist`} onClick={(e) => { e.stopPropagation(); onAddToPlaylist([t.id], t.name); }} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text)', flexShrink: 0 }}>
                     <Icon name="plus" size={16} />
                   </button>
                 ) : null}
               </div>
             ))}
       </div>
     </div>
   </div>
 );
}

function AlbumCard({ album, artistName, onRequest, onOpen }) {
 const [requested, setRequested] = useState(false);
 const [requesting, setRequesting] = useState(false);
 const [noCover, setNoCover] = useState(false);
 const request = async (e) => {
   e.stopPropagation();
   setRequesting(true);
   try {
     const res = await fetch('/api/request/album', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ artistName, albumTitle: album.title }) });
     const data = await res.json();
     if (data.success) setRequested(true);
     onRequest(data.message, data.success ? 'success' : 'error', true);
   } catch { onRequest('Request failed', 'error', true); }
   finally { setRequesting(false); }
 };
 const activateRequest = (e) => {
   if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
   if (e.type === 'keydown') e.preventDefault();
   request(e);
 };
 return (
   /** The whole card takes focus, so moving the remote onto it scrolls the title into view along with the picture. */
   <div role="button" tabIndex={0} aria-label={onOpen ? `Open album ${album.title}` : `Request album ${album.title}`} style={{ width: 110, textAlign: 'center', cursor: 'pointer', borderRadius: 8, padding: '4px 0' }} onClick={onOpen ? (e) => { if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return; if (e.type === 'keydown') e.preventDefault(); onOpen(album); } : activateRequest} onKeyDown={onOpen ? (e) => { if (e.key !== 'Enter' && e.key !== ' ') return; e.preventDefault(); onOpen(album); } : activateRequest}>
     <div style={{ position: 'relative' }}>
       {album.poster && !noCover ? <img src={album.poster} alt="" loading="lazy" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, margin: '0 auto 5px', display: 'block' }} onError={() => setNoCover(true)} /> : <div style={{ width: 100, height: 100, background: 'var(--bg3)', borderRadius: 8, margin: '0 auto 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>💿</div>}
       <div style={{ position: 'absolute', bottom: 10, right: 5, background: requested ? 'var(--green)' : requesting ? 'var(--text3)' : 'var(--accent)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}><Icon name={requested ? 'check' : 'plus'} size={14} /></div>
     </div>
     <div style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{album.title}</div>
     <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{album.year}</div>
   </div>
 );
}

/** Discover → Audiobooks must hit :8788; eBooks tab (popular / bookshelf-search) hits :8787. Item.source alone is unreliable (both flows use type audiobook-request). */
function bookshelfShelfFromDiscoverItem(item) {
  if (item.zflixBookshelfShelf === 'audiobook' || item.zflixBookshelfShelf === 'ebook') {
    return item.zflixBookshelfShelf;
  }
  const ebookBySource = item.source === 'bookshelf-search' || item.source === 'book-discover';
  return ebookBySource ? 'ebook' : 'audiobook';
}

/** Movies/episodes only — same-origin iframe to Jellyfin’s web player (not Live TV). */
function jfEmbedEligible(item) {
  return (
    item &&
    item.source === 'jellyfin' &&
    !item.liveStreamId &&
    (item.type === 'movie' || item.type === 'episode') &&
    item.id != null &&
    String(item.id).trim() !== ''
  );
}

function zflixReadVisualSkipEnabled() {
  try {
    const raw = localStorage.getItem('zflix_visual_skip_enabled');
    if (raw === '0' || raw === 'false') return false;
  } catch (_) {}
  return true;
}

/** Instant seek past mapped zones; pad expands the window (playback only). */
function zflixApplyVisualSkips(video, zones, padBefore, padAfter, enabled) {
  if (!enabled || !video || !zones?.length) return;
  const t = video.currentTime;
  const pb = Math.max(0, Number(padBefore) || 0);
  const pa = Math.max(0, Number(padAfter) || 0);
  for (const z of zones) {
    const a = Number(z.startSec) - pb;
    const b = Number(z.endSec) + pa;
    if (Number.isFinite(a) && Number.isFinite(b) && t >= Math.max(0, a) && t < b - 0.05) {
      video.currentTime = b;
      break;
    }
  }
}

/** Jellyfin `#/video` is only the OSD — it does not start playback from query params; open `#/details` then trigger Play. */
function jellyfinWebDetailsEmbedSrc(itemId, serverId) {
  const id = encodeURIComponent(String(itemId));
  const sid = encodeURIComponent(String(serverId));
  return `/jellyfin/web/index.html#/details?id=${id}&serverId=${sid}`;
}

function jellyfinTryClickPlayInIframeDoc(doc) {
  if (!doc?.querySelector) return false;
  const root = doc.querySelector('.detailPagePrimaryContainer') || doc.body || doc;
  const resume = root.querySelector('.btnPlay:not(.hide)');
  const play = root.querySelector('.btnReplay:not(.hide)');
  const btn = resume || play;
  if (btn && typeof btn.click === 'function') {
    btn.click();
    return true;
  }
  return false;
}

/** Best-effort close release-note / about dialogs only (avoid clicking through Sign-in). */
function jellyfinDismissBlockingDialogs(doc) {
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll('.dialogContainer .dialog.opened').forEach((dialog) => {
    if (dialog.querySelector?.('video')) return;
    const hay = String(dialog.textContent || '').toLowerCase();
    const titleHay = String(
      dialog.querySelector?.('.dialogHeader, .dialogTitle, h1, h2, [class*="dialogTitle"]')?.textContent || '',
    ).toLowerCase();
    if (
      !/what.?s new|release notes|new in jellyfin|what is new|learn more about|about jellyfin|welcome to jellyfin|getting started|copy version|build version|gpl license|the jellyfin contributors/i.test(
        hay,
      ) &&
      !/^[\s]*about[\s]*jellyfin[\s]*$/i.test(titleHay.trim())
    )
      return;
    const buttons = [...dialog.querySelectorAll('button')];
    const pick = buttons.find((b) =>
      /^(got it|ok|close|skip|continue|dismiss|maybe later)\s*$/i.test(String(b.textContent || '').trim()),
    );
    if (pick && typeof pick.click === 'function') pick.click();
  });
}

/** Jellyfin-web hashes we never allow inside the Zflix embed (forced back to this title). */
function jellyfinEmbedBlockedShellHash(hash) {
  const h = String(hash || '');
  if (!h || h === '#') return false;
  const tail = h.replace(/^#![/]?/, '').replace(/^#[/]?/, '');
  if (/^about(?:\.html)?(?:[?#]|$)/i.test(tail)) return true;
  if (/[/]about(?:\.html)?(?:[?#]|$)/i.test(tail)) return true;
  if (/quickstart|gettingstarted|setupwizard|firstrun|startwizard/i.test(tail)) return true;
  return false;
}

function jellyfinEmbedSnapToDetails(win, itemId, serverId) {
  if (!win || itemId == null || serverId == null) return;
  const id = encodeURIComponent(String(itemId));
  const sid = encodeURIComponent(String(serverId));
  const targetHash = `#/details?id=${id}&serverId=${sid}`;
  try {
    const path = win.location.pathname || '';
    const search = win.location.search || '';
    win.location.replace(`${path}${search}${targetHash}`);
  } catch (_) {}
}

/** Jellyfin-web global chrome — strip header/nav/drawer handles inside Zflix embed (does not remove player OSD). */
function jellyfinEmbedInjectHideShellChrome(doc) {
  if (!doc?.head || doc.getElementById('zflix-jf-no-shell')) return;
  const st = doc.createElement('style');
  st.id = 'zflix-jf-no-shell';
  st.textContent = `
    .skinHeader, .skinHeader.scrolled {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      overflow: hidden !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
    .mainDrawer, .mainDrawerHandle, .backdropDrawer,
    .headerTabs,
    .appfooter {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
  `;
  doc.head.appendChild(st);
}

function jellyfinEmbedPlaybackSurfaceVisible(doc, win) {
  let hash = '';
  try {
    hash = String(win?.location?.hash || '');
  } catch (_) {}
  const lc = hash.toLowerCase();
  if (/\bvideo\b|\bplayback\b|\bosd\b|\bplayer\b|\bvideoplayback\b/.test(lc)) return true;
  try {
    const vp = doc?.querySelector?.('.videoPlayerContainer:not(.hide)');
    const vid = vp?.querySelector?.('video');
    if (vid && vid.readyState >= 2) return true;
  } catch (_) {}
  return false;
}

/** One-shot CSS + recurring hide of drawer/menu links to About inside embedded jellyfin-web. */
function jellyfinEmbedHideAboutChrome(doc) {
  if (!doc?.querySelectorAll) return;
  if (doc.head && !doc.getElementById('zflix-jf-no-about')) {
    const st = doc.createElement('style');
    st.id = 'zflix-jf-no-about';
    st.textContent = `
      a[href="#/about"], a[href="#!/about"], a[href="#!/about.html"], a[href="#/about.html"],
      a[href*="!/about.html"], a[href*="about.html"] {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    doc.head.appendChild(st);
  }
  doc.querySelectorAll('a[href*="about"]').forEach((a) => {
    const href = String(a.getAttribute('href') || '');
    if (!/#!?[/]?[^#"']*about/i.test(href) && !/about\.html/i.test(href)) return;
    const row = a.closest('.listItem') || a.closest('.navMenuOption') || a.closest('[role="menuitem"]') || a;
    try {
      row.style.setProperty('display', 'none', 'important');
      row.style.setProperty('visibility', 'hidden', 'important');
      row.style.setProperty('pointer-events', 'none', 'important');
    } catch (_) {}
  });
}

function jellyfinEmbedEnforceShellRestrictions(win, doc, itemId, serverId) {
  if (!win) return;
  let hash = '';
  try {
    hash = win.location.hash || '';
  } catch (_) {}
  if (jellyfinEmbedBlockedShellHash(hash)) {
    jellyfinEmbedSnapToDetails(win, itemId, serverId);
    return;
  }
  if (doc) {
    jellyfinEmbedInjectHideShellChrome(doc);
    jellyfinEmbedHideAboutChrome(doc);
  }
}

/** Matches jellyfin-web `localStorage['jellyfin_credentials']` shape for same-origin `/jellyfin/` proxied servers. */
function jellyfinWebStoredCredentialsJson(serverId, webSession, manualAddressRoot) {
  const addr = String(manualAddressRoot || '').replace(/\/$/, '');
  const now = Date.now();
  return JSON.stringify({
    Servers: [
      {
        DateAdded: now,
        Id: serverId,
        LastConnectionKind: 'Manual',
        ManualAddress: addr,
        Name: 'Zflix',
        UserId: webSession.userId,
        AccessToken: webSession.accessToken,
        LocalAddress: addr,
        RemoteAddress: addr,
      },
    ],
  });
}

function jellyfinBootstrapIframeCredentials(iframe, credentialsJson, detailsAbsoluteUrl) {
  const doc = iframe.contentDocument;
  if (!doc) return;
  const credLit = JSON.stringify(credentialsJson);
  const urlLit = JSON.stringify(detailsAbsoluteUrl);
  doc.open();
  doc.write('<!DOCTYPE html><meta charset="utf-8"><script>');
  doc.write(`\ntry{localStorage.setItem('jellyfin_credentials',${credLit});}catch(e){}\nlocation.replace(${urlLit});\n`);
  doc.write('<\/script>');
  doc.close();
}

/** Prefer srcdoc (reliable on TV WebView); fall back to about:blank document.write. */
function jellyfinApplyEmbedBootstrap(iframe, credentialsJson, detailsAbsoluteUrl) {
  if (!iframe) return;
  const credLit = JSON.stringify(credentialsJson);
  const urlLit = JSON.stringify(detailsAbsoluteUrl);
  const bootHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body><script>
try{localStorage.setItem('jellyfin_credentials',${credLit});}catch(e){}
location.replace(${urlLit});
</script></body></html>`;
  try {
    iframe.removeAttribute('src');
    iframe.srcdoc = bootHtml;
    return;
  } catch (_) {}
  try {
    iframe.src = 'about:blank';
  } catch (_) {}
  jellyfinBootstrapIframeCredentials(iframe, credentialsJson, detailsAbsoluteUrl);
}

function jellyfinEmbedShowsLogin(doc) {
  if (!doc?.querySelector) return false;
  if (doc.querySelector('.loginForm, #loginPage, [data-testid="login-form"], form[name="loginForm"]')) return true;
  const pw = doc.querySelector('input[type="password"]');
  if (pw && doc.querySelector('input[name="Username"], input[name="txtManualUsername"], #txtUsername')) return true;
  return false;
}

function JellyfinEmbedPlayer({ item, onExit }) {
  const iframeRef = useRef(null);
  const autoClickedRef = useRef(false);
  const bootstrapDoneRef = useRef(false);
  const bootstrapRetryRef = useRef(0);
  const popHandledRef = useRef(false);
  const [serverId, setServerId] = useState(null);
  const [webSession, setWebSession] = useState(null);
  const [jfCtxErr, setJfCtxErr] = useState(null);
  const [jfCtxReady, setJfCtxReady] = useState(false);
  const [embedUiBlocked, setEmbedUiBlocked] = useState(true);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [showTouchExit, setShowTouchExit] = useState(false);
  const isTouchDevice =
    typeof window !== 'undefined' &&
    (navigator.maxTouchPoints > 0 || 'ontouchstart' in window || window.matchMedia?.('(pointer: coarse)').matches);
  const [jfVisualSkipEnabled, setJfVisualSkipEnabled] = useState(() => zflixReadVisualSkipEnabled());
  const [jfVisualSkipCount, setJfVisualSkipCount] = useState(0);
  const jfVisualZonesRef = useRef([]);
  const jfVisualPadRef = useRef({ before: 0, after: 0 });

  const closeEmbed = useCallback(() => {
    scheduleZflixResumeStale({ delayMs: 1200, retryMs: 3500 });
    if (typeof onExit === 'function') onExit();
  }, [onExit]);

  useEffect(() => {
    let cancelled = false;
    setJfCtxErr(null);
    setServerId(null);
    setWebSession(null);
    setJfCtxReady(false);
    const apply = (ctx) => {
      if (cancelled) return;
      if (!ctx?.serverId) setJfCtxErr(ctx?.error || 'Could not load Jellyfin server id');
      else {
        setServerId(ctx.serverId);
        setWebSession(ctx.webSession || null);
      }
    };
    const cached = peekZflixJellyfinContext();
    if (cached?.serverId) apply(cached);
    fetchZflixJellyfinContext({ force: !cached?.serverId })
      .then(apply)
      .catch(() => {
        if (!cancelled && !cached?.serverId) setJfCtxErr('Could not load Jellyfin server id');
      })
      .finally(() => {
        if (!cancelled) setJfCtxReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  useEffect(() => {
    autoClickedRef.current = false;
    bootstrapDoneRef.current = false;
    bootstrapRetryRef.current = 0;
    popHandledRef.current = false;
    setEmbedUiBlocked(true);
    setIframeSrc(null);
  }, [item.id, serverId]);

  useEffect(() => {
    if (!isTouchDevice) return undefined;
    setShowTouchExit(true);
    const t = setTimeout(() => setShowTouchExit(false), 2400);
    return () => clearTimeout(t);
  }, [item.id, isTouchDevice]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      window.history.pushState({ zflixEmbedPlayer: true, t: Date.now() }, '');
    } catch (_) {}
    const onPop = () => {
      popHandledRef.current = true;
      closeEmbed();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!popHandledRef.current) {
        try {
          window.history.back();
        } catch (_) {}
      }
    };
  }, [closeEmbed]);

  const embedSrc = serverId ? jellyfinWebDetailsEmbedSrc(item.id, serverId) : null;

  useEffect(() => {
    if (!jfCtxReady || !embedSrc || !serverId) {
      setIframeSrc(null);
      return;
    }
    if (webSession) {
      setIframeSrc(null);
      return;
    }
    setIframeSrc(embedSrc);
  }, [jfCtxReady, embedSrc, serverId, webSession]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!jfCtxReady || !embedSrc || !serverId || !webSession || !iframe || bootstrapDoneRef.current) return;
    bootstrapDoneRef.current = true;
    try {
      const creds = jellyfinWebStoredCredentialsJson(serverId, webSession, `${window.location.origin}/jellyfin`);
      jellyfinApplyEmbedBootstrap(iframe, creds, `${window.location.origin}${embedSrc}`);
    } catch (_) {
      iframe.src = embedSrc;
    }
  }, [jfCtxReady, embedSrc, serverId, webSession]);

  useEffect(() => {
    if (!embedSrc) return undefined;
    let ticks = 0;
    const iv = setInterval(() => {
      ticks++;
      try {
        const doc = iframeRef.current?.contentDocument;
        const win = iframeRef.current?.contentWindow;
        if (doc && ticks % 3 === 0) jellyfinDismissBlockingDialogs(doc);
        if (win && doc && serverId != null) jellyfinEmbedEnforceShellRestrictions(win, doc, item.id, serverId);
        if (doc && win && jellyfinEmbedPlaybackSurfaceVisible(doc, win)) {
          setEmbedUiBlocked(false);
        }
        if (ticks > 70) setEmbedUiBlocked(false);
        if (doc && !autoClickedRef.current && jellyfinTryClickPlayInIframeDoc(doc)) autoClickedRef.current = true;
        if (
          doc &&
          webSession &&
          jellyfinEmbedShowsLogin(doc) &&
          bootstrapRetryRef.current < 2
        ) {
          bootstrapRetryRef.current += 1;
          bootstrapDoneRef.current = false;
          fetchZflixJellyfinContext({ force: true })
            .then((ctx) => {
              if (!ctx.webSession || !iframeRef.current) return;
              setWebSession(ctx.webSession);
              const creds = jellyfinWebStoredCredentialsJson(
                ctx.serverId,
                ctx.webSession,
                `${window.location.origin}/jellyfin`,
              );
              jellyfinApplyEmbedBootstrap(
                iframeRef.current,
                creds,
                `${window.location.origin}${embedSrc}`,
              );
              bootstrapDoneRef.current = true;
            })
            .catch(() => {});
        }
      } catch (_) {}
    }, 280);
    const stop = setTimeout(() => clearInterval(iv), 90000);
    return () => {
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, [embedSrc, item.id, serverId, webSession]);

  useEffect(() => {
    if (!jfEmbedEligible(item)) {
      jfVisualZonesRef.current = [];
      jfVisualPadRef.current = { before: 0, after: 0 };
      setJfVisualSkipCount(0);
      return undefined;
    }
    let cancelled = false;
    fetch(`/api/library/items/${encodeURIComponent(item.id)}/visual-skips`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const zones = Array.isArray(j.zones) ? j.zones : [];
        jfVisualZonesRef.current = zones;
        jfVisualPadRef.current = {
          before: Math.max(0, Number(j.playbackPadBeforeSec) || 0),
          after: Math.max(0, Number(j.playbackPadAfterSec) || 0),
        };
        setJfVisualSkipCount(zones.length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [item.id, item.type, item.source, item.liveStreamId]);

  useEffect(() => {
    if (!embedSrc || !iframeSrc || jfVisualSkipCount === 0) return undefined;
    const iv = setInterval(() => {
      if (embedUiBlocked) return;
      try {
        const doc = iframeRef.current?.contentDocument;
        const video = doc?.querySelector('video');
        if (!video || video.readyState < 1) return;
        const pad = jfVisualPadRef.current;
        zflixApplyVisualSkips(video, jfVisualZonesRef.current, pad.before, pad.after, jfVisualSkipEnabled);
      } catch (_) {}
    }, 150);
    return () => clearInterval(iv);
  }, [embedSrc, iframeSrc, jfVisualSkipCount, jfVisualSkipEnabled, embedUiBlocked]);

  const onIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !embedSrc || !serverId) return;
    if (webSession && !bootstrapDoneRef.current) {
      bootstrapDoneRef.current = true;
      try {
        const creds = jellyfinWebStoredCredentialsJson(serverId, webSession, `${window.location.origin}/jellyfin`);
        jellyfinApplyEmbedBootstrap(iframe, creds, `${window.location.origin}${embedSrc}`);
      } catch (_) {
        iframe.src = embedSrc;
      }
      return;
    }
    queueMicrotask(() => {
      try {
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        jellyfinDismissBlockingDialogs(doc);
        if (win && doc && serverId != null) jellyfinEmbedEnforceShellRestrictions(win, doc, item.id, serverId);
        if (doc && jellyfinTryClickPlayInIframeDoc(doc)) autoClickedRef.current = true;
      } catch (_) {}
    });
  }, [iframeSrc, webSession, embedSrc, serverId, item.id]);

  return (
    <div data-zflix-player data-zflix-jellyfin-embed style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#000', display: 'flex', flexDirection: 'column' }}>
      {embedSrc && (iframeSrc || webSession) ? (
        <div style={{ flex: 1, position: 'relative', minHeight: 0, background: '#000' }}>
          <iframe
            ref={iframeRef}
            key={`jf-${item.id}-${serverId}-${webSession ? 'w' : 'n'}`}
            title="Library"
            src={iframeSrc || undefined}
            onLoad={onIframeLoad}
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            style={{
              position: 'absolute',
              inset: 0,
              border: 'none',
              width: '100%',
              height: '100%',
              background: '#000',
              visibility: embedUiBlocked ? 'hidden' : 'visible',
            }}
          />
          {jfVisualSkipCount > 0 && !embedUiBlocked ? (
            <label
              style={{
                position: 'absolute',
                bottom: isTouchDevice ? 72 : 18,
                right: 14,
                zIndex: 5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.2)',
                fontSize: 12,
                color: '#e8e8e8',
                cursor: 'pointer',
                userSelect: 'none',
                maxWidth: 'min(92%, 280px)',
              }}
            >
              <input
                type="checkbox"
                checked={jfVisualSkipEnabled}
                onChange={(ev) => {
                  const on = ev.target.checked;
                  setJfVisualSkipEnabled(on);
                  try {
                    localStorage.setItem('zflix_visual_skip_enabled', on ? '1' : '0');
                  } catch (_) {}
                }}
              />
              Skip mapped scenes ({jfVisualSkipCount})
            </label>
          ) : null}
          {embedUiBlocked ? <div style={{ position: 'absolute', inset: 0, background: '#000', zIndex: 2 }} aria-hidden /> : null}
          {isTouchDevice ? (
            <>
              <button
                type="button"
                aria-label="Back to Zflix"
                onClick={closeEmbed}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: `translateY(-50%) translateX(${showTouchExit ? '0' : '-20px'})`,
                  width: 28,
                  height: 56,
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.25)',
                  background: 'rgba(0,0,0,0.42)',
                  color: '#fff',
                  cursor: 'pointer',
                  zIndex: 4,
                  opacity: showTouchExit ? 0.92 : 0.26,
                  transition: 'opacity .18s ease, transform .18s ease',
                  padding: 0,
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ‹
              </button>
              <div
                aria-hidden
                onTouchStart={(e) => {
                  const t = e.touches?.[0];
                  if (!t) return;
                  const startX = t.clientX;
                  const startY = t.clientY;
                  const edge = startX <= 26;
                  if (!edge) return;
                  const onMove = (me) => {
                    const m = me.touches?.[0];
                    if (!m) return;
                    const dx = m.clientX - startX;
                    const dy = Math.abs(m.clientY - startY);
                    if (dx > 52 && dy < 48) {
                      closeEmbed();
                    }
                  };
                  const clear = () => {
                    window.removeEventListener('touchmove', onMove);
                    window.removeEventListener('touchend', clear);
                    window.removeEventListener('touchcancel', clear);
                  };
                  window.addEventListener('touchmove', onMove, { passive: true });
                  window.addEventListener('touchend', clear, { passive: true });
                  window.addEventListener('touchcancel', clear, { passive: true });
                }}
                style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 24, zIndex: 3 }}
              />
            </>
          ) : null}
        </div>
      ) : embedSrc ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14, padding: 24, textAlign: 'center' }}>
          Loading…
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14, padding: 24, textAlign: 'center' }}>
          {jfCtxErr ? <span style={{ color: '#f85149' }}>{jfCtxErr}</span> : 'Loading…'}
        </div>
      )}
    </div>
  );
}

/** @typedef {{ id: string, label: string, description?: string }} VisualPresetRow */

const vfPresetSelectStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg3)',
  color: 'var(--text)',
  fontSize: 14,
};

/** Library default ladder + “queue scan with clean request” (Filters page). */
function LibraryVisualSkipDefaults({ onNotify }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState(/** @type {VisualPresetRow[]} */ ([]));
  const [libraryPreset, setLibraryPreset] = useState('standard');
  const [hasLibraryCustom, setHasLibraryCustom] = useState(false);
  const [alsoQueue, setAlsoQueue] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(/** @type {number|null} */ (null));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch('/api/settings/visual-filters', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (Array.isArray(d.presets)) setPresets(d.presets);
        if (typeof d.libraryDefaultPreset === 'string') setLibraryPreset(d.libraryDefaultPreset);
        setHasLibraryCustom(!!d.hasLibraryCustom);
        if (typeof d.alsoQueueVisualSkipWithCleanRequest === 'boolean') setAlsoQueue(d.alsoQueueVisualSkipWithCleanRequest);
        if (typeof d.updatedAt === 'number') setUpdatedAt(d.updatedAt);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch('/api/settings/visual-filters', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          libraryDefaultPreset: libraryPreset,
          alsoQueueVisualSkipWithCleanRequest: alsoQueue,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        onNotify?.(typeof d.error === 'string' ? d.error : 'Save failed', 'error', true);
        return;
      }
      if (typeof d.updatedAt === 'number') setUpdatedAt(d.updatedAt);
      if (typeof d.libraryDefaultPreset === 'string') setLibraryPreset(d.libraryDefaultPreset);
      setHasLibraryCustom(!!d.hasLibraryCustom);
      onNotify?.('Saved library defaults', 'success', true);
    } catch {
      onNotify?.('Save failed', 'error', true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>Loading filter presets…</div>;
  }

  const selectedBlurb = presets.find((x) => x.id === libraryPreset)?.description;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: '14px',
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'var(--bg3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Scene skips — library default
      </div>
      <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.55, margin: 0 }}>
        The scan looks for nudity and sexual content only. Applies when a title hasn’t set its own. Override from the movie sheet → <strong style={{ color: 'var(--text)' }}>Set filters</strong>.
      </p>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: 'var(--text2)' }}>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>Default sensitivity</span>
        <TvSelect value={libraryPreset} onChange={(e) => setLibraryPreset(e.target.value)} style={{ ...vfPresetSelectStyle, maxWidth: '100%' }}>
          {hasLibraryCustom ? <option value="custom">Custom (from your previous advanced settings)</option> : null}
          {presets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </TvSelect>
        {selectedBlurb ? <span style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45 }}>{selectedBlurb}</span> : null}
      </label>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '10px 12px',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--bg2)',
          cursor: 'pointer',
          fontSize: 13,
          color: 'var(--text2)',
          lineHeight: 1.5,
        }}
      >
        <input type="checkbox" checked={alsoQueue} onChange={(e) => setAlsoQueue(e.target.checked)} style={{ marginTop: 3 }} />
        <span>Also run a scene-skip scan when I request clean playback.</span>
      </label>
      {updatedAt ? <div style={{ fontSize: 11, color: 'var(--text3)' }}>Last saved: {new Date(updatedAt).toLocaleString()}</div> : null}
      <Btn variant="ghost" type="button" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save library defaults'}
      </Btn>
    </div>
  );
}

/** Description + word samples for one clean level. */
function CleanLevelSummary({ level }) {
  if (!level) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, margin: '0 0 8px' }}>{level.description}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {(level.masked || []).map((m) => (
          <span
            key={m}
            style={{
              fontSize: 11,
              fontFamily: 'monospace',
              padding: '3px 8px',
              background: 'var(--bg2)',
              color: 'var(--text2)',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            {m}
          </span>
        ))}
      </div>
    </div>
  );
}

/** All clean levels at once, each with its masked words; plain buttons so the remote can pick one. */
function CleanLevelPicker({ levels, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {levels.map((lv) => {
        const on = lv.id === value;
        return (
          <button
            key={lv.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(lv.id)}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 10,
              cursor: 'pointer',
              font: 'inherit',
              color: 'var(--text)',
              background: 'transparent',
              border: on ? '2px solid var(--accent)' : '2px solid var(--border)',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14 }}>{on ? `✓ ${lv.label}` : lv.label}</div>
            <CleanLevelSummary level={lv} />
          </button>
        );
      })}
    </div>
  );
}

/** Options menu: last night's health check. Each line takes focus so the remote can move through a long list. */
function ServerHealthRow({ dense }) {
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/health/report', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.report) setReport(d.report);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!report) return null;
  const count = report.problems.length;
  const stale = Date.now() - report.generatedAt > 30 * 3600 * 1000;
  const summary = stale ? 'nightly check did not run' : count ? `${count} problem${count > 1 ? 's' : ''}` : 'all good';
  const lineStyle = { fontSize: dense ? 9 : 11, lineHeight: 1.4, color: 'var(--text2)', padding: '4px 8px', borderRadius: 6 };
  return (
    <Fragment>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--border)', color: count || stale ? 'var(--accent)' : 'var(--text)', fontSize: dense ? 10 : 12, cursor: 'pointer', textAlign: 'left' }}
      >
        Server health: {summary}
      </button>
      {open ? (
        <Fragment>
          {report.problems.map((p) => (
            <div key={p.text} tabIndex={0} style={lineStyle}>
              <strong style={{ color: 'var(--text)' }}>{p.area}:</strong> {p.text}
            </div>
          ))}
          <div tabIndex={0} style={lineStyle}>
            Checked {new Date(report.generatedAt).toLocaleString()}: {report.checked.join(', ')}.
          </div>
        </Fragment>
      ) : null}
    </Fragment>
  );
}

/** Clean audio level on the Filters page (saved as soon as it is picked). */
function CleanLevelDefaults({ onNotify }) {
  const [levels, setLevels] = useState([]);
  const [level, setLevel] = useState('strong');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/clean/level-prefs', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (Array.isArray(d.levels)) setLevels(d.levels);
        if (typeof d.level === 'string') setLevel(d.level);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const pick = async (id) => {
    const before = level;
    setLevel(id);
    try {
      const r = await fetch('/api/clean/level-prefs', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: id }),
      });
      if (!r.ok) throw new Error('save');
      onNotify?.('Saved clean audio level', 'success', true);
    } catch {
      setLevel(before);
      onNotify?.('Could not save clean level', 'error', true);
    }
  };

  if (!levels.length) return null;
  return (
    <div style={{ marginBottom: 16, padding: '14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg3)', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Clean audio — words to mute</div>
      <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.55, margin: 0 }}>
        Applies to all of Zflix. To clean a title, open it and choose <strong style={{ color: 'var(--text)' }}>Request clean playback</strong>. Titles already cleaned keep their old level until you choose <strong style={{ color: 'var(--text)' }}>Re-request clean playback</strong> on them.
      </p>
      <CleanLevelPicker levels={levels} value={level} onChange={pick} />
    </div>
  );
}

/** Second-screen modal: scan level + playback padding for movies; clean-level + note for TV series. */
async function queueSceneSkipRescan(itemId) {
  const r = await fetch(`/api/library/items/${encodeURIComponent(itemId)}/request-visual-skip`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const j = await r.json().catch(() => ({}));
  return { ok: r.ok, j };
}

function MovieVisualSkipFiltersModal({ itemId, title, onClose, onNotify, variant = 'movie', onSceneSkipQueued }) {
  const panelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState(/** @type {VisualPresetRow[]} */ ([]));
  const [draft, setDraft] = useState('inherit');
  const [initial, setInitial] = useState('inherit');
  const [draftPadBefore, setDraftPadBefore] = useState(0);
  const [draftPadAfter, setDraftPadAfter] = useState(0);
  const [initialPadBefore, setInitialPadBefore] = useState(0);
  const [initialPadAfter, setInitialPadAfter] = useState(0);
  const [draftSubShift, setDraftSubShift] = useState(0);
  const [initialSubShift, setInitialSubShift] = useState(0);
  const [shiftingSubs, setShiftingSubs] = useState(false);
  const [hasLibraryCustom, setHasLibraryCustom] = useState(false);
  const [effectivePreset, setEffectivePreset] = useState('standard');
  const [cleanLevel, setCleanLevel] = useState('strong');
  const [initialCleanLevel, setInitialCleanLevel] = useState('strong');
  const [cleanLevels, setCleanLevels] = useState([]);
  const [queueingSceneSkip, setQueueingSceneSkip] = useState(false);

  useEffect(() => {
    panelRef.current?.focus({ preventScroll: true });
  }, [itemId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    if (variant === 'series') {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/library/items/${encodeURIComponent(itemId)}/visual-skip-prefs`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (Array.isArray(d.presets)) setPresets(d.presets);
        const p = typeof d.itemPreset === 'string' ? d.itemPreset : 'inherit';
        setDraft(p);
        setInitial(p);
        const pb = Math.min(120, Math.max(0, Number(d.playbackPadBeforeSec) || 0));
        const pa = Math.min(120, Math.max(0, Number(d.playbackPadAfterSec) || 0));
        setDraftPadBefore(pb);
        setDraftPadAfter(pa);
        setInitialPadBefore(pb);
        setInitialPadAfter(pa);
        const ss = Math.min(300, Math.max(-300, Number(d.subtitleCueShiftSec) || 0));
        setDraftSubShift(ss);
        setInitialSubShift(ss);
        setHasLibraryCustom(!!d.hasLibraryCustom);
        if (typeof d.effectivePreset === 'string') setEffectivePreset(d.effectivePreset);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, variant]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/clean/level-prefs', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (Array.isArray(d.levels)) setCleanLevels(d.levels);
        if (typeof d.level === 'string') {
          setCleanLevel(d.level);
          setInitialCleanLevel(d.level);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const inheritLabel = hasLibraryCustom ? 'Match library default (custom)' : 'Match library default';
  const dirty =
    variant === 'series'
      ? cleanLevel !== initialCleanLevel
      : draft !== initial ||
        draftPadBefore !== initialPadBefore ||
        draftPadAfter !== initialPadAfter ||
        draftSubShift !== initialSubShift ||
        cleanLevel !== initialCleanLevel;
  const effectiveLabel =
    presets.find((x) => x.id === effectivePreset)?.label ||
    (effectivePreset === 'custom' ? 'Custom' : effectivePreset);

  const scanLevelDescription = useMemo(() => {
    if (draft !== 'inherit') return presets.find((x) => x.id === draft)?.description || '';
    if (effectivePreset === 'custom') {
      return 'Library default uses custom sensitivity.';
    }
    return presets.find((x) => x.id === effectivePreset)?.description || '';
  }, [draft, effectivePreset, presets]);

  const save = async () => {
    if (variant === 'series') {
      setSaving(true);
      try {
        if (cleanLevel === initialCleanLevel) {
          onClose();
          return;
        }
        const cr = await fetch('/api/clean/level-prefs', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level: cleanLevel }),
        });
        const cj = await cr.json().catch(() => ({}));
        if (!cr.ok) {
          onNotify?.(typeof cj.error === 'string' ? cj.error : 'Could not save clean level', 'error', true);
          return;
        }
        setInitialCleanLevel(cleanLevel);
        onNotify?.('Saved clean level.', 'success', true);
        onClose();
      } catch {
        onNotify?.('Save failed', 'error', true);
      } finally {
        setSaving(false);
      }
      return;
    }
    const hadPresetChange = draft !== initial;
    const hadCleanLevelChange = cleanLevel !== initialCleanLevel;
    setSaving(true);
    try {
      const r = await fetch(`/api/library/items/${encodeURIComponent(itemId)}/visual-skip-prefs`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset: draft,
          playbackPadBeforeSec: draftPadBefore,
          playbackPadAfterSec: draftPadAfter,
          subtitleCueShiftSec: draftSubShift,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        onNotify?.(typeof d.error === 'string' ? d.error : 'Could not save', 'error', true);
        return;
      }
      if (hadCleanLevelChange) {
        const cr = await fetch('/api/clean/level-prefs', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level: cleanLevel }),
        });
        const cj = await cr.json().catch(() => ({}));
        if (!cr.ok) {
          onNotify?.(typeof cj.error === 'string' ? cj.error : 'Could not save clean level', 'error', true);
          return;
        }
        setInitialCleanLevel(cleanLevel);
      }
      const saved = typeof d.itemPreset === 'string' ? d.itemPreset : draft;
      setInitial(saved);
      setDraft(saved);
      const outB = Math.min(120, Math.max(0, Number(d.playbackPadBeforeSec) || 0));
      const outA = Math.min(120, Math.max(0, Number(d.playbackPadAfterSec) || 0));
      setInitialPadBefore(outB);
      setInitialPadAfter(outA);
      setDraftPadBefore(outB);
      setDraftPadAfter(outA);
      const outSub = Math.min(300, Math.max(-300, Number(d.subtitleCueShiftSec) || 0));
      setInitialSubShift(outSub);
      setDraftSubShift(outSub);
      if (typeof d.effectivePreset === 'string') setEffectivePreset(d.effectivePreset);
      if (hadPresetChange) {
        onNotify?.('Saved. Regenerate the scene-skip map to apply the new sensitivity.', 'success', true);
      } else {
        onNotify?.('Saved.', 'success', true);
      }
      onClose();
    } catch {
      onNotify?.('Save failed', 'error', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1250,
        background: 'rgba(0,0,0,0.82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="zflix-movie-skip-filters-title"
        ref={panelRef}
        tabIndex={-1}
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '22px 22px 20px',
          maxWidth: 440,
          width: '100%',
          maxHeight: 'min(88vh, 560px)',
          overflowY: 'auto',
          boxShadow: '0 20px 56px rgba(0,0,0,0.65)',
          outline: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
          <h2 id="zflix-movie-skip-filters-title" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25, margin: 0 }}>
            {variant === 'series' ? 'Set clean level' : 'Set filters'}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              flexShrink: 0,
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text)',
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 16px', lineHeight: 1.45 }}>{title}</p>
        {variant === 'series' ? (
          <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 16px', lineHeight: 1.45 }}>
            Scene-skip sensitivity is set per <strong style={{ color: 'var(--text)' }}>episode</strong> (open an episode → <strong>Set filters</strong>). For the whole series, pick dialogue strictness below, then tap <strong>Request clean playback</strong> on this show — every episode that has a video file on disk is queued for the clean worker.
          </p>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 16px', lineHeight: 1.45 }}>
            {draft === 'inherit' ? (
              <>
                Scan sensitivity: <strong style={{ color: 'var(--text)' }}>{effectiveLabel}</strong> (library default).
              </>
            ) : (
              <>This title uses its own sensitivity.</>
            )}
          </p>
        )}
        {variant !== 'series' ? (
          loading ? (
            <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>Loading…</div>
          ) : (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>Sensitivity</span>
                <TvSelect
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  style={{ ...vfPresetSelectStyle, maxWidth: '100%', fontSize: 15, padding: '12px 14px' }}
                >
                  <option value="inherit">{inheritLabel}</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </TvSelect>
              </label>
              {scanLevelDescription ? (
                <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, margin: '0 0 14px' }}>{scanLevelDescription}</p>
              ) : null}
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'var(--bg3)',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13, marginBottom: 6 }}>Skip margin (seconds)</div>
                <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45, margin: '0 0 12px' }}>
                  Optional buffer before and after each mapped skip. Playback only; does not edit the map.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--text2)', flex: '1 1 120px' }}>
                    <span>Before</span>
                    <input
                      type="number"
                      min={0}
                      max={120}
                      step={0.5}
                      value={draftPadBefore}
                      onChange={(e) => {
                        const v = Math.min(120, Math.max(0, Number(e.target.value) || 0));
                        setDraftPadBefore(v);
                      }}
                      style={{ ...vfPresetSelectStyle, padding: '10px 12px' }}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--text2)', flex: '1 1 120px' }}>
                    <span>After</span>
                    <input
                      type="number"
                      min={0}
                      max={120}
                      step={0.5}
                      value={draftPadAfter}
                      onChange={(e) => {
                        const v = Math.min(120, Math.max(0, Number(e.target.value) || 0));
                        setDraftPadAfter(v);
                      }}
                      style={{ ...vfPresetSelectStyle, padding: '10px 12px' }}
                    />
                  </label>
                </div>
              </div>
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'var(--bg3)',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13, marginBottom: 6 }}>Subtitle sync (seconds)</div>
                <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45, margin: '0 0 12px' }}>
                  On each <strong style={{ color: 'var(--text2)' }}>Request clean</strong>, Zflix can auto-sync subs to the audio (ffsubsync)
                  before building dialogue mutes. If subs still look wrong, set a manual offset here (positive if subs appear{' '}
                  <strong style={{ color: 'var(--text2)' }}>before</strong> dialogue — try 60). Manual offset disables auto-sync for that title.
                  Scene skips follow the video, not subtitles.
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--text2)', flex: '1 1 140px' }}>
                    <span>Offset</span>
                    <input
                      type="number"
                      min={-300}
                      max={300}
                      step={1}
                      value={draftSubShift}
                      onChange={(e) => {
                        const v = Math.min(300, Math.max(-300, Number(e.target.value) || 0));
                        setDraftSubShift(v);
                      }}
                      style={{ ...vfPresetSelectStyle, padding: '10px 12px' }}
                    />
                  </label>
                  <Btn
                    type="button"
                    variant="secondary"
                    disabled={shiftingSubs || draftSubShift === 0}
                    onClick={async () => {
                      setShiftingSubs(true);
                      try {
                        const r = await fetch(
                          `/api/library/items/${encodeURIComponent(itemId)}/shift-subtitle-file`,
                          {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ shiftSec: draftSubShift }),
                          },
                        );
                        const j = await r.json().catch(() => ({}));
                        if (!r.ok) {
                          onNotify?.(typeof j.error === 'string' ? j.error : 'Could not shift subtitle file', 'error', true);
                          return;
                        }
                        onNotify?.(
                          'Subtitle file updated on disk (backup saved). Re-request clean to refresh baked mutes; refresh playback for Jellyfin subs.',
                          'success',
                          true,
                        );
                      } catch {
                        onNotify?.('Could not shift subtitle file', 'error', true);
                      } finally {
                        setShiftingSubs(false);
                      }
                    }}
                    style={{ padding: '10px 14px', fontSize: 12 }}
                  >
                    {shiftingSubs ? 'Applying…' : 'Fix .srt on disk'}
                  </Btn>
                </div>
              </div>
            </>
          )
        ) : null}
        {cleanLevels.length > 0 ? (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'var(--bg3)',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13, marginBottom: 6 }}>Clean audio level</div>
                <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45, margin: '0 0 10px' }}>
                  How strict the dialogue mute is. Applies on the next <strong style={{ color: 'var(--text2)' }}>Request clean playback</strong>; already-rendered clean tracks keep their original level until re-requested.
                </p>
                <CleanLevelPicker levels={cleanLevels} value={cleanLevel} onChange={setCleanLevel} />
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 4 }}>
              {variant !== 'series' ? (
                <Btn
                  variant="ghost"
                  type="button"
                  disabled={saving || queueingSceneSkip || dirty}
                  title={dirty ? 'Save filter changes first' : 'Queue a new NudeNet scan using the saved sensitivity (no clean re-encode).'}
                  onClick={async () => {
                    setQueueingSceneSkip(true);
                    try {
                      const { ok, j } = await queueSceneSkipRescan(itemId);
                      if (!ok) {
                        onNotify?.(typeof j.error === 'string' ? j.error : 'Could not queue scene-skip scan', 'error', true);
                        return;
                      }
                      if (j.queued === false) {
                        onNotify?.(typeof j.message === 'string' ? j.message : 'Scene-skip scan already in progress', 'success', true);
                      } else {
                        onNotify?.('Scene-skip scan queued.', 'success', true);
                      }
                      onSceneSkipQueued?.();
                      onClose();
                    } catch {
                      onNotify?.('Could not queue scene-skip scan', 'error', true);
                    } finally {
                      setQueueingSceneSkip(false);
                    }
                  }}
                  style={{ marginRight: 'auto', padding: '9px 14px', fontSize: 12, border: '1px solid var(--border)' }}
                >
                  {queueingSceneSkip ? 'Queueing…' : 'Regenerate scene-skip map'}
                </Btn>
              ) : null}
              <Btn variant="ghost" type="button" onClick={onClose} disabled={saving}>
                Cancel
              </Btn>
              <Btn variant="primary" type="button" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save'}
              </Btn>
            </div>
      </div>
    </div>
  );
}

function VisualFiltersPage({ showToast }) {
 return (
   <div style={{ maxWidth: 720 }}>
     <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Filters</h2>
     <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>
       What Clean Play mutes and skips. To change one title, open it and choose <strong style={{ color: 'var(--text)' }}>Set filters</strong>.
     </p>
     <CleanLevelDefaults onNotify={(msg, type) => showToast(msg, type)} />
     <LibraryVisualSkipDefaults onNotify={(msg, type) => showToast(msg, type)} />
   </div>
 );
}

function DetailModal({ item, onClose, onPlay, onRequest, isLibrary, onPlayAudiobook, onPickLibraryCredit, onBrowseSeriesEpisodes, onOpenAlbum }) {
 const dlgRef = useRef(null);
 const myList = useMyList();
 const inMyList = myList.some((x) => x.key === myListKey(item));
 const trailer = useTrailer(item && (item.type === 'movie' || item.type === 'show') ? item : null);
 const [trailerOpen, setTrailerOpen] = useState(false);
 /** Discover: where this title stands (in Zflix, downloading and how far, or waiting for a copy). Checked again every 15 s while it moves. */
 const [reqStatus, setReqStatus] = useState(null);
 const statusReloadRef = useRef(null);
 const statusTmdb = item && item.source === 'tmdb' && (item.type === 'movie' || item.type === 'show') ? item.tmdbId : null;
 const statusType = item?.type;
 useEffect(() => {
   setReqStatus(null);
   if (!statusTmdb) return undefined;
   let dead = false;
   let t = null;
   const load = async () => {
     try {
       const r = await fetch(`/api/request/status?type=${statusType}&tmdbId=${encodeURIComponent(statusTmdb)}`, { credentials: 'include' });
       const j = await r.json();
       if (dead) return;
       setReqStatus(j);
       if (j && ['downloading', 'importing', 'waiting'].includes(j.state)) t = setTimeout(load, 15000);
     } catch (_) {}
   };
   statusReloadRef.current = () => {
     clearTimeout(t);
     setTimeout(load, 2500);
   };
   load();
   return () => {
     dead = true;
     clearTimeout(t);
     statusReloadRef.current = null;
   };
 }, [statusTmdb, statusType]);
 const trailerBtn = (
   <Btn variant="ghost" type="button" data-zflix-trailer-button style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => setTrailerOpen(true)}>
     <Icon name="play" size={16} /> Trailer
   </Btn>
 );
 const [albumsBusy, setAlbumsBusy] = useState(false);
 const [bioOpen, setBioOpen] = useState(false);
 const [requesting, setRequesting] = useState(false);
 const [requested, setRequested] = useState(false);
 const [albums, setAlbums] = useState([]);
 const [absDetail, setAbsDetail] = useState(null);
 const [podcastDetail, setPodcastDetail] = useState(null);
 const [mvPayload, setMvPayload] = useState(null);
 const [mvReady, setMvReady] = useState(false);
 const [cleanRequesting, setCleanRequesting] = useState(false);
 const [visualSkipRequesting, setVisualSkipRequesting] = useState(false);
 const [reportingBad, setReportingBad] = useState(false);
 const [radarrReportConfirmOpen, setRadarrReportConfirmOpen] = useState(false);
 const radarrReportSectionRef = useRef(null);
 const [vsSkip, setVsSkip] = useState(null);
 const [filmRowKey, setFilmRowKey] = useState(null);
 const [showSkipFiltersModal, setShowSkipFiltersModal] = useState(false);
 /** Library lists no longer carry the cast (it made every list slow); it is fetched when a title is opened. */
 const [lazyCast, setLazyCast] = useState(null);
 const [tvSendOpen, setTvSendOpen] = useState(false);
 useEffect(() => {
   setLazyCast(null);
   if (item.source !== 'jellyfin' || (item.type !== 'movie' && item.type !== 'show') || !item.id) return undefined;
   if ((item.cast && item.cast.length) || (item.people && item.people.length)) return undefined;
   let dead = false;
   fetch(`/api/library/items/${encodeURIComponent(item.id)}/cast`)
     .then((r) => (r.ok ? r.json() : []))
     .then((a) => { if (!dead && Array.isArray(a)) setLazyCast(a); })
     .catch(() => {});
   return () => { dead = true; };
 }, [item.id, item.source, item.type]);
 const listedCast = item.cast || (item.people ? item.people.filter(p => p.type === 'Actor') : []);
 const cast = listedCast.length ? listedCast : (lazyCast || []);

 /** Any Jellyfin movie detail — clean request + variants (not limited to one browse tab). */
 const isJfMovieDetail = item.source === 'jellyfin' && item.type === 'movie';
 /** Jellyfin TV series sheet — queue clean for all on-disk episodes (no per-episode UI). */
 const isJfSeriesDetail = item.source === 'jellyfin' && item.type === 'show';
 const [seriesClean, setSeriesClean] = useState(null);
 const [seriesCleanReady, setSeriesCleanReady] = useState(false);

 useEffect(() => {
   setShowSkipFiltersModal(false);
 }, [item.id]);

 useEffect(() => {
   if (dlgRef.current) focusZflixModalPrimary(dlgRef.current);
 }, [item.id, item.tmdbId, item.source, item.type, mvReady, seriesCleanReady]);

 useEffect(() => {
   setFilmRowKey(null);
 }, [item.id, item.tmdbPersonId, item.type, item.source, item.filmographyKind]);

 useEffect(() => {
   setRadarrReportConfirmOpen(false);
 }, [item.id]);

 useEffect(() => {
   if (item.source !== 'audiobookshelf' || item.type !== 'audiobook') {
     setAbsDetail(null);
     return;
   }
   fetch(`/api/audiobooks/item/${encodeURIComponent(item.id)}`)
     .then((r) => r.json())
     .then(setAbsDetail)
     .catch(() => setAbsDetail(null));
 }, [item.id, item.source, item.type]);

 useEffect(() => {
   if (item.source !== 'audiobookshelf' || item.type !== 'podcast') {
     setPodcastDetail(null);
     return;
   }
   fetch(`/api/podcasts/${encodeURIComponent(item.id)}`)
     .then((r) => r.json())
     .then(setPodcastDetail)
     .catch(() => setPodcastDetail(null));
 }, [item.id, item.source, item.type]);

 useEffect(() => {
   setAlbums([]);
   setAlbumsBusy(false);
   setBioOpen(false);
   let url = null;
   if (item.type === 'musicartist' && item.source === 'jellyfin' && item.id) {
     url = item.virtualArtist || String(item.id || '').startsWith('zartist:')
       ? `/api/music/artists/${encodeURIComponent(item.id)}/albums`
       : `/api/music/artist/${encodeURIComponent(item.id)}/albums`;
   } else if (item.type === 'artist') {
     url = item.lidarrId ? `/api/discover/music/albums?artistId=${item.lidarrId}` : `/api/discover/music/albums/byname?name=${encodeURIComponent(item.title)}`;
   }
   if (!url) return undefined;
   let cancelled = false;
   setAlbumsBusy(true);
   fetch(url)
     .then(r => r.json())
     .then(d => { if (!cancelled) setAlbums(d.items || []); })
     .catch(() => { if (!cancelled) setAlbums([]); })
     .finally(() => { if (!cancelled) setAlbumsBusy(false); });
   return () => { cancelled = true; };
 }, [item.id, item.lidarrId, item.title, item.type, item.source]);

 useEffect(() => {
   if (!isJfMovieDetail) {
     setMvPayload(null);
     setMvReady(true);
     return undefined;
   }
   let cancelled = false;
   setMvReady(false);
   fetch(`/api/movies/${encodeURIComponent(item.id)}/variants`, { credentials: 'include' })
     .then((r) => r.json())
     .then((p) => { if (!cancelled) setMvPayload(p); })
     .catch(() => { if (!cancelled) setMvPayload(null); })
     .finally(() => { if (!cancelled) setMvReady(true); });
   return () => { cancelled = true; };
 }, [item.id, isJfMovieDetail]);

 useEffect(() => {
   if (!isJfSeriesDetail) {
     setSeriesClean(null);
     setSeriesCleanReady(true);
     return undefined;
   }
   let cancelled = false;
   setSeriesCleanReady(false);
   fetch(`/api/shows/${encodeURIComponent(item.id)}/clean-status`, { credentials: 'include' })
     .then((r) => r.json())
     .then((p) => {
       if (!cancelled) setSeriesClean(p);
     })
     .catch(() => {
       if (!cancelled) setSeriesClean(null);
     })
     .finally(() => {
       if (!cancelled) setSeriesCleanReady(true);
     });
   return () => {
     cancelled = true;
   };
 }, [item.id, isJfSeriesDetail]);

 useEffect(() => {
   if (!isJfSeriesDetail || !seriesClean || seriesClean.cleanJobStatus !== 'preparing') return undefined;
   const id = setInterval(() => {
     fetch(`/api/shows/${encodeURIComponent(item.id)}/clean-status`, { credentials: 'include' })
       .then((r) => r.json())
       .then(setSeriesClean)
       .catch(() => {});
   }, 12000);
   return () => clearInterval(id);
 }, [item.id, isJfSeriesDetail, seriesClean?.cleanJobStatus]);

 useEffect(() => {
   if (!isJfMovieDetail || !mvPayload || mvPayload.cleanJobStatus !== 'preparing') return undefined;
   const id = setInterval(() => {
     fetch(`/api/movies/${encodeURIComponent(item.id)}/variants`, { credentials: 'include' })
       .then((r) => r.json())
       .then(setMvPayload)
       .catch(() => {});
   }, 12000);
   return () => clearInterval(id);
 }, [item.id, isJfMovieDetail, mvPayload?.cleanJobStatus]);

 useEffect(() => {
   if (!isJfMovieDetail) {
     setVsSkip(null);
     return undefined;
   }
   setVsSkip(null);
   let cancelled = false;
   const tick = () => {
     fetch(`/api/library/items/${encodeURIComponent(item.id)}/visual-skip-status`, { credentials: 'include' })
       .then((r) => r.json())
       .then((x) => {
         if (!cancelled) setVsSkip(x);
       })
       .catch(() => {});
   };
   tick();
   const id = setInterval(tick, 12000);
   return () => {
     cancelled = true;
     clearInterval(id);
   };
 }, [item.id, isJfMovieDetail]);

 useEffect(() => {
   if (!isJfMovieDetail) return undefined;
   if (!vsSkip || (vsSkip.status !== 'pending' && vsSkip.status !== 'scanning')) return undefined;
   let cancelled = false;
   const id = setInterval(() => {
     fetch(`/api/library/items/${encodeURIComponent(item.id)}/visual-skip-status`, { credentials: 'include' })
       .then((r) => r.json())
       .then((x) => {
         if (!cancelled) setVsSkip(x);
       })
       .catch(() => {});
   }, 4000);
   return () => {
     cancelled = true;
     clearInterval(id);
   };
 }, [item.id, isJfMovieDetail, vsSkip?.status]);

 const merged = { ...item, ...(absDetail || {}), ...(podcastDetail || {}) };
 const coverArt = merged.poster || merged.cover;
 const isPodcastDetail = item.source === 'audiobookshelf' && item.type === 'podcast';
 const overviewText = merged.overview || merged.description || merged.summary || '';
 /** A band's life story must not push its albums off the screen. */
 const longBio = (item.type === 'artist' || item.type === 'musicartist') && overviewText.length > 240;
 const authorLine = merged.authorLabel || merged.author || (merged.authors && merged.authors.map((a) => (typeof a === 'string' ? a : a.name)).filter(Boolean).join(', '));

 const bookshelfShelf = bookshelfShelfFromDiscoverItem(item);
 const booksTabRequest = bookshelfShelf === 'ebook';

 const handleRequest = async () => {
   setRequesting(true);
   try {
     let endpoint, body;
     if (item.type === 'movie') { endpoint = '/api/request/movie'; body = { tmdbId: item.tmdbId, title: item.title, year: item.year }; }
     else if (item.type === 'show') { endpoint = '/api/request/show'; body = { tmdbId: item.tmdbId, title: item.title, year: item.year }; }
     else if (item.type === 'artist') { endpoint = '/api/request/music'; body = { lidarrId: item.lidarrId, artistName: item.title }; }
     else return;
     const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
     const data = await res.json();
     if (data.success) {
       setRequested(true);
       statusReloadRef.current?.();
     }
     onRequest(data.message, data.success ? 'success' : 'error');
   } catch { onRequest('Request failed', 'error'); }
   finally { setRequesting(false); }
 };

 const handleBookRequest = async () => {
   setRequesting(true);
   try {
     const shelf = bookshelfShelf;
     let body;
     if (item.source === 'bookshelf-search')
       body = {
         shelf,
         bookshelfBook: item.bookshelfBook,
         title: item.title,
         author: item.author || (item.authors && item.authors[0]),
       };
     else body = { shelf, title: item.title, author: item.author || (item.authors && item.authors[0]) };
     const res = await fetch('/api/request/book', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
     const data = await res.json();
     if (data.success) setRequested(true);
     onRequest(data.message || data.error || 'Done', data.success ? 'success' : 'error');
   } catch {
     onRequest(`${booksTabRequest ? 'eBook' : 'Audiobook'} request failed`, 'error');
   } finally {
     setRequesting(false);
   }
 };

 const handlePodcastRequest = async () => {
   if (!item.feedUrl) {
     onRequest('Missing podcast feed URL', 'error');
     return;
   }
   setRequesting(true);
   try {
     const res = await fetch('/api/podcasts/request', {
       method: 'POST',
       credentials: 'include',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ feedUrl: item.feedUrl, title: item.title }),
     });
     const data = await res.json().catch(() => ({}));
     if (res.ok) setRequested(true);
     onRequest(data.message || data.error || 'Done', res.ok ? 'success' : 'error');
   } catch {
     onRequest('Podcast request failed', 'error');
   } finally {
     setRequesting(false);
   }
 };

 const requestFilmographyRow = async (row) => {
   const rk = `${row.type}-${row.tmdbId}`;
   setFilmRowKey(rk);
   try {
     let endpoint;
     let body;
     if (row.type === 'movie') {
       endpoint = '/api/request/movie';
       body = { tmdbId: row.tmdbId, title: row.title, year: row.year };
     } else {
       endpoint = '/api/request/show';
       body = { tmdbId: row.tmdbId, title: row.title, year: row.year };
     }
     const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
     const data = await res.json();
     onRequest(data.message || data.error || 'Done', data.success ? 'success' : 'error');
   } catch {
     onRequest('Request failed', 'error');
   } finally {
     setFilmRowKey(null);
   }
 };

 const showBookRequest =
   item.type === 'audiobook-request' ||
   item.source === 'audiobook-discover' ||
   item.source === 'book-discover' ||
   item.source === 'bookshelf-search';
 const showPodcastRequest =
   item.type === 'podcast-request' &&
   (item.source === 'podcast-discover' || item.source === 'podcast-index');
 const bookRequestBtnLabel = requesting ? 'Sending…' : requested ? (booksTabRequest ? 'Added to Bookshelf' : 'Sent to Bookshelf') : booksTabRequest ? 'Request eBook' : 'Request audiobook';
 const podcastRequestBtnLabel = requesting ? 'Sending…' : requested ? 'Added to library' : 'Request podcast';
 const showMediaRequest = !isLibrary && ['movie', 'show', 'artist'].includes(item.type);

 const submitRadarrBadMovieReport = async () => {
   setReportingBad(true);
   try {
     const r = await fetch('/api/library/report-bad-stream', {
       method: 'POST',
       credentials: 'include',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ jellyfinItemId: item.id }),
     });
     const j = await r.json().catch(() => ({}));
     if (!r.ok) {
       onRequest(typeof j.error === 'string' ? j.error : 'Report failed', 'error', true);
       return;
     }
     onRequest(j.message || 'Radarr update queued.', 'success', true);
     setRadarrReportConfirmOpen(false);
   } catch {
     onRequest('Network error', 'error', true);
   } finally {
     setReportingBad(false);
   }
 };

 const requestCleanEdition = async (isRerequest = false) => {
   setCleanRequesting(true);
   try {
     const body = isRerequest ? { queueVisualSkip: false } : {};
     const r = await fetch(`/api/movies/${encodeURIComponent(item.id)}/request-clean`, {
       method: 'POST',
       credentials: 'include',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify(body),
     });
     const j = await r.json().catch(() => ({}));
     if (r.ok) {
       let msg = isRerequest
         ? 'Clean playback re-requested — rebaking subtitle mutes.'
         : 'Clean edition requested — preparing.';
       if (j.visualSkipQueued === true) msg += ' Scene-skip scan queued.';
       else if (!isRerequest && j.visualSkipQueued === false) {
         msg += ' Scene-skip scan not queued.';
       }
       onRequest(msg, 'success', true);
       fetch(`/api/movies/${encodeURIComponent(item.id)}/variants`, { credentials: 'include' })
         .then((res) => res.json())
         .then(setMvPayload)
         .catch(() => {});
       fetch(`/api/library/items/${encodeURIComponent(item.id)}/visual-skip-status`, { credentials: 'include' })
         .then((r) => r.json())
         .then(setVsSkip)
         .catch(() => {});
     } else onRequest(typeof j.error === 'string' ? j.error : 'Request failed', 'error', true);
   } catch {
     onRequest('Request failed', 'error', true);
   } finally {
     setCleanRequesting(false);
   }
 };

 const refreshVisualSkipStatus = () => {
   fetch(`/api/library/items/${encodeURIComponent(item.id)}/visual-skip-status`, { credentials: 'include' })
     .then((r) => r.json())
     .then(setVsSkip)
     .catch(() => {});
 };

 const requestSceneSkipRescan = async () => {
   setVisualSkipRequesting(true);
   try {
     const { ok, j } = await queueSceneSkipRescan(item.id);
     if (!ok) {
       onRequest(typeof j.error === 'string' ? j.error : 'Could not queue scene-skip scan', 'error', true);
       return;
     }
     if (j.queued === false) {
       onRequest(typeof j.message === 'string' ? j.message : 'Scene-skip scan already in progress', 'success', true);
     } else {
       onRequest('Scene-skip scan queued.', 'success', true);
     }
     refreshVisualSkipStatus();
   } catch {
     onRequest('Could not queue scene-skip scan', 'error', true);
   } finally {
     setVisualSkipRequesting(false);
   }
 };

 const requestCleanSeries = async () => {
   setCleanRequesting(true);
   try {
     const r = await fetch(`/api/shows/${encodeURIComponent(item.id)}/request-clean`, {
       method: 'POST',
       credentials: 'include',
       headers: { 'Content-Type': 'application/json' },
       body: '{}',
     });
     const j = await r.json().catch(() => ({}));
     if (r.ok) {
       const n = typeof j.queued === 'number' ? j.queued : 0;
       const tot = typeof j.episodeCount === 'number' ? j.episodeCount : 0;
       onRequest(
         n > 0
           ? `Queued clean audio for ${n} episode file${n !== 1 ? 's' : ''} (${tot} with media in this series).`
           : j.message || 'Nothing new to queue (episodes already preparing, or no on-disk media).',
         'success',
         true,
       );
       fetch(`/api/shows/${encodeURIComponent(item.id)}/clean-status`, { credentials: 'include' })
         .then((res) => res.json())
         .then(setSeriesClean)
         .catch(() => {});
     } else onRequest(typeof j.error === 'string' ? j.error : 'Request failed', 'error', true);
   } catch {
     onRequest('Request failed', 'error', true);
   } finally {
     setCleanRequesting(false);
   }
 };

 const sceneSkipScanBlocksPlayClean =
   isJfMovieDetail &&
   !!mvPayload?.clean &&
   !!vsSkip &&
   (vsSkip.status === 'pending' || vsSkip.status === 'scanning');

 return (
   <Fragment>
   <div data-zflix-modal-root style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
     <div role="dialog" aria-modal="true" aria-label={merged.title || merged.name} ref={dlgRef} tabIndex={-1} style={{ background: 'var(--bg2)', borderRadius: isTvApp() ? 0 : '16px 16px 0 0', width: '100%', maxWidth: 860, maxHeight: '92vh', overflowY: 'scroll', WebkitOverflowScrolling: 'touch', outline: 'none' }} onClick={e => e.stopPropagation()}>
       <div style={{ position: 'relative' }}>
         {merged.backdrop || coverArt ? <img src={merged.backdrop || coverArt} alt="" style={{ width: '100%', height: 240, objectFit: 'cover', borderRadius: isTvApp() ? 0 : '16px 16px 0 0', display: 'block' }} /> : <div style={{ width: '100%', height: 180, background: 'var(--bg3)', borderRadius: isTvApp() ? 0 : '16px 16px 0 0' }} />}
         {trailer && (merged.backdrop || coverArt) ? <BackdropTrailer trailer={trailer} paused={trailerOpen} poster={merged.backdrop || coverArt} onTap={() => setTrailerOpen(true)} /> : null}
         <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,var(--bg2) 0%,rgba(0,0,0,0.2) 60%)', borderRadius: isTvApp() ? 0 : '16px 16px 0 0', pointerEvents: 'none', zIndex: 2 }} />
         {/* The dim backdrop behind the panel: 88% black over #0a0a0f. */}
         {isTvApp() ? <RoundCorners r={16} color="rgb(1,1,2)" bottom={false} /> : null}
         {!(coverArt && merged.type !== 'artist' && merged.type !== 'person') ? <PlaybackProgressBar fraction={merged.resumeProgress} height={5} /> : null}
         <button style={{ position: 'absolute', top: 14, right: 14, zIndex: 6, background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }} onClick={onClose}><Icon name="close" size={18} /></button>
       </div>
       <div style={{ padding: '16px 18px 40px' }}>
         <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
           {coverArt ? (
             <div
               style={{
                 position: 'relative',
                 flexShrink: 0,
                 marginTop: 0,
                 borderRadius: merged.type === 'artist' || merged.type === 'person' ? '50%' : 8,
                 overflow: 'hidden',
                 boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                 alignSelf: 'flex-start',
               }}
             >
               <img
                 src={coverArt}
                 alt=""
                 style={{
                   width: 90,
                   height: merged.type === 'artist' || merged.type === 'person' ? 90 : 135,
                   display: 'block',
                   objectFit: 'cover',
                   borderRadius: merged.type === 'artist' || merged.type === 'person' ? '50%' : 0,
                 }}
                 onError={(e) => {
                   e.target.style.display = 'none';
                 }}
               />
               {merged.type !== 'artist' && merged.type !== 'person' && coverArt ? <PlaybackProgressBar fraction={merged.resumeProgress} height={4} /> : null}
             </div>
           ) : null}
           <div style={{ flex: 1, paddingTop: 0, minWidth: 0 }}>
             <h2 style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, marginBottom: 8 }}>{merged.title || merged.name}</h2>
             {merged.subtitle ? <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>{merged.subtitle}</div> : null}
             {authorLine ? <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>{authorLine}</div> : null}
             <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
               {merged.year && <span style={{ color: 'var(--text2)', fontSize: 13 }}>{merged.year}</span>}
               {merged.runtime && <span style={{ color: 'var(--text2)', fontSize: 13 }}>{merged.runtime}m</span>}
               {merged.publishYear && <span style={{ color: 'var(--text2)', fontSize: 13 }}>{merged.publishYear}</span>}
               {merged.rating && <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--gold)', fontSize: 13, fontWeight: 700 }}><Icon name="star" size={12} />{merged.rating?.toFixed(1)}</span>}
               {merged.officialRating && <Tag>{merged.officialRating}</Tag>}
             </div>
             <div style={{ marginBottom: 12 }}>{(merged.genres || []).map(g => <Tag key={g}>{g}</Tag>)}</div>
            {(merged.releaseStatus === 'theaters' || merged.releaseStatus === 'upcoming') && (
              <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: merged.releaseStatus === 'theaters' ? '#f59e0b' : 'var(--text2)' }}>
                {merged.releaseStatus === 'theaters'
                  ? `In theaters now — no home release yet${merged.homeReleaseDate ? ` (expected ${merged.homeReleaseDate})` : ''}`
                  : `Not released yet${merged.theatricalDate ? ` — in theaters ${merged.theatricalDate}` : ''}`}
              </div>
            )}
             {merged.narrators?.length > 0 && (
               <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
                 <span style={{ fontWeight: 600 }}>Narrators: </span>
                 {merged.narrators.join(', ')}
               </div>
             )}
             <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
               {item.source === 'audiobookshelf' && item.type === 'audiobook' && onPlayAudiobook && (
                 <Btn
                   data-zflix-modal-primary-action
                   onClick={async () => {
                     const r = await fetch('/api/audiobooks/play', {
                       method: 'POST',
                       credentials: 'include',
                       headers: { 'Content-Type': 'application/json' },
                       body: JSON.stringify({ libraryItemId: item.id }),
                     });
                     const d = await r.json();
                     if (!r.ok) {
                       onRequest(typeof d.error === 'string' ? d.error : 'Could not start playback', 'error');
                       return;
                     }
                     onPlayAudiobook(d);
                     onClose();
                   }}
                 >
                   <Icon name="play" size={16} /> Listen
                 </Btn>
               )}
               {isPodcastDetail && item.resumeEpisodeId && onPlayAudiobook && (
                 <Btn
                   data-zflix-modal-primary-action
                   onClick={async () => {
                     const r = await fetch('/api/podcasts/play', {
                       method: 'POST',
                       credentials: 'include',
                       headers: { 'Content-Type': 'application/json' },
                       body: JSON.stringify({ libraryItemId: item.id, episodeId: item.resumeEpisodeId }),
                     });
                     const d = await r.json();
                     if (!r.ok) {
                       onRequest(typeof d.error === 'string' ? d.error : 'Could not start playback', 'error');
                       return;
                     }
                     onPlayAudiobook({ ...d, episodeId: d.episodeId || item.resumeEpisodeId });
                     onClose();
                   }}
                 >
                   <Icon name="play" size={16} /> Continue
                 </Btn>
               )}
               {isJfSeriesDetail && typeof onBrowseSeriesEpisodes === 'function' && (
                 <Btn
                   variant="primary"
                   type="button"
                   data-zflix-modal-primary-action
                   onClick={() => {
                     onBrowseSeriesEpisodes(item);
                   }}
                 >
                   <Icon name="tv" size={16} /> Episodes
                 </Btn>
               )}
               {isLibrary && item.streamUrl && !(item.source === 'audiobookshelf') && (
                 isJfMovieDetail ? (
                   !mvReady ? (
                     <span style={{ fontSize: 13, color: 'var(--text3)', alignSelf: 'center' }}>Loading playback…</span>
                   ) : mvPayload?.clean ? (
                     <>
                       <div style={{ width: '100%', flexBasis: '100%', marginBottom: 4 }}>
                         <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                           Edition
                         </div>
                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                           <Btn data-zflix-modal-primary-action onClick={() => onPlay(mvPayload.theatrical || item)}><Icon name="play" size={16} /> Play original</Btn>
                           <Btn
                             onClick={() => onPlay(mvPayload.clean)}
                             disabled={sceneSkipScanBlocksPlayClean}
                             title={
                               sceneSkipScanBlocksPlayClean
                                 ? 'Scene-skip scan is still running. Play clean unlocks when it finishes so clean audio and scene skips stay paired.'
                                 : undefined
                             }
                           >
                             <Icon name="play" size={16} /> Play clean
                           </Btn>
                         </div>
                         {mvPayload.cleanPlaybackKind === 'separate_jellyfin_item' ? (
                           <p style={{ fontSize: 11, color: 'var(--text3)', margin: '8px 0 0', lineHeight: 1.45 }}>
                             Both versions are linked to this title — only one card appears in browse and search.
                           </p>
                         ) : null}
                       </div>
                     </>
                   ) : (
                     <Btn data-zflix-modal-primary-action onClick={() => onPlay(mvPayload?.theatrical || item)}><Icon name="play" size={16} /> Play</Btn>
                   )
                 ) : (
                   <Btn data-zflix-modal-primary-action onClick={() => onPlay(item)}><Icon name="play" size={16} /> Play</Btn>
                 )
               )}
               {isLibrary && tvSendEligible(item) && typeof document !== 'undefined' && !document.documentElement.classList.contains('zflix-tv-apk-density') ? (
                 <Btn variant="ghost" type="button" onClick={() => setTvSendOpen(true)} style={{ padding: '8px 14px', fontSize: 13 }}>
                   Play on TV
                 </Btn>
               ) : null}
               {tvSendOpen ? (
                 <TvSendPicker
                   item={mvPayload?.theatrical || item}
                   onClose={() => setTvSendOpen(false)}
                   onPlaying={() => {}}
                 />
               ) : null}
               {item.source === 'jellyfin' &&
                 (item.type === 'movie' || item.type === 'episode') &&
                 item.id != null &&
                 ((merged.resumeProgress ?? item.resumeProgress) > 0) && (
                   <Btn
                     variant="ghost"
                     type="button"
                     style={{ padding: '8px 14px', fontSize: 13 }}
                     onClick={async () => {
                       /** Forget this profile's saved position; the title drops off Continue Watching without being marked watched. */
                       try {
                         await fetch('/api/library/resume/clear', {
                           method: 'POST',
                           credentials: 'include',
                           headers: { 'Content-Type': 'application/json' },
                           body: JSON.stringify({ itemId: item.id }),
                         });
                       } catch (_) {}
                       bumpZflixResumeStale();
                       onClose();
                     }}
                   >
                     Remove from Continue Watching
                   </Btn>
                 )}
               {trailer && !showMediaRequest ? trailerBtn : null}
               {trailerOpen && trailer ? <TrailerPlayer trailer={trailer} title={item.title || item.name} onClose={() => setTrailerOpen(false)} /> : null}
               {myListEligible(item) ? (
                 <Btn
                   variant="ghost"
                   type="button"
                   data-zflix-mylist-toggle
                   style={{ padding: '8px 14px', fontSize: 13 }}
                   onClick={() => setInMyList(item, !inMyList)}
                 >
                   <Icon name={inMyList ? 'check' : 'plus'} size={16} /> My List
                 </Btn>
               ) : null}
               {isLibrary && item.source === 'jellyfin' && item.type === 'movie' ? <OfflineButton id={(mvPayload?.theatrical || item).id} /> : null}
               {showBookRequest && (
                 <Btn data-zflix-modal-primary-action variant={requested ? 'success' : 'primary'} onClick={handleBookRequest} disabled={requesting || requested}>
                   <Icon name={requested ? 'check' : 'plus'} size={16} />
                   {bookRequestBtnLabel}
                 </Btn>
               )}
               {showPodcastRequest && (
                 <Btn data-zflix-modal-primary-action variant={requested ? 'success' : 'primary'} onClick={handlePodcastRequest} disabled={requesting || requested}>
                   <Icon name={requested ? 'check' : 'plus'} size={16} />
                   {podcastRequestBtnLabel}
                 </Btn>
               )}
               {showMediaRequest && (
                 <Btn data-zflix-modal-primary-action variant={requested ? 'success' : 'primary'} onClick={handleRequest} disabled={requesting || requested}>
                   <Icon name={requested ? 'check' : 'plus'} size={16} />
                   {requesting ? 'Requesting...' : requested ? 'Requested!' : item.type === 'artist' ? 'Request Artist' : 'Request'}
                 </Btn>
               )}
               {/* Discover: the trailer right beside Request, to see what it is before asking for it. */}
               {trailer && showMediaRequest ? trailerBtn : null}
             </div>
             {reqStatus && reqStatus.state && !['none', 'unknown'].includes(reqStatus.state) ? (() => {
               const s = reqStatus;
               const left = (() => {
                 const m = /^(?:(\d+)\.)?(\d+):(\d+):(\d+)$/.exec(String(s.timeleft || ''));
                 if (!m) return '';
                 const mins = Number(m[1] || 0) * 1440 + Number(m[2]) * 60 + Number(m[3]);
                 return mins >= 120 ? `about ${Math.round(mins / 60)} hours left` : mins >= 1 ? `about ${mins} min left` : 'less than a minute left';
               })();
               const eps = s.want ? `${s.have || 0} of ${s.want} episodes in Zflix` : '';
               const text =
                 s.state === 'ready' ? (eps || 'In Zflix: ready to watch')
                   : s.state === 'downloading' ? ['Downloading', `${s.pct || 0}%`, left, eps].filter(Boolean).join(' · ')
                   : s.state === 'importing' ? 'Almost ready: adding it to Zflix'
                   : merged.releaseStatus === 'theaters' || merged.releaseStatus === 'upcoming' ? 'Requested: waiting for its home release'
                   : ['Requested: looking for a good copy', eps].filter(Boolean).join(' · ');
               const color = s.state === 'ready' ? '#4ade80' : s.state === 'downloading' || s.state === 'importing' ? '#60a5fa' : 'var(--text2)';
               return (
                 <div data-zflix-request-status={s.state} style={{ marginTop: 12 }}>
                   <div style={{ fontSize: 13, fontWeight: 600, color }}>{text}</div>
                   {s.state === 'downloading' ? (
                     <div style={{ marginTop: 6, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.12)', overflow: 'hidden', maxWidth: 360 }}>
                       <div style={{ width: `${s.pct || 0}%`, height: '100%', background: '#60a5fa', transition: 'width 0.6s' }} />
                     </div>
                   ) : null}
                 </div>
               );
             })() : null}
             {isJfMovieDetail ? (
               <div style={{ marginTop: 10 }}>
                 <button
                   type="button"
                   onClick={() => radarrReportSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                   style={{
                     padding: 0,
                     border: 'none',
                     background: 'none',
                     cursor: 'pointer',
                     fontSize: 12,
                     fontWeight: 600,
                     color: 'var(--text3)',
                     textDecoration: 'underline',
                     textUnderlineOffset: 3,
                   }}
                 >
                   Jump to Report to Radarr ↓
                 </button>
               </div>
             ) : null}
           </div>
         </div>
         {isPodcastDetail && Array.isArray(merged.episodes) && merged.episodes.length > 0 && (
           <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
             <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
               Episodes ({merged.episodes.length})
             </div>
             <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
               {merged.episodes.map((ep) => (
                 <div
                   key={ep.id}
                   style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 12,
                     padding: '10px 12px',
                     background: 'var(--surface)',
                     borderRadius: 8,
                     border: '1px solid var(--border)',
                   }}
                 >
                   <div style={{ flex: 1, minWidth: 0 }}>
                     <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{ep.title}</div>
                     {ep.publishedAt ? (
                       <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{ep.publishedAt}</div>
                     ) : null}
                     {ep.resumeProgress > 0 && ep.resumeProgress < 0.98 ? (
                       <div style={{ height: 3, marginTop: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                         <div style={{ height: '100%', width: `${Math.min(100, ep.resumeProgress * 100)}%`, background: 'var(--accent)', borderRadius: 2 }} />
                       </div>
                     ) : null}
                   </div>
                   {onPlayAudiobook ? (
                     <Btn
                       onClick={async () => {
                         const r = await fetch('/api/podcasts/play', {
                           method: 'POST',
                           credentials: 'include',
                           headers: { 'Content-Type': 'application/json' },
                           body: JSON.stringify({ libraryItemId: item.id, episodeId: ep.id }),
                         });
                         const d = await r.json();
                         if (!r.ok) {
                           onRequest(typeof d.error === 'string' ? d.error : 'Could not start playback', 'error');
                           return;
                         }
                         onPlayAudiobook({ ...d, episodeId: d.episodeId || ep.id });
                         onClose();
                       }}
                     >
                       <Icon name="play" size={16} />
                     </Btn>
                   ) : null}
                 </div>
               ))}
             </div>
           </div>
         )}
         {overviewText ? <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.7, marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 16, ...(longBio && !bioOpen ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', paddingBottom: 0 } : {}) }}>{overviewText}</p> : null}
         {longBio ? <button type="button" onClick={() => setBioOpen((o) => !o)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 0', marginTop: 2 }}>{bioOpen ? 'Show less' : 'Read more'}</button> : null}
         {isJfMovieDetail ? (
           <div style={{ marginTop: 18, paddingTop: 18, borderTop: overviewText ? undefined : '1px solid var(--border)' }}>
             <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Clean playback</div>
             <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, margin: '0 0 12px' }}>
               Clean audio from your server. Scene-skip sensitivity and timing: <strong style={{ color: 'var(--text)' }}>Set filters</strong>.
             </p>
             <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
               <Btn variant="ghost" type="button" onClick={() => setShowSkipFiltersModal(true)} style={{ padding: '9px 16px', fontSize: 13, border: '1px solid var(--border)' }}>
                 <Icon name="filters" size={16} />
                 Set filters
               </Btn>
             </div>
             <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45, margin: '0 0 14px' }}>
               Dialogue muting uses <span style={{ fontFamily: 'monospace', fontSize: 10 }}>clean_wordlist.txt</span> and Bazarr on the server.
             </p>
             {!mvReady ? (
               <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>Loading title status…</div>
             ) : mvPayload ? (
               <>
                 <div style={{ marginBottom: 14 }}>
                   <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>Status</div>
                   <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55 }}>
                     {mvPayload.cleanJobStatus === 'preparing' ? (
                       <div>Clean audio: preparing from subtitle mutes…</div>
                     ) : mvPayload.cleanJobStatus === 'failed' ? (
                       <div style={{ color: '#f85149' }}>
                         Clean audio: failed
                         {mvPayload.cleanJobError ? ` — ${mvPayload.cleanJobError}` : ''}
                       </div>
                     ) : mvPayload.clean ? (
                       <div>
                         {mvPayload.cleanPlaybackKind === 'live_mute'
                           ? 'Clean audio: ready — Zflix mutes the flagged lines while the original plays; no extra file is stored (use Play clean above).'
                           : mvPayload.cleanPlaybackKind === 'dual_audio_stream'
                           ? 'Clean audio: ready — this rip includes a clean track (use Play clean above).'
                           : 'Clean audio: ready — linked clean edition (use Play clean above).'}
                         {sceneSkipScanBlocksPlayClean ? (
                           <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text3)', lineHeight: 1.45 }}>
                             Play clean is turned off until the scene-skip scan finishes, so you are not watching clean audio without the matching skip map.
                           </div>
                         ) : null}
                       </div>
                     ) : (
                       <div>Clean audio: not generated yet.</div>
                     )}
                     <div style={{ marginTop: 8 }}>
                       {!vsSkip ? (
                         <span style={{ color: 'var(--text3)' }}>Scene skips: loading…</span>
                       ) : (
                         <>
                           {vsSkip.status === 'ready' && (
                             <span>
                               Scene skips: ready ({vsSkip.zoneCount} zone{vsSkip.zoneCount !== 1 ? 's' : ''}).
                             </span>
                           )}
                           {vsSkip.status === 'pending' && <span>Scene skips: scan queued…</span>}
                           {vsSkip.status === 'scanning' && <span>Scene skips: scanning…</span>}
                           {vsSkip.status === 'failed' && (
                             <span style={{ color: '#f85149' }}>
                               Scene skips: failed{vsSkip.error ? ` — ${vsSkip.error}` : ''}
                             </span>
                           )}
                           {vsSkip.status === 'none' && <span>Scene skips: no map yet.</span>}
                         </>
                       )}
                     </div>
                   </div>
                 </div>
                 {mvPayload.tmdbLinked === false ? (
                   <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginTop: 4, marginBottom: 12 }}>
                     No TMDB id: fine for dual-audio Clean (same file). To auto-pair a separate Clean movie in Jellyfin, link TMDB on both items and put “Clean” in the edition title or path.
                   </p>
                 ) : null}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  {mvPayload.cleanJobStatus === 'preparing' ? (
                    <span style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 600 }}>Clean playback pipeline running…</span>
                  ) : mvPayload.clean ? (
                    <>
                      <Btn
                        variant="ghost"
                        type="button"
                        onClick={() => requestCleanEdition(true)}
                        disabled={cleanRequesting}
                        style={{ padding: '9px 16px', fontSize: 13, border: '1px solid var(--border)' }}
                        title="Re-import subtitles and re-mux the clean audio track only — does not re-run scene-skip scan."
                      >
                        <Icon name="movie" size={16} />
                        {cleanRequesting ? 'Sending…' : 'Re-request clean playback'}
                      </Btn>
                      <Btn
                        variant="ghost"
                        type="button"
                        onClick={requestSceneSkipRescan}
                        disabled={
                          visualSkipRequesting ||
                          vsSkip?.status === 'pending' ||
                          vsSkip?.status === 'scanning'
                        }
                        style={{ padding: '9px 16px', fontSize: 13, border: '1px solid var(--border)' }}
                        title="Re-run NudeNet scene-skip scan with saved sensitivity (Set filters). Does not re-encode clean audio."
                      >
                        <Icon name="filters" size={16} />
                        {visualSkipRequesting
                          ? 'Queueing…'
                          : vsSkip?.status === 'pending' || vsSkip?.status === 'scanning'
                            ? 'Scene-skip scan running…'
                            : vsSkip?.status === 'ready'
                              ? 'Re-request scene skip'
                              : 'Regenerate scene-skip map'}
                      </Btn>
                    </>
                  ) : (
                    <Btn variant="primary" type="button" onClick={() => requestCleanEdition(false)} disabled={cleanRequesting}>
                      <Icon name="movie" size={16} />
                      {cleanRequesting ? 'Sending…' : mvPayload.cleanJobStatus === 'failed' ? 'Retry clean playback' : 'Request clean playback'}
                    </Btn>
                  )}
                </div>
               </>
             ) : (
               <div style={{ fontSize: 13, color: 'var(--text3)' }}>Could not load title status.</div>
             )}
           </div>
         ) : null}
         {isJfSeriesDetail ? (
           <div style={{ marginTop: 18, paddingTop: 18, borderTop: overviewText ? undefined : '1px solid var(--border)' }}>
             <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Clean playback (whole series)</div>
             <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, margin: '0 0 12px' }}>
               Queue subtitle-based clean audio for <strong style={{ color: 'var(--text)' }}>every episode</strong> that has a video file on disk (one encode at a time on the server). Mute strictness: <strong style={{ color: 'var(--text)' }}>Set filters</strong>.
             </p>
             <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
               <Btn variant="ghost" type="button" onClick={() => setShowSkipFiltersModal(true)} style={{ padding: '9px 16px', fontSize: 13, border: '1px solid var(--border)' }}>
                 <Icon name="filters" size={16} />
                 Set filters
               </Btn>
             </div>
             <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45, margin: '0 0 14px' }}>
               Per-episode clean is not offered here — use this section for a full-series pass only.
             </p>
             {!seriesCleanReady ? (
               <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>Loading series clean status…</div>
             ) : seriesClean ? (
               <>
                 <div style={{ marginBottom: 14 }}>
                   <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>Status</div>
                   <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55 }}>
                     <div>
                       {typeof seriesClean.episodeCount === 'number'
                         ? `${seriesClean.episodeCount} episode file${seriesClean.episodeCount !== 1 ? 's' : ''} with media in this series.`
                         : 'Episodes on disk.'}
                       {typeof seriesClean.preparingEpisodeCount === 'number' && seriesClean.preparingEpisodeCount > 0 ? (
                         <> {seriesClean.preparingEpisodeCount} still preparing.</>
                       ) : null}
                       {typeof seriesClean.failedEpisodeCount === 'number' && seriesClean.failedEpisodeCount > 0 ? (
                         <span style={{ color: '#f85149' }}> {seriesClean.failedEpisodeCount} failed.</span>
                       ) : null}
                     </div>
                     {seriesClean.cleanJobStatus === 'failed' && seriesClean.cleanJobError ? (
                       <div style={{ marginTop: 8, fontSize: 12, color: '#f85149', lineHeight: 1.45 }}>{seriesClean.cleanJobError}</div>
                     ) : null}
                   </div>
                 </div>
                 {seriesClean.cleanJobStatus === 'preparing' ? (
                   <span style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 600 }}>Clean playback pipeline running…</span>
                 ) : (
                   <Btn variant="primary" type="button" onClick={requestCleanSeries} disabled={cleanRequesting || !seriesClean.episodeCount}>
                     <Icon name="movie" size={16} />
                     {cleanRequesting
                       ? 'Sending…'
                       : seriesClean.failedEpisodeCount > 0
                         ? 'Retry clean for series'
                         : 'Request clean playback for series'}
                   </Btn>
                 )}
               </>
             ) : (
               <div style={{ fontSize: 13, color: 'var(--text3)' }}>Could not load series clean status.</div>
             )}
           </div>
         ) : null}
         {merged.director && <div style={{ marginTop: 10, fontSize: 13 }}><span style={{ color: 'var(--text2)' }}>Director: </span><b>{merged.director}</b></div>}
         {albumsBusy ? <div style={{ marginTop: 20, fontSize: 12, color: 'var(--text3)' }}>Loading albums…</div> : null}
         {albums.length > 0 && (
           <div style={{ marginTop: 20 }}>
             <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 12 }}>{item.type === 'musicartist' ? 'Albums in your library' : 'Albums — tap + to request'}</div>
             <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '10px 6px', justifyItems: 'center', alignItems: 'start' }}>{albums.map((a, i) =><AlbumCard key={a.id || i} album={a} artistName={item.title} onRequest={onRequest} onOpen={item.type === 'musicartist' && item.source === 'jellyfin' ? onOpenAlbum : undefined} />)}</div>
           </div>
         )}
         {Array.isArray(item.filmography) && item.filmography.length > 0 ? (
           <div style={{ marginTop: 20 }}>
             <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 12 }}>
               {item.filmographyKind === 'library' ? 'In your library' : 'Movies & TV — request to add'}
             </div>
             <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
               {item.filmography.map((row) => {
                 const rk =
                   item.filmographyKind === 'library'
                     ? `${row.source}-${row.type}-${row.id}`
                     : `${row.type}-${row.tmdbId}`;
                 if (item.filmographyKind === 'library') {
                   return (
                     <button
                       type="button"
                       key={rk}
                       onClick={() => onPickLibraryCredit?.(row)}
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 12,
                         padding: '8px 10px',
                         background: 'var(--surface)',
                         borderRadius: 8,
                         border: '1px solid var(--border)',
                         cursor: 'pointer',
                         color: 'inherit',
                         textAlign: 'left',
                         width: '100%',
                       }}
                     >
                       {row.poster ? <img src={row.poster} alt="" style={{ width: 36, height: 54, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} /> : <div style={{ width: 36, height: 54, background: 'var(--bg3)', borderRadius: 4, flexShrink: 0 }} aria-hidden />}
                       <div style={{ flex: 1, minWidth: 0 }}>
                         <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title}</div>
                         <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                           {row.year || '—'}
                           {row.type === 'show' ? ' · TV' : row.type === 'movie' ? ' · Movie' : ' · Audiobook'}
                         </div>
                       </div>
                       <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}>View</span>
                     </button>
                   );
                 }
                 return (
                   <div key={rk} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                     {row.poster ? <img src={row.poster} alt="" style={{ width: 36, height: 54, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} /> : <div style={{ width: 36, height: 54, background: 'var(--bg3)', borderRadius: 4, flexShrink: 0 }} aria-hidden />}
                     <div style={{ flex: 1, minWidth: 0 }}>
                       <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title}</div>
                       <div style={{ fontSize: 11, color: 'var(--text3)' }}>{row.year || '—'}{row.type === 'show' ? ' · TV' : ' · Movie'}</div>
                     </div>
                     <Btn variant="primary" type="button" disabled={filmRowKey === rk} onClick={() => requestFilmographyRow(row)} style={{ flexShrink: 0, padding: '6px 12px', fontSize: 12 }}>
                       {filmRowKey === rk ? '…' : 'Request'}
                     </Btn>
                   </div>
                 );
               })}
             </div>
           </div>
         ) : null}
         {cast.length > 0 && (
           <div style={{ marginTop: 20 }}>
             <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 12 }}>Cast</div>
             <div className="media-row" style={{ gap: 12 }}>
               {cast.map((c, i) => (
                 <div key={i} style={{ flexShrink: 0, width: 76, textAlign: 'center' }}>
                   {(c.photo || c.thumb) ? <img src={c.photo || c.thumb} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 5px' }} onError={e => e.target.style.display = 'none'} /> : <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--bg3)', margin: '0 auto 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>👤</div>}
                   <div style={{ fontSize: 10, fontWeight: 600 }}>{c.name}</div>
                   <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 1 }}>{c.character || c.role}</div>
                 </div>
               ))}
             </div>
           </div>
         )}
         {isJfMovieDetail ? (
           <div
             id="zflix-detail-radarr-report"
             ref={radarrReportSectionRef}
             style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--border)' }}
           >
             <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>Bad quality or bad stream</div>
             {item.tmdbId ? (
               <>
                 <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.55, margin: '0 0 10px' }}>
                   Report to Radarr: mark grab as failed when possible, delete this file, search for a replacement.
                 </p>
                 <Btn
                   variant="ghost"
                   type="button"
                   onClick={() => setRadarrReportConfirmOpen(true)}
                   disabled={reportingBad}
                   style={{ border: '1px solid rgba(248, 81, 73, 0.35)', color: '#f85149', background: 'rgba(248, 81, 73, 0.06)' }}
                 >
                   {reportingBad ? 'Working…' : 'Report to Radarr'}
                 </Btn>
               </>
             ) : (
               <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.55, margin: 0 }}>
                 Link TMDB metadata on this Jellyfin movie so Radarr can match it — then this button enables.
               </p>
             )}
           </div>
         ) : null}
       </div>
       {radarrReportConfirmOpen ? (
         <div
           role="presentation"
           style={{
             position: 'fixed',
             inset: 0,
             zIndex: 1200,
             background: 'rgba(0,0,0,0.72)',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             padding: 20,
           }}
           onClick={() => !reportingBad && setRadarrReportConfirmOpen(false)}
         >
           <div
             role="alertdialog"
             aria-labelledby="zflix-radarr-report-title"
             aria-describedby="zflix-radarr-report-desc"
             style={{
               background: 'var(--bg2)',
               border: '1px solid var(--border)',
               borderRadius: 12,
               padding: '22px 24px',
               maxWidth: 440,
               width: '100%',
               boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
             }}
             onClick={(e) => e.stopPropagation()}
           >
             <h3 id="zflix-radarr-report-title" style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>
               Are you sure you want to delete this movie?
             </h3>
             <p id="zflix-radarr-report-desc" style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55, marginBottom: 20 }}>
               This file will be deleted for good and Radarr will look for a new copy. The movie won’t play until a replacement downloads, which can take a while. If no other copy can be found, it stays missing.
             </p>
             <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
               <button
                 type="button"
                 autoFocus
                 disabled={reportingBad}
                 onClick={() => setRadarrReportConfirmOpen(false)}
                 style={{
                   padding: '8px 16px',
                   borderRadius: 8,
                   border: '1px solid var(--border)',
                   background: 'transparent',
                   color: 'var(--text)',
                   cursor: reportingBad ? 'default' : 'pointer',
                   fontSize: 13,
                   fontWeight: 600,
                   opacity: reportingBad ? 0.5 : 1,
                 }}
               >
                 Cancel
               </button>
               <button
                 type="button"
                 disabled={reportingBad}
                 onClick={() => submitRadarrBadMovieReport()}
                 style={{
                   padding: '8px 16px',
                   borderRadius: 8,
                   border: 'none',
                   background: '#f85149',
                   color: '#fff',
                   cursor: reportingBad ? 'default' : 'pointer',
                   fontSize: 13,
                   fontWeight: 600,
                   opacity: reportingBad ? 0.6 : 1,
                 }}
               >
                 {reportingBad ? 'Working…' : 'Yes, delete and replace'}
               </button>
             </div>
           </div>
         </div>
       ) : null}
     </div>
   </div>
   {showSkipFiltersModal && isJfMovieDetail ? (
     <MovieVisualSkipFiltersModal
       variant="movie"
       itemId={item.id}
       title={merged.title || merged.name || 'This movie'}
       onClose={() => setShowSkipFiltersModal(false)}
       onNotify={onRequest}
       onSceneSkipQueued={refreshVisualSkipStatus}
     />
   ) : null}
   {showSkipFiltersModal && isJfSeriesDetail ? (
     <MovieVisualSkipFiltersModal
       variant="series"
       itemId={item.id}
       title={merged.title || merged.name || 'This series'}
       onClose={() => setShowSkipFiltersModal(false)}
       onNotify={onRequest}
     />
   ) : null}
   </Fragment>
 );
}

const HEADER_SEARCH_SECTION_SCOPED = new Set(['movies', 'shows', 'music', 'audiobooks', 'podcasts']);

/** One Live TV or Recordings line in the main search results. */
function SearchLine({ when, title, sub, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 12px', marginBottom: 6, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', textAlign: 'left', cursor: 'pointer' }}>
      <span style={{ flex: 'none', width: 110, fontSize: 12, color: 'var(--text2)' }}>{when}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        {sub ? <span style={{ display: 'block', fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span> : null}
      </span>
    </button>
  );
}

const searchDay = (t) => {
  const d = new Date(t);
  const days = Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  const day = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${day} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

function GlobalSearchView({ query, onItemClick, onOpenLive, onOpenRecordings }) {
  const [liveHits, setLiveHits] = useState([]);
  const [recHits, setRecHits] = useState([]);
  const [movies, setMovies] = useState([]);
  const [shows, setShows] = useState([]);
  const [music, setMusic] = useState([]);
  const [audiobooks, setAudiobooks] = useState([]);
  const [creatorActors, setCreatorActors] = useState([]);
  const [creatorArtists, setCreatorArtists] = useState([]);
  const [creatorAuthors, setCreatorAuthors] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchTimer = useRef();

  useEffect(() => {
    if (!query) {
      setLiveHits([]);
      setRecHits([]);
      setMovies([]);
      setShows([]);
      setMusic([]);
      setAudiobooks([]);
      setCreatorActors([]);
      setCreatorArtists([]);
      setCreatorAuthors([]);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      const q = encodeURIComponent(query);
      const empty = { items: [] };
      const emptyCreators = { actors: [], artists: [], authors: [] };
      const [live, dvr, m, s, mu, ab, cr] = await Promise.all([
        fetch(`/api/livetv/search?q=${q}`).then((r) => (r.ok ? r.json() : {})).catch(() => ({})),
        fetch('/api/dvr').then((r) => (r.ok ? r.json() : {})).catch(() => ({})),
        fetch(`/api/movies?limit=12&start=0&sortBy=SortName&search=${q}`).then((r) => r.json()).catch(() => empty),
        fetch(`/api/shows?limit=12&start=0&search=${q}`).then((r) => r.json()).catch(() => empty),
        fetch(`/api/music?limit=12&start=0&search=${q}`).then((r) => r.json()).catch(() => empty),
        fetch(`/api/audiobooks/search?q=${q}`).then((r) => (r.ok ? r.json() : empty)).catch(() => empty),
        fetch(`/api/library/creators-search?q=${q}`).then((r) => (r.ok ? r.json() : emptyCreators)).catch(() => emptyCreators),
      ]);
      const words = query.toLowerCase().split(/\s+/).filter(Boolean);
      const hit = (r) => words.every((w) => `${r.title || ''} ${r.episodeTitle || ''} ${r.channelName || ''}`.toLowerCase().includes(w));
      setLiveHits([...(live.programs || []), ...(live.games || []).map((g) => ({ ...g, channelName: g.channelName || g.network }))].slice(0, 5));
      setRecHits([...(dvr.recordings || []), ...(dvr.scheduled || [])].filter(hit).slice(0, 5));
      setMovies(m.items || []);
      setShows(s.items || []);
      setMusic(mu.items || []);
      setAudiobooks(ab.items || []);
      setCreatorActors(Array.isArray(cr.actors) ? cr.actors : []);
      setCreatorArtists(Array.isArray(cr.artists) ? cr.artists : []);
      setCreatorAuthors(Array.isArray(cr.authors) ? cr.authors : []);
      setLoading(false);
    }, 320);
  }, [query]);

  const hasAnything =
    liveHits.length +
      recHits.length +
      movies.length +
      shows.length +
      music.length +
      audiobooks.length +
      creatorActors.length +
      creatorArtists.length +
      creatorAuthors.length >
    0;

  if (!query) return <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}><div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>Start typing to search your library</div>;
  if (loading) return <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Searching...</div>;
  if (!hasAnything) return <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}><div style={{ fontSize: 40, marginBottom: 12 }}>😔</div>No results for "{query}"</div>;

  const hasTitles = movies.length + shows.length + music.length + audiobooks.length > 0;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Results for "{query}"</h2>
      <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 20 }}>
        Tap a person or author to see what you have with them. Titles below match names, cast, artists, and contributors in your libraries.
      </p>
      {liveHits.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.2px', margin: '8px 0' }}>Live TV</div>
          {liveHits.map((p) => (
            <SearchLine key={`${p.start}${p.title}`} when={new Date(p.start).getTime() <= Date.now() ? 'On now' : searchDay(p.start)} title={p.title} sub={p.channelName} onClick={() => onOpenLive(query)} />
          ))}
        </>
      )}
      {recHits.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.2px', margin: '8px 0' }}>Recordings</div>
          {recHits.map((r) => (
            <SearchLine key={r.id} when={r.status === 'recording' ? 'Recording now' : r.status ? 'Recorded' : searchDay(r.start)} title={r.title} sub={r.channelName} onClick={onOpenRecordings} />
          ))}
        </>
      )}
      {creatorActors.length > 0 && (
        <MediaRow title={`People — movies & TV (${creatorActors.length})`} items={creatorActors} onItemClick={onItemClick} />
      )}
      {creatorArtists.length > 0 && (
        <MediaRow title={`Music artists (${creatorArtists.length})`} items={creatorArtists} onItemClick={onItemClick} />
      )}
      {creatorAuthors.length > 0 && (
        <MediaRow title={`Audiobook authors (${creatorAuthors.length})`} items={creatorAuthors} onItemClick={onItemClick} />
      )}
      {hasTitles && (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.2px', margin: '8px 0 8px' }}>Titles</div>
      )}
      {movies.length > 0 && <MediaRow title={`Movies (${movies.length})`} items={movies} onItemClick={onItemClick} />}
      {shows.length > 0 && <MediaRow title={`TV shows (${shows.length})`} items={shows} onItemClick={onItemClick} />}
      {music.length > 0 && <MediaRow title={`Music (${music.length})`} items={music} onItemClick={onItemClick} />}
      {audiobooks.length > 0 && <MediaRow title={`Audiobooks (${audiobooks.length})`} items={audiobooks} onItemClick={onItemClick} />}
    </div>
  );
}

function HomeView({ onItemClick, onPlay, activeLiveTv }) {
  const tvDense = useZflixTvApkDensity();
  const myList = useMyList();
  const homeSnap = useMemo(() => (typeof window !== 'undefined' ? readHomeShelfCache() : null), []);
  const [hero, setHero] = useState(homeSnap?.hero ?? null);
  /** Trailer hero (Zach, 2026-11-04): the featured title's trailer plays muted at the top of Home. */
  const heroTrailer = useTrailer(hero && (hero.type === 'movie' || hero.type === 'show') ? hero : null);
  const [movies, setMovies] = useState(homeSnap?.movies ?? []);
  const [shows, setShows] = useState(homeSnap?.shows ?? []);
  const [music, setMusic] = useState(homeSnap?.music ?? []);
  const [audiobooks, setAudiobooks] = useState(homeSnap?.audiobooks ?? []);
  const [favoriteTv, setFavoriteTv] = useState(homeSnap?.favoriteTv ?? []);
  const [resume, setResume] = useState(homeSnap?.resume ?? []);
  const [resumeLoading, setResumeLoading] = useState(!(homeSnap?.resume?.length));
  const [loading, setLoading] = useState(() => !homeShelfHasContent(homeSnap));
  const [loadingTvPlayId, setLoadingTvPlayId] = useState(null);
  /** Per-profile suggestions from Jellyfin (based on what this profile has watched). */
  const [recommended, setRecommended] = useState([]);
  /** "Because you watched ..." rows from the server (similar titles to this profile's last two finished). */
  const [because, setBecause] = useState([]);
  useEffect(() => {
    let dead = false;
    fetch('/api/home/because', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => !dead && setBecause(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, []);
  const resumeRefreshNonce = useZflixResumeRefresh();
  const resumeRecordings = useRef((homeSnap?.resume ?? []).filter((x) => x.type === 'recording'));

  useEffect(() => {
    let cancelled = false;
    fetch('/api/recommendations')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        if (!cancelled && Array.isArray(list)) setRecommended(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let again = null;
    const ask = (url) => fetch(`${url}?_=${Date.now()}`).then((r) => {
      if (!r.ok) throw new Error(url);
      return r.json();
    });
    /** Movies, shows and recordings together, most recently watched first. */
    const show = (rows) => {
      const merged = rows.sort((a, b) => (b.lastActivity || 0) - (a.lastActivity || 0)).slice(0, 18);
      setResume(merged);
      const snap = readHomeShelfCache();
      if (snap) writeHomeShelfCache({ ...snap, resume: merged });
    };
    const load = (tries) => {
      const recordings = ask('/api/dvr/continue').catch(() => null);
      Promise.all([ask('/api/movies/resume'), ask('/api/shows/resume')])
        .then(([rm, re]) => {
          if (cancelled) return null;
          const videos = [...(Array.isArray(rm) ? rm : []), ...(Array.isArray(re) ? re : [])];
          /** Recordings take longest to look up: the row does not wait for them, and keeps the ones it had until they arrive. */
          show([...videos, ...resumeRecordings.current]);
          setResumeLoading(false);
          return recordings.then((rec) => {
            if (cancelled || !Array.isArray(rec)) return;
            resumeRecordings.current = rec;
            show([...videos, ...rec]);
          });
        })
        /** A failed answer is not an empty list: the row stays as it is and asks again. */
        .catch(() => {
          if (cancelled) return;
          if (tries < 8) again = setTimeout(() => load(tries + 1), 4000);
          else setResumeLoading(false);
        });
    };
    load(0);
    return () => {
      cancelled = true;
      clearTimeout(again);
    };
  }, [resumeRefreshNonce]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [movieRecent, showRecent, musicRes, tvRaw, absStat, absLibs] = await Promise.all([
          fetch('/api/movies/recent').then((r) => (r.ok ? r.json() : null)).catch(() => null),
          fetch('/api/shows/recent').then((r) => (r.ok ? r.json() : null)).catch(() => null),
          fetchZflixList('/api/music?limit=18&start=0'),
          fetch('/api/livetv').then((r) => (r.ok ? r.json() : null)).catch(() => null),
          fetch('/api/audiobooks/status').then((r) => r.json()).catch(() => ({ configured: false })),
          fetch('/api/audiobooks/libraries').then((r) => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        let absRecent = null;
        const libs = Array.isArray(absLibs) ? absLibs : [];
        if (absStat?.configured && libs[0]?.id) {
          const rr = await fetch(`/api/audiobooks/recent?libraryId=${encodeURIComponent(libs[0].id)}&limit=18`);
          if (rr.ok) absRecent = await rr.json();
        }
        if (cancelled) return;
        const moviesArr = Array.isArray(movieRecent) ? movieRecent : null;
        const showsArr = Array.isArray(showRecent) ? showRecent : null;
        const musicSlice = musicRes ? musicRes.items.slice(0, 18) : null;
        const absSlice = Array.isArray(absRecent) ? absRecent.slice(0, 18) : null;
        /** This profile's channels, most recently watched first (older entries without a timestamp fall back to play count). */
        const favSlice = Array.isArray(tvRaw)
          ? tvRaw
              .filter((c) => (c.lastWatchedAt || 0) > 0 || (c.watchCount || 0) > 0)
              .sort((a, b) => (b.lastWatchedAt || 0) - (a.lastWatchedAt || 0) || (b.watchCount || 0) - (a.watchCount || 0))
              .slice(0, 18)
          : null;
        if (moviesArr) setMovies(moviesArr.slice(0, 18));
        if (showsArr) setShows(showsArr.slice(0, 18));
        if (musicSlice) setMusic(musicSlice);
        if (absSlice) setAudiobooks(absSlice);
        if (favSlice) setFavoriteTv(favSlice);

        let nextHero = null;
        const heroMovies = (moviesArr || []).filter((m) => m.streamUrl).slice(0, 10);
        if (heroMovies.length > 0) {
          nextHero = heroMovies[Math.floor(Math.random() * Math.min(5, heroMovies.length))];
        } else if (showsArr?.length) {
          nextHero = showsArr[Math.floor(Math.random() * Math.min(5, showsArr.length))];
        }
        if (nextHero) setHero(nextHero);
        if (moviesArr || showsArr || musicSlice || absSlice || favSlice) {
          writeHomeShelfCache({
            hero: nextHero,
            movies: (moviesArr || []).slice(0, 18),
            shows: (showsArr || []).slice(0, 18),
            music: musicSlice || [],
            audiobooks: absSlice || [],
            favoriteTv: favSlice || [],
          });
        }
      } catch {
        /* keep stale home shelf cache on network/auth errors */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const playLiveTvChannel = async (ch) => {
    if (!ch?.id || loadingTvPlayId) return;
    setLoadingTvPlayId(ch.id);
    try {
      if (activeLiveTv?.liveStreamId || activeLiveTv?.playSessionId) {
        await fetch('/api/livetv/stop', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            liveStreamId: activeLiveTv.liveStreamId,
            playSessionId: activeLiveTv.playSessionId,
          }),
        });
        await new Promise((r) => setTimeout(r, 550));
      }
      const r = await fetch(`/api/livetv/${ch.id}/play`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.streamUrl) return;
      onPlay({
        ...ch,
        title: ch.name,
        streamUrl: d.streamUrl,
        liveStreamId: d.liveStreamId,
        playSessionId: d.playSessionId,
      });
    } finally {
      setLoadingTvPlayId(null);
    }
  };

  const tileClick = (it) => {
    if (it?.__liveTvChannel) {
      playLiveTvChannel(it.__liveTvChannel);
      return;
    }
    onItemClick(it);
  };

  const liveTvRowItems = favoriteTv.map((ch) => ({
    id: `home-livetv-${ch.id}`,
    title: ch.name,
    name: ch.number != null && String(ch.number).trim() !== '' ? `${ch.number} · ${ch.name}` : ch.name,
    poster: ch.logo,
    source: 'jellyfin',
    type: 'album',
    __liveTvChannel: ch,
  }));

  return (
    <div>
      {hero && (
        <div
          data-zflix-home-hero
          style={{
            position: 'relative',
            /** Clipped square on the TV (a rounded clip turns the trailer black there); RoundCorners below rounds it. */
            borderRadius: isTvApp() ? 0 : 12,
            overflow: 'hidden',
            marginBottom: tvDense ? 14 : 28,
            aspectRatio: tvDense ? '2.1/1' : window.innerWidth < 600 ? '16/9' : '21/9',
            maxHeight: tvDense ? 232 : 440,
          }}
        >
          <img src={hero.backdrop || hero.poster} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {heroTrailer ? (
            <BackdropTrailer
              trailer={heroTrailer}
              radius={12}
              label=""
              poster={hero.backdrop || hero.poster}
              yieldTo="[data-zflix-player], [data-zflix-modal-root], [data-zflix-trailer]"
              onTap={() => onItemClick({ ...hero, forceDetails: true })}
            />
          ) : null}
          <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to right,rgba(10,10,15,0.95) 20%,transparent 60%),linear-gradient(to top,rgba(10,10,15,0.9) 0%,transparent 50%)' }} />
          {isTvApp() ? <RoundCorners r={12} color="var(--bg)" /> : null}
          <div style={{ position: 'absolute', zIndex: 3, bottom: tvDense ? 14 : 20, left: tvDense ? 14 : 20, right: tvDense ? 14 : 20, maxWidth: tvDense ? 420 : 500 }}>
            <h1
              style={{
                fontSize: tvDense ? 'clamp(17px,2.4vw,30px)' : 'clamp(24px,4vw,48px)',
                fontWeight: 800,
                lineHeight: 1.1,
                marginBottom: tvDense ? 4 : 8,
              }}
            >
              {hero.title}
            </h1>
            {hero.overview && (tvDense ? window.innerWidth > 400 : window.innerWidth > 500) && (
              <p
                style={{
                  fontSize: tvDense ? 11 : 13,
                  color: 'var(--text2)',
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: tvDense ? 1 : 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  marginBottom: tvDense ? 8 : 14,
                }}
              >
                {hero.overview}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn
                onClick={() => {
                  if (hero.streamUrl) onPlay(hero);
                  else onItemClick(hero);
                }}
              >
                <Icon name="play" size={16} /> Play
              </Btn>
              <Btn variant="ghost" onClick={() => onItemClick({ ...hero, forceDetails: true })}>
                <Icon name="info" size={16} /> Info
              </Btn>
            </div>
          </div>
        </div>
      )}
      {resume.length > 0 || resumeLoading ? (
        <MediaRow
          title="Continue watching"
          items={resume}
          onItemClick={(it) => (it.type === 'recording' || (it.type === 'episode' && it.streamUrl && !(it.seriesId && (it.continueWatching || it.resumeProgress != null))) ? onPlay(it) : tileClick(it))}
          loading={resumeLoading && resume.length === 0}
          mediaRowKind="continue-watching"
        />
      ) : null}
      {myList.length > 0 ? <MediaRow title="My List" items={myList} onItemClick={tileClick} mediaRowKind="my-list" /> : null}
      {because.map((row) => (
        <MediaRow key={row.title} title={row.title} items={row.items} onItemClick={tileClick} mediaRowKind="because" />
      ))}
      {recommended.length > 0 ? (
        <MediaRow title="Recommended for you" items={recommended} onItemClick={tileClick} mediaRowKind="recommended" />
      ) : null}
      <MediaRow title="Movies" items={movies} onItemClick={tileClick} loading={loading} />
      <MediaRow title="TV shows" items={shows} onItemClick={tileClick} loading={loading} />
      <MediaRow title="Music" items={music} onItemClick={tileClick} loading={loading} />
      <MediaRow title="Audiobooks" items={audiobooks} onItemClick={tileClick} loading={loading} />
      <MediaRow title="Recently watched Live TV channels" items={liveTvRowItems} onItemClick={tileClick} loading={loading} />
      {!loading &&
        !movies.length &&
        !shows.length &&
        !music.length &&
        !audiobooks.length &&
        !liveTvRowItems.length &&
        !hero && (
          <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 48 }}>
            Nothing loaded yet — check Jellyfin / Audiobookshelf and try again.
          </div>
        )}
    </div>
  );
}

function MoviesView({ onItemClick, searchQuery }) {
 const myMovies = useMyList().filter((x) => x.type === 'movie');
 const [sort, setSort] = useState('DateCreated');
 const gridInit = typeof window !== 'undefined' ? readMoviesGridCache('DateCreated', '') : null;
 const [movies, setMovies] = useState(gridInit?.items || []);
 const [total, setTotal] = useState(typeof gridInit?.total === 'number' ? gridInit.total : 0);
 const [loading, setLoading] = useState(!gridInit?.items?.length);
 const [loadingMore, setLoadingMore] = useState(false);
 const browseInit = typeof window !== 'undefined' ? readMoviesBrowseCache() : null;
 const [resumeM, setResumeM] = useState(browseInit?.resumeM || []);
 /** Filled in November and December only; the row hides itself when this is empty. */
 const [christmas, setChristmas] = useState([]);
 useEffect(() => {
   let cancelled = false;
   fetch('/api/movies/christmas')
     .then((r) => (r.ok ? r.json() : []))
     .then((list) => {
       if (!cancelled && Array.isArray(list)) setChristmas(list);
     })
     .catch(() => {});
   return () => {
     cancelled = true;
   };
 }, []);
 const [recentM, setRecentM] = useState(browseInit?.recentM || []);
 const [genreRows, setGenreRows] = useState(browseInit?.genreRows || []);
 const [collectionRows, setCollectionRows] = useState(browseInit?.collectionRows || []);
 const [browseLoading, setBrowseLoading] = useState(
   !(
     browseInit?.resumeM?.length ||
     browseInit?.recentM?.length ||
     browseInit?.genreRows?.length ||
     browseInit?.collectionRows?.length
   ),
 );

 useEffect(() => {
   const q = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
   const cached = readMoviesGridCache(sort, searchQuery);
   if (cached?.items?.length) {
     setMovies(cached.items);
     setTotal(typeof cached.total === 'number' ? cached.total : cached.items.length);
     setLoading(false);
   } else {
     setLoading(true);
     setMovies([]);
     setTotal(0);
   }
   let cancelled = false;
   fetchZflixList(`/api/movies?limit=50&start=0&sortBy=${sort}${q}`)
     .then((data) => {
       if (cancelled || !data) return;
       setMovies(data.items);
       setTotal(data.total);
       writeMoviesGridCache(sort, searchQuery, { items: data.items, total: data.total });
     })
     .catch(() => {})
     .finally(() => {
       if (!cancelled) setLoading(false);
     });
   return () => {
     cancelled = true;
   };
 }, [sort, searchQuery]);

 const resumeRefreshNonce = useZflixResumeRefresh();

 useEffect(() => {
   if (searchQuery) {
     setResumeM([]);
     setRecentM([]);
     setGenreRows([]);
     setCollectionRows([]);
     setBrowseLoading(false);
     return;
   }
   let cancelled = false;
   setBrowseLoading(true);
   (async () => {
     try {
       /** Continue Watching shows as soon as it arrives instead of waiting for every genre row below it. */
       const resumeFirst = fetch(`/api/movies/resume?_=${Date.now()}`).then((r) => (r.ok ? r.json() : null));
       resumeFirst.then((list) => {
         if (!cancelled && Array.isArray(list)) setResumeM(list);
       }).catch(() => {});
       const [resResume, resRecent, resGenres, resColl] = await Promise.all([
         resumeFirst,
         fetch('/api/movies/recent').then((r) => (r.ok ? r.json() : [])),
         fetch('/api/movies/genres').then((r) => (r.ok ? r.json() : [])),
         fetch('/api/movies/collections').then((r) => (r.ok ? r.json() : [])),
       ]);
       const genres = Array.isArray(resGenres) ? resGenres.slice(0, 6) : [];
       const rows = await Promise.all(
         genres.map(async (g) => {
           const q = g.id != null && g.id !== ''
             ? `genreId=${encodeURIComponent(g.id)}`
             : `name=${encodeURIComponent(g.name)}`;
           const rr = await fetch(`/api/movies/by-genre?${q}&limit=18`);
           const items = rr.ok ? await rr.json() : [];
           return { key: String(g.id || g.name), title: g.name, items: Array.isArray(items) ? items : [] };
         }),
       );
       /** A failed answer is not an empty list: what is on screen stays. */
       if (!Array.isArray(resResume)) {
         if (!cancelled) setBrowseLoading(false);
       } else if (!cancelled) {
         const rm = resResume;
         const rc = Array.isArray(resRecent) ? resRecent : [];
         const coll = Array.isArray(resColl) ? resColl : [];
         const gr = rows.filter((row) => row.items?.length);
         setResumeM(rm);
         setRecentM(rc);
         setCollectionRows(coll);
         setGenreRows(gr);
         setBrowseLoading(false);
         writeMoviesBrowseCache({ resumeM: rm, recentM: rc, genreRows: gr, collectionRows: coll });
       }
     } catch {
       if (!cancelled) setBrowseLoading(false);
     }
   })();
   return () => { cancelled = true; };
 }, [searchQuery, resumeRefreshNonce]);

 const loadMore = () => {
   if (loadingMore || movies.length >= total) return;
   setLoadingMore(true);
   const q = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
   fetch(`/api/movies?limit=50&start=${movies.length}&sortBy=${sort}${q}`).then(r => r.json()).then(data => { setMovies(prev => [...prev, ...(data.items || [])]); setLoadingMore(false); }).catch(() => setLoadingMore(false));
 };

 return (
   <div>
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
       <h2 style={{ fontSize: 24, fontWeight: 700 }}>Movies</h2>
       <span style={{ color: 'var(--text2)', fontSize: 12 }}>{total} titles</span>
       <div
         data-zflix-linear-tab-strip
         className="zflix-discover-tab-strip"
         style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}
       >
         {[['DateCreated', 'New'], ['SortName', 'A–Z'], ['CommunityRating', 'Top'], ['ProductionYear', 'Year']].map(([val, label]) => (
           <button key={val} type="button" onClick={() => setSort(val)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: sort === val ? 600 : 400, background: sort === val ? 'var(--accent)' : 'var(--surface)', color: sort === val ? '#fff' : 'var(--text2)', border: 'none', cursor: 'pointer' }}>{label}</button>
         ))}
       </div>
     </div>
     {!searchQuery && (
       <>
         <MediaRow title="Continue watching" items={resumeM} onItemClick={onItemClick} loading={browseLoading && resumeM.length === 0} mediaRowKind="continue-watching" />
         <MediaRow title="My List" items={myMovies} onItemClick={onItemClick} mediaRowKind="my-list" />
         <MediaRow title="Christmas" items={christmas} onItemClick={onItemClick} />
         <MediaRow title="Recently added" items={recentM} onItemClick={onItemClick} loading={browseLoading && recentM.length === 0} />
         {collectionRows.map((row) => (
           <MediaRow key={`coll-${row.key}`} title={row.title} items={row.items} onItemClick={onItemClick} />
         ))}
         {genreRows.map((row) => (
           <MediaRow key={row.key} title={row.title} items={row.items} onItemClick={onItemClick} />
         ))}
       </>
     )}
     <div className="card-grid">
       {loading ? [...Array(20)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} /></div>) : movies.map(m => <MediaCard key={m.id} item={m} onClick={onItemClick} />)}
     </div>
     {loadingMore && movies.length > 0 ? (
       <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
     ) : null}
     {!loading && movies.length > 0 ? (
       <InfiniteScrollSentinel hasMore={movies.length < total} loading={loadingMore} onLoad={loadMore} />
     ) : null}
   </div>
 );
}

function ShowsView({ onItemClick, onPlay, searchQuery }) {
 const myShows = useMyList().filter((x) => x.type !== 'movie');
 const gridInit = typeof window !== 'undefined' ? readShowsGridCache('') : null;
 const [shows, setShows] = useState(gridInit?.items || []);
 const [total, setTotal] = useState(typeof gridInit?.total === 'number' ? gridInit.total : 0);
 const [loading, setLoading] = useState(!gridInit?.items?.length);
 const [loadingMore, setLoadingMore] = useState(false);
 const browseInit = typeof window !== 'undefined' ? readShowsBrowseCache() : null;
 const [resumeE, setResumeE] = useState(browseInit?.resumeE || []);
 const [recentS, setRecentS] = useState(browseInit?.recentS || []);
 const [genreRows, setGenreRows] = useState(browseInit?.genreRows || []);
 const [collectionRows, setCollectionRows] = useState(browseInit?.collectionRows || []);
 const [browseLoading, setBrowseLoading] = useState(
   !(
     browseInit?.resumeE?.length ||
     browseInit?.recentS?.length ||
     browseInit?.genreRows?.length ||
     browseInit?.collectionRows?.length
   ),
 );

 useEffect(() => {
   const q = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
   const cached = readShowsGridCache(searchQuery);
   if (cached?.items?.length) {
     setShows(cached.items);
     setTotal(typeof cached.total === 'number' ? cached.total : cached.items.length);
     setLoading(false);
   } else {
     setLoading(true);
     setShows([]);
     setTotal(0);
   }
   let cancelled = false;
   fetchZflixList(`/api/shows?limit=50&start=0${q}`)
     .then((data) => {
       if (cancelled || !data) return;
       setShows(data.items);
       setTotal(data.total);
       writeShowsGridCache(searchQuery, { items: data.items, total: data.total });
     })
     .catch(() => {})
     .finally(() => {
       if (!cancelled) setLoading(false);
     });
   return () => {
     cancelled = true;
   };
 }, [searchQuery]);

 const resumeRefreshNonce = useZflixResumeRefresh();

 useEffect(() => {
   if (searchQuery) {
     setResumeE([]);
     setRecentS([]);
     setGenreRows([]);
     setCollectionRows([]);
     setBrowseLoading(false);
     return;
   }
   let cancelled = false;
   setBrowseLoading(true);
   (async () => {
     try {
       /** Continue Watching shows as soon as it arrives instead of waiting for every genre row below it. */
       const resumeFirst = fetch(`/api/shows/resume?_=${Date.now()}`).then((r) => (r.ok ? r.json() : null));
       resumeFirst.then((list) => {
         if (!cancelled && Array.isArray(list)) setResumeE(list);
       }).catch(() => {});
       const [resResume, resRecent, resGenres, resColl] = await Promise.all([
         resumeFirst,
         fetch('/api/shows/recent').then((r) => (r.ok ? r.json() : [])),
         fetch('/api/shows/genres').then((r) => (r.ok ? r.json() : [])),
         fetch('/api/shows/collections').then((r) => (r.ok ? r.json() : [])),
       ]);
       const genres = Array.isArray(resGenres) ? resGenres.slice(0, 6) : [];
       const rows = await Promise.all(
         genres.map(async (g) => {
           const qry = g.id != null && g.id !== ''
             ? `genreId=${encodeURIComponent(g.id)}`
             : `name=${encodeURIComponent(g.name)}`;
           const rr = await fetch(`/api/shows/by-genre?${qry}&limit=18`);
           const items = rr.ok ? await rr.json() : [];
           return { key: String(g.id || g.name), title: g.name, items: Array.isArray(items) ? items : [] };
         }),
       );
       /** A failed answer is not an empty list: what is on screen stays. */
       if (!Array.isArray(resResume)) {
         if (!cancelled) setBrowseLoading(false);
       } else if (!cancelled) {
         const re = resResume;
         const rs = Array.isArray(resRecent) ? resRecent : [];
         const coll = Array.isArray(resColl) ? resColl : [];
         const gr = rows.filter((row) => row.items?.length);
         setResumeE(re);
         setRecentS(rs);
         setCollectionRows(coll);
         setGenreRows(gr);
         setBrowseLoading(false);
         writeShowsBrowseCache({ resumeE: re, recentS: rs, genreRows: gr, collectionRows: coll });
       }
     } catch {
       if (!cancelled) setBrowseLoading(false);
     }
   })();
   return () => { cancelled = true; };
 }, [searchQuery, resumeRefreshNonce]);

 const loadMore = () => {
   if (loadingMore || shows.length >= total) return;
   setLoadingMore(true);
   const q = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
   fetch(`/api/shows?limit=50&start=${shows.length}${q}`).then(r => r.json()).then(data => { setShows(prev => [...prev, ...(data.items || [])]); setLoadingMore(false); }).catch(() => setLoadingMore(false));
 };

 return (
   <div>
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
       <h2 style={{ fontSize: 24, fontWeight: 700 }}>TV Shows</h2>
       <span style={{ color: 'var(--text2)', fontSize: 12 }}>{total} series</span>
     </div>
     {!searchQuery && (
       <>
         <MediaRow title="Continue watching" items={resumeE} onItemClick={(item) => (item.seriesId && (item.continueWatching || item.resumeProgress != null) ? onItemClick(item) : onPlay(item))} loading={browseLoading && resumeE.length === 0} mediaRowKind="continue-watching" />
         <MediaRow title="My List" items={myShows} onItemClick={onItemClick} mediaRowKind="my-list" />
         <MediaRow title="Recently added" items={recentS} onItemClick={onItemClick} loading={browseLoading && recentS.length === 0} />
         {collectionRows.map((row) => (
           <MediaRow key={`coll-${row.key}`} title={row.title} items={row.items} onItemClick={onItemClick} />
         ))}
         {genreRows.map((row) => (
           <MediaRow key={row.key} title={row.title} items={row.items} onItemClick={onItemClick} />
         ))}
       </>
     )}
     <div className="card-grid">
       {loading ? [...Array(20)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} /></div>) : shows.map(s => <MediaCard key={s.id} item={s} onClick={onItemClick} />)}
     </div>
     {loadingMore && shows.length > 0 ? (
       <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
     ) : null}
     {!loading && shows.length > 0 ? (
       <InfiniteScrollSentinel hasMore={shows.length < total} loading={loadingMore} onLoad={loadMore} />
     ) : null}
   </div>
 );
}

function MusicView({ onItemClick, searchQuery, onOpenAlbum, onAddToPlaylist, onPlayTrack }) {
 const [tab, setTab] = useState('artists');
 const [artists, setArtists] = useState([]);
 const [artistTotal, setArtistTotal] = useState(0);
 const [albums, setAlbums] = useState([]);
 const [albumTotal, setAlbumTotal] = useState(0);
 const [playlists, setPlaylists] = useState([]);
 const [selectedPlaylist, setSelectedPlaylist] = useState(null);
 const [playlistTracks, setPlaylistTracks] = useState([]);
 const [playlistTracksLoading, setPlaylistTracksLoading] = useState(false);
 const [newPlaylistName, setNewPlaylistName] = useState('');
 const [playlistBusy, setPlaylistBusy] = useState(false);
 const [searchHits, setSearchHits] = useState(null);
 const [selectedArtist, setSelectedArtist] = useState(null);
 const [artistAlbums, setArtistAlbums] = useState([]);
 const [artistAlbumsLoading, setArtistAlbumsLoading] = useState(false);
 const artistAlbumsReqRef = useRef(0);
 const [loading, setLoading] = useState(true);
 const [loadingMore, setLoadingMore] = useState(false);
 /** This profile's recently played tracks ("Continue listening"). */
 const [recentTracks, setRecentTracks] = useState([]);

 useEffect(() => {
   let cancelled = false;
   fetch('/api/profile/music/recent')
     .then((r) => (r.ok ? r.json() : []))
     .then((list) => {
       if (!cancelled && Array.isArray(list)) setRecentTracks(list.filter((t) => t && t.id && t.streamUrl));
     })
     .catch(() => {});
   return () => {
     cancelled = true;
   };
 }, []);

 const q = (searchQuery || '').trim();

 useEffect(() => {
   setSelectedArtist(null);
   setArtistAlbums([]);
   setArtistAlbumsLoading(false);
   artistAlbumsReqRef.current += 1;
   setSelectedPlaylist(null);
   setPlaylistTracks([]);
 }, [q]);

 const loadPlaylists = () => {
   fetch('/api/music/playlists')
     .then((r) => r.json())
     .then((d) => setPlaylists(d.playlists || d.items || []))
     .catch(() => setPlaylists([]));
 };

 const openPlaylist = (pl) => {
   setSelectedPlaylist(pl);
   setPlaylistTracks([]);
   setPlaylistTracksLoading(true);
   fetch(`/api/music/playlists/${encodeURIComponent(pl.id)}/tracks`)
     .then((r) => r.json())
     .then((d) => setPlaylistTracks(d.tracks || []))
     .catch(() => setPlaylistTracks([]))
     .finally(() => setPlaylistTracksLoading(false));
 };

 const createPlaylist = async () => {
   const name = newPlaylistName.trim();
   if (!name || playlistBusy) return;
   setPlaylistBusy(true);
   try {
     const res = await fetch('/api/music/playlists', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ name }),
     });
     const data = await res.json().catch(() => ({}));
     if (!res.ok) throw new Error(data.error || 'Could not create playlist');
     setNewPlaylistName('');
     loadPlaylists();
   } catch (_) { /* ignore */ }
   finally { setPlaylistBusy(false); }
 };

 const deletePlaylist = async (pl) => {
   if (playlistBusy || !window.confirm(`Delete playlist "${pl.name}"?`)) return;
   setPlaylistBusy(true);
   try {
     await fetch(`/api/music/playlists/${encodeURIComponent(pl.id)}`, { method: 'DELETE' });
     if (selectedPlaylist?.id === pl.id) {
       setSelectedPlaylist(null);
       setPlaylistTracks([]);
     }
     loadPlaylists();
   } catch (_) { /* ignore */ }
   finally { setPlaylistBusy(false); }
 };

 const removeFromPlaylist = async (trackId) => {
   if (!selectedPlaylist || playlistBusy) return;
   setPlaylistBusy(true);
   try {
     const res = await fetch(
       `/api/music/playlists/${encodeURIComponent(selectedPlaylist.id)}/tracks/${encodeURIComponent(trackId)}`,
       { method: 'DELETE' },
     );
     if (res.ok) {
       setPlaylistTracks((prev) => prev.filter((t) => t.id !== trackId));
       loadPlaylists();
     }
   } catch (_) { /* ignore */ }
   finally { setPlaylistBusy(false); }
 };

 useEffect(() => {
   if (!q) {
     setSearchHits(null);
     return undefined;
   }
   let cancelled = false;
   setLoading(true);
   const timer = setTimeout(() => {
     fetch(`/api/music/search?q=${encodeURIComponent(q)}`)
       .then((r) => r.json())
       .then((data) => { if (!cancelled) setSearchHits(data); })
       .catch(() => { if (!cancelled) setSearchHits({ artists: [], albums: [], songs: [] }); })
       .finally(() => { if (!cancelled) setLoading(false); });
   }, 280);
   return () => { cancelled = true; clearTimeout(timer); };
 }, [q]);

 useEffect(() => {
   if (q) return undefined;
   let cancelled = false;
   setLoading(true);
   if (tab === 'artists') {
     fetchZflixList('/api/music/artists?limit=80&start=0')
       .then((data) => {
         if (cancelled || !data) return;
         setArtists(data.items || []);
         setArtistTotal(data.total || 0);
       })
       .catch(() => {})
       .finally(() => { if (!cancelled) setLoading(false); });
   } else if (tab === 'albums') {
     const mInit = typeof window !== 'undefined' ? readMusicGridCache('') : null;
     if (mInit?.items?.length) {
       setAlbums(mInit.items);
       setAlbumTotal(mInit.total ?? mInit.items.length);
       setLoading(false);
     }
     fetchZflixList('/api/music?limit=50&start=0')
       .then((data) => {
         if (cancelled || !data) return;
         setAlbums(data.items);
         setAlbumTotal(data.total);
         writeMusicGridCache('', { items: data.items, total: data.total });
       })
       .catch(() => {})
       .finally(() => { if (!cancelled) setLoading(false); });
   } else if (tab === 'playlists') {
     loadPlaylists();
     if (!cancelled) setLoading(false);
   }
   return () => { cancelled = true; };
 }, [tab, q]);

 const openArtist = (artist) => {
   const reqId = ++artistAlbumsReqRef.current;
   setSelectedArtist(artist);
   setArtistAlbums([]);
   setArtistAlbumsLoading(true);
   fetch(`/api/music/artists/${encodeURIComponent(artist.id)}/albums`)
     .then((r) => r.json())
     .then((d) => {
       if (artistAlbumsReqRef.current !== reqId) return;
       const items = d.items || [];
       setArtistAlbums(items);
     })
     .catch(() => {
       if (artistAlbumsReqRef.current !== reqId) return;
       setArtistAlbums([]);
     })
     .finally(() => {
       if (artistAlbumsReqRef.current === reqId) setArtistAlbumsLoading(false);
     });
 };

 const loadMoreAlbums = () => {
   if (loadingMore || albums.length >= albumTotal || q) return;
   setLoadingMore(true);
   fetch(`/api/music?limit=50&start=${albums.length}`)
     .then((r) => r.json())
     .then((data) => { setAlbums((prev) => [...prev, ...(data.items || [])]); setLoadingMore(false); })
     .catch(() => setLoadingMore(false));
 };

 const tabBtn = (id, label) => (
   <button
     type="button"
     onClick={() => setTab(id)}
     style={{
       padding: '8px 16px',
       borderRadius: 20,
       border: '1px solid var(--border)',
       background: tab === id ? 'var(--accent)' : 'var(--bg3)',
       color: tab === id ? '#fff' : 'var(--text2)',
       fontSize: 13,
       fontWeight: 600,
       cursor: 'pointer',
     }}
   >
     {label}
   </button>
 );

 const fmtRuntime = (s) => (s ? `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}` : '');

 if (selectedPlaylist) {
   return (
     <div>
       <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
         <button type="button" onClick={() => { setSelectedPlaylist(null); setPlaylistTracks([]); }} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
           <Icon name="back" size={16} /> Back
         </button>
         <h2 style={{ fontSize: 22, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedPlaylist.name}</h2>
         {playlistTracks.length > 0 && onPlayTrack ? (
           <Btn onClick={() => onPlayTrack(playlistTracks[0], playlistTracks)} style={{ padding: '8px 14px', fontSize: 13 }}><Icon name="play" size={14} /> Play All</Btn>
         ) : null}
         <button type="button" disabled={playlistBusy} onClick={() => deletePlaylist(selectedPlaylist)} style={{ background: 'rgba(248,81,73,0.15)', border: '1px solid rgba(248,81,73,0.4)', borderRadius: 8, padding: '8px 12px', color: '#f85149', cursor: playlistBusy ? 'default' : 'pointer', fontSize: 12 }}>Delete</button>
       </div>
       {playlistTracksLoading ? (
         <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Loading tracks…</div>
       ) : playlistTracks.length === 0 ? (
         <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>No tracks yet — add songs from albums or search.</div>
       ) : (
         <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
           {playlistTracks.map((t, i) => (
             <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderRadius: 8 }}>
               <button type="button" onClick={() => onPlayTrack?.(t, playlistTracks)} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, minWidth: 0, textAlign: 'left' }}>
                 <span style={{ fontSize: 13, color: 'var(--text3)', width: 24, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                 <span style={{ flex: 1, minWidth: 0 }}>
                   <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                   <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t.artist}{t.album ? ` · ${t.album}` : ''}</div>
                 </span>
                 {t.runtime ? <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>{fmtRuntime(t.runtime)}</span> : null}
                 <div style={{ background: 'var(--accent)', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="play" size={14} /></div>
               </button>
               <button type="button" aria-label={`Remove ${t.name}`} disabled={playlistBusy} onClick={() => removeFromPlaylist(t.id)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: playlistBusy ? 'default' : 'pointer', color: 'var(--text2)', flexShrink: 0 }}>
                 <Icon name="close" size={16} />
               </button>
             </div>
           ))}
         </div>
       )}
     </div>
   );
 }

 if (selectedArtist) {
   const artistAlbumCount = artistAlbums.length;
   return (
     <div>
       <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
         <button type="button" onClick={() => { artistAlbumsReqRef.current += 1; setSelectedArtist(null); setArtistAlbums([]); setArtistAlbumsLoading(false); }} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
           <Icon name="back" size={16} /> Back
         </button>
         <h2 style={{ fontSize: 22, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedArtist.title}</h2>
         {!artistAlbumsLoading ? (
           <span style={{ color: 'var(--text2)', fontSize: 12 }}>{artistAlbumCount} album{artistAlbumCount === 1 ? '' : 's'}</span>
         ) : null}
       </div>
       <div className="card-grid">
         {artistAlbumsLoading ? (
           [...Array(12)].map((_, i) => (
             <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}>
               <div style={{ aspectRatio: '1/1', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} />
             </div>
           ))
         ) : artistAlbumCount === 0 ? (
           <div style={{ color: 'var(--text2)', gridColumn: '1 / -1', textAlign: 'center', padding: 40 }}>No albums found</div>
         ) : (
           artistAlbums.map((a) => <MediaCard key={a.id} item={a} onClick={onOpenAlbum || onItemClick} />)
         )}
       </div>
     </div>
   );
 }

 if (q) {
   if (!searchHits) {
     return (
       <div>
         <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Results for &ldquo;{q}&rdquo;</h2>
         <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Searching…</div>
       </div>
     );
   }
   const empty = !searchHits.artists?.length && !searchHits.albums?.length && !searchHits.songs?.length;
   return (
     <div>
       <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Results for &ldquo;{q}&rdquo;</h2>
       {empty ? (
         <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}>No music matches</div>
       ) : (
         <>
           {searchHits.artists?.length > 0 && (
             <>
               <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 10 }}>Artists</div>
               <div className="card-grid card-grid--music-artists" style={{ marginBottom: 20 }}>{searchHits.artists.map((a) => <MediaCard key={a.id} item={a} onClick={() => openArtist(a)} />)}</div>
             </>
           )}
           {searchHits.albums?.length > 0 && (
             <>
               <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 10 }}>Albums</div>
               <div className="card-grid" style={{ marginBottom: 20 }}>{searchHits.albums.map((a) => <MediaCard key={a.id} item={a} onClick={onOpenAlbum || onItemClick} />)}</div>
             </>
           )}
           {searchHits.songs?.length > 0 && (
             <>
               <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 10 }}>Songs</div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                 {searchHits.songs.map((s) => (
                   <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                     <button type="button" onClick={() => onItemClick(s)} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}>
                       {s.poster ? <img src={s.poster} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} /> : <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--bg3)' }} />}
                       <div style={{ flex: 1, minWidth: 0 }}>
                         <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title || s.name}</div>
                         <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.artist}{s.album ? ` · ${s.album}` : ''}</div>
                       </div>
                       <Icon name="play" size={18} />
                     </button>
                     {onAddToPlaylist ? (
                       <button type="button" aria-label={`Add ${s.title || s.name} to playlist`} onClick={() => onAddToPlaylist([s.id], s.title || s.name)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text)', flexShrink: 0 }}>
                         <Icon name="plus" size={16} />
                       </button>
                     ) : null}
                   </div>
                 ))}
               </div>
             </>
           )}
         </>
       )}
     </div>
   );
 }

 return (
   <div>
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
       <h2 style={{ fontSize: 24, fontWeight: 700, marginRight: 'auto' }}>Music</h2>
       {tabBtn('artists', 'Artists')}
       {tabBtn('albums', 'Albums')}
       {tabBtn('playlists', 'Playlists')}
       <span style={{ color: 'var(--text2)', fontSize: 12, width: '100%' }}>
         {tab === 'artists' ? `${artistTotal} artists` : tab === 'albums' ? `${albumTotal} albums` : `${playlists.length} playlists`}
       </span>
     </div>
     {recentTracks.length > 0 && onPlayTrack ? (
       <MediaRow
         title="Continue listening"
         mediaRowKind="music-recent"
         items={recentTracks.map((t) => ({ ...t, type: 'album', subtitle: t.artist || t.album || '' }))}
         onItemClick={(it) => {
           const track = recentTracks.find((t) => t.id === it.id);
           if (track) onPlayTrack(track, recentTracks);
         }}
       />
     ) : null}
     {tab === 'artists' ? (
       <div className="card-grid card-grid--music-artists">
         {loading ? [...Array(20)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '1/1', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: '50%' }} /></div>) : artists.map((a) => (
           <MediaCard key={a.id} item={{ ...a, subtitle: a.albumCount ? `${a.albumCount} albums` : undefined }} onClick={() => openArtist(a)} />
         ))}
       </div>
     ) : tab === 'albums' ? (
       <>
         <div className="card-grid">
           {loading ? [...Array(20)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '1/1', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} /></div>) : albums.map((a) => <MediaCard key={a.id} item={a} onClick={onOpenAlbum || onItemClick} />)}
         </div>
         {loadingMore && albums.length > 0 ? <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading…</div> : null}
         {!loading && albums.length > 0 ? <InfiniteScrollSentinel hasMore={albums.length < albumTotal} loading={loadingMore} onLoad={loadMoreAlbums} /> : null}
       </>
     ) : (
       <div>
         <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
           <input value={newPlaylistName} onChange={(e) => setNewPlaylistName(e.target.value)} placeholder="New playlist name" style={{ flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 14 }} onKeyDown={(e) => { if (e.key === 'Enter') createPlaylist(); }} />
           <Btn onClick={createPlaylist} disabled={playlistBusy || !newPlaylistName.trim()} style={{ padding: '10px 14px', fontSize: 13, flexShrink: 0 }}>Create</Btn>
         </div>
         {playlists.length === 0 ? (
           <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 48 }}>No playlists yet — create one above or add tracks from an album.</div>
         ) : (
           <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
             {playlists.map((p) => (
               <button key={p.id} type="button" onClick={() => openPlaylist(p)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 12px', border: 'none', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', borderRadius: 10, textAlign: 'left' }}>
                 <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="music" size={22} /></div>
                 <div style={{ flex: 1, minWidth: 0 }}>
                   <div style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                   <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{p.trackCount ?? p.itemCount ?? 0} tracks</div>
                 </div>
                 <Icon name="next" size={18} />
               </button>
             ))}
           </div>
         )}
       </div>
     )}
   </div>
 );
}
/** Match backend sort: most-watched first, then channel number, then name. */
function compareLiveTvChannels(a, b) {
  const wc = (b.watchCount || 0) - (a.watchCount || 0);
  if (wc !== 0) return wc;
  const an = String(a.number ?? '').padStart(8, '0');
  const bn = String(b.number ?? '').padStart(8, '0');
  if (an !== bn) return an.localeCompare(bn);
  return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
}

 function LiveTVView({ onPlay, activeLiveTv }) {
 const [channels, setChannels] = useState([]);
 const [guide, setGuide] = useState([]);
 const [loading, setLoading] = useState(true);
 const [view, setView] = useState('grid');
 const [search, setSearch] = useState('');
 const [categoryFilter, setCategoryFilter] = useState('all');
 const [loadingChannel, setLoadingChannel] = useState(null);
 const [playErr, setPlayErr] = useState('');

 useEffect(() => {
   Promise.all([fetch('/api/livetv').then(r => r.json()), fetch('/api/livetv/guide').then(r => r.json())])
     .then(([ch, g]) => { setChannels(Array.isArray(ch) ? ch : []); setGuide(Array.isArray(g) ? g : []); setLoading(false); })
     .catch(() => setLoading(false));
 }, []);

 const guideByChannel = useMemo(() => {
   const acc = {};
   for (const p of guide) {
     const cid = p.channelId;
     if (!cid) continue;
     if (!acc[cid]) acc[cid] = [];
     acc[cid].push(p);
   }
   Object.keys(acc).forEach((k) => {
     acc[k].sort((a, b) => new Date(a.start) - new Date(b.start));
   });
   return acc;
 }, [guide]);

 const filtered = useMemo(() => {
   const q = search.trim().toLowerCase();
   return channels.filter((c) => {
     if (q && !String(c.name || '').toLowerCase().includes(q)) return false;
     const hay = buildLiveTvHaystack(c, guideByChannel[c.id]);
     return matchesLiveTvCategory(categoryFilter, hay);
   });
 }, [channels, guideByChannel, search, categoryFilter]);

 useEffect(() => {
   if (view !== 'guide') return;
   fetch('/api/livetv/guide')
     .then((r) => r.json())
     .then((g) => { if (Array.isArray(g)) setGuide(g); })
     .catch(() => {});
 }, [view]);

 useEffect(() => {
   setPlayErr('');
 }, [view]);
 const now = new Date();
 const fmt = (d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

 const playChannel = async (ch) => {
   if (loadingChannel) return;
   setLoadingChannel(ch.id);
   setPlayErr('');
   try {
     if (activeLiveTv?.liveStreamId || activeLiveTv?.playSessionId) {
       await fetch('/api/livetv/stop', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({
           liveStreamId: activeLiveTv.liveStreamId,
           playSessionId: activeLiveTv.playSessionId,
         }),
       });
       await new Promise((r) => setTimeout(r, 550));
     }
     const r = await fetch(`/api/livetv/${ch.id}/play`);
     const d = await r.json().catch(() => ({}));
     if (!r.ok || d.error) {
       const msg =
         typeof d.error === 'string'
           ? d.error
           : typeof d.error === 'object' && d.error?.message
             ? d.error.message
             : typeof d.message === 'string'
               ? d.message
               : 'Could not start channel.';
       setPlayErr(msg.length > 120 ? 'Could not start channel.' : msg);
       return;
     }
     if (!d.streamUrl) {
       setPlayErr('Could not start channel.');
       return;
     }
     setChannels((prev) =>
       [...prev.map((c) => (c.id === ch.id ? { ...c, watchCount: (c.watchCount || 0) + 1 } : c))].sort(compareLiveTvChannels),
     );
     onPlay({
       ...ch,
       title: ch.name,
       streamUrl: d.streamUrl,
       liveStreamId: d.liveStreamId,
       playSessionId: d.playSessionId,
     });
   } catch {
     setPlayErr('Could not start channel.');
   }
   finally { setLoadingChannel(null); }
 };

 return (
   <div data-zflix-livetv-root="true" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
     <div
       data-zflix-spatial-toolbar="true"
       style={{
         flexShrink: 0,
         background: 'var(--bg)',
         paddingBottom: 12,
         marginBottom: 0,
         borderBottom: '1px solid var(--border)',
       }}
     >
       <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
         <h2 style={{ fontSize: 24, fontWeight: 700 }}>Live TV</h2>
         <span style={{ background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>LIVE</span>
         <div data-zflix-linear-tab-strip style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
           {['grid', 'guide'].map(v => <button key={v} type="button" onClick={() => setView(v)} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, background: view === v ? 'var(--accent)' : 'var(--surface)', color: view === v ? '#fff' : 'var(--text2)', border: 'none', cursor: 'pointer', textTransform: 'capitalize', fontWeight: view === v ? 600 : 400 }}>{v}</button>)}
         </div>
         <div data-zflix-horizontal-controls style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px' }}>
           <Icon name="search" size={14} />
           <input style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 13, width: 130 }} placeholder="Filter..." value={search} onChange={e => setSearch(e.target.value)} />
         </div>
       </div>
       <div data-zflix-livetv-category-strip data-zflix-linear-tab-strip style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 10 }}>
         <span style={{ fontSize: 11, color: 'var(--text3)', marginRight: 4 }}>Type:</span>
         {LIVE_TV_CATEGORIES.map((cat) => (
           <button
             key={cat.id}
             type="button"
             aria-current={categoryFilter === cat.id ? 'page' : undefined}
             onClick={() => setCategoryFilter(cat.id)}
             style={{
               padding: '5px 11px',
               borderRadius: 6,
               fontSize: 11,
               fontWeight: categoryFilter === cat.id ? 600 : 400,
               background: categoryFilter === cat.id ? 'var(--accent)' : 'var(--surface)',
               color: categoryFilter === cat.id ? '#fff' : 'var(--text2)',
               border: '1px solid var(--border)',
               cursor: 'pointer',
             }}
           >
             {cat.label}
           </button>
         ))}
       </div>
       {loadingChannel && <div style={{ marginTop: 8, background: 'rgba(229,9,20,0.1)', border: '1px solid var(--accent)', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>⏳ Loading channel stream...</div>}
       {playErr && (
         <div role="alert" style={{ marginTop: 8, fontSize: 13, color: 'var(--text2)' }}>
           {playErr}
         </div>
       )}
     </div>

     <div data-zflix-livetv-scroll style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch', paddingTop: 12 }}>
     {!loading && filtered.length === 0 ? (
       <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>
         No channels match this type or name filter. Try <strong style={{ color: 'var(--text2)' }}>All</strong> or clear the search box.
       </div>
     ) : null}
     {view === 'grid' ? (
       <div className="channel-grid">
         {loading ? [...Array(24)].map((_, i) => <div key={i} style={{ height: 110, background: 'var(--surface)', borderRadius: 8, animation: 'pulse 1.5s infinite' }} />) :
           filtered.map(ch => {
             const programs = guideByChannel[ch.id] || [];
             const current = programs.find(p => new Date(p.start) <= now && new Date(p.end) >= now);
             const isLoading = loadingChannel === ch.id;
             return (
               <div key={ch.id} className="livetv-channel-tile" role="button" tabIndex={0} aria-label={`Play ${ch.name}`}
                 onClick={() => playChannel(ch)}
                 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); playChannel(ch); } }}
                 style={{ background: isLoading ? 'rgba(229,9,20,0.15)' : 'var(--surface)', borderRadius: 8, padding: 10, cursor: 'pointer', border: `1px solid ${isLoading ? 'var(--accent)' : 'var(--border)'}`, transition: 'all 0.15s' }}
                 onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'scale(1.04)'; }}
                 onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'scale(1)'; } }}>
                 {ch.logo ? <img src={ch.logo} alt="" style={{ width: 48, height: 48, objectFit: 'contain', margin: '0 auto 6px', borderRadius: 6, display: 'block' }} onError={e => e.target.style.display = 'none'} /> : <div style={{ width: 48, height: 48, margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📺</div>}
                 <div style={{ fontSize: 10, fontWeight: 600, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.name}</div>
                 {isLoading && <div style={{ fontSize: 9, color: 'var(--accent)', textAlign: 'center', marginTop: 2 }}>Loading...</div>}
                 {!isLoading && current && <div style={{ fontSize: 9, color: 'var(--text3)', textAlign: 'center', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.title}</div>}
               </div>
             );
           })}
       </div>
     ) : (
       <div style={{ overflowX: 'auto' }}>
         {filtered.map(ch => {
           const programs = (guideByChannel[ch.id] || []).slice(0, 4);
           const isLoading = loadingChannel === ch.id;
           return (
             <div
               key={ch.id}
               className="livetv-guide-row"
               role="button"
               tabIndex={0}
               aria-label={`Channel ${ch.name}. Press Enter or Space to play.`}
               onClick={() => {
                 if (!loadingChannel) playChannel(ch);
               }}
               onKeyDown={(ev) => {
                 if (ev.key === 'Enter' || ev.key === ' ') {
                   ev.preventDefault();
                   playChannel(ch);
                 }
               }}
               style={{ display: 'flex', gap: 0, marginBottom: 2, minWidth: 700, cursor: loadingChannel ? 'default' : 'pointer' }}
             >
               <div style={{ width: 200, flexShrink: 0, background: 'var(--surface2)', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid var(--border)' }}>
                 {ch.logo ? <img src={ch.logo} alt="" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} onError={e => e.target.style.display = 'none'} /> : <span style={{ fontSize: 16 }}>📺</span>}
                 <span style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{ch.name}</span>
                 <span aria-hidden style={{ flexShrink: 0, background: isLoading ? 'var(--text3)' : 'var(--accent)', borderRadius: 6, padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 11, fontWeight: 600, opacity: loadingChannel && !isLoading ? 0.5 : 1 }}>
                   {isLoading ? 'Loading...' : <><Icon name="play" size={12} /> Play</>}
                 </span>
               </div>
               <div style={{ flex: 1, display: 'flex', gap: 2 }}>
                 {programs.length > 0 ? programs.map(p => {
                   const isCurrent = new Date(p.start) <= now && new Date(p.end) >= now;
                   return <div key={p.id} className={isCurrent ? 'livetv-guide-program-current' : undefined} style={{ flex: 1, background: isCurrent ? 'rgba(229,9,20,0.12)' : 'var(--surface)', padding: '6px 10px', borderLeft: isCurrent ? '2px solid var(--accent)' : '1px solid var(--border)', minWidth: 120 }}>
                     <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</div>
                     <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2 }}>{fmt(p.start)} – {fmt(p.end)}</div>
                   </div>;
                 }) : <div style={{ flex: 1, background: 'var(--surface)', padding: '6px 10px' }}><span style={{ fontSize: 11, color: 'var(--text3)' }}>No guide data</span></div>}
               </div>
             </div>
           );
         })}
       </div>
     )}
     </div>
   </div>
 );
}

function DiscoverView({ onItemClick, showToast }) {
 const [tab, setTab] = useState('movies');
 const [query, setQuery] = useState('');
 const [booksQuery, setBooksQuery] = useState('');
 const [results, setResults] = useState([]);
 const [total, setTotal] = useState(0);
 const [page, setPage] = useState(1);
 const [loading, setLoading] = useState(false);
 const [loadingMore, setLoadingMore] = useState(false);
 const [discoverErr, setDiscoverErr] = useState('');
 const searchTimer = useRef();
 const discoverFetchSeq = useRef(0);
 const discoverAppendRef = useRef(false);
 /** Movies and TV open on themed rows. Searching still shows the ordinary grid, and so does a failed load. */
 const [shelves, setShelves] = useState({});
 const shelfKind = tab === 'movies' ? 'movie' : tab === 'shows' ? 'show' : null;
 const shelfRows = shelfKind && !query.trim() ? shelves[shelfKind] : null;
 const shelvesWaiting = !!shelfKind && !query.trim() && shelves[shelfKind] === undefined;
 const showShelves = shelvesWaiting || !!shelfRows?.length;

 useEffect(() => {
   if (!shelfKind || shelves[shelfKind] !== undefined) return;
   let cancelled = false;
   fetch(`/api/discover/shelves?kind=${shelfKind}`)
     .then((r) => (r.ok ? r.json() : []))
     .catch(() => [])
     .then((rows) => {
       if (!cancelled) setShelves((prev) => ({ ...prev, [shelfKind]: Array.isArray(rows) ? rows : [] }));
     });
   return () => {
     cancelled = true;
   };
 }, [shelfKind, shelves]);

 useEffect(() => {
   setQuery('');
   setBooksQuery('');
   discoverFetchSeq.current += 1;
   if (tab === 'books' || tab === 'roms') return;
   loadContent(tab, '', 1, true);
 }, [tab]);

 const loadContent = (t, q, p, reset = false) => {
   if (t === 'books' || t === 'roms') return;
   if (reset) {
     discoverAppendRef.current = false;
     setLoading(true);
   } else {
     if (discoverAppendRef.current) return;
     discoverAppendRef.current = true;
     setLoadingMore(true);
   }
   let endpoint;
   if (t === 'movies') endpoint = `/api/discover/movies?page=${p}${q ? `&query=${encodeURIComponent(q)}` : ''}`;
   else if (t === 'shows') endpoint = `/api/discover/shows?page=${p}${q ? `&query=${encodeURIComponent(q)}` : ''}`;
   else if (t === 'music') endpoint = `/api/discover/music?page=${p}${q ? `&query=${encodeURIComponent(q)}` : ''}`;
   else if (t === 'audiobooks')
     endpoint = q.trim()
       ? `/api/discover/bookshelf-search?shelf=audiobook&q=${encodeURIComponent(q.trim())}`
       : `/api/discover/audiobooks?page=${p}`;
   else if (t === 'podcasts')
     endpoint = q.trim()
       ? `/api/discover/podcasts?q=${encodeURIComponent(q.trim())}`
       : `/api/discover/podcasts?page=${p}`;
   else endpoint = `/api/discover/movies?page=${p}`;
   const seq = ++discoverFetchSeq.current;
   fetch(endpoint)
     .then((r) => r.json())
     .then((data) => {
       if (seq !== discoverFetchSeq.current) return;
       const items = data.items || [];
       /**
        * The trending list moves while you scroll, so the next page often repeats titles from the last one
        * (movies and TV shows alike). Keep the first copy of each title only.
        */
       const keyOf = (it) => `${it.type || ''}:${it.tmdbId ?? it.id ?? it.personId ?? `${it.title || it.name || ''}|${it.year || ''}`}`;
       setResults((prev) => {
         const seen = new Set();
         const out = [];
         for (const it of reset ? items : [...prev, ...items]) {
           const k = keyOf(it);
           if (seen.has(k)) continue;
           seen.add(k);
           out.push(it);
         }
         return out;
       });
       setTotal(data.total || items.length);
       setPage(p);
       setDiscoverErr(typeof data.error === 'string' ? data.error : '');
     })
     .catch(() => {})
     .finally(() => {
       discoverAppendRef.current = false;
       if (seq !== discoverFetchSeq.current) return;
       setLoading(false);
       setLoadingMore(false);
     });
 };

 const handleSearch = (val) => {
   setQuery(val);
   clearTimeout(searchTimer.current);
   searchTimer.current = setTimeout(() => loadContent(tab, val, 1, true), 400);
 };

 /** After data loads, land on first poster — not the header search (reachable with ArrowUp). */
 useEffect(() => {
   if (tab === 'books' || tab === 'roms') return;
   if (loading) return;
   const id = requestAnimationFrame(() => {
     /** Typing in the search box: results reload after every pause, and jumping to the posters closed the TV keyboard mid-word. */
     const a = document.activeElement;
     if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA')) return;
     const grid = document.querySelector('[data-zflix-discover-grid]');
     if (!grid?.querySelector('[role="button"]')) return;
     focusZflixDiscoverGrid();
   });
   return () => cancelAnimationFrame(id);
 }, [tab, loading, shelvesWaiting]);

 useEffect(() => {
   if (tab !== 'books' && tab !== 'roms') return;
   const id = requestAnimationFrame(() =>
     requestAnimationFrame(() => {
       if (tab === 'books') focusZflixDiscoverGrid();
       else focusZflixRomsPrimary();
     }),
   );
   return () => cancelAnimationFrame(id);
 }, [tab]);

 const headerSearchPlaceholder =
   tab === 'books'
     ? 'Search eBooks…'
     : tab === 'audiobooks'
       ? 'Search audiobooks…'
       : tab === 'podcasts'
         ? 'Search podcasts…'
         : tab === 'movies' || tab === 'shows'
         ? 'Search titles or actors…'
         : tab === 'music'
           ? 'Search artists…'
           : 'Search anything...';
 const headerSearchValue = tab === 'books' ? booksQuery : query;
 /** Audiobooks typed search uses Readarr (:8788); podcast search uses Podcast Index — no infinite scroll in those modes. */
 const audiobookBookshelfQuery = tab === 'audiobooks' && query.trim();
 const podcastDiscoverQuery = tab === 'podcasts' && query.trim();

 const tabBtns = [['movies', 'Movies'], ['shows', 'TV Shows'], ['music', 'Music'], ['audiobooks', 'Audiobooks'], ['podcasts', 'Podcasts'], ['books', 'eBooks'], ['roms', 'Roms']];

 return (
   <div data-zflix-discover-root="true">
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap', rowGap: 12 }}>
       <h2 style={{ fontSize: 24, fontWeight: 700, flexShrink: 0, margin: 0 }}>Discover</h2>
       <div
         data-zflix-linear-tab-strip
         className="zflix-discover-tab-strip"
         style={{
           display: 'flex',
           gap: 8,
           flexWrap: 'nowrap',
           alignItems: 'center',
           /** A full row of its own when the screen is too narrow for the tabs and the search box together. */
           flex: '1 1 560px',
           minWidth: 0,
           order: 3,
           overflowX: 'auto',
           scrollbarWidth: 'none',
           msOverflowStyle: 'none',
           WebkitOverflowScrolling: 'touch',
           paddingBottom: 2,
         }}
       >
         {tabBtns.map(([id, label]) => (
           <button key={id} type="button" aria-current={tab === id ? 'page' : undefined} onClick={() => setTab(id)} style={{ flex: 'none', whiteSpace: 'nowrap', padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: tab === id ? 600 : 400, background: tab === id ? 'var(--accent)' : 'var(--surface)', color: tab === id ? '#fff' : 'var(--text2)', border: 'none', cursor: 'pointer' }}>{label}</button>
         ))}
       </div>
       {tab !== 'roms' ? (
         <div data-zflix-horizontal-controls style={{ flexShrink: 0, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px' }}>
           <Icon name="search" size={14} />
           <input
             style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 13, width: 180 }}
             placeholder={headerSearchPlaceholder}
             value={headerSearchValue}
             onChange={(e) => {
               const v = e.target.value;
               if (tab === 'books') setBooksQuery(v);
               else handleSearch(v);
             }}
           />
           {headerSearchValue ? (
             <button
               type="button"
               style={{ color: 'var(--text3)', padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
               onClick={() => {
                 if (tab === 'books') setBooksQuery('');
                 else {
                   setQuery('');
                   loadContent(tab, '', 1, true);
                 }
               }}
               aria-label="Clear search"
             >
               <Icon name="close" size={14} />
             </button>
           ) : null}
         </div>
       ) : null}
     </div>
     {tab === 'books' ? (
       <BooksDiscoverView
         onItemClick={(item) => onItemClick({ ...item, zflixBookshelfShelf: 'ebook' })}
         showToast={showToast}
         searchQuery={booksQuery}
       />
     ) : tab === 'roms' ? (
       <RomsDiscoverView showToast={showToast} />
     ) : (
       <>
         {discoverErr ? (
           tab === 'podcasts' && /podcast index.*not configured|podcast_index_api/i.test(discoverErr) ? (
             <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>
               Podcast search needs free API keys.{' '}
               <a href="https://api.podcastindex.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
                 Get keys at api.podcastindex.org
               </a>
               {' '}→ add <code style={{ fontSize: 12 }}>PODCAST_INDEX_API_KEY</code> and{' '}
               <code style={{ fontSize: 12 }}>PODCAST_INDEX_API_SECRET</code> to <code style={{ fontSize: 12 }}>zfin/.env</code>, then restart the backend.
             </div>
           ) : (
             <div style={{ fontSize: 13, color: 'var(--accent)', marginBottom: 12 }}>{discoverErr}</div>
           )
         ) : null}
         {!loading && !discoverErr && results.length === 0 && tab === 'podcasts' && query.trim() ? (
           <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>No matches.</div>
         ) : null}
         {showShelves ? (
           <div data-zflix-discover-grid>
             {shelvesWaiting ? <MediaRow title="Popular Right Now" items={[]} onItemClick={onItemClick} loading /> : null}
             {(shelfRows || []).map((row) => (
               <MediaRow key={row.key} title={row.title} items={row.items} onItemClick={onItemClick} />
             ))}
           </div>
         ) : (
         <>
         <div data-zflix-discover-grid>
         <div className="card-grid">
           {loading ? [...Array(20)].map((_, i) => <div key={i} style={{ borderRadius: 8, background: 'var(--surface)' }}><div style={{ aspectRatio: '2/3', background: 'var(--bg3)', animation: 'pulse 1.5s infinite', borderRadius: 8 }} /></div>) :
             results.map((item) => (
               <MediaCard
                 key={item.listKey || item.id || item.tmdbId || item.tmdbPersonId || item.openLibraryKey || `${item.title}-${item.author || ''}-${item.year}`}
                 item={item}
                 onClick={(clicked) =>
                   onItemClick(
                     tab === 'audiobooks' ? { ...clicked, zflixBookshelfShelf: 'audiobook' } : clicked,
                   )
                 }
               />
             ))}
         </div>
         </div>
         {/* Music browsing pages through the charts (top chart, then genre charts); a music search is a single page. */}
         {!(tab === 'music' && query.trim()) && !audiobookBookshelfQuery && !podcastDiscoverQuery && (
           <>
             {loadingMore && results.length > 0 ? (
               <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
             ) : null}
             {!loading && results.length > 0 ? (
               <InfiniteScrollSentinel
                 hasMore={results.length < total}
                 loading={loadingMore || loading}
                 onLoad={() => {
                   if (loadingMore || loading || results.length >= total) return;
                   loadContent(tab, query, page + 1, false);
                 }}
               />
             ) : null}
           </>
         )}
         </>
         )}
       </>
     )}
   </div>
 );
}

const PHOTO_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'recent', label: 'Recent' },
  { id: 'favorites', label: 'Favorites' },
  { id: 'albums', label: 'Albums' },
  { id: 'people', label: 'People' },
  { id: 'places', label: 'Places' },
];

const BROWSE_GROUPS_PAGE = 10;
const BROWSE_PHOTOS_PER_GROUP = 24;

function PhotoGroupSection({ group, roundThumb, onSelectPhoto, onLoadMore, loadingMore }) {
 if (!group.photos?.length) return null;
 return (
   <section data-zflix-photo-group style={{ marginBottom: 28 }}>
     <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
       {group.thumbnailUrl ? (
         <img
           src={group.thumbnailUrl}
           alt=""
           style={{
             width: roundThumb ? 48 : 56,
             height: roundThumb ? 48 : 56,
             borderRadius: roundThumb ? '50%' : 8,
             objectFit: 'cover',
             flexShrink: 0,
           }}
           onError={(e) => { e.target.style.display = 'none'; }}
         />
       ) : (
         <div style={{ width: 56, height: 56, borderRadius: roundThumb ? '50%' : 8, background: 'var(--bg3)', flexShrink: 0 }} />
       )}
       <div style={{ flex: '1 1 auto', minWidth: 0 }}>
         <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</div>
         <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{group.totalPhotos ?? group.photos.length} photos</div>
       </div>
       {group.hasMorePhotos ? (
         <Btn variant="ghost" onClick={() => onLoadMore(group)} disabled={loadingMore} style={{ padding: '5px 10px', fontSize: 11, flexShrink: 0 }}>
           {loadingMore ? 'Loading…' : 'Load more'}
         </Btn>
       ) : null}
     </div>
     <div className="media-row" data-zflix-photos-group-row>
       {group.photos.map((p) => (
         <img
           key={p.id}
           role="button"
           tabIndex={0}
           alt={p.city ? `${p.city}` : group.name}
           src={p.url}
           style={{ flexShrink: 0, width: 140, height: 140, objectFit: 'cover', cursor: 'pointer', borderRadius: 6 }}
           onClick={() => onSelectPhoto(p)}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectPhoto(p); } }}
           onError={(e) => { e.target.style.display = 'none'; }}
         />
       ))}
     </div>
   </section>
 );
}

const postJson = (url, body) => fetch(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(async (r) => ({ ok: r.ok, data: await r.json().catch(() => ({})) }));

const photoChip = (on) => ({ padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: on ? 'var(--accent)' : 'var(--bg3)', color: '#fff' });

/** A pop-up the remote can work: arrows move between its buttons, OK presses, Back closes it and returns to the button that opened it. */
function PhotoDialog({ label, onClose, children }) {
 const ref = useRef(null);
 useEffect(() => {
   const opener = document.activeElement;
   if (ref.current) focusZflixModalPrimary(ref.current);
   const earlier = window.__zflixOwnBack;
   window.__zflixOwnBack = () => {
     onClose();
     return true;
   };
   return () => {
     window.__zflixOwnBack = earlier;
     if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
   };
 }, []);
 return (
   <div data-zflix-modal-root style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
     <div ref={ref} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, width: '100%', maxWidth: 420, maxHeight: '88vh', overflowY: 'auto', outline: 'none' }} onClick={(e) => e.stopPropagation()}>
       <div style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 12 }}>{label}</div>
       {children}
     </div>
   </div>
 );
}

/** Number pad instead of a text box, so a PIN takes only the arrows and OK on a TV remote. */
function PhotoPinPad({ label, note, error, busy, onSubmit, onClose, extra }) {
 const [pin, setPin] = useState('');
 const key = { height: 44, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 19, fontWeight: 700, cursor: 'pointer' };
 const add = (d) => setPin((p) => (p.length < 8 ? p + d : p));
 return (
   <PhotoDialog label={label} onClose={onClose}>
     <div aria-label={`${pin.length} digits entered`} style={{ textAlign: 'center', fontSize: 22, letterSpacing: 8, minHeight: 30, marginBottom: 4 }}>{pin ? '●'.repeat(pin.length) : <span style={{ fontSize: 13, letterSpacing: 0, color: 'var(--text2)' }}>{note}</span>}</div>
     {error ? <div style={{ color: '#ff8a8a', fontSize: 13, textAlign: 'center', marginBottom: 6 }}>{error}</div> : null}
     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8 }}>
       {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
         <button key={d} type="button" style={key} {...(d === '5' ? { 'data-zflix-modal-primary-action': '' } : {})} onClick={() => add(d)}>{d}</button>
       ))}
       <button type="button" style={{ ...key, fontSize: 14 }} onClick={() => setPin((p) => p.slice(0, -1))}>Erase</button>
       <button type="button" style={key} onClick={() => add('0')}>0</button>
       <button type="button" style={{ ...key, fontSize: 14, background: 'var(--accent)', color: '#fff', border: 'none' }} disabled={busy} onClick={() => { if (pin.length >= 4) onSubmit(pin); }}>{busy ? '…' : 'OK'}</button>
     </div>
     <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
       {extra}
       <button type="button" style={photoChip(false)} onClick={onClose}>Cancel</button>
     </div>
   </PhotoDialog>
 );
}

/** Which albums each other profile can see, and the PIN that keeps the rest of the photos to this profile. */
function PhotoOwnerPanels({ hasPin, onChanged }) {
 const [open, setOpen] = useState('');
 const [sharing, setSharing] = useState(null);
 const [msg, setMsg] = useState('');
 const [busy, setBusy] = useState(false);
 useEffect(() => {
   if (open !== 'share') return;
   fetch('/api/photos/sharing', { credentials: 'include' }).then((r) => r.json()).then(setSharing).catch(() => setSharing({ albums: [], profiles: [] }));
 }, [open]);
 const toggle = async (album, profileId) => {
   const on = album.profiles.includes(profileId);
   const profileIds = on ? album.profiles.filter((x) => x !== profileId) : [...album.profiles, profileId];
   setSharing((s) => ({ ...s, albums: s.albums.map((a) => (a.id === album.id ? { ...a, profiles: profileIds } : a)) }));
   await postJson('/api/photos/sharing', { albumId: album.id, profileIds });
 };
 const savePin = async (next) => {
   setBusy(true);
   const r = await postJson('/api/photos/pin', { pin: next });
   setBusy(false);
   if (r.ok) {
     setOpen('');
     setMsg(next ? 'PIN saved.' : 'PIN removed.');
     onChanged();
   } else setMsg(r.data.error || 'That did not work');
 };
 return (
   <div style={{ marginBottom: 14 }}>
     <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
       <span style={{ fontSize: 13, color: 'var(--text2)' }}>Private to your profile</span>
       <button type="button" style={photoChip(false)} onClick={() => setOpen('share')}>Share albums</button>
       <button type="button" style={photoChip(false)} onClick={() => { setMsg(''); setOpen('pin'); }}>{hasPin ? 'Change PIN' : 'Set a PIN'}</button>
       {msg ? <span style={{ fontSize: 13, color: 'var(--text2)' }}>{msg}</span> : null}
     </div>
     {open === 'pin' ? (
       <PhotoPinPad
         label={hasPin ? 'Change your Photos PIN' : 'Set a Photos PIN'}
         note="Pick 4 to 8 digits, then OK"
         error={msg}
         busy={busy}
         onSubmit={savePin}
         onClose={() => setOpen('')}
         extra={hasPin ? <button type="button" style={photoChip(false)} onClick={() => savePin('')}>Remove PIN</button> : null}
       />
     ) : null}
     {open === 'share' ? (
       <PhotoDialog label="Share albums" onClose={() => setOpen('')}>
         <div style={{ textAlign: 'center', marginBottom: 8 }}>
           <button type="button" data-zflix-modal-primary-action style={photoChip(true)} onClick={() => setOpen('')}>Done</button>
         </div>
         {!sharing ? <div style={{ color: 'var(--text2)', fontSize: 13 }}>Loading albums…</div> : null}
         {sharing && !sharing.albums.length ? <div style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center' }}>No albums yet. Make one in Immich, then share it here.</div> : null}
         {sharing?.albums.map((a) => (
           <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
             <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{a.name} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>{a.count}</span></div>
             <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
               {sharing.profiles.map((p) => (
                 <button key={p.id} type="button" aria-pressed={a.profiles.includes(p.id)} style={photoChip(a.profiles.includes(p.id))} onClick={() => toggle(a, p.id)}>{p.name}</button>
               ))}
             </div>
           </div>
         ))}
       </PhotoDialog>
     ) : null}
   </div>
 );
}

/** Photos are the original profile's. Others only see albums shared with them; the server enforces it, not this page. */
function PhotosView() {
 const [access, setAccess] = useState(null);
 const [asking, setAsking] = useState(true);
 const [err, setErr] = useState('');
 const [busy, setBusy] = useState(false);
 const load = useCallback(() => {
   fetch('/api/photos/access', { credentials: 'include' }).then((r) => r.json()).then(setAccess).catch(() => setAccess({ owner: false, shared: 0 }));
 }, []);
 useEffect(() => { load(); }, [load]);
 const unlock = async (pin) => {
   setBusy(true);
   const r = await postJson('/api/photos/unlock', { pin });
   setBusy(false);
   if (r.ok) {
     setErr('');
     load();
   } else setErr(r.data.error || 'Wrong PIN');
 };
 if (!access) return <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Loading photos…</div>;
 if (access.pinRequired) {
   return (
     <div style={{ textAlign: 'center', padding: 50 }}>
       <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
       <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Photos are locked</div>
       <Btn onClick={() => setAsking(true)}>Enter PIN</Btn>
       {asking ? <PhotoPinPad label="Enter your Photos PIN" note="Digits, then OK" error={err} busy={busy} onSubmit={unlock} onClose={() => setAsking(false)} /> : null}
     </div>
   );
 }
 if (!access.owner && !access.shared) {
   return (
     <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}>
       <div style={{ fontSize: 40, marginBottom: 10 }}>📷</div>
       No photos have been shared with this profile yet.
     </div>
   );
 }
 return (
   <>
     {access.owner ? <PhotoOwnerPanels hasPin={access.hasPin} onChanged={load} /> : null}
     <PhotosLibrary />
   </>
 );
}

function PhotosLibrary() {
 const currentYear = new Date().getFullYear();
 const [photos, setPhotos] = useState([]);
 const [total, setTotal] = useState(0);
 const [loading, setLoading] = useState(true);
 const [selected, setSelected] = useState(null);
 const [category, setCategory] = useState('all');
 const [city, setCity] = useState('');
 const [yearFrom, setYearFrom] = useState('');
 const [yearTo, setYearTo] = useState('');
 const [locationDraft, setLocationDraft] = useState('');
 const [placeSuggest, setPlaceSuggest] = useState([]);
 const [browseGroups, setBrowseGroups] = useState([]);
 const [browseGroupTotal, setBrowseGroupTotal] = useState(0);
 const [photoPage, setPhotoPage] = useState(1);
 const [photosHasMore, setPhotosHasMore] = useState(false);
 const [photosLoadingMore, setPhotosLoadingMore] = useState(false);
 const [browseHasMoreGroups, setBrowseHasMoreGroups] = useState(false);
 const [browseGroupOffset, setBrowseGroupOffset] = useState(0);
 const [browseLoading, setBrowseLoading] = useState(false);
 const [browseLoadingMoreGroups, setBrowseLoadingMoreGroups] = useState(false);
 const [groupPhotosLoadingId, setGroupPhotosLoadingId] = useState('');

 const isGroupedBrowse =
   (category === 'albums' || category === 'people' || category === 'places')
   && !(category === 'places' && city);

 const searchQs = useMemo(() => {
   const p = new URLSearchParams({ size: '100', page: '1' });
   if (category === 'favorites') p.set('isFavorite', 'true');
   if (city) p.set('city', city);
   if (yearFrom) p.set('takenAfter', `${yearFrom}-01-01T00:00:00.000Z`);
   if (yearTo) p.set('takenBefore', `${yearTo}-12-31T23:59:59.999Z`);
   return p.toString();
 }, [category, city, yearFrom, yearTo]);

 const browseQs = useMemo(() => {
   const p = new URLSearchParams({
     groupBy: category,
     groupsLimit: String(BROWSE_GROUPS_PAGE),
     photosPerGroup: String(BROWSE_PHOTOS_PER_GROUP),
     groupOffset: String(browseGroupOffset),
   });
   if (yearFrom) p.set('takenAfter', `${yearFrom}-01-01T00:00:00.000Z`);
   if (yearTo) p.set('takenBefore', `${yearTo}-12-31T23:59:59.999Z`);
   return p.toString();
 }, [category, browseGroupOffset, yearFrom, yearTo]);

 useEffect(() => {
   if (isGroupedBrowse) return;
   setLoading(true);
   setPhotoPage(1);
   setPhotosHasMore(false);
   fetch(`/api/immich/search?${searchQs}`)
     .then((r) => r.json())
     .then((data) => {
       setPhotos(Array.isArray(data.photos) ? data.photos : []);
       setTotal(data.total || 0);
       setPhotosHasMore(!!data.hasMore);
       setLoading(false);
     })
     .catch(() => { setPhotos([]); setTotal(0); setLoading(false); });
 }, [searchQs, isGroupedBrowse]);

 /** Each screenful is 100; the next one loads on reaching the bottom, until every photo is shown. */
 const loadMorePhotos = () => {
   if (photosLoadingMore || !photosHasMore) return;
   setPhotosLoadingMore(true);
   const qs = new URLSearchParams(searchQs);
   const next = photoPage + 1;
   qs.set('page', String(next));
   fetch(`/api/immich/search?${qs}`)
     .then((r) => r.json())
     .then((data) => {
       const more = Array.isArray(data.photos) ? data.photos : [];
       setPhotos((prev) => [...prev, ...more.filter((p) => !prev.some((x) => x.id === p.id))]);
       setPhotoPage(next);
       setPhotosHasMore(!!data.hasMore);
     })
     .catch(() => setPhotosHasMore(false))
     .finally(() => setPhotosLoadingMore(false));
 };

 useEffect(() => {
   if (!isGroupedBrowse) {
     setBrowseGroups([]);
     setBrowseGroupTotal(0);
     setBrowseHasMoreGroups(false);
     return;
   }
   const reset = browseGroupOffset === 0;
   if (reset) {
     setBrowseLoading(true);
     setBrowseGroups([]);
   } else {
     setBrowseLoadingMoreGroups(true);
   }
   fetch(`/api/immich/browse?${browseQs}`)
     .then((r) => r.json())
     .then((data) => {
       const groups = Array.isArray(data.groups) ? data.groups : [];
       setBrowseGroups((prev) => (reset ? groups : [...prev, ...groups]));
       setBrowseGroupTotal(data.groupTotal || 0);
       setBrowseHasMoreGroups(!!data.hasMoreGroups);
       setBrowseLoading(false);
       setBrowseLoadingMoreGroups(false);
     })
     .catch(() => {
       if (reset) setBrowseGroups([]);
       setBrowseGroupTotal(0);
       setBrowseHasMoreGroups(false);
       setBrowseLoading(false);
       setBrowseLoadingMoreGroups(false);
     });
 }, [browseQs, isGroupedBrowse, browseGroupOffset]);

 useEffect(() => {
   setBrowseGroupOffset(0);
 }, [category, yearFrom, yearTo, city]);

 useEffect(() => {
   const q = locationDraft.trim();
   if (q.length < 2) { setPlaceSuggest([]); return undefined; }
   const t = setTimeout(() => {
     fetch(`/api/immich/places/suggest?q=${encodeURIComponent(q)}`)
       .then((r) => r.json())
       .then((data) => setPlaceSuggest(Array.isArray(data) ? data : []))
       .catch(() => setPlaceSuggest([]));
   }, 280);
   return () => clearTimeout(t);
 }, [locationDraft]);

 useEffect(() => {
   if (!selected) return;
   const onKey = (e) => {
     if (isZflixTvBackKey(e)) {
       setSelected(null);
       if (typeof window !== 'undefined') window.__ZFLIX_ESCAPE_HANDLED = true;
       queueMicrotask(() => focusZflixContentPrimary());
     }
   };
   window.addEventListener('keydown', onKey);
   return () => window.removeEventListener('keydown', onKey);
 }, [selected]);

 const clearFilters = () => {
   setCity('');
   setLocationDraft('');
   setYearFrom('');
   setYearTo('');
   setPlaceSuggest([]);
 };

 const activeFilter = city || yearFrom || yearTo || category === 'favorites';

 const yearOptions = useMemo(() => {
   const yrs = [];
   for (let y = currentYear; y >= currentYear - 30; y -= 1) yrs.push(String(y));
   return yrs;
 }, [currentYear]);

 const pickPlace = (pl) => {
   setCity(pl.name);
   setLocationDraft(pl.name);
 };

 const loadMoreBrowseGroups = () => {
   if (browseLoadingMoreGroups || !browseHasMoreGroups) return;
   setBrowseGroupOffset((prev) => prev + BROWSE_GROUPS_PAGE);
 };

 const loadMoreGroupPhotos = (group) => {
   if (groupPhotosLoadingId) return;
   const nextPage = Math.floor((group.photos?.length || 0) / BROWSE_PHOTOS_PER_GROUP) + 1;
   setGroupPhotosLoadingId(group.id);
   const p = new URLSearchParams({
     groupBy: category,
     groupId: group.id,
     photosPerGroup: String(BROWSE_PHOTOS_PER_GROUP),
     photoPage: String(nextPage),
   });
   if (yearFrom) p.set('takenAfter', `${yearFrom}-01-01T00:00:00.000Z`);
   if (yearTo) p.set('takenBefore', `${yearTo}-12-31T23:59:59.999Z`);
   fetch(`/api/immich/browse?${p}`)
     .then((r) => r.json())
     .then((data) => {
       const extra = Array.isArray(data.group?.photos) ? data.group.photos : [];
       setBrowseGroups((prev) => prev.map((g) => (
         g.id === group.id
           ? {
             ...g,
             photos: [...(g.photos || []), ...extra],
             hasMorePhotos: !!data.group?.hasMorePhotos,
             totalPhotos: data.group?.totalPhotos ?? g.totalPhotos,
           }
           : g
       )));
       setGroupPhotosLoadingId('');
     })
     .catch(() => setGroupPhotosLoadingId(''));
 };

 const browsePhotoCount = useMemo(
   () => browseGroups.reduce((sum, g) => sum + (g.photos?.length || 0), 0),
   [browseGroups],
 );

 return (
   <div data-zflix-photos-view>
     <div data-zflix-spatial-toolbar="true" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
       <h2 style={{ fontSize: 24, fontWeight: 700 }}>Photos</h2>
       {isGroupedBrowse && !browseLoading && browseGroupTotal > 0 ? (
         <span style={{ color: 'var(--text2)', fontSize: 12 }}>
           {browseGroupTotal} {category === 'albums' ? 'albums' : category === 'people' ? 'people' : 'places'}
           {browsePhotoCount > 0 ? ` · ${browsePhotoCount} shown` : ''}
         </span>
       ) : null}
       {!isGroupedBrowse && !loading && total > 0 ? <span style={{ color: 'var(--text2)', fontSize: 12 }}>{photos.length}{photosHasMore ? '+' : ''} photos</span> : null}
       {activeFilter ? (
         <Btn variant="ghost" onClick={clearFilters} style={{ padding: '5px 10px', fontSize: 11, marginLeft: 'auto' }}>Clear filters</Btn>
       ) : null}
     </div>
     <div data-zflix-photos-category-strip data-zflix-linear-tab-strip style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
       {PHOTO_CATEGORIES.map((cat) => (
         <button
           key={cat.id}
           type="button"
           aria-current={category === cat.id ? 'page' : undefined}
           onClick={() => {
             setCategory(cat.id);
             if (cat.id !== 'places') { setCity(''); setLocationDraft(''); }
           }}
           style={{
             padding: '5px 11px',
             borderRadius: 6,
             fontSize: 11,
             fontWeight: category === cat.id ? 600 : 400,
             background: category === cat.id ? 'var(--accent)' : 'var(--surface)',
             color: category === cat.id ? '#fff' : 'var(--text2)',
             border: '1px solid var(--border)',
             cursor: 'pointer',
           }}
         >
           {cat.label}
         </button>
       ))}
     </div>
     <div data-zflix-horizontal-controls style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
       <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
         From
         <TvSelect value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} style={{ background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
           <option value="">Any</option>
           {yearOptions.map((y) => <option key={`f-${y}`} value={y}>{y}</option>)}
         </TvSelect>
       </label>
       <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
         To
         <TvSelect value={yearTo} onChange={(e) => setYearTo(e.target.value)} style={{ background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
           <option value="">Any</option>
           {yearOptions.map((y) => <option key={`t-${y}`} value={y}>{y}</option>)}
         </TvSelect>
       </label>
       <div style={{ position: 'relative', flex: '1 1 160px', minWidth: 140, maxWidth: 280 }}>
         <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px' }}>
           <Icon name="search" size={14} />
           <input
             style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 13, width: '100%' }}
             placeholder="City or place…"
             value={locationDraft}
             onChange={(e) => { setLocationDraft(e.target.value); if (!e.target.value.trim()) setCity(''); }}
             onKeyDown={(e) => { if (e.key === 'Enter' && locationDraft.trim()) { setCity(locationDraft.trim()); setPlaceSuggest([]); } }}
           />
         </div>
         {placeSuggest.length > 0 ? (
           <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflow: 'auto' }}>
             {placeSuggest.map((pl) => (
               <button
                 key={`${pl.name}-${pl.state || ''}`}
                 type="button"
                 onClick={() => { pickPlace(pl); setPlaceSuggest([]); }}
                 style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', color: 'var(--text)', fontSize: 12, cursor: 'pointer' }}
               >
                 {pl.name}{pl.state ? `, ${pl.state}` : ''}
               </button>
             ))}
           </div>
         ) : null}
       </div>
     </div>
     {city ? (
       <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
         Place: {city}
       </div>
     ) : null}
     {isGroupedBrowse ? (
       <>
         {browseLoading ? <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Loading {category}…</div> : null}
         {!browseLoading && browseGroups.length === 0 ? (
           <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}>
             <div style={{ fontSize: 40, marginBottom: 10 }}>📷</div>
             {category === 'albums' ? 'No albums with photos.' : category === 'people' ? 'No named faces yet.' : 'No geotagged cities yet.'}
           </div>
         ) : null}
         <div data-zflix-photos-browse-groups>
           {browseGroups.map((g) => (
             <PhotoGroupSection
               key={g.id}
               group={g}
               roundThumb={category === 'people'}
               onSelectPhoto={setSelected}
               onLoadMore={loadMoreGroupPhotos}
               loadingMore={groupPhotosLoadingId === g.id}
             />
           ))}
         </div>
         {browseHasMoreGroups ? (
           <InfiniteScrollSentinel
             hasMore={browseHasMoreGroups}
             loading={browseLoadingMoreGroups}
             onLoad={loadMoreBrowseGroups}
           />
         ) : null}
         {browseLoadingMoreGroups ? <div style={{ textAlign: 'center', padding: 14, color: 'var(--text3)', fontSize: 12 }}>Loading more…</div> : null}
       </>
     ) : (
       <>
         {loading && <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Loading photos…</div>}
         {!loading && photos.length === 0 && (
           <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}>
             <div style={{ fontSize: 40, marginBottom: 10 }}>📷</div>
             {category === 'places' && !city ? 'Search by place above or browse grouped cities.' : 'No photos match these filters.'}
           </div>
         )}
         <div className="card-grid" data-zflix-photos-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 4 }}>
           {photos.map((p) => (
             <img
               key={p.id}
               role="button"
               tabIndex={0}
               alt={p.city ? `${p.city}` : ''}
               src={p.url}
               style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', cursor: 'pointer', borderRadius: 4 }}
               onClick={() => setSelected(p)}
               onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(p); } }}
               onError={(e) => { e.target.style.display = 'none'; }}
             />
           ))}
         </div>
         {!loading && photos.length > 0 ? <InfiniteScrollSentinel hasMore={photosHasMore} loading={photosLoadingMore} onLoad={loadMorePhotos} /> : null}
       </>
     )}
     {selected ? (
       <div data-zflix-photos-viewer style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelected(null)}>
         <img src={selected.fullUrl || selected.url} alt="" style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: 8 }} />
         <button type="button" aria-label="Close" style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }} onClick={() => setSelected(null)}><Icon name="close" size={18} /></button>
       </div>
     ) : null}
   </div>
 );
}

export default function App() {
 const [auth, setAuth] = useState({ loading: true, ok: false, disabled: true });
 /** A game plays on a page of its own and comes back here through #games; an update reload names the page it left. */
 const [section, setSection] = useState(() => {
   if (window.location.hash === '#games') return 'games';
   if (window.location.hash === '#books') return 'books';
   /** A phone alert for a followed team's game links here: ?watch=<channel id> starts it, ?sports opens Sports. */
   try {
     const q = new URLSearchParams(window.location.search);
     const open = q.get('open');
     if (open && /^[a-f0-9]{32}$/i.test(open)) {
       window.__zflixOpenItem = { id: open, type: q.get('type') === 'show' ? 'show' : 'movie' };
       window.history.replaceState(null, '', window.location.pathname + window.location.hash);
       return q.get('type') === 'show' ? 'shows' : 'movies';
     }
     const w = q.get('watch');
     if (w || q.has('sports')) {
       window.history.replaceState(null, '', window.location.pathname + window.location.hash);
       if (w && /^[a-z0-9-]{6,64}$/i.test(w)) {
         window.__zflixLiveTvPlay = w;
         return 'livetv';
       }
       return 'sports';
     }
   } catch (_) {}
   try {
     const back = window.sessionStorage.getItem('zflix_return_section');
     window.sessionStorage.removeItem('zflix_return_section');
     if (back) return back;
   } catch (_) {}
   return 'home';
 });
 window.__zflixSection = section;
 const [selectedItem, setSelectedItem] = useState(null);
 const [showEpisodes, setShowEpisodes] = useState(null);
 const [showTracks, setShowTracks] = useState(null);
 const [playlistPick, setPlaylistPick] = useState(null);
 const [playingItem, setPlayingItem] = useState(null);
 /** Live TV keeps playing in a small player (docked in the Live TV preview, or in the corner elsewhere). */
 const [playerMini, setPlayerMini] = useState(false);
 const liveMini = !!(playingItem?.liveStreamId && playerMini);
 /** Tell the server this live stream is still being watched, so it can close streams whose app froze or was killed. */
 const liveBeatId = playingItem?.liveStreamId || null;
 const liveBeatSession = playingItem?.playSessionId || null;
 useEffect(() => {
   if (!liveBeatId) return undefined;
   const beat = () => {
     fetch('/api/livetv/heartbeat', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({ liveStreamId: liveBeatId, playSessionId: liveBeatSession }),
     }).catch(() => {});
   };
   beat();
   const iv = setInterval(beat, 30 * 1000);
   return () => clearInterval(iv);
 }, [liveBeatId, liveBeatSession]);

 const stopLiveTv = useCallback(() => {
   setPlayingItem((cur) => {
     if (cur?.liveStreamId) {
       fetch('/api/livetv/stop', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ liveStreamId: cur.liveStreamId, playSessionId: cur.playSessionId }),
       }).catch(() => {});
     }
     return null;
   });
   setPlayerMini(false);
 }, []);
 /**
  * The small live player only exists on the Live TV screen (Zach, 2026-10-05: it could not be closed with a remote
  * elsewhere). Leaving Live TV - or pressing Back from a full-screen channel started from another screen - stops it.
  */
 useEffect(() => {
   if (liveMini && section !== 'livetv') stopLiveTv();
 }, [liveMini, section, stopLiveTv]);
 const [playingTrack, setPlayingTrack] = useState(null);
 const [playingTracks, setPlayingTracks] = useState(null);
 /** "Up next" card shown when an episode finishes: the next episode, or null. */
 const [upNext, setUpNext] = useState(null);
 /** Bumped to start the next episode in a fresh player without closing the screen in between. */
 const [playerGen, setPlayerGen] = useState(0);
 useEffect(() => {
   if (!playingItem) setUpNext(null);
 }, [playingItem]);
 const [playingAudiobook, setPlayingAudiobook] = useState(null);
 const [toast, setToast] = useState(null);
 const [searchQuery, setSearchQuery] = useState('');
 const [showSearch, setShowSearch] = useState(false);
 const [tvSearchOpen, setTvSearchOpen] = useState(false);
 const [tvRemoteOpen, setTvRemoteOpen] = useState(false);
 const [headerSearchOrigin, setHeaderSearchOrigin] = useState('home');
 /** Profiles: { profiles, activeId, max, avatars } from the backend; null until loaded. */
 const [profilesInfo, setProfilesInfo] = useState(null);
 /** This device already has a remembered profile (cookie) that still exists — otherwise ask "Who's watching?". */
 const [profileChosen, setProfileChosen] = useState(false);
 const [profilePickerOpen, setProfilePickerOpen] = useState(false);
 /** null | { mode: 'create' } | { mode: 'edit', profile } */
 const [profileEditor, setProfileEditor] = useState(null);
 const [profileBusy, setProfileBusy] = useState(false);
 /** The old "use the Jellyfin web player" switch is gone; forget it on devices that had it on. */
 useEffect(() => {
   try {
     window.localStorage.removeItem('zflixUseJfWebPlayer');
   } catch (_) {}
 }, []);
 const [forceTvUi, setForceTvUiState] = useState(() => readZflixForceTvUi());
 const splashPreviewMode = useMemo(() => isSplashPreviewQuery(), []);
 const [splashPhase, setSplashPhase] = useState(() => {
   /** A profile switch reloads the app; don't replay the intro for that. */
   try {
     if (window.sessionStorage.getItem('zflix_profile_switch') === '1') {
       window.sessionStorage.removeItem('zflix_profile_switch');
       return 'done';
     }
   } catch (_) {}
   return initialSplashPhase();
 });
 const [splashVariant, setSplashVariant] = useState(() => readSplashVariant());
 const [splashSoundOn, setSplashSoundOn] = useState(() => readSplashSoundEnabled());
 const [activeGameId, setActiveGameId] = useState(null);
 const [mobileNavMoreOpen, setMobileNavMoreOpen] = useState(false);
 const [sidebarOptsOpen, setSidebarOptsOpen] = useState(false);
 const [sidebarOptsPos, setSidebarOptsPos] = useState(
   /** @type {{ top: number; left: number; width: number; maxHeight: number }} */ ({ top: 0, left: 0, width: 260, maxHeight: 280 }),
 );
 const sidebarOptsTriggerRef = useRef(null);
 const sidebarOptsPanelRef = useRef(null);
 const sidebarOptsOpenRef = useRef(false);
 sidebarOptsOpenRef.current = sidebarOptsOpen;
 const tvApkDense = useZflixTvApkDensity();
 /** Leaving Zflix on a TV stops the sound. Phones and tablets keep playing with the screen off. */
 useEffect(() => {
   if (!tvApkDense) return undefined;
   const stop = () => {
     if (document.hidden) document.querySelectorAll('audio, video').forEach((m) => m.pause());
   };
   document.addEventListener('visibilitychange', stop);
   return () => document.removeEventListener('visibilitychange', stop);
 }, [tvApkDense]);

 const showToast = (msg, type = 'info') => setToast({ message: msg, type });
 /** TVs pick up a new build of the site by themselves when nothing is playing. */
 useTvAutoUpdate(tvApkDense, !!(playingItem || playingTrack || playingAudiobook));

 /** Set when this page was opened from a TV's sign-in QR code. */
 const [pairToken, setPairToken] = useState(() => zflixPairTokenFromUrl());
 /**
  * The approval page (and the password page before it) is a small upright form, so hold it in portrait while it is
  * showing, even if the phone is sideways.
  */
 useEffect(() => {
   if (!pairToken) return undefined;
   try {
     const p = window.screen?.orientation?.lock?.('portrait');
     if (p && typeof p.catch === 'function') p.catch(() => {});
   } catch (_) {}
   return () => zflixLetScreenTurn();
 }, [pairToken]);
 /** On start: a phone turns with the phone (see zflixLetScreenTurn). */
 useEffect(() => {
   if (!zflixPairTokenFromUrl()) zflixLetScreenTurn();
 }, []);

 useEffect(() => {
   fetch('/api/auth/status')
     .then((r) => r.json())
     .then((d) => {
       const disabled = !!d.authDisabled;
       const ok = disabled || !!d.authenticated;
       setAuth({ loading: false, ok, disabled });
       if (ok) prefetchZflixJellyfinContext();
     })
     .catch(() => setAuth({ loading: false, ok: false, disabled: false }));
 }, []);

 useEffect(() => {
   const onNeedAuth = () => setAuth((a) => ({ ...a, ok: false }));
   window.addEventListener('zflix-auth-required', onNeedAuth);
   return () => window.removeEventListener('zflix-auth-required', onNeedAuth);
 }, []);

 useEffect(() => {
   if (typeof window === 'undefined') return;
   const params = new URLSearchParams(window.location.search);
   if (params.get('radio')) setSection('radio');
 }, []);

 /** Cold load / refresh: start focus on Home in the rail (TV remotes), after shell + splash. */
 useEffect(() => {
   if (auth.loading || !auth.ok) return undefined;
   if (splashPhase !== 'done') return undefined;
   const id = requestAnimationFrame(() => {
     requestAnimationFrame(() => {
       focusZflixNavHome();
     });
   });
   return () => cancelAnimationFrame(id);
 }, [auth.loading, auth.ok, splashPhase]);

 useLayoutEffect(() => {
   if (!sidebarOptsOpen) return undefined;
   const el = sidebarOptsTriggerRef.current;
   if (!el) return undefined;
   const run = () => {
     const r = el.getBoundingClientRect();
     const margin = 8;
     const railRoot = el.closest?.('.sidebar') || document.querySelector('.sidebar');
     const rail = railRoot?.getBoundingClientRect?.();
     const minLeft = (rail?.left ?? 0) + margin;
     const maxRight = (rail?.right ?? window.innerWidth) - margin;
     const availW = Math.max(160, maxRight - minLeft);
     const width = Math.min(288, Math.max(220, availW));
     let left = r.left;
     if (left + width > maxRight) left = Math.max(minLeft, maxRight - width);
     if (left < minLeft) left = minLeft;
     const maxH = Math.min(320, window.innerHeight - 2 * margin);
     let top = r.bottom + margin;
     if (top + maxH > window.innerHeight - margin) {
       top = Math.max(margin, r.top - maxH - margin);
     }
     if (top < margin) top = margin;
     setSidebarOptsPos({ top, left, width, maxHeight: maxH });
   };
   run();
   window.addEventListener('resize', run);
   window.addEventListener('scroll', run, true);
   return () => {
     window.removeEventListener('resize', run);
     window.removeEventListener('scroll', run, true);
   };
 }, [sidebarOptsOpen]);

 useEffect(() => {
   if (!sidebarOptsOpen) return undefined;
   const onDoc = (e) => {
     const t = sidebarOptsTriggerRef.current;
     const p = sidebarOptsPanelRef.current;
     const node = /** @type {Node} */ (e.target);
     if ((t && t.contains(node)) || (p && p.contains(node))) return;
     setSidebarOptsOpen(false);
   };
   document.addEventListener('mousedown', onDoc);
   document.addEventListener('touchstart', onDoc, { passive: true });
   return () => {
     document.removeEventListener('mousedown', onDoc);
     document.removeEventListener('touchstart', onDoc);
   };
 }, [sidebarOptsOpen]);

 /** The remote moves real focus: put it inside the pop-up, and back on the Options button when it closes. */
 useEffect(() => {
   if (!sidebarOptsOpen) return undefined;
   const id = requestAnimationFrame(() => {
     const first = sidebarOptsPanelRef.current?.querySelector('button, input, [tabindex="0"]');
     if (first) first.focus({ preventScroll: true });
   });
   const trigger = sidebarOptsTriggerRef.current;
   return () => {
     cancelAnimationFrame(id);
     if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
   };
 }, [sidebarOptsOpen]);

 useEffect(() => {
   if (!sidebarOptsOpen) return undefined;
   const onKey = (e) => {
     if (e.key === 'Escape') setSidebarOptsOpen(false);
   };
   window.addEventListener('keydown', onKey, true);
   return () => window.removeEventListener('keydown', onKey, true);
 }, [sidebarOptsOpen]);

 const signOut = async () => {
   setSidebarOptsOpen(false);
   try {
     await fetch('/api/auth/logout', { method: 'POST' });
   } catch {}
   clearZflixShelfCaches();
   clearZflixJellyfinContextCache();
   clearSplashShownThisSession();
   setSplashPhase('pending'); // next sign-in shows intro again
   setAuth({ loading: false, ok: false, disabled: false });
   setPlayingItem(null);
   setZflixPostPlayerFocusTarget(null);
   setPlayingTrack(null);
   setPlayingTracks(null);
   setPlayingAudiobook(null);
   setSelectedItem(null);
 };

 /** When the last popup closes (Back, the X, or after a request), put focus back on the poster that opened it. */
 const anyZflixModalOpen = !!(selectedItem || showEpisodes || showTracks);
 const prevZflixModalOpenRef = useRef(false);
 useEffect(() => {
   const wasOpen = prevZflixModalOpenRef.current;
   prevZflixModalOpenRef.current = anyZflixModalOpen;
   if (wasOpen && !anyZflixModalOpen && !playingItem) {
     requestAnimationFrame(() => focusZflixLastContent());
   }
 }, [anyZflixModalOpen, playingItem]);

 useEffect(() => {
   /**
    * Shared TV / remote Back handling. Android APK calls window.__zflixDispatchTvBack() because synthetic
    * KeyboardEvents are not reliably delivered to listeners in WebView.
    */
   const applyTvBack = (e) => {
     const active = document.activeElement;
     const consume = () => {
       if (e && typeof e.preventDefault === 'function') {
         e.preventDefault();
         e.stopPropagation();
       }
       if (typeof window !== 'undefined') window.__ZFLIX_ESCAPE_HANDLED = true;
     };

     if (typeof window !== 'undefined') window.__ZFLIX_ESCAPE_HANDLED = false;

     /** A screen with its own popup (Live TV category menu) closes it first. */
     if (typeof window !== 'undefined' && typeof window.__zflixOwnBack === 'function' && window.__zflixOwnBack()) {
       consume();
       return;
     }

     /** Profile screens sit above everything. Back closes the editor, then the switcher (not the first-run "Who's watching?"). */
     if (profileEditor) {
       setProfileEditor(null);
       consume();
       return;
     }
     if (profilePickerOpen) {
       setProfilePickerOpen(false);
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
     if (document.querySelector('[data-zflix-profile-picker]')) {
       consume();
       return;
     }

     /** Photos full-screen viewer keeps its own state; the APK's Back never reaches its keydown listener, so close it here. */
     const photoViewer = document.querySelector('[data-zflix-photos-viewer]');
     if (photoViewer) {
       photoViewer.querySelector('button[aria-label="Close"]')?.click();
       consume();
       queueMicrotask(() => focusZflixContentPrimary());
       return;
     }

     if (activeGameId) {
       setActiveGameId(null);
       consume();
       queueMicrotask(() => focusZflixGamesGrid());
       return;
     }

     /** Must run before loose-focus shortcuts — TV WebViews often leave focus on body while Jellyfin plays in an iframe. */
     if (playingItem && !liveMini) {
       /** The Zflix player closes its own Audio / Subtitles / Quality menu first. */
       if (typeof window.__zflixPlayerBack === 'function' && window.__zflixPlayerBack()) {
         consume();
         return;
       }
       /** Live TV: Back shrinks it to the mini player and keeps it running; Stop is on the mini player's controls. */
       if (playingItem.liveStreamId && playingItem._zflixSurface !== 'jf-web') {
         setPlayerMini(true);
         consume();
         setTimeout(() => {
           const row = document.querySelector('[data-ltv-col="channels"] [data-playing="true"]');
           if (row) row.focus();
           else document.querySelector('[data-zflix-live-mini-chip] button')?.focus();
         }, 80);
         return;
       }
       if (playingItem.liveStreamId) {
         fetch('/api/livetv/stop', {
           method: 'POST',
           headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({ liveStreamId: playingItem.liveStreamId, playSessionId: playingItem.playSessionId }),
         });
       }
       setPlayingItem(null);
       consume();
       scheduleFocusAfterPlayerClose();
       return;
     }

     if (sidebarOptsOpenRef.current) {
       setSidebarOptsOpen(false);
       consume();
       return;
     }

     const looseFocus =
       !active ||
       !(active instanceof HTMLElement) ||
       active === document.body ||
       active === document.documentElement;
     if (looseFocus && document.querySelector('.main-content')) {
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
     if (playingAudiobook) {
       setPlayingAudiobook(null);
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
     if (playingTrack) {
       setPlayingTrack(null);
       setPlayingTracks(null);
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
     /** Popups: focus returns to the poster that opened them (see the modal-close effect above). */
     if (showTracks) {
       setShowTracks(null);
       consume();
       return;
     }
     if (showEpisodes) {
       setShowEpisodes(null);
       consume();
       return;
     }
     if (selectedItem) {
       setSelectedItem(null);
       consume();
       return;
     }
     if (showSearch) {
       /** Back out of search returns to the section the search was started from, not Home. */
       setShowSearch(false);
       setSearchQuery('');
       if (section === 'search') setSection(headerSearchOrigin === 'search' ? 'home' : headerSearchOrigin);
       consume();
       /** Wait for the unfiltered section to render, or focus lands on a search result that then scrolls far away. */
       setTimeout(() => {
         const ca = document.querySelector('.content-area');
         if (ca) ca.scrollTop = 0;
         focusZflixContentPrimary();
       }, 350);
       return;
     }
     const mainEl = document.querySelector('.main-content');
     if (
       mainEl?.contains(active) &&
       !active.closest('[role="dialog"]') &&
       !active.closest('[data-zflix-player]')
     ) {
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
     if (shouldMoveFocusFromMainContentToNav(active)) {
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
     if (active?.closest?.('.sidebar') || active?.closest?.('.bottom-nav')) {
       consume();
       return;
     }
     if (document.querySelector('.main-content')) {
       consume();
       queueMicrotask(() => focusZflixNavRail());
       return;
     }
   };

   window.__zflixDispatchTvBack = () => {
     applyTvBack(null);
     return window.__ZFLIX_ESCAPE_HANDLED === true;
   };

   const handleKey = (e) => {
     const active = document.activeElement;
     const tvBack = isZflixTvBackKey(e);
     const backspaceToNav = e.key === 'Backspace' && shouldMoveFocusFromMainContentToNav(active);

     if (!tvBack && !backspaceToNav) return;

     if (tvBack) {
       applyTvBack(e);
       return;
     }

     if (shouldMoveFocusFromMainContentToNav(active)) {
       e.preventDefault();
       queueMicrotask(() => focusZflixNavRail());
     }
   };
   window.addEventListener('keydown', handleKey, true);
   return () => {
     window.removeEventListener('keydown', handleKey, true);
     delete window.__zflixDispatchTvBack;
   };
 }, [playingItem, liveMini, playingAudiobook, playingTrack, showTracks, showEpisodes, selectedItem, showSearch, activeGameId, section, headerSearchOrigin, profileEditor, profilePickerOpen]);

 /** Phone or browser Back: player, then the episode list or details behind it, then Home, then out of Zflix. */
 usePhoneBack(!!((playingItem && !liveMini && playingItem._zflixSurface !== 'jf-web') || activeGameId || showTracks || showEpisodes || selectedItem || showSearch || section !== 'home'), () => {
   if (window.__zflixOwnBack?.()) return;
   if (playingItem && !liveMini) {
     if (window.__zflixPlayerBack?.()) return;
     /** Live TV shrinks to the small player and keeps running, the same as Back on the remote. */
     if (playingItem.liveStreamId) setPlayerMini(true);
     else window.__zflixPlayerClose?.();
     return;
   }
   if (activeGameId) setActiveGameId(null);
   else if (showTracks) setShowTracks(null);
   else if (showEpisodes) setShowEpisodes(null);
   else if (selectedItem) setSelectedItem(null);
   else if (showSearch) {
     setShowSearch(false);
     setSearchQuery('');
     if (section === 'search') setSection(headerSearchOrigin === 'search' ? 'home' : headerSearchOrigin);
   } else setSection('home');
 });

 const handleItemClick = async (item) => {
   if (item.forceDetails) { setSelectedItem({ ...item, forceDetails: false }); return; }
   /**
    * A show on Continue Watching opens its episode list with "Resume" on top (Zach, 2026-10-11): the tile shows which
    * show is in progress, but the episode wanted is not always the one saved.
    */
   if (item.type === 'episode' && item.source === 'jellyfin' && item.seriesId && (item.continueWatching || item.resumeProgress != null)) {
     setSelectedItem(null);
     setShowEpisodes({ id: item.seriesId, title: String(item.title || '').split(' · ')[0] || item.title, source: 'jellyfin', type: 'show', resumeEpisode: item });
     return;
   }
   if (item.type === 'episode' && item.source === 'jellyfin' && item.streamUrl) {
     setSelectedItem(null);
     setPlayingItem(item);
     setZflixPostPlayerFocusTarget(item);
     return;
   }
   if (item.type === 'movie' && item.source === 'jellyfin') {
     try {
       const vr = await fetch(`/api/movies/${encodeURIComponent(item.id)}/variants`, { credentials: 'include' });
       if (vr.ok) {
         const variants = await vr.json();
         const theatrical = variants.theatrical;
         if (theatrical?.id && theatrical.id !== item.id) {
           setSelectedItem({
             ...theatrical,
             streamUrl: theatrical.streamUrl || item.streamUrl,
             poster: theatrical.poster || item.poster,
             backdrop: theatrical.backdrop || item.backdrop,
           });
           return;
         }
       }
     } catch (_) {
       /* fall through */
     }
     setSelectedItem(item);
     return;
   }
   if (item.type === 'show' && item.source === 'jellyfin') {
     setSelectedItem(item);
     return;
   }
   if (item.type === 'song' && item.source === 'jellyfin' && item.streamUrl) {
     const tr = { id: item.id, name: item.title || item.name, streamUrl: item.streamUrl };
     setShowTracks(null);
     setPlayingTrack(tr);
     setPlayingTracks([tr]);
     return;
   }
   if (item.type === 'album' && item.source === 'jellyfin') { setShowTracks(item); return; }
   if (item.type === 'musicartist' && item.source === 'jellyfin') {
     setSelectedItem(item);
     return;
   }
   if (item.source === 'jf-person-search' && item.jfPersonId) {
     try {
       const res = await fetch(`/api/library/jellyfin-person/${encodeURIComponent(item.jfPersonId)}`);
       setSelectedItem(res.ok ? await res.json() : item);
     } catch {
       setSelectedItem(item);
     }
     return;
   }
   if (item.source === 'abs-author-search' && item.absAuthorId) {
     try {
       const res = await fetch(`/api/audiobooks/author/${encodeURIComponent(item.absAuthorId)}`);
       setSelectedItem(res.ok ? await res.json() : item);
     } catch {
       setSelectedItem(item);
     }
     return;
   }
   if (item.source === 'tmdb-person' && item.tmdbPersonId) {
     try {
       const res = await fetch(`/api/discover/person/${encodeURIComponent(item.tmdbPersonId)}`);
       setSelectedItem({ ...item, ...(await res.json()) });
     } catch {
       setSelectedItem(item);
     }
     return;
   }
   if (item.source === 'tmdb') {
     try {
       const endpoint = item.type === 'movie' ? `/api/discover/movies/${item.tmdbId}` : `/api/discover/shows/${item.tmdbId}`;
       const res = await fetch(endpoint);
       setSelectedItem(await res.json());
     } catch { setSelectedItem(item); }
     return;
   }
   if (item.type === 'podcast-episode' && item.showId) {
     setSelectedItem({
       id: item.showId,
       title: item.subtitle || item.showTitle || item.title,
       source: 'audiobookshelf',
       type: 'podcast',
       poster: item.poster,
     });
     return;
   }
   setSelectedItem(item);
 };

 const handlePickLibraryCredit = (row) => {
   setSelectedItem(null);
   queueMicrotask(() => {
     if (row.type === 'show' && row.source === 'jellyfin') {
       setSelectedItem({
         id: row.id,
         title: row.title,
         source: 'jellyfin',
         type: 'show',
         poster: row.poster,
         year: row.year,
       });
       return;
     }
     if (row.type === 'movie' && row.source === 'jellyfin') {
       handleItemClick({
         id: row.id,
         title: row.title,
         source: 'jellyfin',
         type: 'movie',
         poster: row.poster,
         year: row.year,
       });
       return;
     }
     if (row.source === 'audiobookshelf') {
       setSelectedItem({
         id: row.id,
         title: row.title,
         source: 'audiobookshelf',
         type: 'audiobook',
         poster: row.poster,
         year: row.year,
       });
     }
   });
 };

 /** Phones and tablets: get the Downloads page ready for watching with no internet (its own service worker, /offline/ only). */
 useEffect(() => {
   registerOfflinePage();
 }, []);
 /** A "Ready to watch" phone notice links here: ?open=<Jellyfin id>&type=movie|show opens its details once signed in. */
 useEffect(() => {
   const want = window.__zflixOpenItem;
   if (!want || auth.loading || !auth.ok || !profileChosen) return;
   delete window.__zflixOpenItem;
   handleItemClick({ id: want.id, source: 'jellyfin', type: want.type, title: '' });
 }, [auth.loading, auth.ok, profileChosen]);
 const handlePlay = (item, opts = {}) => {
   setSelectedItem(null);
   setPlayerMini(!!opts.mini);
   /** Everything plays in the Zflix player; the Jellyfin web view was taken out (Zach, 2026-11-03: "going backwards"). */
   setPlayingItem({ ...item, _zflixSurface: 'zflix' });
   setZflixPostPlayerFocusTarget(item);
 };
 /* ----- Profiles ----- */
 const loadProfilesInfo = useCallback(async () => {
   try {
     const r = await fetch('/api/profiles', { credentials: 'include' });
     if (!r.ok) return null;
     const d = await r.json();
     setProfilesInfo(d);
     const remembered = readZflixProfileCookie();
     setProfileChosen(!!remembered && (d.profiles || []).some((p) => p.id === remembered));
     return d;
   } catch (_) {
     return null;
   }
 }, []);

 useEffect(() => {
   if (auth.loading || !auth.ok) return;
   loadProfilesInfo();
 }, [auth.loading, auth.ok, loadProfilesInfo]);

 const activeProfile = useMemo(() => {
   const list = profilesInfo?.profiles || [];
   return list.find((p) => p.id === profilesInfo?.activeId) || list.find((p) => p.original) || list[0] || null;
 }, [profilesInfo]);

 /** Pick a profile. The choice is remembered on this device (cookie), like the sign-in. */
 const selectProfile = async (p) => {
   if (!p || profileBusy) return;
   setProfileBusy(true);
   try {
     const wasChosen = profileChosen;
     const previousId = profilesInfo?.activeId;
     const r = await fetch('/api/profiles/select', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({ id: p.id }),
     });
     if (!r.ok) throw new Error('Could not switch profile');
     if (wasChosen && previousId === p.id) {
       setProfilePickerOpen(false);
       return;
     }
     /** Everything on screen (shelves, resume rows, caches) belongs to the previous profile — start clean. */
     try {
       clearZflixShelfCaches();
       clearZflixJellyfinContextCache();
     } catch (_) {}
     if (wasChosen) {
       try {
         window.sessionStorage.setItem('zflix_profile_switch', '1');
       } catch (_) {}
       window.location.reload();
       return;
     }
     setProfilesInfo((prev) => (prev ? { ...prev, activeId: p.id } : prev));
     setProfileChosen(true);
     setProfilePickerOpen(false);
     reloadMyList();
     prefetchZflixJellyfinContext();
     requestAnimationFrame(() => requestAnimationFrame(() => focusZflixNavHome()));
   } catch (e) {
     showToast(e.message || 'Could not switch profile', 'error');
   } finally {
     setProfileBusy(false);
   }
 };

 const saveProfileFromEditor = async ({ name, avatar }) => {
   const editing = profileEditor?.mode === 'edit' ? profileEditor.profile : null;
   const r = await fetch(editing ? `/api/profiles/${encodeURIComponent(editing.id)}` : '/api/profiles', {
     method: editing ? 'PATCH' : 'POST',
     headers: { 'Content-Type': 'application/json' },
     credentials: 'include',
     body: JSON.stringify({ name, avatar }),
   });
   const d = await r.json().catch(() => ({}));
   if (!r.ok) throw new Error(d.error || 'Could not save profile');
   await loadProfilesInfo();
   setProfileEditor(null);
 };

 const deleteProfileFromEditor = async () => {
   const editing = profileEditor?.mode === 'edit' ? profileEditor.profile : null;
   if (!editing) return;
   const wasActive = editing.id === activeProfile?.id;
   const r = await fetch(`/api/profiles/${encodeURIComponent(editing.id)}`, { method: 'DELETE', credentials: 'include' });
   const d = await r.json().catch(() => ({}));
   if (!r.ok) throw new Error(d.error || 'Could not delete profile');
   setProfileEditor(null);
   if (wasActive) {
     /** The profile on screen is gone: forget it on this device and ask who's watching. */
     try {
       clearZflixShelfCaches();
       clearZflixJellyfinContextCache();
       window.sessionStorage.setItem('zflix_profile_switch', '1');
     } catch (_) {}
     window.location.reload();
     return;
   }
   await loadProfilesInfo();
 };

 const handlePlayTrack = (track, tracks) => {
   setShowTracks(null);
   setPlayingTrack(track);
   setPlayingTracks(tracks);
   /** Per-profile "continue listening" for music. */
   if (track?.id != null) {
     fetch('/api/profile/music/played', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({ track }),
       keepalive: true,
     }).catch(() => {});
   }
 };
 const handlePlayEpisode = (ep) => handlePlay(ep);
 const handleNav = (id) => {
   setSidebarOptsOpen(false);
   if (playingItem?.liveStreamId) {
     /** Live TV carries on in the mini player while you browse. */
     setPlayerMini(true);
   } else if (playingItem) {
     setPlayingItem(null);
     setZflixPostPlayerFocusTarget(null);
     scheduleZflixResumeStale();
   }
   setPlayingAudiobook(null);
   setActiveGameId(null);
   setSection(id);
   if (id === 'home' || id === 'movies' || id === 'shows') scheduleZflixResumeStale();
   setSearchQuery('');
   setShowSearch(false);
   /** Mouse / tap: move focus into the pane so :focus-visible does not stick on the rail (e.g. Home “always red”). */
   queueMicrotask(() => {
     requestAnimationFrame(() => {
       if (id === 'discover') focusZflixDiscoverGrid();
       else if (id === 'games') focusZflixGamesGrid();
       else focusZflixContentPrimary();
     });
   });
 };

 const isLibrary =
   selectedItem &&
   selectedItem.source === 'jellyfin' &&
   selectedItem.type !== 'artist' &&
   selectedItem.type !== 'musicartist';

 if (splashPreviewMode) {
   return <SplashPreviewPicker />;
 }

 if (auth.loading) {
   return (
     <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text2)' }}>
       Loading…
     </div>
   );
 }

 if (!auth.ok && !auth.disabled) {
   return (
     <LoginScreen
       onSuccess={() => {
         clearSplashShownThisSession();
         prefetchZflixJellyfinContext();
         requestAnimationFrame(() => {
           setSplashPhase('pending');
           setAuth({ loading: false, ok: true, disabled: false });
         });
       }}
     />
   );
 }

 if (auth.ok && !auth.disabled && pairToken) {
   return (
     <PairApproveScreen
       token={pairToken}
       onDone={() => {
         try {
           window.history.replaceState({}, '', window.location.pathname);
         } catch (_) {}
         setPairToken('');
       }}
     />
   );
 }

 if (auth.ok && !splashPreviewMode && splashPhase !== 'done') {
   return (
     <SplashScreen
       variant={splashVariant}
       playSound={splashSoundOn}
       onComplete={() => setSplashPhase('done')}
     />
   );
 }

 const profileEditorEl = profileEditor ? (
   <ProfileEditor
     key={profileEditor.mode === 'edit' ? profileEditor.profile.id : 'new'}
     profile={profileEditor.mode === 'edit' ? profileEditor.profile : null}
     avatars={profilesInfo?.avatars || Object.keys(PROFILE_AVATARS)}
     usedAvatars={(profilesInfo?.profiles || []).map((p) => p.avatar)}
     onSave={saveProfileFromEditor}
     onDelete={deleteProfileFromEditor}
     onClose={() => setProfileEditor(null)}
   />
 ) : null;

 /** No profile remembered on this device yet (first run, or its profile was deleted): ask before showing anything. */
 if (auth.ok && profilesInfo && !profileChosen) {
   return (
     <Fragment>
       <ProfilePicker
         profiles={profilesInfo.profiles || []}
         max={profilesInfo.max || 4}
         activeId={null}
         busy={profileBusy}
         onPick={selectProfile}
         onAdd={() => setProfileEditor({ mode: 'create' })}
       />
       {profileEditorEl}
       {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
     </Fragment>
   );
 }

 return (
   <div style={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden', background: 'var(--bg)' }}>
     {profilePickerOpen && profilesInfo ? (
       <ProfilePicker
         profiles={profilesInfo.profiles || []}
         max={profilesInfo.max || 4}
         activeId={activeProfile?.id || null}
         busy={profileBusy}
         onPick={selectProfile}
         onAdd={() => setProfileEditor({ mode: 'create' })}
         onClose={() => setProfilePickerOpen(false)}
       />
     ) : null}
     {profileEditorEl}
     <div className="sidebar">
       <div className="sidebar-brand">
         {activeProfile ? (
           <button
             type="button"
             className="zflix-profile-switch"
             data-zflix-profile-switch
             aria-label={`${activeProfile.name} — switch profile`}
             title="Switch profile"
             onClick={() => setProfilePickerOpen(true)}
           >
             <ProfileAvatar avatar={activeProfile.avatar} size={tvApkDense ? 32 : 52} />
             <span className="zflix-profile-switch__text">
               <span className="zflix-profile-switch__name">{activeProfile.name}</span>
               {!tvApkDense ? <span className="zflix-profile-switch__sub">Switch profile</span> : null}
             </span>
           </button>
         ) : (
           <ZFlixLogo size={tvApkDense ? 'small' : 'normal'} />
         )}
       </div>
       <div className="sidebar-scroll">
         <nav className="sidebar-nav">
           {navItems.filter((nav) => nav.id !== 'filters').map(nav => (
           <button
             key={nav.id}
             type="button"
             className="sidebar-nav-btn"
             data-zflix-nav-id={nav.id}
             aria-label={nav.label}
             aria-current={section === nav.id ? 'page' : undefined}
             onClick={() => handleNav(nav.id)}
             onKeyDown={(e) => {
               if (e.key !== 'Enter' && e.key !== ' ') return;
               e.preventDefault();
               handleNav(nav.id);
             }}
             style={{
               display: 'flex',
               alignItems: 'center',
               gap: tvApkDense ? 6 : 12,
               padding: tvApkDense ? '3px 6px' : '12px 18px',
               width: '100%',
               textAlign: 'left',
               color: section === nav.id ? 'var(--text)' : 'var(--text2)',
               background: section === nav.id ? 'rgba(229,9,20,0.1)' : 'transparent',
               border: 'none',
               borderLeft:
                 section === nav.id
                   ? `${tvApkDense ? 3 : 3}px solid var(--accent)`
                   : `${tvApkDense ? 3 : 3}px solid transparent`,
               cursor: 'pointer',
               fontSize: tvApkDense ? 11 : 14,
               fontWeight: section === nav.id ? 600 : 400,
               transition: 'all 0.15s',
               lineHeight: tvApkDense ? 1.2 : undefined,
             }}
           >
             <Icon name={nav.icon} size={18} />
             <span className="sidebar-nav-label">{nav.label}</span>
             {nav.id === 'livetv' && (
               <span
                 className={tvApkDense ? 'sidebar-live-pip' : undefined}
                 style={
                   tvApkDense
                     ? {
                         marginLeft: 'auto',
                         background: 'var(--accent)',
                         color: '#fff',
                         fontSize: 8,
                         fontWeight: 700,
                         padding: '1px 4px',
                         borderRadius: 3,
                       }
                     : {
                         marginLeft: 'auto',
                         background: 'var(--accent)',
                         color: '#fff',
                         fontSize: 9,
                         fontWeight: 700,
                         padding: '1px 5px',
                         borderRadius: 3,
                       }
                 }
               >
                 LIVE
               </span>
             )}
           </button>
         ))}
         </nav>
         <div className="sidebar-footer">
           {/* The profile button took the logo's old spot at the top of the side menu; the brand lives here now. */}
           <div className="sidebar-footer-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
             <ZFlixLogo size="inline" />
             <span>v3.0</span>
           </div>
           <button
             ref={sidebarOptsTriggerRef}
             type="button"
             className="sidebar-footer-options-trigger"
             aria-expanded={sidebarOptsOpen}
             aria-controls="zflix-sidebar-options-panel"
             aria-haspopup="dialog"
             onClick={() => setSidebarOptsOpen((o) => !o)}
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               gap: tvApkDense ? 6 : 8,
               marginTop: tvApkDense ? 2 : 10,
               padding: tvApkDense ? '6px 8px' : '8px 12px',
               width: '100%',
               background: 'var(--bg3)',
               border: '1px solid var(--border)',
               borderRadius: 6,
               color: 'var(--text2)',
               cursor: 'pointer',
               fontSize: tvApkDense ? 10 : 12,
               fontWeight: 500,
             }}
           >
             <Icon name="options" size={tvApkDense ? 16 : 18} />
             <span>Options</span>
           </button>
           {(() => {
             const filtersNav = navItems.find((n) => n.id === 'filters');
             if (!filtersNav) return null;
             return (
               <button
                 type="button"
                 data-zflix-nav-id="filters"
                 aria-label={filtersNav.label}
                 aria-current={section === 'filters' ? 'page' : undefined}
                 onClick={() => handleNav('filters')}
                 onKeyDown={(e) => {
                   if (e.key !== 'Enter' && e.key !== ' ') return;
                   e.preventDefault();
                   handleNav('filters');
                 }}
                 style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: tvApkDense ? 8 : 12,
                   marginTop: tvApkDense ? 6 : 10,
                   padding: tvApkDense ? '6px 10px' : '10px 14px',
                   width: '100%',
                   textAlign: 'left',
                   color: section === 'filters' ? 'var(--text)' : 'var(--text2)',
                   background: section === 'filters' ? 'rgba(229,9,20,0.1)' : 'transparent',
                   border: '1px solid var(--border)',
                   borderLeft: section === 'filters' ? '3px solid var(--accent)' : '3px solid transparent',
                   borderRadius: 6,
                   cursor: 'pointer',
                   fontSize: tvApkDense ? 12 : 13,
                   fontWeight: section === 'filters' ? 600 : 400,
                   transition: 'all 0.15s',
                 }}
               >
                 <Icon name={filtersNav.icon} size={18} />
                 <span>{filtersNav.label}</span>
               </button>
             );
           })()}
           {!auth.disabled && (
             <button
               type="button"
               className="sidebar-sign-out"
               onClick={signOut}
               style={{
                 marginTop: tvApkDense ? 4 : 10,
                 background: 'transparent',
                 border: '1px solid var(--border)',
                 borderRadius: 6,
                 padding: tvApkDense ? '5px 8px' : '6px 10px',
                 color: 'var(--text2)',
                 cursor: 'pointer',
                 fontSize: tvApkDense ? 10 : 11,
                 width: '100%',
               }}
             >
               Sign out
             </button>
           )}
         </div>
       </div>
     </div>

       <div className="main-content">
       <div data-zflix-app-header="true" style={{ height: 'var(--header)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg2)', flexShrink: 0 }}>
         {/* Mobile logo */}
         <div className="mobile-logo" style={{ display: 'none' }}>
           <ZFlixLogo size="small" />
         </div>
         {showSearch ? (
           <div data-zflix-horizontal-controls style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', flex: 1 }}>
             <Icon name="search" size={16} />
             <input
               autoFocus
               style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 14, flex: 1 }}
               placeholder={
                 headerSearchOrigin === 'movies'
                   ? 'Search your movies…'
                   : headerSearchOrigin === 'shows'
                     ? 'Search your TV shows…'
                     : headerSearchOrigin === 'music'
                       ? 'Search your music…'
                       : headerSearchOrigin === 'audiobooks'
                         ? 'Search your audiobooks…'
                         : headerSearchOrigin === 'podcasts'
                           ? 'Search your podcasts…'
                           : 'Search your library — titles, actors, artists, authors…'
               }
               value={searchQuery}
               onChange={(e) => {
                 const v = e.target.value;
                 setSearchQuery(v);
                 if (!HEADER_SEARCH_SECTION_SCOPED.has(headerSearchOrigin) && v.trim()) setSection('search');
               }}
             />
             {/* Phones and computers: show this search on a TV (type it, or say it with the keyboard's mic). */}
             {!tvApkDense && searchQuery.trim() ? (
               <button type="button" aria-label="Show this search on a TV" title="Show on TV" onClick={() => setTvSearchOpen(true)} style={{ color: 'var(--text2)', padding: '0 4px', display: 'inline-flex', alignItems: 'center' }}>
                 <Icon name="tv" size={16} />
               </button>
             ) : null}
             {tvSearchOpen ? <TvSearchSend query={searchQuery.trim()} onClose={() => setTvSearchOpen(false)} onToast={showToast} /> : null}
             <button
               type="button"
               style={{ color: 'var(--text3)', padding: 0 }}
               onClick={() => {
                 setShowSearch(false);
                 setSearchQuery('');
                 if (section === 'search') setSection(headerSearchOrigin === 'search' ? 'home' : headerSearchOrigin);
               }}
             >
               <Icon name="close" size={16} />
             </button>
           </div>
         ) : (
           <div
             data-zflix-horizontal-controls
             style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}
           >
             <span
               style={{
                 fontSize: 16,
                 fontWeight: 700,
                 flex: 1,
                 minWidth: 0,
                 overflow: 'hidden',
                 textOverflow: 'ellipsis',
                 whiteSpace: 'nowrap',
               }}
             >
               {navItems.find((n) => n.id === section)?.label}
             </span>
             {liveMini ? (
               <div
                 data-zflix-live-mini-chip
                 data-zflix-horizontal-controls
                 style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0, minWidth: 0, background: 'rgba(229,9,20,0.14)', border: '1px solid var(--accent)', borderRadius: 6, padding: tvApkDense ? '2px 4px' : '4px 6px' }}
               >
                 <span style={{ fontSize: tvApkDense ? 8 : 11, fontWeight: 700, color: 'var(--accent)' }}>LIVE</span>
                 <span style={{ fontSize: tvApkDense ? 9 : 12, maxWidth: tvApkDense ? 90 : 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{playingItem.title || playingItem.name}</span>
                 <button type="button" onClick={() => setPlayerMini(false)} style={{ fontSize: tvApkDense ? 9 : 12, fontWeight: 600, padding: tvApkDense ? '2px 6px' : '4px 9px', borderRadius: 5, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>
                   Full screen
                 </button>
                 <button type="button" onClick={stopLiveTv} style={{ fontSize: tvApkDense ? 9 : 12, fontWeight: 600, padding: tvApkDense ? '2px 6px' : '4px 9px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}>
                   Stop
                 </button>
               </div>
             ) : null}
             <button
               type="button"
               data-zflix-global-search-trigger="true"
               aria-label={
                 HEADER_SEARCH_SECTION_SCOPED.has(section)
                   ? `Search your ${(navItems.find((n) => n.id === section)?.label || 'library').toLowerCase()}`
                   : 'Search your library'
               }
               style={{
                 display: 'inline-flex',
                 alignItems: 'center',
                 gap: 8,
                 padding: '8px 12px',
                 borderRadius: 6,
                 background: 'rgba(255,255,255,0.08)',
                 color: 'var(--text)',
                 border: 'none',
                 cursor: 'pointer',
                 flexShrink: 0,
                 marginLeft: liveMini ? 8 : 'auto',
               }}
               onClick={() => {
                 setHeaderSearchOrigin(section);
                 setShowSearch(true);
               }}
             >
               <Icon name="search" size={18} />
             </button>
             {/* The phone as a TV remote, one tap from every screen (Zach, 2026-11-04: Options was too hidden). */}
             {!tvApkDense && !forceTvUi ? (
               <button
                 type="button"
                 data-zflix-remote-trigger
                 aria-label="TV remote"
                 title="TV remote"
                 onClick={() => setTvRemoteOpen(true)}
                 style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: 'none', cursor: 'pointer', flexShrink: 0, fontSize: 12, fontWeight: 600 }}
               >
                 <Icon name="tv" size={18} /> Remote
               </button>
             ) : null}
           </div>
         )}
       </div>
       <div
         data-zflix-section={section}
         className={`content-area${section === 'livetv' ? ' content-area-livetv' : ''}`}
         style={{ paddingBottom: playingTrack || playingAudiobook ? 100 : 20 }}
       >
          {section === 'search' && (
            <GlobalSearchView
              query={searchQuery}
              onItemClick={handleItemClick}
              onOpenLive={(q) => {
                window.__zflixLiveTvQuery = q;
                setShowSearch(false);
                setSearchQuery('');
                setSection('livetv');
              }}
              onOpenRecordings={() => {
                setShowSearch(false);
                setSearchQuery('');
                setSection('recordings');
              }}
            />
          )}
         {section === 'home' && (
          <HomeView
            onItemClick={handleItemClick}
            onPlay={handlePlay}
            activeLiveTv={playingItem?.liveStreamId || playingItem?.playSessionId ? playingItem : null}
          />
        )}
         {section === 'movies' && <MoviesView onItemClick={handleItemClick} searchQuery={searchQuery} />}
         {section === 'shows' && <ShowsView onItemClick={handleItemClick} onPlay={handlePlay} searchQuery={searchQuery} />}
         {section === 'music' && <MusicView onItemClick={handleItemClick} searchQuery={searchQuery} onOpenAlbum={(album) => setShowTracks({ ...album, source: 'jellyfin', type: 'album' })} onAddToPlaylist={(ids, label) => setPlaylistPick({ trackIds: ids, trackLabel: label })} onPlayTrack={handlePlayTrack} />}
         {section === 'radio' && <Suspense fallback={null}><RadioView showToast={showToast} /></Suspense>}
         {section === 'audiobooks' && <AudiobooksView onItemClick={handleItemClick} searchQuery={searchQuery} />}
         {section === 'podcasts' && (
           <PodcastsView onItemClick={handleItemClick} searchQuery={searchQuery} />
         )}
         {section === 'photos' && <PhotosView />}
         {section === 'books' && <BooksView />}
         {section === 'livetv' && (
           <LiveTvView
             onPlay={handlePlay}
             nowPlaying={playingItem?.liveStreamId ? playingItem : null}
             playerMini={playerMini}
             onExpand={() => setPlayerMini(false)}
             onStop={stopLiveTv}
           />
         )}
         {section === 'recordings' && <RecordingsView onPlay={(it) => handlePlay(it, { surface: 'zflix' })} onToast={showToast} />}
         {section === 'sports' && <Suspense fallback={null}><SportsView onToast={showToast} onWatch={(channelId) => { window.__zflixLiveTvPlay = channelId; setSection('livetv'); }} /></Suspense>}
         {section === 'games' && (
           <Suspense fallback={null}>
             <GamesView
               activeGameId={activeGameId}
               onSelectGame={setActiveGameId}
               onExitGame={() => {
                 setActiveGameId(null);
                 queueMicrotask(() => focusZflixGamesGrid());
               }}
             />
           </Suspense>
         )}
         {section === 'discover' && <DiscoverView onItemClick={handleItemClick} showToast={showToast} />}
         {section === 'filters' && <VisualFiltersPage showToast={showToast} />}
       </div>
     </div>

     {sidebarOptsOpen &&
       typeof document !== 'undefined' &&
       createPortal(
         <Fragment>
           <div
             className="zflix-sidebar-options-backdrop"
             aria-hidden
             style={{
               position: 'fixed',
               inset: 0,
               zIndex: 1090,
               background: 'rgba(0,0,0,0.22)',
               pointerEvents: 'auto',
             }}
           />
           <div
             id="zflix-sidebar-options-panel"
             data-zflix-modal-root
             ref={sidebarOptsPanelRef}
             className="zflix-sidebar-options-popover sidebar-footer-options"
             role="dialog"
             aria-modal="true"
             aria-label="Options"
             style={{
               position: 'fixed',
               top: sidebarOptsPos.top,
               left: sidebarOptsPos.left,
               width: sidebarOptsPos.width,
               maxHeight: sidebarOptsPos.maxHeight,
               overflowY: 'auto',
               WebkitOverflowScrolling: 'touch',
               zIndex: 1100,
               padding: '12px 12px 10px',
               borderRadius: 10,
               border: '1px solid var(--border)',
               background: 'var(--bg2)',
               boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
               display: 'flex',
               flexDirection: 'column',
               gap: tvApkDense ? 8 : 10,
               boxSizing: 'border-box',
             }}
           >
             {activeProfile ? (
               <button
                 type="button"
                 className="zflix-options-edit-profile"
                 onClick={() => {
                   setSidebarOptsOpen(false);
                   setProfileEditor({ mode: 'edit', profile: activeProfile });
                 }}
                 style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: tvApkDense ? 10 : 12, cursor: 'pointer', textAlign: 'left' }}
               >
                 <ProfileAvatar avatar={activeProfile.avatar} size={tvApkDense ? 20 : 24} />
                 <span>Edit profile ({activeProfile.name})</span>
               </button>
             ) : null}
             <ServerHealthRow dense={tvApkDense} />
             {!tvApkDense && !forceTvUi ? (
               <button
                 type="button"
                 onClick={() => {
                   setSidebarOptsOpen(false);
                   setTvRemoteOpen(true);
                 }}
                 style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
               >
                 📺 TV remote (use this phone)
               </button>
             ) : null}
             {offlineSupported() ? (
               <button
                 type="button"
                 onClick={() => {
                   setSidebarOptsOpen(false);
                   window.location.href = '/offline/';
                 }}
                 style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
               >
                 ⬇ Downloads (watch with no internet)
               </button>
             ) : null}
             {!tvApkDense ? (
               <label
                 tabIndex={0}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' || e.key === ' ') {
                     e.preventDefault();
                     const on = !forceTvUi;
                     setZflixForceTvUi(on);
                     setForceTvUiState(on);
                     applyZflixDesktopBrowserDensity();
                   }
                 }}
                 style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: 8,
                   cursor: 'pointer',
                   fontSize: 11,
                   color: 'var(--text2)',
                   userSelect: 'none',
                 }}
                 title="Override auto layout when needed. Clear: localStorage.removeItem('zflixForceTvUi')."
               >
                 <input
                   type="checkbox"
                   checked={forceTvUi}
                   onChange={(e) => {
                     const on = e.target.checked;
                     setZflixForceTvUi(on);
                     setForceTvUiState(on);
                     applyZflixDesktopBrowserDensity();
                   }}
                 />
                 <span>Compact TV layout (sidebar & shelves)</span>
               </label>
             ) : null}
             <div style={{ borderTop: '1px solid var(--border)', paddingTop: tvApkDense ? 6 : 8, marginTop: 2 }}>
               <div style={{ fontSize: tvApkDense ? 9 : 11, color: 'var(--text2)', marginBottom: 4 }}>Intro style</div>
               <div style={{ fontSize: tvApkDense ? 8 : 10, color: 'var(--text2)', marginBottom: 6, opacity: 0.85 }}>
                 Default: Blox bounce
               </div>
               <TvSelect
                 value={splashVariant}
                 onChange={(e) => {
                   const id = e.target.value;
                   writeSplashVariant(id);
                   setSplashVariant(id);
                 }}
                 style={{
                   width: '100%',
                   padding: '6px 8px',
                   borderRadius: 6,
                   border: '1px solid var(--border)',
                   background: 'var(--bg3)',
                   color: 'var(--text)',
                   fontSize: tvApkDense ? 10 : 12,
                 }}
                 aria-label="Intro animation style"
               >
                 {SPLASH_VARIANT_OPTIONS.map((o) => (
                   <option key={o.id} value={o.id}>
                     {o.label}
                   </option>
                 ))}
               </TvSelect>
               <label
                 tabIndex={0}
                 style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: tvApkDense ? 6 : 8,
                   marginTop: 8,
                   cursor: 'pointer',
                   fontSize: tvApkDense ? 9 : 11,
                   color: 'var(--text2)',
                   userSelect: 'none',
                 }}
               >
                 <input
                   type="checkbox"
                   checked={splashSoundOn}
                   onChange={(e) => {
                     const on = e.target.checked;
                     writeSplashSoundEnabled(on);
                     setSplashSoundOn(on);
                   }}
                 />
                 <span>Intro sound</span>
               </label>
               <button
                 type="button"
                 onClick={() => {
                   setSidebarOptsOpen(false);
                   try {
                     const url = new URL(window.location.href);
                     url.searchParams.set('splashPreview', '1');
                     window.location.assign(url.toString());
                   } catch {
                     window.location.assign('?splashPreview=1');
                   }
                 }}
                 style={{
                   marginTop: 8,
                   width: '100%',
                   padding: '6px 8px',
                   borderRadius: 6,
                   border: '1px solid var(--border)',
                   background: 'var(--bg3)',
                   color: 'var(--text2)',
                   fontSize: tvApkDense ? 9 : 11,
                   cursor: 'pointer',
                 }}
               >
                 Preview all intros…
               </button>
             </div>
           </div>
         </Fragment>,
         document.body
       )}

     <nav className="bottom-nav" aria-label="Main">
       <div className="bottom-nav-grid">
         {navItems
           .filter((nav) => mobileBottomNavDockIds.has(nav.id))
           .map((nav) => (
             <button
               key={nav.id}
               type="button"
               data-zflix-nav-id={nav.id}
               className={`bottom-nav-item${section === nav.id ? ' active' : ''}`}
               onClick={() => {
                 setMobileNavMoreOpen(false);
                 handleNav(nav.id);
               }}
             >
               <Icon name={nav.icon} size={22} />
               <span className="bottom-nav-item-label">{nav.label}</span>
             </button>
           ))}
         <button
           type="button"
           className={`bottom-nav-item${mobileNavMoreOpen || !mobileBottomNavDockIds.has(section) ? ' active' : ''}`}
           aria-expanded={mobileNavMoreOpen}
           aria-haspopup="dialog"
           onClick={() => setMobileNavMoreOpen((o) => !o)}
         >
           <Icon name="options" size={22} />
           <span className="bottom-nav-item-label">More</span>
         </button>
       </div>
     </nav>
     {mobileNavMoreOpen ? (
       <div
         className="bottom-nav-more-backdrop"
         role="presentation"
         onClick={() => setMobileNavMoreOpen(false)}
       >
         <div
           className="bottom-nav-more-sheet"
           role="dialog"
           aria-modal="true"
           aria-label="More sections"
           onClick={(e) => e.stopPropagation()}
         >
           <div className="bottom-nav-more-sheet-header">
             <span>More</span>
             <button type="button" className="bottom-nav-more-close" aria-label="Close" onClick={() => setMobileNavMoreOpen(false)}>
               <Icon name="close" size={18} />
             </button>
           </div>
           <div className="bottom-nav-more-grid">
             {navItems
               .filter((nav) => !mobileBottomNavDockIds.has(nav.id))
               .map((nav) => (
                 <button
                   key={nav.id}
                   type="button"
                   data-zflix-nav-id={nav.id}
                   className={`bottom-nav-more-item${section === nav.id ? ' active' : ''}`}
                   onClick={() => {
                     setMobileNavMoreOpen(false);
                     handleNav(nav.id);
                   }}
                 >
                   <Icon name={nav.icon} size={22} />
                   <span>{nav.label}</span>
                 </button>
               ))}
           </div>
         </div>
       </div>
     ) : null}

     {selectedItem && (
       <DetailModal
         item={selectedItem}
         onClose={() => setSelectedItem(null)}
         onPlay={handlePlay}
         onPlayAudiobook={(session) => setPlayingAudiobook(session)}
         onPickLibraryCredit={handlePickLibraryCredit}
         onBrowseSeriesEpisodes={(series) => {
           setSelectedItem(null);
           setShowEpisodes(series);
         }}
         onRequest={(msg, type, keepOpen) => {
           showToast(msg, type);
           if (!keepOpen) setSelectedItem(null);
         }}
         onOpenAlbum={(album) => {
           setSelectedItem(null);
           setShowTracks({ ...album, artist: album.artist || selectedItem?.title, source: 'jellyfin', type: 'album' });
         }}
         isLibrary={isLibrary}
       />
     )}
     {showEpisodes && <EpisodesModal show={showEpisodes} onClose={() => setShowEpisodes(null)} onPlay={handlePlayEpisode} onToast={showToast} />}
     {showTracks && <AlbumTracksModal album={showTracks} onClose={() => setShowTracks(null)} onPlay={handlePlayTrack} onAddToPlaylist={(ids, label) => setPlaylistPick({ trackIds: ids, trackLabel: label })} />}
     {playlistPick && <PlaylistPickerModal trackIds={playlistPick.trackIds} trackLabel={playlistPick.trackLabel} onClose={() => setPlaylistPick(null)} onDone={() => showToast('Added to playlist', 'success')} />}
     {playingItem &&
       (playingItem._zflixSurface === 'jf-web' ? (
        <JellyfinEmbedPlayer
          item={playingItem}
          onExit={() => {
            setPlayingItem(null);
            scheduleFocusAfterPlayerClose();
          }}
        />
       ) : (
         <Player
           key={playerGen}
           item={playingItem}
           mini={liveMini}
           onExpand={() => setPlayerMini(false)}
           onEnded={(ended) => {
             /** An episode finished: offer the next one (Zach, 2026-10-28: it used to just stop). */
             if (!ended || ended.type !== 'episode' || ended.source !== 'jellyfin' || ended.liveStreamId || ended.id == null) return;
             fetchNextEpisode(ended.id).then((nxt) => {
               if (nxt) setUpNext({ next: nxt, fromId: ended.id });
             });
           }}
           onClose={() => {
             /**
              * Backing out of an episode lands on that show's episode sheet (Resume + pick another), not on the
              * list behind it (Zach, 2026-10-11). The saved position comes from the server, so drop the old one.
              */
             const closed = playingItem;
             const isEpisode = !!closed && closed.type === 'episode' && closed.source === 'jellyfin' && !closed.liveStreamId;
             const sheetWillShow = isEpisode && (!!showEpisodes || !!closed.seriesId);
             if (sheetWillShow) {
               const { _zflixSurface, ...rest } = closed;
               const resumeEpisode = {
                 ...rest,
                 episodeIndex: closed.episodeIndex != null ? closed.episodeIndex : closed.index,
                 streamUrl:
                   typeof closed.streamUrl === 'string'
                     ? closed.streamUrl.replace(/([?&])StartTimeTicks=\d+&?/, '$1').replace(/[?&]$/, '')
                     : closed.streamUrl,
               };
               setSelectedItem(null);
               setShowEpisodes((prev) =>
                 prev
                   ? { ...prev, resumeEpisode }
                   : { id: closed.seriesId, title: String(closed.title || '').split(' · ')[0] || closed.title, source: 'jellyfin', type: 'show', resumeEpisode },
               );
             }
             setPlayingItem(null);
             setPlayerMini(false);
             if (!sheetWillShow) scheduleFocusAfterPlayerClose();
           }}
         />
       ))}
     {upNext && playingItem && playingItem.id === upNext.fromId ? (
       <UpNextOverlay
         next={upNext.next}
         onCancel={() => setUpNext(null)}
         onPlay={(nxt) => {
           setUpNext(null);
           /**
            * A new player in the same render, not none for a moment: closing the player and opening the next one 300 ms
            * later showed the Home screen in between (Zach, 2026-11-03).
            */
           setPlayerGen((g) => g + 1);
           handlePlay(nxt, { surface: 'zflix' });
         }}
       />
     ) : null}
     {playingTrack && <AudioPlayer track={playingTrack} tracks={playingTracks} onClose={() => { setPlayingTrack(null); setPlayingTracks(null); }} />}
     {playingAudiobook && <AudiobookPlayer session={playingAudiobook} onClose={() => setPlayingAudiobook(null)} />}
     {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
     {tvRemoteOpen ? <TvRemote onClose={() => setTvRemoteOpen(false)} /> : null}
     <TvSendReceiver
       enabled={(tvApkDense || forceTvUi) && !auth.loading && auth.ok}
       nowPlayingTitle={playingItem ? playingItem.title || 'Something' : playingTrack ? playingTrack.title || playingTrack.name || 'Music' : playingAudiobook ? 'An audiobook' : null}
       onStop={() => {
         if (playingItem?.liveStreamId) stopLiveTv();
         else setPlayingItem(null);
         setPlayerMini(false);
       }}
       onSearch={(q) => {
         /** A search sent from a phone: the library-wide results, as if typed here. */
         setSelectedItem(null);
         setShowEpisodes(null);
         setHeaderSearchOrigin('search');
         setShowSearch(true);
         setSearchQuery(q);
         setSection('search');
       }}
       onPlay={(sent, cmd) => {
         /**
          * A phone sent a title to this TV. If the TV is still on "Who's watching?", pick the sender's profile
          * (Zach, 2026-10-11: the TV must be reachable from that screen too), then start it.
          */
         if (!profileChosen) {
           const list = profilesInfo?.profiles || [];
           const p = list.find((x) => x.id === cmd?.fromProfileId) || list.find((x) => x.original) || list[0];
           if (p) {
             selectProfile(p).then(() => setTimeout(() => handlePlay(sent, { surface: 'zflix' }), 400));
             return;
           }
         }
         setProfilePickerOpen(false);
         setProfileEditor(null);
         /** Close whatever is open, then start it in the Zflix player. */
         setSelectedItem(null);
         setShowEpisodes(null);
         setShowTracks(null);
         setPlayingTrack(null);
         setPlayingTracks(null);
         setPlayingAudiobook(null);
         const wasPlaying = !!playingItem;
         if (playingItem?.liveStreamId) stopLiveTv();
         else if (wasPlaying) setPlayingItem(null);
         setPlayerMini(false);
         setTimeout(() => handlePlay(sent, { surface: 'zflix' }), wasPlaying ? 500 : 0);
       }}
     />

     <style>{`
       .bottom-nav { display: none; }
       .mobile-logo { display: none; }
       @media(max-width:768px) {
         .bottom-nav { display: block !important; }
         .mobile-logo { display: flex !important; margin-right: 8px; }
         .content-area { padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)) !important; }
       }
       @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
       @keyframes slideUp { from{transform:translateY(30px);opacity:0} to{transform:translateY(0);opacity:1} }
     `}</style>
   </div>
 );
}
